import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import html2pdf from 'html2pdf.js';
import {
  resolveProductCareDollarNumber,
  resolveServiceFeeDollarNumber,
  resolveProductCarePercentNumber,
  resolveServiceFeePercentNumber,
  DEFAULT_PC_PCT,
  DEFAULT_SF_PCT,
  computeComplimentaryPresentation,
  getComplimentaryRowDisplay
} from './utils/calculations';
import {
  captureEvent as posthogCapture,
  identifyClient as posthogIdentify,
  resetClient as posthogReset
} from './services/posthog';
import AcceptProposalFlow from './components/AcceptProposalFlow';
import MaykerTimeInput from './components/MaykerTimeInput';
import {
  getAcceptanceFromProposal,
  getAcceptanceUiState,
  isConfirmationReplayProject,
  submitProposalAcceptance,
  confirmationAndPaymentSentence,
} from './lib/proposalAcceptance';
import { isPendingReviewProposal, proposalMatchesProjectVersion, waitForClientProposalVersion } from './lib/changeRequestDraft';
import { proposalProductImageUrl } from './lib/proposalProductImage';
import {
  ChangeRequestReviewView,
  PendingReviewBanner,
  ProposalViewHeader,
  ProposalWelcomePanel,
  RequestAnotherProduct,
  RequestChangesGuidancePanel,
  RequestChangesModeHeader,
  RequestChangesProductControls,
  RequestChangesStickyBar,
  useRequestChangesDraft,
  useStickyChromeOffset,
} from './components/requestChanges';
import {
  getProposalWelcomeStorageKey,
  hasDismissedProposalWelcome,
  markProposalWelcomeDismissed,
} from './lib/proposalWelcome';
import { formatDisplayDate, formatDisplayDateRange } from './utils/displayDate';

// ============================================
// CONFIGURATION - Using Environment Variables
// ============================================
// IMPORTANT: Set these values in .env file (see .env.example)
// For Vercel deployments, add these in Vercel dashboard > Settings > Environment Variables

// Google Apps Script Web App URLs
const CLIENT_API_URL = process.env.REACT_APP_CLIENT_API_URL || '';
const PROPOSALS_API_URL = process.env.REACT_APP_PROPOSALS_API_URL || '';

// Shopify Storefront API Configuration
const SHOPIFY_STORE = process.env.REACT_APP_SHOPIFY_STORE || 'maykerevents';
const SHOPIFY_STOREFRONT_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN || '';
const SHOPIFY_API_URL = `https://${SHOPIFY_STORE}.myshopify.com/api/2024-01/graphql.json`;

// Client Proposal View URL
const CLIENT_PROPOSAL_VIEW_URL = process.env.REACT_APP_CLIENT_PROPOSAL_VIEW_URL || 'https://reserve.maykerevents.com';

// Public API base for shareable proposal links (getPublishedProposalByProjectNumber - no auth)
const PUBLIC_PROPOSAL_API_BASE = PROPOSALS_API_URL || process.env.REACT_APP_CLIENT_API_URL || 'https://script.google.com/macros/s/AKfycbzB7gHa5o-gBep98SJgQsG-z2EsEspSWC6NXvLFwurYBGpxpkI-weD-HVcfY2LDA4Yz/exec';
const PUBLISHED_PROPOSAL_API = `${process.env.REACT_APP_ADMIN_DASHBOARD_URL || 'https://app.maykerevents.com'}/api/proposals/published`;

async function readPublishedProposalResponse(url) {
  const response = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
  const text = await response.text();
  try {
    const data = JSON.parse(text);
    return {
      ready: response.ok && data && typeof data.success === 'boolean',
      proposal: data && data.success && data.proposal ? data.proposal : null,
    };
  } catch {
    return { ready: false, proposal: null };
  }
}

export async function fetchPublishedProposal(projectNumber, version) {
  const number = String(projectNumber || '').trim();
  if (!number) return null;
  const versionStr = version == null || String(version).trim() === '' ? 'latest' : String(version);
  const dashboardUrl = `${PUBLISHED_PROPOSAL_API}?projectNumber=${encodeURIComponent(number)}&version=${encodeURIComponent(versionStr)}`;
  try {
    const fromDatabase = await readPublishedProposalResponse(dashboardUrl);
    if (fromDatabase.ready) return fromDatabase.proposal;
  } catch {
    /* The dashboard read is not available yet. Use the sheet copy. */
  }
  const sheetUrl = `${PUBLIC_PROPOSAL_API_BASE}?action=getPublishedProposalByProjectNumber&projectNumber=${encodeURIComponent(number)}&version=${encodeURIComponent(versionStr)}`;
  try {
    const fromSheet = await readPublishedProposalResponse(sheetUrl);
    return fromSheet.proposal;
  } catch {
    return null;
  }
}
const CLIENT_ACCEPTANCE_API = 'https://clients.maykerevents.com/api/proposal-acceptance';

/**
 * Parse URL for shareable proposal (query ?projectNumber=&version= or path /client/X/Y).
 * Returns { projectNumber, version } or null.
 */
function parseShareableProposalFromUrl() {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname || '';
  let projectNumber = null;
  let version = 1;
  if (urlParams.has('projectNumber')) {
    projectNumber = urlParams.get('projectNumber');
    version = urlParams.has('version') ? parseInt(urlParams.get('version'), 10) : 1;
  } else {
    const match = pathname.match(/^\/client\/(\d+)(?:\/(\d+))?$/);
    if (match) {
      projectNumber = match[1];
      version = match[2] ? parseInt(match[2], 10) : 1;
    }
  }
  if (!projectNumber || !String(projectNumber).trim()) return null;
  return { projectNumber: String(projectNumber).trim(), version: isNaN(version) ? 1 : version };
}

// ============================================
// STANDARDIZED BUTTON STYLES
// ============================================

// Primary Button - Black background for main CTAs
const primaryButtonStyle = {
  backgroundColor: '#1a1a1a',
  color: 'white',
  border: 'none',
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '400',
  fontFamily: "'NeueHaasUnica', sans-serif",
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  outline: 'none'
};

const primaryButtonHover = (e) => {
  e.currentTarget.style.opacity = '0.8';
};

const primaryButtonLeave = (e) => {
  e.currentTarget.style.opacity = '1';
};

// Secondary Button - Off-white background for secondary actions
const secondaryButtonStyle = {
  padding: '12px 24px',
  backgroundColor: '#FAF8F3',
  color: '#000000',
  border: '1px solid #e8e8e3',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: '600',
  fontFamily: "'NeueHaasUnica', sans-serif",
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  outline: 'none'
};

const secondaryButtonHover = (e) => {
  e.currentTarget.style.backgroundColor = '#f5f5f2';
  e.currentTarget.style.borderColor = '#d1d5db';
  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)';
  e.currentTarget.style.transform = 'translateY(-1px)';
};

const secondaryButtonLeave = (e) => {
  e.currentTarget.style.backgroundColor = '#FAF8F3';
  e.currentTarget.style.borderColor = '#e8e8e3';
  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
  e.currentTarget.style.transform = 'translateY(0)';
};

// Text Link Button - Underlined text for inline actions
const textLinkButtonStyle = {
  background: 'none',
  border: 'none',
  padding: '0',
  fontSize: '10px',
  fontWeight: '400',
  color: '#000000',
  textDecoration: 'underline',
  textUnderlineOffset: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  fontFamily: "'NeueHaasUnica', sans-serif",
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  outline: 'none'
};

const textLinkButtonHover = (e) => {
  e.currentTarget.style.opacity = '0.7';
};

const textLinkButtonLeave = (e) => {
  e.currentTarget.style.opacity = '1';
};

// Small Button - Compact version of secondary
const smallButtonStyle = {
  padding: '8px 16px',
  backgroundColor: '#FAF8F3',
  color: '#000000',
  border: '1px solid #e8e8e3',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: '600',
  fontFamily: "'NeueHaasUnica', sans-serif",
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  outline: 'none'
};

const smallButtonHover = (e) => {
  e.currentTarget.style.backgroundColor = '#f5f5f2';
  e.currentTarget.style.borderColor = '#d1d5db';
  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)';
  e.currentTarget.style.transform = 'translateY(-1px)';
};

const smallButtonLeave = (e) => {
  e.currentTarget.style.backgroundColor = '#FAF8F3';
  e.currentTarget.style.borderColor = '#e8e8e3';
  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
  e.currentTarget.style.transform = 'translateY(0)';
};

// Destructive Button - For remove/delete actions
const destructiveButtonStyle = {
  padding: '8px 16px',
  backgroundColor: '#fee2e2',
  color: '#dc2626',
  border: 'none',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: '500',
  fontFamily: "'NeueHaasUnica', sans-serif",
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  outline: 'none'
};

const destructiveButtonHover = (e) => {
  e.currentTarget.style.backgroundColor = '#fecaca';
  e.currentTarget.style.color = '#b91c1c';
};

const destructiveButtonLeave = (e) => {
  e.currentTarget.style.backgroundColor = '#fee2e2';
  e.currentTarget.style.color = '#dc2626';
};

// ============================================
// CUSTOM MODAL COMPONENTS
// ============================================

// Custom Alert Modal Component
function AlertModal({ message, onClose, isOpen }) {
  const brandCharcoal = '#2C2C2C'; // Footer black color
  
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#F7F6F0',
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        textAlign: 'center'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <img 
            src="/mayker_round-stamp-lines-black.png" 
            alt="Mayker Events" 
            style={{ height: '60px', width: 'auto' }}
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_round-stamp-lines-black.png';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_round-stamp-lines-black.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
        <div style={{
          fontSize: '14px',
          color: brandCharcoal,
          marginBottom: '24px',
          lineHeight: '1.5'
        }}>
          {message}
        </div>
        <button
          onClick={onClose}
          style={{
            ...secondaryButtonStyle,
            width: '100%'
          }}
          onMouseEnter={secondaryButtonHover}
          onMouseLeave={secondaryButtonLeave}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Custom Confirm Modal Component
function ConfirmModal({ message, onConfirm, onCancel, isOpen, title = '', confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmPrimary = false }) {
  const brandCharcoal = '#2C2C2C'; // Footer black color
  
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
    }} onClick={onCancel}>
      <div style={{
        backgroundColor: '#F7F6F0',
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '450px',
        width: '90%',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
        textAlign: 'center',
        position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <img 
            src="/mayker_round-stamp-lines-black.png" 
            alt="Mayker Events" 
            style={{ height: '60px', width: 'auto', display: 'block', margin: '0 auto' }}
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_round-stamp-lines-black.png';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_round-stamp-lines-black.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
        {title ? (
          <div style={{
            fontSize: '15px',
            color: brandCharcoal,
            marginBottom: '10px',
            lineHeight: '1.5',
            fontWeight: 400
          }}>
            {title}
          </div>
        ) : null}
        <div style={{
          fontSize: '14px',
          color: brandCharcoal,
          marginBottom: '30px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap'
        }}>
          {message}
        </div>
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center'
        }}>
          <button
            onClick={onCancel}
            style={{
              ...secondaryButtonStyle,
              flex: 1
            }}
            onMouseEnter={secondaryButtonHover}
            onMouseLeave={secondaryButtonLeave}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              ...(confirmPrimary ? primaryButtonStyle : secondaryButtonStyle),
              flex: 1
            }}
            onMouseEnter={confirmPrimary ? primaryButtonHover : secondaryButtonHover}
            onMouseLeave={confirmPrimary ? primaryButtonLeave : secondaryButtonLeave}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Custom Prompt Modal Component
function PromptModal({ message, placeholder, onConfirm, onCancel, isOpen, defaultValue = '' }) {
  const brandMahogany = '#3E0D12';
  const brandSage = '#545142';
  const brandWheat = '#DABE86';
  const [inputValue, setInputValue] = React.useState(defaultValue);
  
  React.useEffect(() => {
    if (isOpen) {
      setInputValue(defaultValue);
    }
  }, [isOpen, defaultValue]);
  
  if (!isOpen) return null;
  
  const handleConfirm = () => {
    if (inputValue.trim()) {
      onConfirm(inputValue.trim());
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
    }} onClick={onCancel}>
      <div style={{
        backgroundColor: brandWheat,
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: `1px solid ${brandSage}30`
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '16px'
        }}>
          <img 
            src="/mayker_icon-black.png" 
            alt="Mayker Events" 
            style={{ height: '40px', width: '40px' }}
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_icon-black.png';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_icon-black.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
        <div style={{
          fontSize: '14px',
          color: '#000000',
          marginBottom: '16px',
          lineHeight: '1.5'
        }}>
          {message}
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          autoFocus
          style={{
            width: '100%',
            padding: '10px',
            border: `1px solid ${brandSage}40`,
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            color: brandMahogany,
            marginBottom: '24px',
            boxSizing: 'border-box'
          }}
        />
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              backgroundColor: '#F7F6F0',
              color: '#000000',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '400',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              outline: 'none'
            }}
            onMouseEnter={primaryButtonHover}
            onMouseLeave={primaryButtonLeave}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!inputValue.trim()}
            style={{
              ...primaryButtonStyle,
              backgroundColor: inputValue.trim() ? '#1a1a1a' : '#9ca3af',
              color: inputValue.trim() ? '#FFFFFF' : '#FFFFFF',
              opacity: inputValue.trim() ? 1 : 0.6,
              cursor: inputValue.trim() ? 'pointer' : 'not-allowed'
            }}
            onMouseEnter={(e) => {
              if (inputValue.trim()) {
                primaryButtonHover(e);
              }
            }}
            onMouseLeave={(e) => {
              if (inputValue.trim()) {
                primaryButtonLeave(e);
              }
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PASSWORD RESET MODAL
// ============================================

function PasswordResetModal({ isOpen, onClose, initialEmail, showAlert }) {
  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const [email, setEmail] = useState(initialEmail || '');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const brandMahogany = '#3E0D12';
  const brandSage = '#545142';
  const brandWheat = '#DABE86';
  const brandCharcoal = brandMahogany;
  
  useEffect(() => {
    if (isOpen) {
      setStep('request');
      setEmail(initialEmail || '');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setGeneratedToken('');
      setError('');
      setLoading(false);
    }
  }, [isOpen, initialEmail]);
  
  if (!isOpen) return null;
  
  const handleRequestToken = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }
    
    try {
      const result = await authService.requestPasswordReset(email);
      
      if (result.success && result.resetToken) {
        setGeneratedToken(result.resetToken);
        setResetToken(result.resetToken);
        setStep('reset');
        setLoading(false);
      } else {
        setError(result.error || 'Failed to generate reset token');
        setLoading(false);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.message || 'Network error');
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!resetToken || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await authService.resetPassword(email, resetToken, newPassword);
      
      if (result.success) {
        showAlert('Password reset successful! You can now login with your new password.');
        onClose();
      } else {
        setError(result.error || 'Failed to reset password');
        setLoading(false);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || 'Network error');
      setLoading(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: "'NeueHaasUnica', sans-serif"
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '40px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: '1px solid #e5e0d9'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
        
        {step === 'request' ? (
          <form onSubmit={handleRequestToken}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#000000',
              fontFamily: "'Domaine Text', serif"
            }}>
              Reset Your Password
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Enter your email address and we'll generate a reset token for you to use immediately.
            </p>
            
            {error && (
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '24px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000'
              }}>
                Email:
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Enter your email"
                autoFocus
              />
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#000000',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = brandCharcoal;
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...secondaryButtonStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    secondaryButtonHover(e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    secondaryButtonLeave(e);
                  }
                }}
              >
                {loading ? 'Generating...' : 'Generate Reset Token'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#000000',
              fontFamily: "'Domaine Text', serif"
            }}>
              Enter New Password
            </h2>
            
            {generatedToken && (
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#0369a1',
                  marginBottom: '8px'
                }}>
                  Your reset token:
                </p>
                <div style={{
                  backgroundColor: 'white',
                  padding: '10px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  border: '1px solid #d1d5db',
                  marginBottom: '8px'
                }}>
                  {generatedToken}
                </div>
                <p style={{
                  fontSize: '12px',
                  color: '#666',
                  margin: '0',
                  fontStyle: 'italic'
                }}>
                  This token is already filled in below. Enter your new password to complete the reset.
                </p>
              </div>
            )}
            
            {error && (
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '24px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000'
              }}>
                Email:
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Enter your email"
              />
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000'
              }}>
                Reset Token:
              </label>
              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  backgroundColor: generatedToken ? '#f0f9ff' : 'white',
                  fontFamily: generatedToken ? 'monospace' : 'inherit'
                }}
                placeholder="Enter reset token"
              />
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000'
              }}>
                New Password:
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Enter new password (min 6 characters)"
                autoFocus
              />
            </div>
            
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000'
              }}>
                Confirm New Password:
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Confirm new password"
              />
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setResetToken('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#000000',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = brandCharcoal;
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...secondaryButtonStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    secondaryButtonHover(e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    secondaryButtonLeave(e);
                  }
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================
// ANALYTICS SERVICE
// ============================================

const analyticsService = {
  // Track user actions
  trackAction(action, page = '', details = '') {
    // Mirror into PostHog (no-op if unconfigured).
    posthogCapture(action, page, details);

    const token = localStorage.getItem('clientToken');
    if (!token) return; // Don't track if not logged in
    
    // Fire and forget - don't block UI
    fetch('/api/analytics/track-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: token,
        action: action,
        page: page,
        details: details
      })
    }).catch(() => {}); // Silently fail - analytics shouldn't break the app
  },
  
  // Track page/section views
  trackPageView(section) {
    this.trackAction('view_section', `/${section}`, section);
  },
  
  // Track proposal views
  trackProposalView(proposalId) {
    this.trackAction('view_proposal', `/proposal/${proposalId}`, proposalId);
  },
  
  // Track PDF downloads
  trackPDFDownload(proposalId) {
    this.trackAction('download_pdf', `/proposal/${proposalId}`, 'PDF');
  },
  
  // Track proposal status changes
  trackProposalStatusChange(proposalId, status) {
    this.trackAction('change_status', `/proposal/${proposalId}`, status);
  }
};

// ============================================
// AUTHENTICATION SERVICE
// ============================================

const authService = {
  // Login
  async login(email, password) {
    try {
      // Use Vercel API endpoint for login tracking
      const url = new URL('/api/auth/login', window.location.origin);
      url.searchParams.set('email', email);
      url.searchParams.set('password', password);
      
      const requestUrl = url.toString();
      console.log('Making login GET request to Vercel API:', requestUrl.replace(/password=[^&]+/, 'password=***'));
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store' // Prevent browser caching
      });
      
      console.log('Login response status:', response.status);
      console.log('Login response statusText:', response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Login HTTP error:', response.status, response.statusText);
        console.error('Error response text:', errorText);
        
        // Handle 401 (Unauthorized) with user-friendly message
        if (response.status === 401) {
          // Try to parse the error response
          try {
            const errorData = JSON.parse(errorText);
            // If it's an invalid credentials error, show friendly message
            if (errorData.error && (errorData.error.toLowerCase().includes('invalid') || errorData.error.toLowerCase().includes('credential'))) {
              return { success: false, error: 'Invalid email or password. Please try again.' };
            }
          } catch (e) {
            // If parsing fails, still show friendly message for 401
          }
          return { success: false, error: 'Invalid email or password. Please try again.' };
        }
        
        // For other errors, show a generic friendly message
        return { success: false, error: 'Unable to sign in. Please check your connection and try again.' };
      }
      
      let data;
      try {
        const text = await response.text();
        console.log('Login response text:', text);
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse login response:', parseError);
        return { success: false, error: 'Invalid response from server' };
      }
      
      console.log('[CRITICAL DEBUG] Login response data:', data);
      console.log('[CRITICAL DEBUG] Login response keys:', Object.keys(data));
      console.log('[CRITICAL DEBUG] clientCompanyName in response:', data.clientCompanyName);
      console.log('[CRITICAL DEBUG] companyName in response:', data.companyName);
      console.log('[CRITICAL DEBUG] Full response JSON:', JSON.stringify(data, null, 2));
      
      // #region agent log - Debug login response
      console.log('[Login] Response received:', {success:data.success,hasToken:!!data.token,clientCompanyName:data.clientCompanyName,companyName:data.companyName,email:data.email,allKeys:Object.keys(data)});
      // #endregion
      
      // Check if we got the wrong response type (proposals/catalog instead of login response)
      if (data.proposals || data.catalog) {
        console.error('ERROR: Received proposals/catalog response instead of login response!');
        console.error('This suggests the login action parameter was not recognized by the server.');
        console.error('Response URL was:', url.toString().replace(/password=[^&]+/, 'password=***'));
        return { 
          success: false, 
          error: 'Server returned wrong response type. The login endpoint may not be working correctly. Please check the Apps Script deployment.' 
        };
      }
      
      if (data.success && data.token) {
        // Store session in localStorage
        const token = data.token;
        console.log('Login successful, storing token:', token);
        console.log('Token length:', token.length);
        console.log('Full token:', token);
        
        // Get clientCompanyName from response (try multiple field names)
        const clientCompanyName = data.clientCompanyName || data.companyName || data.client_name || '';
        console.log('[CRITICAL DEBUG] Login response data:', {
          clientCompanyName: data.clientCompanyName,
          companyName: data.companyName,
          client_name: data.client_name,
          selected: clientCompanyName,
          email: data.email,
          fullName: data.fullName,
          firstName: data.firstName,
          lastName: data.lastName
        });
        console.log('[CRITICAL DEBUG] Storing clientCompanyName:', clientCompanyName);
        
        localStorage.setItem('clientToken', token);
        const clientInfoToStore = {
          email: data.email,
          clientCompanyName: clientCompanyName,
          fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          title: data.title || '',
          isPre2026Member: data.isPre2026Member || false,
          isTrialAccount: data.isTrialAccount || false,
          trialExpires: data.trialExpires || null,
          trialDaysRemaining: data.trialDaysRemaining || null
        };
        localStorage.setItem('clientInfo', JSON.stringify(clientInfoToStore));
        
        // Verify clientCompanyName was stored
        const storedClientInfo = JSON.parse(localStorage.getItem('clientInfo') || '{}');
        console.log('[CRITICAL DEBUG] Stored clientCompanyName:', storedClientInfo.clientCompanyName);
        console.log('[CRITICAL DEBUG] Full stored clientInfo:', storedClientInfo);
        
        // #region agent log - Debug login storage
        console.log('[Login] clientCompanyName storage:', {responseClientCompanyName:data.clientCompanyName,storedClientCompanyName:storedClientInfo.clientCompanyName,email:data.email});
        // #endregion
        
        // Verify token was stored
        const storedToken = localStorage.getItem('clientToken');
        console.log('Token stored verification:', storedToken === token ? 'SUCCESS' : 'FAILED');
        console.log('Stored token:', storedToken);
        
        return { success: true, data };
      } else {
        console.error('Login failed:', data.error || 'Unknown error');
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login exception:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  },
  
  // Logout
  async logout() {
    const token = localStorage.getItem('clientToken');
    if (token) {
      // Call new Vercel logout endpoint to track session duration
      try {
        await fetch('/api/auth/logout', {
        method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: token })
        }).catch(() => {}); // Ignore errors - logout should work even if tracking fails
      } catch (error) {
        console.warn('Logout tracking failed:', error);
      }
    }
    
    // Clear all cache on logout
    cacheService.clear();
    
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientInfo');
  },
  
  // Get current session
  getSession() {
    const token = localStorage.getItem('clientToken');
    const clientInfo = localStorage.getItem('clientInfo');
    
    if (token && clientInfo) {
      return {
        token: token,
        clientInfo: JSON.parse(clientInfo)
      };
    }
    return null;
  },
  
  // Check if logged in
  isAuthenticated() {
    const token = localStorage.getItem('clientToken');
    console.log('Checking authentication, token in localStorage:', token ? token.substring(0, 20) + '...' : 'null');
    return !!token;
  },
  
  // Get current token (for debugging)
  getCurrentToken() {
    return localStorage.getItem('clientToken');
  },
  
  // Diagnostic function to check token format
  diagnoseToken() {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      return { error: 'No token found in localStorage' };
    }
    
    return {
      token: token,
      length: token.length,
      trimmed: token.trim(),
      trimmedLength: token.trim().length,
      hasWhitespace: token !== token.trim(),
      format: token.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) ? 'UUID' : 'OTHER',
      firstChars: token.substring(0, 20),
      lastChars: token.substring(Math.max(0, token.length - 10))
    };
  },
  
  // Request password reset
  async requestPasswordReset(email) {
    try {
      const url = new URL(CLIENT_API_URL);
      url.searchParams.set('action', 'requestPasswordReset');
      url.searchParams.set('email', email);
      url.searchParams.set('_t', Date.now().toString());
      
      console.log('Requesting password reset for:', email);
      console.log('Request URL:', url.toString().replace(/email=[^&]+/, 'email=***'));
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        redirect: 'follow',
        cache: 'no-store'
      });
      
      console.log('Password reset response status:', response.status);
      console.log('Password reset response ok:', response.ok);
      
      const text = await response.text();
      console.log('Password reset response text:', text);
      
      if (!response.ok) {
        console.error('HTTP error:', response.status, response.statusText);
        return { success: false, error: `Server error: ${response.status} - ${text.substring(0, 200)}` };
      }
      
      let data;
      try {
        data = JSON.parse(text);
        console.log('Password reset response data:', data);
        console.log('Success:', data.success);
        console.log('ResetToken:', data.resetToken ? 'Present' : 'Missing');
        console.log('Error:', data.error);
      } catch (parseError) {
        console.error('Failed to parse password reset response:', parseError);
        console.error('Response text was:', text);
        return { success: false, error: 'Invalid response from server. Please try again.' };
      }
      
      if (data.success && data.resetToken) {
        console.log('Password reset token generated successfully');
        return { success: true, resetToken: data.resetToken, error: data.error };
      } else {
        console.error('Password reset failed:', data.error || 'No reset token returned');
        return { success: false, error: data.error || 'Failed to generate reset token' };
      }
    } catch (error) {
      console.error('Password reset request exception:', error);
      console.error('Error stack:', error.stack);
      return { success: false, error: error.message || 'Network error' };
    }
  },
  
  // Reset password with token
  async resetPassword(email, resetToken, newPassword) {
    try {
      const url = new URL(CLIENT_API_URL);
      url.searchParams.set('action', 'resetPassword');
      url.searchParams.set('email', email);
      url.searchParams.set('resetToken', resetToken);
      url.searchParams.set('newPassword', newPassword);
      url.searchParams.set('_t', Date.now().toString());
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        redirect: 'follow',
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `Server error: ${response.status} - ${errorText}` };
      }
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      return { success: data.success, error: data.error };
    } catch (error) {
      console.error('Password reset exception:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }
};

// ============================================
// CACHE SERVICE
// ============================================

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const CACHE_PREFIX = 'api_cache_';

const cacheService = {
  // Generate cache key from action, params, and endpoint source
  getCacheKey(action, params = {}, endpointSource = '') {
    const paramsStr = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    // Include endpoint source in cache key to prevent conflicts between Vercel and Apps Script
    const endpointSuffix = endpointSource ? `_${endpointSource}` : '';
    return `${CACHE_PREFIX}${action}_${paramsStr}${endpointSuffix}`;
  },
  
  // Get cached response
  get(key) {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          console.log(`[Cache] Hit for key: ${key.substring(0, 50)}...`);
          return data;
        } else {
          console.log(`[Cache] Expired for key: ${key.substring(0, 50)}...`);
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('[Cache] Error reading cache:', error);
    }
    return null;
  },
  
  // Set cached response
  set(key, data) {
    try {
      const cacheData = JSON.stringify({
        data,
        timestamp: Date.now()
      });
      localStorage.setItem(key, cacheData);
      console.log(`[Cache] Stored for key: ${key.substring(0, 50)}...`);
    } catch (error) {
      console.error('[Cache] Error writing cache:', error);
      // If storage is full (QuotaExceededError), clear old cache entries and retry
      if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
        console.warn('[Cache] localStorage quota exceeded, clearing old entries...');
        const cleared = this.clearOld();
        if (cleared > 0) {
          // Retry once after clearing
          try {
            const cacheData = JSON.stringify({
              data,
              timestamp: Date.now()
            });
            localStorage.setItem(key, cacheData);
            console.log(`[Cache] Successfully stored after clearing old entries`);
          } catch (retryError) {
            console.warn('[Cache] Still unable to store after clearing. Skipping cache for this item.');
            // Don't throw - allow the app to continue without caching
          }
        } else {
          console.warn('[Cache] No old entries to clear. Skipping cache for this item.');
        }
      } else {
        // Other errors - log but don't retry
        console.error('[Cache] Unexpected cache error:', error);
      }
    }
  },
  
  // Clear old cache entries (if storage is full)
  // Returns number of entries cleared
  clearOld() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(k => k.startsWith(CACHE_PREFIX));
      
      if (cacheKeys.length === 0) {
        return 0;
      }
      
      // Sort by timestamp and remove oldest entries
      const entries = cacheKeys.map(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            return { key, timestamp };
          }
        } catch (e) {}
        return { key, timestamp: 0 };
      }).sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 75% (more aggressive than 50%)
      const toRemove = entries.slice(0, Math.floor(entries.length * 0.75));
      toRemove.forEach(({ key }) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn('[Cache] Error removing key:', key, e);
        }
      });
      console.log(`[Cache] Cleared ${toRemove.length} old cache entries (${Math.floor(entries.length * 0.75)}% of ${entries.length} total)`);
      return toRemove.length;
    } catch (error) {
      console.error('[Cache] Error clearing old cache:', error);
      return 0;
    }
  },
  
  // Clear all cache
  clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.filter(k => k.startsWith(CACHE_PREFIX)).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('[Cache] Cleared all cache');
    } catch (error) {
      console.error('[Cache] Error clearing cache:', error);
    }
  },
  
  // Invalidate cache for specific action
  invalidate(action) {
    try {
      const keys = Object.keys(localStorage);
      keys.filter(k => k.startsWith(`${CACHE_PREFIX}${action}_`)).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log(`[Cache] Invalidated cache for action: ${action}`);
    } catch (error) {
      console.error('[Cache] Error invalidating cache:', error);
    }
  }
};

// ============================================
// API SERVICE
// ============================================

const apiService = {
  // Make authenticated request with caching
  // Routes to Vercel API endpoints where available, falls back to Apps Script
  async request(action, params = {}, options = {}) {
    const { useCache = true, invalidateCache = false } = options;
    
    const session = authService.getSession();
    if (!session || !session.token) {
      console.error('No session found in request');
      throw new Error('Not authenticated');
    }
    
    // Ensure token is clean (trim whitespace)
    const cleanToken = String(session.token).trim();
    if (!cleanToken) {
      console.error('Token is empty after trimming');
      throw new Error('Not authenticated');
    }
    
    // Determine which endpoint will be used (for cache key) - MUST be done before cache check
    let endpointSource = 'appsscript'; // default
    if (action === 'validate') {
      endpointSource = 'vercel';
    } else if (action === 'proposals') {
      // FORCE Apps Script for proposals - Vercel endpoint disabled
      endpointSource = 'appsscript';
    }
    
    // Check cache first (unless explicitly disabled)
    // IMPORTANT: Include endpoint source in cache key to prevent conflicts between Vercel and Apps Script
    if (useCache && !invalidateCache) {
      const cacheKey = cacheService.getCacheKey(action, params, endpointSource);
      const cached = cacheService.get(cacheKey);
      if (cached) {
        console.log(`[API Request] Using cached data from ${endpointSource} endpoint`);
        return cached;
      }
    }
    
    // Invalidate cache if requested
    if (invalidateCache) {
      cacheService.invalidate(action);
    }
    
    console.log('Making API request:', {
      action: action,
      endpointSource: endpointSource,
      tokenLength: cleanToken.length,
      tokenPreview: cleanToken.substring(0, 20) + '...',
      params: params,
      cached: false
    });
    
    // Route to Vercel API endpoints for auth actions
    if (action === 'validate') {
      const url = new URL('/api/auth/validate', window.location.origin);
      url.searchParams.set('token', cleanToken);
      
      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache'
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Validation failed');
        }
        
        // Cache successful responses (unless explicitly disabled)
        if (useCache && data.success) {
          const cacheKey = cacheService.getCacheKey(action, params, endpointSource);
          cacheService.set(cacheKey, data);
        }
        
        return data;
      } catch (error) {
        console.error('[API Request] Vercel validate error:', error);
        throw error;
      }
    }
    
    // Route proposals requests
    // ALWAYS use Apps Script for proposals (Vercel endpoint disabled)
    // This ensures consistent behavior and avoids any potential conflicts
    let apiUrl = CLIENT_API_URL;
    if (action === 'proposals') {
      // Always use Apps Script - Vercel endpoint disabled
      // Use PROPOSALS_API_URL if available, otherwise fall back to CLIENT_API_URL
      apiUrl = PROPOSALS_API_URL || CLIENT_API_URL;
      console.log('[API Request] Using Apps Script proposals endpoint:', apiUrl.substring(0, 50) + '...');
      console.log('[API Request] Vercel endpoint disabled - forcing Apps Script usage');
    }
    
    // For other actions, use Apps Script (will migrate incrementally)
    const url = new URL(apiUrl);
    url.searchParams.set('action', action);
    url.searchParams.set('token', cleanToken);
    
    Object.keys(params).forEach(key => {
      url.searchParams.set(key, params[key]);
    });
    
    // Log the URL (without full token for security)
    const urlForLogging = url.toString().replace(/token=[^&]+/, 'token=***');
    console.log('Request URL:', urlForLogging);
    
    try {
      console.log('[API Request] Fetching from:', url.toString().replace(/token=[^&]+/, 'token=***'));
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json'
        }
      }).catch(fetchError => {
        // Enhanced error logging for network failures
        console.error('[API Request] Fetch failed:', fetchError);
        console.error('[API Request] Error type:', fetchError.name);
        console.error('[API Request] Error message:', fetchError.message);
        console.error('[API Request] URL attempted:', url.toString().replace(/token=[^&]+/, 'token=***'));
        console.error('[API Request] Possible causes:');
        console.error('  1. Apps Script web app URL is incorrect or deployment was deleted');
        console.error('  2. CORS configuration issue');
        console.error('  3. Network connectivity problem');
        console.error('  4. Apps Script web app is not deployed or access is restricted');
        throw fetchError;
      });
      
      console.log('[API Request] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        console.error('[API Request] HTTP error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[API Request] Error response text:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
      
      let data;
      let text;
      try {
        text = await response.text();
        console.log('API response text length:', text.length);
        console.log('Response starts with:', text.substring(0, 100));
        
        // Google Apps Script Web Apps sometimes wrap JSON in HTML comments
        // Try to extract JSON if wrapped
        let jsonText = text.trim();
        if (jsonText.startsWith('<!--') && jsonText.includes('-->')) {
          const jsonMatch = jsonText.match(/<!--([\s\S]*?)-->/);
          if (jsonMatch) {
            jsonText = jsonMatch[1].trim();
            console.log('Extracted JSON from HTML comment');
          }
        }
        
        data = JSON.parse(jsonText);
      } catch (error) {
        console.error('Failed to parse response:', error);
        console.error('Response text that failed to parse:', text?.substring(0, 500));
        throw new Error('Invalid response from server: ' + error.message);
      }
      
      console.log('API response success:', data.success);
      if (data.error) {
        console.error('API response error:', data.error);
      }
      
      if (!data || typeof data !== 'object') {
        console.error('Invalid response format:', data);
        throw new Error('Invalid response format from server');
      }
      
      if (!data.success) {
        // Log the error for debugging
        const errorMsg = data.error || data.message || 'Request failed';
        console.error('API Error Details:', {
          error: errorMsg,
          action: action,
          tokenLength: cleanToken.length,
          tokenPreview: cleanToken.substring(0, 10) + '...',
          fullResponse: data
        });
        
        // If it's a session error, provide more context
        if (errorMsg.includes('Invalid or expired session') || errorMsg.includes('No session token')) {
          console.error('Session validation failed. Token being used:', cleanToken.substring(0, 20) + '...');
          console.error('Check if this token exists in the Clients sheet, column J');
        }
        
        throw new Error(errorMsg);
      }
      
      // Track action for analytics
      if (action !== 'validate') {
        analyticsService.trackAction(`api_${action}`, `/${action}`, Object.keys(params).length > 0 ? JSON.stringify(params) : '');
      }
      
      // Cache successful responses (unless explicitly disabled)
      // IMPORTANT: Use endpointSource-aware cache key to prevent conflicts
      if (useCache && data.success) {
        const cacheKey = cacheService.getCacheKey(action, params, endpointSource);
        cacheService.set(cacheKey, data);
      }
      
      return data;
    } catch (error) {
      // Enhanced error handling for network errors
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.name === 'TypeError') {
        console.error('[API Request] Network error detected:', error);
        console.error('[API Request] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        console.error('[API Request] CLIENT_API_URL:', CLIENT_API_URL);
        console.error('[API Request] Troubleshooting steps:');
        console.error('  1. Verify the Apps Script web app is deployed');
        console.error('  2. Check that the web app URL is correct');
        console.error('  3. Ensure the web app has "Execute as: Me" and "Who has access: Anyone"');
        console.error('  4. Check browser console for CORS errors');
        console.error('  5. Try accessing the URL directly in a browser');
        
        // Provide more helpful error message
        const helpfulMessage = 'Unable to connect to the server. This could be due to:\n' +
          '• The server URL may be incorrect\n' +
          '• The server may be temporarily unavailable\n' +
          '• Please check your internet connection\n\n' +
          'If this persists, please contact support.';
        throw new Error(helpfulMessage);
      }
      throw error;
    }
  },
  
  // Get client proposals (with caching)
  // OPTIMIZATION: Load recent proposals by default (last 1000), load all only when needed
  // This significantly improves initial load time for clients with many proposals
  async getProposals(invalidateCache = false, loadAll = false) {
    const result = await this.request('proposals', { loadAll: loadAll ? 'true' : 'false' }, { invalidateCache });
    // Track proposals view
    analyticsService.trackAction('view_proposals', '/proposals', `count:${result.proposals?.length || 0}`);
    return result;
  },
  
  // Get yearly spend (with caching)
  async getSpend(year, invalidateCache = false) {
    const result = await this.request('spend', { year: year }, { invalidateCache });
    // Track spend view
    analyticsService.trackAction('view_spend', '/performance', `year:${year}`);
    return result;
  },
  
  // Get single proposal (with caching)
  async getProposal(proposalId, invalidateCache = false) {
    const result = await this.request('proposal', { id: proposalId }, { invalidateCache });
    // Track proposal view
    analyticsService.trackProposalView(proposalId);
    return result;
  },
  
  // Validate session (no caching - always fresh) - Uses Vercel API
  async validateSession() {
    return this.request('validate', {}, { useCache: false });
  },
  
  // Get client profile information (no caching - always fresh)
  async getClientInfo() {
    const result = await this.request('getClientInfo', {}, { useCache: false });
    // Track profile view
    analyticsService.trackAction('view_profile', '/account', '');
    return result;
  },
  
  // Update client profile information
  async updateClientInfo(profileData, retryCount = 0) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    // Use exact same format as working submitNewProject request
    // 'text/plain' Content-Type avoids CORS preflight issues
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second delay between retries
    
    try {
      const response = await fetch(CLIENT_API_URL, {
      method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        action: 'updateClientInfo',
        token: token,
        ...profileData
        }),
        mode: 'cors'
      });
      
      if (!response.ok) {
        // If rate limited (429) or server error (5xx), retry
        if ((response.status === 429 || response.status >= 500) && retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return this.updateClientInfo(profileData, retryCount + 1);
        }
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
    
    const result = await response.json();
    if (!result.success) {
        // If session expired, don't retry
        if (result.error && result.error.includes('session')) {
          throw new Error(result.error || 'Session expired. Please log in again.');
        }
        // Retry on other errors
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
          return this.updateClientInfo(profileData, retryCount + 1);
        }
      throw new Error(result.error || 'Failed to update profile');
    }
    return result;
    } catch (error) {
      // Retry on network errors
      if ((error.message.includes('Failed to fetch') || error.message.includes('CORS') || error.name === 'TypeError') && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
        return this.updateClientInfo(profileData, retryCount + 1);
      }
      // Provide helpful error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS') || error.name === 'TypeError') {
        throw new Error('Network error: Unable to connect to server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Clear all cache
  clearCache() {
    cacheService.clear();
  },
  
  // Invalidate specific cache
  invalidateCache(action) {
    cacheService.invalidate(action);
  },
  
  // Submit change request
  async submitChangeRequest(changeRequestData) {
    const response = await fetch(PROPOSALS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(changeRequestData),
      mode: 'cors'
    });
    
    const result = await response.json();
    
    if (result.success === false) {
      throw new Error(result.error || 'Failed to submit change request');
    }
    
    // Track change request submission
    analyticsService.trackAction('submit_change_request', `/proposal/${changeRequestData.proposalId}`, changeRequestData.proposalId);
    
    // Invalidate proposals cache after submitting change request
    // This ensures fresh data is fetched next time
    cacheService.invalidate('proposals');
    
    return result;
  }
};

// ============================================
// SHOPIFY SERVICE
// ============================================

const shopifyService = {
  // Fetch products tagged with "Mayker Reserve Product"
  async getFeaturedProducts() {
    try {
      const query = `
        query getProducts($tag: String!) {
          products(first: 10, query: $tag) {
            edges {
              node {
                id
                title
                description
                handle
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      `;
      
      const variables = {
        tag: 'tag:"Mayker Reserve Product"'
      };
      
      const response = await fetch(SHOPIFY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query, variables })
      });
      
      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.errors) {
        console.error('Shopify GraphQL errors:', data.errors);
        return [];
      }
      
      // Transform Shopify data to our format
      return data.data.products.edges.map(edge => {
        const product = edge.node;
        const image = product.images.edges[0]?.node;
        const price = parseFloat(product.priceRange.minVariantPrice.amount);
        
        return {
          id: product.id,
          title: product.title,
          description: product.description || '',
          handle: product.handle,
          productType: product.productType || '',
          imageUrl: image?.url || '',
          imageAlt: image?.altText || product.title,
          price: price,
          currencyCode: product.priceRange.minVariantPrice.currencyCode,
          url: `https://${SHOPIFY_STORE}.myshopify.com/products/${product.handle}`
        };
      });
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      return [];
    }
  },

  // Fetch products by collection handle
  async getProductsByCollection(collectionHandle, first = 250) {
    try {
      const query = `
        query getCollectionProducts($handle: String!, $first: Int!) {
          collection(handle: $handle) {
            id
            title
            handle
            products(first: $first) {
              edges {
                node {
                  id
                  title
                  description
                  handle
                  productType
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      const variables = {
        handle: collectionHandle,
        first: first
      };
      
      const response = await fetch(SHOPIFY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query, variables })
      });
      
      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.errors) {
        console.error('Shopify GraphQL errors:', data.errors);
        return [];
      }
      
      if (!data.data.collection) {
        console.warn(`Collection not found: ${collectionHandle}`);
        return [];
      }
      
      // Transform Shopify data to our format
      return data.data.collection.products.edges.map(edge => {
        const product = edge.node;
        const image = product.images.edges[0]?.node;
        const price = parseFloat(product.priceRange.minVariantPrice.amount);
        
        return {
          id: product.id,
          title: product.title,
          description: product.description || '',
          handle: product.handle,
          productType: product.productType || '',
          imageUrl: image?.url || '',
          imageAlt: image?.altText || product.title,
          price: price,
          currencyCode: product.priceRange.minVariantPrice.currencyCode,
          url: `https://${SHOPIFY_STORE}.myshopify.com/products/${product.handle}`
        };
      });
    } catch (error) {
      console.error(`Error fetching products for collection ${collectionHandle}:`, error);
      return [];
    }
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function parseDateSafely(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1;
    const day = parseInt(dateMatch[3], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
}

function looksLikeMiscFeesJson(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed) || parsed.length === 0) return false;
    return parsed.every((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
      const hasName = typeof item.name === 'string' || typeof item.label === 'string';
      const hasAmount = 'amount' in item || 'defaultAmount' in item;
      return hasName && hasAmount;
    });
  } catch (e) {
    return false;
  }
}

function stripMiscFeesJsonFromNotes(text) {
  const raw = String(text || '');
  if (!raw.trim()) return '';
  if (looksLikeMiscFeesJson(raw)) return '';
  const match = raw.match(/\[\s*\{[\s\S]*("amount"|"defaultAmount")[\s\S]*\}\s*\]/);
  if (!match || !looksLikeMiscFeesJson(match[0])) return raw;
  return raw.replace(match[0], '').replace(/\n{3,}/g, '\n\n').trim();
}

/** Read final-invoice section flags from embedded MAYKER_PAYMENT_V1 meta (default: show). */
function getInvoiceSectionVisibility(customProjectNotes) {
  const text = String(customProjectNotes || '');
  const markers = ['\n\n\u200B\u200BMAYKER_PAYMENT_V1:', '\n\nMAYKER_PAYMENT_V1:', 'MAYKER_PAYMENT_V1:'];
  let meta = null;
  let visibleNotes = text;
  for (const marker of markers) {
    const idx = text.lastIndexOf(marker);
    if (idx === -1) continue;
    try {
      meta = JSON.parse(text.slice(idx + marker.length).trim());
      visibleNotes = text.slice(0, idx).replace(/\s+$/, '');
      break;
    } catch (e) {
      /* keep looking */
    }
  }
  visibleNotes = stripMiscFeesJsonFromNotes(visibleNotes)
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
    .trim();
  return {
    showProjectDescription: meta?.showProjectDescriptionOnInvoice !== false,
    showCustomProjectNotes: meta?.showCustomProjectNotesOnInvoice !== false && !!visibleNotes,
    visibleCustomProjectNotes: visibleNotes,
    showLabelOnProposal: meta?.showLabelOnProposal === true,
  };
}

// Shared utility function to get a sortable date from a proposal
// Returns a Date object that can be used for sorting (most recent first)
function getSortableDateFromProposal(proposal) {
  // For historical projects, try to parse eventDate first (it has the actual event date)
  // Only use timestamp if eventDate can't be parsed
  if (proposal.isHistorical) {
    // Try eventDate first for historical projects (it contains the actual event date)
    if (proposal.eventDate) {
      if (proposal.eventDate instanceof Date) {
        if (!isNaN(proposal.eventDate.getTime())) {
          return proposal.eventDate;
        }
      } else if (typeof proposal.eventDate === 'string') {
        const eventDateStr = proposal.eventDate.trim();
        
        // Try parsing GMT date strings first (e.g., "Tue Aug 19 2025 00:00:00 GMT-0500")
        // Handle strings like "Tue Aug 19 2025 00:00:00 GMT-0500 (Central Daylight Time)"
        // Remove timezone name in parentheses if present, as it can cause parsing issues
        let cleanDateStr = eventDateStr;
        // Remove timezone name in parentheses: "(Central Daylight Time)" or "(Central Standard Time)"
        cleanDateStr = cleanDateStr.replace(/\s*\([^)]+\)\s*$/, '');
        
        const dateObj = new Date(cleanDateStr);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          // Only use if it's a reasonable date (not year 1970 from failed parse)
          if (year >= 2000 && year <= 2100) {
            return dateObj;
          }
        }
        
        // Try parsing formatted strings like "April 5 - 13, 2025" or "April 5, 2025"
        const monthMap = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        // Pattern 1: "Month Day - Day, Year" or "Month Day-Day, Year" (date range)
        // Also handles "March 29 - April 3, 2026" (cross-month ranges)
        let match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\d+),?\s+(\d{4})/i);
        if (!match) {
          // Try cross-month: "March 29 - April 3, 2026"
          match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\w+)\s+(\d+),?\s+(\d{4})/i);
          if (match) {
            const startMonthName = match[1].toLowerCase();
            const startMonth = monthMap[startMonthName];
            const startDay = parseInt(match[2]);
            const year = parseInt(match[5]);
            if (startMonth !== undefined && startDay && year) {
              const parsed = new Date(year, startMonth, startDay);
              if (!isNaN(parsed.getTime())) {
                return parsed;
              }
            }
          }
        } else {
          const monthName = match[1].toLowerCase();
          const month = monthMap[monthName];
          const day = parseInt(match[2]); // Use first date for sorting
          const year = parseInt(match[4]);
          if (month !== undefined && day && year) {
            const parsed = new Date(year, month, day);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
        
        // Pattern 2: "Month Day, Year" (single date)
        match = eventDateStr.match(/(\w+)\s+(\d+),?\s+(\d{4})/i);
        if (match) {
          const monthName = match[1].toLowerCase();
          const month = monthMap[monthName];
          const day = parseInt(match[2]);
          const year = parseInt(match[3]);
          if (month !== undefined && day && year) {
            const parsed = new Date(year, month, day);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
      }
    }
    
    // Fallback to timestamp for historical projects only if eventDate parsing failed
    if (proposal.timestamp) {
      const ts = new Date(proposal.timestamp);
      if (!isNaN(ts.getTime())) {
        return ts;
      }
    }
  }
  
  // Debug: Log if we can't find a date
  if (!proposal.startDate && !proposal.eventDate && !proposal.endDate) {
    console.warn('Proposal has no date fields:', proposal.projectNumber || 'unknown', proposal);
  }
  
  // Priority 2: Use startDate if available
  if (proposal.startDate) {
    if (proposal.startDate instanceof Date) {
      if (!isNaN(proposal.startDate.getTime())) {
        return proposal.startDate;
      }
    } else if (typeof proposal.startDate === 'string') {
      // Try YYYY-MM-DD format first
      if (/^\d{4}-\d{2}-\d{2}/.test(proposal.startDate.trim())) {
        const parsed = parseDateSafely(proposal.startDate);
        if (parsed && !isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      // Try parsing as-is
      const parsed = new Date(proposal.startDate);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  
  // Priority 3: Try to parse eventDate (can be in many formats)
  if (proposal.eventDate) {
    if (proposal.eventDate instanceof Date) {
      if (!isNaN(proposal.eventDate.getTime())) {
        return proposal.eventDate;
      }
    } else if (typeof proposal.eventDate === 'string') {
      const eventDateStr = proposal.eventDate.trim();
      
      // Try parsing as Date object first (handles GMT strings and ISO dates)
      const dateObj = new Date(eventDateStr);
      if (!isNaN(dateObj.getTime())) {
        // Only use this if it's a reasonable date (not year 1970 or 1900 from failed parse)
        const year = dateObj.getFullYear();
        if (year >= 2000 && year <= 2100) {
          return dateObj;
        }
      }
      
      // Try parsing formatted strings like "April 5 - 13, 2025" or "April 5, 2025"
      const monthMap = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
      };
      
      // Pattern 1: "Month Day - Day, Year" or "Month Day-Day, Year" (date range)
      // Also handles "March 29 - April 3, 2026" (cross-month ranges)
      let match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\d+),?\s+(\d{4})/i);
      if (!match) {
        // Try cross-month: "March 29 - April 3, 2026"
        match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\w+)\s+(\d+),?\s+(\d{4})/i);
        if (match) {
          const startMonthName = match[1].toLowerCase();
          const startMonth = monthMap[startMonthName];
          const startDay = parseInt(match[2]);
          const year = parseInt(match[5]);
          if (startMonth !== undefined && startDay && year) {
            const parsed = new Date(year, startMonth, startDay);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
      } else {
        const monthName = match[1].toLowerCase();
        const month = monthMap[monthName];
        const day = parseInt(match[2]); // Use first date for sorting
        const year = parseInt(match[4]);
        if (month !== undefined && day && year) {
          const parsed = new Date(year, month, day);
          if (!isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      
      // Pattern 2: "Month Day, Year" (single date)
      match = eventDateStr.match(/(\w+)\s+(\d+),?\s+(\d{4})/i);
      if (match) {
        const monthName = match[1].toLowerCase();
        const month = monthMap[monthName];
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        if (month !== undefined && day && year) {
          const parsed = new Date(year, month, day);
          if (!isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      
      // Pattern 3: MM/DD/YYYY format
      match = eventDateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (match) {
        const month = parseInt(match[1]) - 1;
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      
      // Pattern 4: YYYY-MM-DD format
      match = eventDateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1]);
        const month = parseInt(match[2]) - 1;
        const day = parseInt(match[3]);
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
  }
  
  // Priority 4: Try endDate as last resort
  if (proposal.endDate) {
    if (proposal.endDate instanceof Date && !isNaN(proposal.endDate.getTime())) {
      return proposal.endDate;
    } else if (typeof proposal.endDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(proposal.endDate.trim())) {
      const parsed = parseDateSafely(proposal.endDate);
      if (parsed && !isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  
  // If we can't parse anything, return a very old date so it sorts to the bottom
  return new Date(0);
}

// Sort proposals by date (most recent first)
function sortProposalsByDate(proposals) {
  return [...proposals].sort((a, b) => {
    const dateA = getSortableDateFromProposal(a);
    const dateB = getSortableDateFromProposal(b);
    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    
    // Primary sort: by date (descending - newest first)
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    
    // Secondary sort: by projectNumber (descending) if dates are equal
    const projectNumA = parseInt(a.projectNumber) || 0;
    const projectNumB = parseInt(b.projectNumber) || 0;
    return projectNumB - projectNumA;
  });
}

function formatDateRange(proposal) {
  // Shared display standard from the proposal's own start/end dates (Nov 4–5, 2026).
  const standard = formatDisplayDateRange(
    proposal && typeof proposal.startDate === 'string' ? proposal.startDate.trim() : '',
    proposal && typeof proposal.endDate === 'string' ? proposal.endDate.trim() : '',
  );
  if (standard) return standard;

  // Historical rows without YYYY-MM-DD dates: use eventDate if available (it's already formatted from the backend)
  // This works for both historical and regular projects that have eventDate set
  // Check eventDate first - this should be set for all projects (historical and regular)
  if (proposal.eventDate) {
    let eventDateStr = '';
    
    // Handle Date objects - convert to formatted string
    if (proposal.eventDate instanceof Date) {
      // If it's a Date object, format it properly
      eventDateStr = formatDisplayDate(proposal.eventDate);
    } else if (typeof proposal.eventDate === 'string') {
      // Check if it's a Date string (like "Wed Mar 19 2025 00:00:00 GMT-0500")
      // If it looks like a Date string, try to parse and format it
      if (proposal.eventDate.includes('GMT') || proposal.eventDate.match(/^\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/)) {
        const parsedDate = new Date(proposal.eventDate);
        if (!isNaN(parsedDate.getTime())) {
          eventDateStr = formatDisplayDate(parsedDate);
        } else {
          eventDateStr = proposal.eventDate.trim();
        }
      } else {
        // It's already a formatted string
        eventDateStr = proposal.eventDate.trim();
      }
    }
    
    if (eventDateStr) {
      // Normalize spacing around hyphens to be consistent (spaces around dash)
      // This handles both "April 5-13, 2025" and "April 5 - 13, 2025"
      return eventDateStr.replace(/\s*-\s*/g, ' - ');
    }
  }
  
  // Second priority: Try to use startDate and endDate if available
  // Only parse if they exist and are valid date strings (YYYY-MM-DD format)
  let start = null;
  let end = null;
  
  if (proposal.startDate && typeof proposal.startDate === 'string' && proposal.startDate.trim()) {
    // Only parse if it looks like a date string (YYYY-MM-DD), not a formatted string
    const startDateStr = proposal.startDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(startDateStr)) {
      start = parseDateSafely(startDateStr);
      if (start && isNaN(start.getTime())) {
        start = null; // Invalid date
      }
    }
  }
  
  if (proposal.endDate && typeof proposal.endDate === 'string' && proposal.endDate.trim()) {
    // Only parse if it looks like a date string (YYYY-MM-DD), not a formatted string
    const endDateStr = proposal.endDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(endDateStr)) {
      end = parseDateSafely(endDateStr);
      if (end && isNaN(end.getTime())) {
        end = null; // Invalid date
      }
    }
  }
  
  // If we have a start date but no end date, format as single date
  if (start && !end) {
    return formatDisplayDate(start);
  }
  
  // If we have both dates, format showing all dates
  if (start && end) {
    return formatAllDates(start, end);
  }
  
  // If we have nothing, return empty string (will show as 'N/A' in the table)
  return '';
}

// Helper function to format date ranges in the correct format
// Examples: "March 1, 2026" / "March 1-3, 2026" / "March 29 - April 3, 2026"
function formatAllDates(start, end) {
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '';
  }
  return formatDisplayDateRange(start, end);
}

function calculateTotal(proposal) {
  // If this is a historical project, use the pre-calculated invoice total
  // Check both isHistorical flag and project number starting with HIST-
  const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
  
  if (isHistorical && (proposal.historicalInvoiceTotal !== null && proposal.historicalInvoiceTotal !== undefined)) {
    console.log('[calculateTotal] Historical project detected:', {
      projectNumber: proposal.projectNumber,
      historicalInvoiceTotal: proposal.historicalInvoiceTotal
    });
    return parseFloat(proposal.historicalInvoiceTotal) || 0;
  }
  
  // Simplified calculation - matches Apps Script logic
  const sections = JSON.parse(proposal.sectionsJSON || '[]');
  
  let baseProductTotal = 0;
  sections.forEach(section => {
    if (section.products && Array.isArray(section.products)) {
      section.products.forEach(product => {
        const quantity = parseFloat(product.quantity) || 0;
        const price = parseFloat(product.price) || 0;
        baseProductTotal += quantity * price;
      });
    }
  });
  
  // Get rental multiplier: custom, or duration (delivery→strike 24h / date-only floor, no +1) — matches Backend
  let rentalMultiplier = 1.0;
  if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
    const parsed = parseFloat(proposal.customRentalMultiplier);
    if (!isNaN(parsed) && parsed > 0) {
      rentalMultiplier = parsed;
    }
  } else {
    const durationInDays = getDurationInDays(proposal);
    rentalMultiplier = getRentalMultiplier(durationInDays);
  }
  
  const extendedProductTotal = baseProductTotal * rentalMultiplier;
  
  // Percentage discount is on BASE product total, not extended (matches Backend)
  const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
  let discountType = proposal.discountType || 'percentage';
  if (!discountType || discountType === 'percentage') {
    if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
      const match = proposal.discountName.match(/^TYPE:(\w+)/);
      if (match) discountType = match[1].toLowerCase();
    }
  }
  const standardRateDiscount = Math.abs(discountType === 'dollar'
    ? discountValue
    : baseProductTotal * (Math.abs(discountValue) / 100));
  
  const rentalTotal = extendedProductTotal - standardRateDiscount;
  
  let waiveProductCare = proposal.waiveProductCare === true || proposal.waiveProductCare === 'true' || String(proposal.waiveProductCare || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:PC')) waiveProductCare = true;
  let waiveServiceFee = proposal.waiveServiceFee === true || proposal.waiveServiceFee === 'true' || String(proposal.waiveServiceFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:SF')) waiveServiceFee = true;

  const pcUsd = resolveProductCareDollarNumber(proposal);
  const sfUsd = resolveServiceFeeDollarNumber(proposal);
  const pcPct = resolveProductCarePercentNumber(proposal);
  const sfPct = resolveServiceFeePercentNumber(proposal);
  const pcPctEff = pcUsd !== null ? null : (pcPct !== null ? pcPct : DEFAULT_PC_PCT);
  const sfPctEff = sfUsd !== null ? null : (sfPct !== null ? sfPct : DEFAULT_SF_PCT);

  let productCareAmount;
  if (pcUsd !== null) productCareAmount = pcUsd;
  else productCareAmount = extendedProductTotal * ((pcPctEff ?? DEFAULT_PC_PCT) / 100);
  const productCare = waiveProductCare ? 0 : productCareAmount;

  const delivery = parseFloat(proposal.deliveryFee) || 0;

  let serviceFeeAmount;
  if (sfUsd !== null) serviceFeeAmount = sfUsd;
  else serviceFeeAmount = (extendedProductTotal + productCare + delivery) * ((sfPctEff ?? DEFAULT_SF_PCT) / 100);
  const serviceFee = waiveServiceFee ? 0 : serviceFeeAmount;
  
  // Subtotal includes all fees (matches Backend — sum all misc fee amounts)
  let miscFeesTotal = 0;
  try {
    const miscFees = typeof proposal.miscFees === 'string' ? JSON.parse(proposal.miscFees || '[]') : (proposal.miscFees || []);
    if (Array.isArray(miscFees)) {
      miscFees.forEach(fee => { miscFeesTotal += parseFloat(fee.amount || 0) || 0; });
    }
  } catch (e) {}
  const subtotal = rentalTotal + productCare + serviceFee + delivery + miscFeesTotal;

  const taxExempt = proposal.taxExempt === true || proposal.taxExempt === 'true';

  // Whether the 3% credit card processing fee applies.
  // Use waiveCreditCardFeeFromColumn when waiveCreditCardFee not set (same as Admin/backend) so shareable and Reserve Portal match
  let waiveCreditCardFee = false;
  if (proposal.discountName && proposal.discountName.includes('WAIVE:CC')) {
    waiveCreditCardFee = true;
  }
  if (proposal.waiveCreditCardFee === true || proposal.waiveCreditCardFee === 'true') {
    waiveCreditCardFee = true;
  }
  if (!waiveCreditCardFee && (proposal.waiveCreditCardFeeFromColumn === true || proposal.waiveCreditCardFeeFromColumn === 'true' || String(proposal.waiveCreditCardFeeFromColumn || '').toLowerCase() === 'true')) {
    waiveCreditCardFee = true;
  }

  // CC fee: 3% of (discounted subtotal + straight tax). Fixed, non-taxable line that
  // also appears on the QuickBooks invoice, so it is based on the straight tax.
  const straightTax = taxExempt ? 0 : subtotal * 0.0975;
  const creditCardFee = waiveCreditCardFee ? 0 : (subtotal + straightTax) * 0.03;

  // Tax mirrors QuickBooks: the discount is prorated across all lines including the
  // non-taxable CC fee, so the slice landing on the CC fee inflates the taxable base.
  let tax;
  if (taxExempt) {
    tax = 0;
  } else if (creditCardFee > 0 && standardRateDiscount > 0) {
    const taxableLinesPreDiscount = subtotal + standardRateDiscount;
    const discountOnCcFee = standardRateDiscount * (creditCardFee / (taxableLinesPreDiscount + creditCardFee));
    tax = (subtotal + discountOnCcFee) * 0.0975;
  } else {
    tax = straightTax;
  }
  const total = subtotal + tax + creditCardFee;

  return total;
}

function formatNumber(num) {
  return (parseFloat(num) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calculateDetailedTotals(proposal) {
  // If this is a historical project, use pre-calculated values
  // Check both isHistorical flag and project number starting with HIST-
  const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
  
  if (isHistorical && (proposal.historicalInvoiceTotal !== null && proposal.historicalInvoiceTotal !== undefined)) {
    console.log('[calculateDetailedTotals] Historical project detected:', {
      projectNumber: proposal.projectNumber,
      isHistorical: proposal.isHistorical,
      historicalInvoiceTotal: proposal.historicalInvoiceTotal,
      historicalProductTotal: proposal.historicalProductTotal
    });
    
    return {
      rentalMultiplier: 1.0,
      productSubtotal: parseFloat(proposal.historicalProductTotal || 0) || 0,
      standardRateDiscount: 0,
      rentalTotal: parseFloat(proposal.historicalProductTotal || 0) || 0,
      productCare: 0,
      serviceFee: 0,
      delivery: 0,
      discount: 0,
      discountType: 'percentage',
      subtotal: parseFloat(proposal.historicalInvoiceTotal || 0) || 0,
      tax: 0,
      total: parseFloat(proposal.historicalInvoiceTotal || 0) || 0
    };
  }
  
  const sections = JSON.parse(proposal.sectionsJSON || '[]');
  
  let baseProductTotal = 0;
  sections.forEach(section => {
    if (section.products && Array.isArray(section.products)) {
      section.products.forEach(product => {
        const quantity = parseFloat(product.quantity) || 0;
        const price = parseFloat(product.price) || 0;
        baseProductTotal += quantity * price;
      });
    }
  });
  
  // Get rental multiplier: custom, or duration (delivery→strike 24h / date-only floor, no +1) — matches Backend
  let rentalMultiplier = 1.0;
  if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
    const parsed = parseFloat(proposal.customRentalMultiplier);
    if (!isNaN(parsed) && parsed > 0) {
      rentalMultiplier = parsed;
    }
  } else {
    const durationInDays = getDurationInDays(proposal);
    rentalMultiplier = getRentalMultiplier(durationInDays);
  }
  
  const extendedProductTotal = baseProductTotal * rentalMultiplier;
  const productSubtotal = extendedProductTotal;
  
  // Percentage discount is on BASE product total, not extended (matches Backend)
  const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
  let discountType = proposal.discountType || 'percentage';
  if (!discountType || discountType === 'percentage') {
    if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
      const match = proposal.discountName.match(/^TYPE:(\w+)/);
      if (match) discountType = match[1].toLowerCase();
    }
  }
  const standardRateDiscount = Math.abs(discountType === 'dollar'
    ? discountValue
    : baseProductTotal * (Math.abs(discountValue) / 100));
  
  const rentalTotal = extendedProductTotal - standardRateDiscount;
  
  let waiveProductCare = proposal.waiveProductCare === true || proposal.waiveProductCare === 'true' || String(proposal.waiveProductCare || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:PC')) {
    waiveProductCare = true;
  }
  let waiveServiceFee = proposal.waiveServiceFee === true || proposal.waiveServiceFee === 'true' || String(proposal.waiveServiceFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:SF')) {
    waiveServiceFee = true;
  }

  const pcUsd = resolveProductCareDollarNumber(proposal);
  const sfUsd = resolveServiceFeeDollarNumber(proposal);
  const pcPct = resolveProductCarePercentNumber(proposal);
  const sfPct = resolveServiceFeePercentNumber(proposal);
  const pcPctEff = pcUsd !== null ? null : (pcPct !== null ? pcPct : DEFAULT_PC_PCT);
  const sfPctEff = sfUsd !== null ? null : (sfPct !== null ? sfPct : DEFAULT_SF_PCT);

  let productCareAmount;
  if (pcUsd !== null) productCareAmount = pcUsd;
  else productCareAmount = extendedProductTotal * ((pcPctEff ?? DEFAULT_PC_PCT) / 100);
  const productCare = waiveProductCare ? 0 : productCareAmount;

  const delivery = parseFloat(proposal.deliveryFee) || 0;

  let serviceFeeAmount;
  if (sfUsd !== null) serviceFeeAmount = sfUsd;
  else serviceFeeAmount = (extendedProductTotal + productCare + delivery) * ((sfPctEff ?? DEFAULT_SF_PCT) / 100);
  const serviceFee = waiveServiceFee ? 0 : serviceFeeAmount;

  const productCareFeeLabelSuffix = pcUsd !== null
    ? null
    : `(${Number(pcPctEff ?? DEFAULT_PC_PCT)}%)`;
  const serviceFeeLabelSuffix = sfUsd !== null
    ? null
    : `(${Number(sfPctEff ?? DEFAULT_SF_PCT)}%)`;
  
  // Subtotal includes all fees (matches Backend — sum all misc fee amounts)
  let miscFeesTotal = 0;
  try {
    const miscFees = typeof proposal.miscFees === 'string' ? JSON.parse(proposal.miscFees || '[]') : (proposal.miscFees || []);
    if (Array.isArray(miscFees)) {
      miscFees.forEach(fee => { miscFeesTotal += parseFloat(fee.amount || 0) || 0; });
    }
  } catch (e) {}
  const subtotal = rentalTotal + productCare + serviceFee + delivery + miscFeesTotal;

  const taxExempt = proposal.taxExempt === true || proposal.taxExempt === 'true' || String(proposal.taxExempt || '').toLowerCase() === 'true';

  // Whether the 3% credit card processing fee applies.
  // Use waiveCreditCardFeeFromColumn when waiveCreditCardFee not set (same as Admin/backend) so shareable and Reserve Portal match
  let waiveCreditCardFee = proposal.waiveCreditCardFee === true || proposal.waiveCreditCardFee === 'true' || String(proposal.waiveCreditCardFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:CC')) {
    waiveCreditCardFee = true;
  }
  if (!waiveCreditCardFee && (proposal.waiveCreditCardFeeFromColumn === true || proposal.waiveCreditCardFeeFromColumn === 'true' || String(proposal.waiveCreditCardFeeFromColumn || '').toLowerCase() === 'true')) {
    waiveCreditCardFee = true;
  }

  // CC fee: 3% of (discounted subtotal + straight tax). Fixed, non-taxable line that
  // also appears on the QuickBooks invoice, so it is based on the straight tax.
  const straightTax = taxExempt ? 0 : subtotal * 0.0975;
  const creditCardFeeAmount = (subtotal + straightTax) * 0.03;
  const creditCardFee = waiveCreditCardFee ? 0 : creditCardFeeAmount;

  // Tax mirrors QuickBooks: the discount is prorated across all lines including the
  // non-taxable CC fee, so the slice landing on the CC fee inflates the taxable base.
  let tax;
  if (taxExempt) {
    tax = 0;
  } else if (creditCardFee > 0 && standardRateDiscount > 0) {
    const taxableLinesPreDiscount = subtotal + standardRateDiscount;
    const discountOnCcFee = standardRateDiscount * (creditCardFee / (taxableLinesPreDiscount + creditCardFee));
    tax = (subtotal + discountOnCcFee) * 0.0975;
  } else {
    tax = straightTax;
  }
  const total = subtotal + tax + creditCardFee;
  
  return {
    rentalMultiplier,
    productSubtotal,
    standardRateDiscount,
    rentalTotal,
    productCare,
    productCareAmount,
    serviceFee,
    serviceFeeAmount,
    delivery,
    miscFees: miscFeesTotal,
    discount: standardRateDiscount,
    discountType,
    subtotal,
    tax,
    creditCardFee,
    creditCardFeeAmount,
    total,
    waiveCreditCardFee,
    waiveProductCare,
    waiveServiceFee,
    productCareFlatDollars: pcUsd,
    serviceFeeFlatDollars: sfUsd,
    productCareFeeLabelSuffix,
    serviceFeeLabelSuffix
  };
}

/**
 * Parse time string (e.g. "5:00 PM") to { hours, minutes }. Matches Backend for duration calc.
 */
function parseTimeToHoursMinutes(timeStr) {
  if (!timeStr || String(timeStr).trim() === '') return null;
  const str = String(timeStr).trim();
  const upper = str.toUpperCase();
  const isPM = upper.indexOf('PM') >= 0;
  const isAM = upper.indexOf('AM') >= 0;
  const numPart = str.replace(/\s*[AP]M\s*$/i, '').trim();
  const colon = numPart.indexOf(':');
  if (colon < 0) return null;
  let hours = parseInt(numPart.substring(0, colon), 10);
  const minutes = parseInt(numPart.substring(colon + 1), 10) || 0;
  if (isNaN(hours)) return null;
  if (isPM && hours !== 12) hours += 12;
  if (isAM && hours === 12) hours = 0;
  hours = Math.max(0, Math.min(23, hours));
  return { hours, minutes: Math.max(0, Math.min(59, minutes)) };
}

/**
 * Build a Date from dateStr and timeStr (e.g. "5:00 PM"). Matches Backend for duration.
 */
function parseDateTimeFromDateAndTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const d = parseDateSafely(dateStr);
  if (!d || isNaN(d.getTime())) return null;
  const tm = parseTimeToHoursMinutes(timeStr);
  if (!tm) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), tm.hours, tm.minutes, 0);
}

/**
 * Get duration in days for rental multiplier. Matches Backend:
 * delivery→strike: ceil(diffHours/24); date-only: floor(diffMs/(24*60*60*1000)) (no +1).
 */
function getDurationInDays(proposal) {
  const deliveryDateTime = parseDateTimeFromDateAndTime(proposal.startDate, proposal.deliveryTime);
  const strikeDateTime = parseDateTimeFromDateAndTime(proposal.endDate, proposal.strikeTime);
  if (deliveryDateTime && strikeDateTime && !isNaN(deliveryDateTime.getTime()) && !isNaN(strikeDateTime.getTime())) {
    const diffHours = (strikeDateTime.getTime() - deliveryDateTime.getTime()) / (1000 * 60 * 60);
    return Math.max(1, Math.ceil(diffHours / 24));
  }
  const start = parseDateSafely(proposal.startDate);
  const end = parseDateSafely(proposal.endDate);
  if (!start || !end) return 1;
  const diffTime = end.getTime() - start.getTime();
  // Inclusive calendar days when only dates (no times): Jan 1–Jan 3 = 3 days
  return Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
}

function getRentalMultiplier(duration) {
  if (duration <= 1) return 1.0;
  if (duration === 2) return 1.1;
  if (duration === 3) return 1.2;
  if (duration === 4) return 1.3;
  if (duration === 5) return 1.4;
  if (duration === 6) return 1.5;
  if (duration >= 7 && duration <= 14) return 2.0;
  if (duration >= 15 && duration <= 21) return 3.0;
  if (duration >= 22 && duration <= 28) return 4.0;
  return 4.0;
}

function isFutureDate(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return false;
  return date > new Date();
}

function isPastDate(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return false;
  return date < new Date();
}

function proposalEventDate(proposal) {
  return proposal?.endDate || proposal?.startDate || proposal?.eventDate || '';
}

function proposalDateHasPassed(proposal) {
  const date = parseDateSafely(proposalEventDate(proposal));
  if (!date || isNaN(date.getTime())) return false;
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDay.getTime() < today.getTime();
}

// Calculate product spend for a proposal (used for points calculation)
// Points = Invoice Total - Tax - Delivery - Credit Card Fee
// PREFERRED: Use stored pointAllocation value from backend (Column AC for regular, Column H for historical)
// FALLBACK: Calculate only if stored value is missing
function calculateProductSpend(proposal) {
  try {
    // PREFERRED: Use stored pointAllocation value if available (single source of truth)
    // Accept 0 as valid value (proposals might have 0 points if they're just fees)
    if (proposal.pointAllocation !== undefined && proposal.pointAllocation !== null) {
      const storedPoints = parseFloat(proposal.pointAllocation);
      if (!isNaN(storedPoints)) {
        // Use stored value even if 0 (0 is a valid point allocation)
        return storedPoints;
      }
    }
    
    // FALLBACK: Calculate if stored value is missing (backward compatibility)
    console.log(`[calculateProductSpend] Stored value missing, calculating for ${proposal.projectNumber || 'N/A'}`);
    
    // Check both isHistorical flag and project number starting with HIST-
    const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
    
    if (isHistorical) {
      // For historical projects, use Column H point allocation if available
      // Otherwise fall back to historicalProductTotal
      if (proposal.pointAllocation !== undefined && proposal.pointAllocation !== null) {
        return parseFloat(proposal.pointAllocation) || 0;
      }
      
      if (proposal.historicalProductTotal !== null && proposal.historicalProductTotal !== undefined) {
        console.log('[calculateProductSpend] Historical project - using historicalProductTotal:', {
          projectNumber: proposal.projectNumber,
          historicalProductTotal: proposal.historicalProductTotal
        });
        return parseFloat(proposal.historicalProductTotal) || 0;
      } else {
        console.warn('[calculateProductSpend] Historical project missing both pointAllocation and historicalProductTotal:', {
          projectNumber: proposal.projectNumber,
          clientName: proposal.clientName
        });
        return 0;
      }
    }
    
    // For regular proposals, calculate using backend formula
    // Points = Invoice Total - Tax - Delivery - Credit Card Fee
    const totals = calculateDetailedTotals(proposal);
    
    const invoiceTotal = totals.total; // Full invoice total (subtotal + tax + credit card fee)
    const tax = totals.tax || 0;
    const delivery = totals.delivery || 0;
    const creditCardFee = totals.creditCardFee || 0;
    
    // Backend formula: Points = Invoice Total - Tax - Delivery - Credit Card Fee
    const points = invoiceTotal - tax - delivery - creditCardFee;
    
    // Debug logging to verify calculation
    console.log(`[Points Calculation] Project ${proposal.projectNumber || 'N/A'}:`, {
      invoiceTotal: invoiceTotal.toFixed(2),
      tax: tax.toFixed(2),
      delivery: delivery.toFixed(2),
      creditCardFee: creditCardFee.toFixed(2),
      points: points.toFixed(2),
      subtotal: totals.subtotal?.toFixed(2),
      rentalTotal: totals.rentalTotal?.toFixed(2),
      productCare: totals.productCare?.toFixed(2),
      serviceFee: totals.serviceFee?.toFixed(2),
      calculation: `(${invoiceTotal.toFixed(2)} - ${tax.toFixed(2)} - ${delivery.toFixed(2)} - ${creditCardFee.toFixed(2)}) = ${points.toFixed(2)}`
    });
    
    return Math.max(0, points); // Ensure non-negative
  } catch (e) {
    console.error('Error calculating product spend:', e);
    return 0;
  }
}

// ============================================
// SHAREABLE PROPOSAL VIEW (no login required)
// Renders the same proposal view as Reserve with discount toggle; used for reserve.maykerevents.com/?projectNumber=X&version=Y or /client/X/Y
// ============================================

function updateShareableProposalUrl(projectNumber, version) {
  if (typeof window === 'undefined') return;
  const pathMatch = window.location.pathname.match(/^\/client\//);
  if (pathMatch) {
    window.history.replaceState({}, '', `/client/${projectNumber}/${version}`);
    return;
  }
  const params = new URLSearchParams(window.location.search);
  params.set('projectNumber', String(projectNumber));
  params.set('version', String(version));
  window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
}

function ShareableProposalView({ showAlert, showConfirm, showPrompt }) {
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [projectNumber, setProjectNumber] = useState(null);
  const [version, setVersion] = useState(1);

  const loadShareableProposal = useCallback((nextProjectNumber, nextVersion) => {
    setLoading(true);
    setNotFound(false);
    return fetchPublishedProposal(nextProjectNumber, nextVersion || '1')
      .then((proposal) => {
        if (proposal) {
          setProposal(proposal);
          setNotFound(false);
        } else {
          setNotFound(true);
          setProposal(null);
        }
      })
      .catch(() => {
        setNotFound(true);
        setProposal(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = parseShareableProposalFromUrl();
    if (!params) {
      setLoading(false);
      return;
    }
    setProjectNumber(params.projectNumber);
    setVersion(params.version);
    loadShareableProposal(params.projectNumber, params.version);
  }, [loadShareableProposal]);

  if (!projectNumber && !loading) return null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Neue Haas Unica', sans-serif", backgroundColor: '#fafaf8' }}>
        <div style={{ fontSize: '16px', color: '#545142' }}>Loading proposal…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", backgroundColor: '#fafafa' }}>
        <div style={{ maxWidth: '600px', textAlign: 'center', backgroundColor: 'white', padding: '60px 40px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>⚠️</div>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#2C2C2C', marginBottom: '16px', fontFamily: "'Domaine Text', serif" }}>Proposal Not Found</h1>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '32px', lineHeight: '1.6' }}>
            The proposal you're looking for (Project #{projectNumber}{version ? `, Version ${version}` : ''}) could not be found.
          </p>
          <p style={{ fontSize: '14px', color: '#999', marginBottom: '32px' }}>This proposal may have been deleted, or the link may be incorrect.</p>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{ padding: '12px 24px', fontSize: '14px', fontWeight: '500', color: '#fff', backgroundColor: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!proposal) return null;

  const sessionClientInfo = (() => {
    try {
      return authService.getSession()?.clientInfo || null;
    } catch (_) {
      return null;
    }
  })();

  return (
    <ProposalDetailView
      proposal={proposal}
      isClientShareableLink={true}
      clientInfo={sessionClientInfo}
      onBack={() => { window.location.href = '/'; }}
      onLogout={() => {}}
      showAlert={showAlert}
      showConfirm={showConfirm}
      showPrompt={showPrompt}
      onRefresh={async (_force, next) => {
        const nextProject = next?.projectNumber || projectNumber;
        const nextVersion = next?.version;
        if (!nextProject || nextVersion == null || nextVersion === '') return;
        setProjectNumber(nextProject);
        setVersion(nextVersion);
        updateShareableProposalUrl(nextProject, nextVersion);
        if (next.proposal && proposalMatchesProjectVersion(next.proposal, nextProject, nextVersion)) {
          setProposal(next.proposal);
          setNotFound(false);
        }
        waitForClientProposalVersion({
          fetchProposal: (targetProject, targetVersion) => fetchPublishedProposal(targetProject, targetVersion),
          projectNumber: nextProject,
          version: nextVersion
        }).then((loaded) => {
          if (proposalMatchesProjectVersion(loaded, nextProject, nextVersion)) {
            setProposal(loaded);
            setNotFound(false);
          }
        }).catch((err) => {
          console.warn('[change-request] background refresh still pending', err);
        });
      }}
    />
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });
  const [promptModal, setPromptModal] = useState({ isOpen: false, message: '', placeholder: '', onConfirm: null, defaultValue: '' });
  
  // Helper function to show alert
  const showAlert = (message) => {
    return new Promise((resolve) => {
      setAlertModal({ isOpen: true, message, onClose: () => {
        setAlertModal({ isOpen: false, message: '' });
        resolve();
      }});
    });
  };
  
  // Helper function to show confirm
  const showConfirm = (message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmModal({ 
        isOpen: true, 
        message,
        title: options.title || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        confirmPrimary: Boolean(options.confirmPrimary),
        onConfirm: () => {
          setConfirmModal({ isOpen: false, message: '', title: '', confirmLabel: 'Confirm', cancelLabel: 'Cancel', confirmPrimary: false, onConfirm: null });
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal({ isOpen: false, message: '', title: '', confirmLabel: 'Confirm', cancelLabel: 'Cancel', confirmPrimary: false, onConfirm: null });
          resolve(false);
        }
      });
    });
  };
  
  // Helper function to show prompt
  const showPrompt = (message, placeholder = '', defaultValue = '') => {
    return new Promise((resolve) => {
      setPromptModal({ 
        isOpen: true, 
        message, 
        placeholder,
        defaultValue,
        onConfirm: (value) => {
          setPromptModal({ isOpen: false, message: '', placeholder: '', onConfirm: null, defaultValue: '' });
          resolve(value);
        },
        onCancel: () => {
          setPromptModal({ isOpen: false, message: '', placeholder: '', onConfirm: null, defaultValue: '' });
          resolve(null);
        }
      });
    });
  };
  
  useEffect(() => {
    // Check if user is already logged in and fetch fresh client info
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        const session = authService.getSession();
        if (session && session.clientInfo) {
          console.log('Session found on mount:', session.clientInfo);
          setClientInfo(session.clientInfo);
          setIsAuthenticated(true);
          
          // Fetch fresh client info from server to sync with Google Sheet
          try {
            const freshInfo = await apiService.getClientInfo(true); // Use cache for faster initial load
            if (freshInfo.success && freshInfo.clientInfo) {
              // Update localStorage with fresh data, but preserve trial info from login
              const currentClientInfo = JSON.parse(localStorage.getItem('clientInfo') || '{}');
              const updatedClientInfo = { 
                ...currentClientInfo, 
                ...freshInfo.clientInfo,
                // ALWAYS preserve trial info from login (it's the source of truth)
                isTrialAccount: currentClientInfo.isTrialAccount !== undefined ? currentClientInfo.isTrialAccount : (freshInfo.clientInfo.isTrialAccount || false),
                trialExpires: currentClientInfo.trialExpires || freshInfo.clientInfo.trialExpires || null,
                trialDaysRemaining: currentClientInfo.trialDaysRemaining !== undefined ? currentClientInfo.trialDaysRemaining : (freshInfo.clientInfo.trialDaysRemaining !== undefined ? freshInfo.clientInfo.trialDaysRemaining : null)
              };
              localStorage.setItem('clientInfo', JSON.stringify(updatedClientInfo));
              setClientInfo(updatedClientInfo);
              console.log('Fresh client info loaded from server', {
                trialInfo: {
                  isTrialAccount: updatedClientInfo.isTrialAccount,
                  trialDaysRemaining: updatedClientInfo.trialDaysRemaining,
                  trialExpires: updatedClientInfo.trialExpires,
                  preservedFromLogin: {
                    isTrialAccount: currentClientInfo.isTrialAccount,
                    trialDaysRemaining: currentClientInfo.trialDaysRemaining
                  }
                }
              });
            }
          } catch (error) {
            console.error('Failed to fetch fresh client info:', error);
            // Continue with cached data if fetch fails
          }
        } else {
          console.log('Session invalid on mount, clearing...');
          authService.logout();
        }
      } else {
        console.log('No session found on mount');
      }
    };
    
    // Small delay to ensure localStorage is ready
    setTimeout(checkAuth, 100);
  }, []);
  
  // Scroll to top when authentication state changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isAuthenticated]);
  
  const handleLogin = async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      const result = await authService.login(email, password);
      console.log('Login result:', result);
      console.log('[Login] Trial info in result.data:', {
        isTrialAccount: result.data?.isTrialAccount,
        trialExpires: result.data?.trialExpires,
        trialDaysRemaining: result.data?.trialDaysRemaining,
        fullResultData: result.data
      });
      
      if (result.success && result.data.token) {
        console.log('Login successful, token received:', result.data.token.substring(0, 20) + '...');
        
        // Verify token was stored in localStorage
        const storedToken = localStorage.getItem('clientToken');
        if (!storedToken || storedToken !== result.data.token) {
          console.error('Token storage verification failed!');
          console.error('Expected:', result.data.token);
          console.error('Stored:', storedToken);
          return { success: false, error: 'Failed to store session. Please try again.' };
        }
        
        console.log('Token stored successfully in localStorage');
        console.log('Setting authentication state');
        
        console.log('[CRITICAL DEBUG] Setting clientInfo state:', {
          email: result.data.email,
          clientCompanyName: result.data.clientCompanyName,
          fullName: result.data.fullName,
          isTrialAccount: result.data.isTrialAccount,
          trialExpires: result.data.trialExpires,
          trialDaysRemaining: result.data.trialDaysRemaining,
          fullResult: result.data
        });
        
        // CRITICAL OPTIMIZATION: Load cached clientInfo immediately for instant tier/points display
        // Check localStorage for previously cached tier/points data
        let cachedClientInfo = {};
        try {
          const cachedStr = localStorage.getItem('clientInfo');
          if (cachedStr) {
            cachedClientInfo = JSON.parse(cachedStr);
          }
        } catch (e) {
          // If localStorage is corrupted, clear it and continue without cache
          console.warn('Failed to parse cached clientInfo, clearing cache:', e);
          try {
            localStorage.removeItem('clientInfo');
          } catch (clearErr) {
            // Ignore errors when clearing
          }
        }
        
        // Calculate trialDaysRemaining if not provided but trialExpires is
        let trialDaysRemaining = result.data.trialDaysRemaining;
        if ((trialDaysRemaining === null || trialDaysRemaining === undefined) && result.data.trialExpires) {
          try {
            const expiresDate = new Date(result.data.trialExpires);
            const now = Date.now();
            const expiresTime = expiresDate.getTime();
            if (expiresTime > now) {
              trialDaysRemaining = Math.ceil((expiresTime - now) / (1000 * 60 * 60 * 24));
            } else {
              trialDaysRemaining = 0;
            }
            console.log('[Login] Calculated trialDaysRemaining from trialExpires:', trialDaysRemaining);
          } catch (e) {
            console.warn('[Login] Error calculating trialDaysRemaining:', e);
          }
        }
        
        const initialClientInfo = {
          email: result.data.email,
          clientCompanyName: result.data.clientCompanyName,
          fullName: result.data.fullName,
          isTrialAccount: result.data.isTrialAccount || false,
          trialExpires: result.data.trialExpires || null,
          trialDaysRemaining: trialDaysRemaining,
          // Use cached tier/points if available (same company) - show immediately
          ...(cachedClientInfo.clientCompanyName === result.data.clientCompanyName ? {
            tier: cachedClientInfo.tier,
            tierDiscount: cachedClientInfo.tierDiscount,
            spend2025: cachedClientInfo.spend2025,
            points2025: cachedClientInfo.points2025,
            spend2026: cachedClientInfo.spend2026,
            points2026: cachedClientInfo.points2026
          } : {})
        };
        
        console.log('[Login] Final initialClientInfo with trial info:', {
          isTrialAccount: initialClientInfo.isTrialAccount,
          trialExpires: initialClientInfo.trialExpires,
          trialDaysRemaining: initialClientInfo.trialDaysRemaining
        });
        
        setClientInfo(initialClientInfo);
        setIsAuthenticated(true);
        // Tie this session to the client in PostHog (no-op if unconfigured).
        posthogIdentify(initialClientInfo);
        console.log('Authentication state set, isAuthenticated should be true');
        console.log('Using cached tier/points:', initialClientInfo.tier ? 'Yes' : 'No');
        
        // CRITICAL PERFORMANCE: Preload DashboardView chunk immediately after login
        // This starts downloading the lazy-loaded component while we fetch fresh data
        // This can save 2-3 seconds of load time
        import('./components/DashboardView').catch(err => {
          console.warn('Failed to preload DashboardView:', err);
          // Continue - it will load when needed
        });
        
        // Fetch fresh clientInfo in background (non-blocking) - will update when ready
        apiService.getClientInfo(false).then(freshInfo => {
          if (freshInfo.success && freshInfo.clientInfo) {
            const updatedClientInfo = {
              email: result.data.email,
              clientCompanyName: result.data.clientCompanyName,
              fullName: result.data.fullName,
              // Preserve trial info from login response
              isTrialAccount: initialClientInfo.isTrialAccount,
              trialExpires: initialClientInfo.trialExpires,
              trialDaysRemaining: initialClientInfo.trialDaysRemaining,
              ...freshInfo.clientInfo // Include tier, points, spend data
            };
            localStorage.setItem('clientInfo', JSON.stringify(updatedClientInfo));
            setClientInfo(updatedClientInfo);
            // Enrich PostHog person properties with tier/spend now that they're loaded.
            posthogIdentify(updatedClientInfo);
            console.log('Fresh client info (tiers/points) loaded and updated', {
              trialInfo: {
                isTrialAccount: updatedClientInfo.isTrialAccount,
                trialDaysRemaining: updatedClientInfo.trialDaysRemaining
              }
            });
          }
        }).catch(err => {
          console.warn('Failed to fetch fresh client info:', err);
          // Continue with cached data
        });
        
        // Validate session in background (non-blocking) - don't wait for it
        setTimeout(async () => {
          try {
            await apiService.validateSession();
          } catch (err) {
            console.warn('Session validation failed, but login was successful');
          }
        }, 1000);
        
        return { success: true };
      } else {
        console.error('Login failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Login handler error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };
  
  const handleLogout = () => {
    authService.logout();
    posthogReset();
    setIsAuthenticated(false);
    setClientInfo(null);
  };
  
  // Shareable proposal link (no login): reserve.maykerevents.com/?projectNumber=X&version=Y or /client/X/Y
  const shareableParams = parseShareableProposalFromUrl();
  if (!isAuthenticated && shareableParams) {
    return (
      <>
        <ShareableProposalView showAlert={showAlert} showConfirm={showConfirm} showPrompt={showPrompt} />
        <AlertModal isOpen={alertModal.isOpen} message={alertModal.message} onClose={alertModal.onClose} />
        <ConfirmModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} confirmLabel={confirmModal.confirmLabel} cancelLabel={confirmModal.cancelLabel} confirmPrimary={confirmModal.confirmPrimary} onConfirm={confirmModal.onConfirm} onCancel={confirmModal.onCancel} />
        <PromptModal isOpen={promptModal.isOpen} message={promptModal.message} placeholder={promptModal.placeholder} defaultValue={promptModal.defaultValue} onConfirm={promptModal.onConfirm} onCancel={promptModal.onCancel} />
      </>
    );
  }
  if (!isAuthenticated) {
    return (
      <>
        <LoginView onLogin={handleLogin} showAlert={showAlert} showPrompt={showPrompt} />
        <AlertModal 
          isOpen={alertModal.isOpen} 
          message={alertModal.message} 
          onClose={alertModal.onClose} 
        />
        <ConfirmModal 
          isOpen={confirmModal.isOpen} 
          title={confirmModal.title}
          message={confirmModal.message} 
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmPrimary={confirmModal.confirmPrimary}
          onConfirm={confirmModal.onConfirm} 
          onCancel={confirmModal.onCancel} 
        />
        <PromptModal 
          isOpen={promptModal.isOpen} 
          message={promptModal.message} 
          placeholder={promptModal.placeholder}
          defaultValue={promptModal.defaultValue}
          onConfirm={promptModal.onConfirm} 
          onCancel={promptModal.onCancel} 
        />
      </>
    );
  }
  
  // Lazy load DashboardView for code splitting (reduces initial bundle size by ~60-70%)
  // Using dynamic import to split DashboardView into a separate chunk
  const LazyDashboardView = lazy(() => import('./components/DashboardView'));
  
  // Determine if trial banner should be shown (shared logic)
  const isTrial = clientInfo?.isTrialAccount === true;
  const daysRemaining = clientInfo?.trialDaysRemaining;
  const showTrialBanner = isTrial && daysRemaining !== null && daysRemaining !== undefined && daysRemaining > 0;
  
  console.log('[Banner] Trial banner check:', {
    clientInfo: clientInfo ? {
      isTrialAccount: clientInfo.isTrialAccount,
      trialDaysRemaining: clientInfo.trialDaysRemaining,
      trialExpires: clientInfo.trialExpires,
      hasClientInfo: true
    } : { hasClientInfo: false },
    isTrial,
    daysRemaining,
    willShow: showTrialBanner
  });
  
  return (
    <>
      {/* Trial Status Banner - Fixed at top, above everything */}
      {showTrialBanner && (
        <div style={{
          backgroundColor: '#000000',
          color: '#FFFFFF',
          padding: '16px 24px',
          textAlign: 'center',
          fontFamily: "'NeueHaasUnica', sans-serif",
          fontSize: '14px',
          lineHeight: '1.5',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 10000,
          boxSizing: 'border-box'
        }}>
          <span style={{ fontWeight: '400' }}>
            {clientInfo.trialDaysRemaining} {clientInfo.trialDaysRemaining === 1 ? 'day' : 'days'} remaining in your trial.{' '}
          </span>
          <a 
            href="https://www.maykerevents.com/pages/mayker-reserve" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#FFFFFF',
              textDecoration: 'underline',
              fontWeight: '500',
              marginLeft: '4px',
              transition: 'opacity 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            Enroll to continue access and benefits.
          </a>
        </div>
      )}
      <div style={{
        paddingTop: showTrialBanner ? '56px' : '0' // Banner height: 16px top + 16px bottom + ~24px line height
      }}>
        <Suspense fallback={
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#FAF8F3',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #2C2C2C',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              <div style={{ fontSize: '14px', color: '#666' }}>Loading dashboard...</div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        }>
          <LazyDashboardView clientInfo={clientInfo} onLogout={handleLogout} showAlert={showAlert} showConfirm={showConfirm} showPrompt={showPrompt} />
        </Suspense>
      </div>
      <AlertModal 
        isOpen={alertModal.isOpen} 
        message={alertModal.message} 
        onClose={alertModal.onClose} 
      />
      <ConfirmModal 
        isOpen={confirmModal.isOpen} 
        title={confirmModal.title}
        message={confirmModal.message} 
        confirmLabel={confirmModal.confirmLabel}
        cancelLabel={confirmModal.cancelLabel}
        confirmPrimary={confirmModal.confirmPrimary}
        onConfirm={confirmModal.onConfirm} 
        onCancel={confirmModal.onCancel} 
      />
      <PromptModal 
        isOpen={promptModal.isOpen} 
        message={promptModal.message} 
        placeholder={promptModal.placeholder}
        defaultValue={promptModal.defaultValue}
        onConfirm={promptModal.onConfirm} 
        onCancel={promptModal.onCancel} 
      />
    </>
  );
}

// ============================================
// LOGIN VIEW
// ============================================

function LoginView({ onLogin, showAlert, showPrompt }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await onLogin(email, password);
      
      if (!result.success) {
        setError(result.error || 'Login failed');
        setLoading(false);
      } else {
        // Login successful - loading will be handled by parent component
        // Don't set loading to false here, let the redirect happen
        console.log('Login form submitted successfully');
      }
    } catch (error) {
      console.error('Login form error:', error);
      setError(error.message || 'Login failed');
      setLoading(false);
    }
  };
  
  const brandMahogany = '#3E0D12';
  const brandSage = '#545142';
  const brandWheat = '#DABE86';
  const brandSlate = '#577591';
  const brandCharcoal = brandMahogany; // Legacy name
  
  return (
    <div className="login-container" style={{ minHeight: '100vh', display: 'flex', position: 'relative' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        body, * {
          font-family: 'NeueHaasUnica', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
        }
        h1, h2, h3, h4, h5, h6 {
          font-family: 'Domaine Text', 'Georgia', serif !important;
        }
        
        /* Mobile: Overlay login form on background */
        @media (max-width: 768px) {
          .login-container {
            flex-direction: column !important;
          }
          .login-form-container {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            background: rgba(255, 255, 255, 0.75) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
            z-index: 10 !important;
            padding: 32px 24px !important;
            overflow-y: auto !important;
          }
          .login-visual-container {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            z-index: 1 !important;
          }
        }
        
        @media (min-width: 769px) {
          .login-form-container {
            position: relative !important;
            background: white !important;
          }
          .login-visual-container {
            position: relative !important;
          }
        }
      ` }} />

      {/* Left Side - Login Form */}
      <div className="login-form-container" style={{ 
        flex: '0 0 50%', 
        backgroundColor: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '40px'
      }}>
        <div style={{ maxWidth: '400px', width: '100%' }}>
          {/* Brand Mark */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            {/* Icon/Brand Mark */}
            <img 
              src="/mayker_icon-black.png" 
              alt="Mayker Reserve" 
              style={{ height: '60px', width: 'auto', marginBottom: '24px', display: 'block', margin: '0 auto 24px auto' }}
              onLoad={() => console.log('✅ Icon loaded:', '/mayker_icon-black.png')}
              onError={(e) => {
                console.error('❌ Icon failed to load:', e.target.src);
                // Try alternative path if image not found
                if (!e.target.src.includes('/assets/')) {
                  e.target.src = '/assets/mayker_icon-black.png';
                } else {
                  console.error('Icon image not found - all paths failed');
                }
              }}
            />
            {/* Mayker Reserve Logo */}
            <img 
              src={encodeURI('/Mayker Reserve - Black – 2.png')}
              alt="Mayker Reserve" 
              style={{ 
                maxHeight: '50px', 
                height: 'auto', 
                width: 'auto', 
                maxWidth: '300px',
                marginBottom: '16px', 
                display: 'block', 
                margin: '0 auto 16px auto',
                objectFit: 'contain'
              }}
              onLoad={() => console.log('✅ Logo loaded')}
              onError={(e) => {
                console.error('❌ Logo failed to load:', e.target.src);
                // Try alternative paths if image not found
                const alternatives = [
                  '/Mayker Reserve - Black - 2.png', // Regular hyphen
                  '/mayker_icon-black.png', // Icon as fallback
                ];
                const currentSrc = e.target.src;
                const triedIndex = alternatives.findIndex(alt => currentSrc.includes(alt.replace(/\s+/g, ' ')));
                if (triedIndex < alternatives.length - 1) {
                  console.log('Trying alternative:', alternatives[triedIndex + 1]);
                  e.target.src = alternatives[triedIndex + 1];
                } else {
                  console.error('All logo paths failed');
                }
              }}
            />
            <p style={{ 
              fontSize: '16px', 
              color: '#666', 
              margin: '0',
              fontWeight: '400',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: "'NeueHaasUnica', sans-serif"
            }}>
              CLIENT PORTAL
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ 
                backgroundColor: '#fee2e2', 
                color: '#dc2626', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '24px', 
                fontSize: '14px' 
              }}>
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px', 
                color: '#000000' 
              }}>
                Email:
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ 
                  width: '100%', 
                  padding: '14px', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  fontSize: '15px', 
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Enter your email"
              />
            </div>
            
            <div style={{ marginBottom: '32px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px', 
                color: '#000000' 
              }}>
                Password:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ 
                  width: '100%', 
                  padding: '14px', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  fontSize: '15px', 
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="Enter your password"
              />
            </div>
            
            {/* Buttons Row */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '24px' 
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowResetModal(true);
                  setError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  color: '#000000',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = brandCharcoal;
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Forgot Your Password
              </button>
              
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...secondaryButtonStyle,
                  flex: 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    secondaryButtonHover(e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    secondaryButtonLeave(e);
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Login'}
              </button>
            </div>
          </form>
          
          {/* Password Reset Modal */}
          <PasswordResetModal
            isOpen={showResetModal}
            onClose={() => setShowResetModal(false)}
            initialEmail={email}
            showAlert={showAlert}
          />
        </div>
      </div>
      
      {/* Right Side - Background Image */}
      <div className="login-visual-container" style={{ 
        flex: '0 0 50%', 
        backgroundColor: '#2C2C2C',
        backgroundImage: 'url(/login-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        width: '100%',
        minHeight: '100vh'
      }}>
        {/* Optional overlay for better text contrast if needed */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.05)' // Lighter overlay
        }} />
      </div>
    </div>
  );
}

// ============================================
// DASHBOARD SECTIONS
// ============================================

// InputField component - defined outside to prevent recreation on every render
const ProfileInputField = React.memo(({ label, value, onChange, onBlur, onFocus, type = 'text', readOnly = false, placeholder = '', displayValue, isEditing, memberId, autoComplete, id }) => {
  const inputStyle = {
    width: '100%',
    padding: '8px 0',
    border: 'none',
    borderBottom: isEditing && !readOnly ? '1px solid #d1d5db' : '1px solid #e5e7eb',
    borderRadius: '0',
    fontSize: '14px',
    fontFamily: "'NeueHaasUnica', sans-serif",
    backgroundColor: 'transparent',
    color: readOnly ? '#999' : '#000000',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <label className="profile-input-label" style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        marginBottom: '8px', 
        color: '#000000',
        fontFamily: "'NeueHaasUnica', sans-serif",
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {label}
      </label>
      {isEditing && !readOnly ? (
        type === 'textarea' ? (
          <textarea
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: '60px'
            }}
            placeholder={placeholder}
            autoComplete={autoComplete || 'street-address'}
            name={autoComplete === 'street-address' ? 'street-address' : undefined}
            id={id || label.toLowerCase().replace(/\s+/g, '-')}
          />
        ) : type === 'month-day' ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={value ? value.split('/')[0] || '' : ''}
              onChange={(e) => {
                const month = e.target.value;
                const day = value ? value.split('/')[1] || '' : '';
                const newValue = month && day ? `${month}/${day}` : month || '';
                onChange({ target: { value: newValue } });
              }}
              onBlur={onBlur}
              onFocus={onFocus}
              style={{
                ...inputStyle,
                flex: '1',
                padding: '8px 4px',
                borderBottom: '1px solid #d1d5db',
                cursor: 'pointer'
              }}
            >
              <option value="">Month</option>
              {Array.from({ length: 12 }, (_, i) => {
                const monthNum = String(i + 1).padStart(2, '0');
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                return (
                  <option key={monthNum} value={monthNum}>
                    {monthNames[i]}
                  </option>
                );
              })}
            </select>
            <select
              value={value ? value.split('/')[1] || '' : ''}
              onChange={(e) => {
                const day = e.target.value;
                const month = value ? value.split('/')[0] || '' : '';
                const newValue = month && day ? `${month}/${day}` : day || '';
                onChange({ target: { value: newValue } });
              }}
              onBlur={onBlur}
              onFocus={onFocus}
              style={{
                ...inputStyle,
                flex: '1',
                padding: '8px 4px',
                borderBottom: '1px solid #d1d5db',
                cursor: 'pointer'
              }}
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => {
                const dayNum = String(i + 1).padStart(2, '0');
                return (
                  <option key={dayNum} value={dayNum}>
                    {dayNum}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <input
            type={type}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            style={inputStyle}
            placeholder={placeholder}
          />
        )
      ) : (
        <div style={{ 
          fontSize: '14px', 
          color: readOnly ? '#999' : '#000000', 
          padding: '8px 0',
          borderBottom: '1px solid #e5e7eb',
          minHeight: '23px',
          whiteSpace: type === 'textarea' ? 'pre-line' : 'normal'
        }}>
          {displayValue !== undefined ? displayValue : (type === 'month-day' && value ? (() => {
            const parts = value.split('/');
            if (parts.length === 2 && parts[0] && parts[1]) {
              const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
              const monthIndex = parseInt(parts[0]) - 1;
              const day = parseInt(parts[1]);
              if (monthIndex >= 0 && monthIndex < 12 && day >= 1 && day <= 31) {
                return `${monthNames[monthIndex]} ${day}`;
              }
            }
            return value;
          })() : (value || (readOnly ? memberId : 'Not set')))}
        </div>
      )}
    </div>
  );
});

export function ProfileSection({ clientInfo, profileData, editingProfile, setEditingProfile, onLogout, showAlert, showPrompt, showConfirm, brandCharcoal = '#2C2C2C', brandBrown = '#603f27', brandBlue = '#7693a9' }) {
  // Detect mobile screen size - initialize immediately to avoid flash
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle between Individual and Company Details
  const [accountView, setAccountView] = useState('individual'); // 'individual' or 'company'

  // Company Management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [selectedMemberForRemoval, setSelectedMemberForRemoval] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  const [submittingAddMember, setSubmittingAddMember] = useState(false);
  const [submittingRemoveMember, setSubmittingRemoveMember] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    title: ''
  });
  
  // Generate Member ID from email hash (or use existing ID if available)
  const generateMemberID = () => {
    if (clientInfo?.memberId) return clientInfo.memberId;
    if (clientInfo?.email) {
      // Simple hash-based ID generation (in production, this should come from backend)
      let hash = 0;
      for (let i = 0; i < clientInfo.email.length; i++) {
        hash = ((hash << 5) - hash) + clientInfo.email.charCodeAt(i);
        hash = hash & hash;
      }
      return 'MR' + Math.abs(hash).toString().padStart(6, '0');
    }
    return 'MR000000';
  };

  const memberId = generateMemberID();

  // Helper function to normalize birthday format (convert date strings to MM/DD)
  const normalizeBirthday = (birthday) => {
    if (!birthday) return '';
    
    // If it's already in MM/DD format, return as is
    if (typeof birthday === 'string' && /^\d{2}\/\d{2}$/.test(birthday)) {
      return birthday;
    }
    
    // If it's a date string, try to parse it
    try {
      const date = new Date(birthday);
      if (!isNaN(date.getTime())) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      }
    } catch (e) {
      // If parsing fails, return as is
    }
    
    return birthday || '';
  };

  // Helper function to create formData from clientInfo
  const createFormDataFromClientInfo = (info) => {
    // Debug: Log title value to help diagnose issue
    console.log('createFormDataFromClientInfo - title from info:', info?.title, 'type:', typeof info?.title);
    return {
      fullName: info?.fullName || '',
      companyName: info?.clientCompanyName || '',
      title: info?.title || '',
    email: info?.email || '',
    phone: info?.phone || '',
    mailingAddress: info?.mailingAddress || '',
    addressType: info?.addressType || 'Residence',
    companyMailingAddress: info?.companyMailingAddress || '',
    companyPhone: info?.companyPhone || '',
    companyEmail: info?.companyEmail || '',
    birthday: normalizeBirthday(info?.birthday),
    favoriteRestaurant: info?.favoriteRestaurant || '',
    favoriteBrand: info?.favoriteBrand || '',
    favoriteFlower: info?.favoriteFlower || '',
    favoriteColor: info?.favoriteColor || '',
    photo: info?.photo || null,
    preferredCommunication: info?.preferredCommunication || '',
    preferredPaymentMethod: info?.preferredPaymentMethod || '',
    taxExempt: info?.taxExempt || 'Not Exempt',
    taxExemptDocument: null,
    taxExemptDocumentUrl: info?.taxExemptDocumentUrl || ''
    };
  };

  // Initialize formData from clientInfo - only runs once on mount
  const [formData, setFormData] = useState(() => createFormDataFromClientInfo(clientInfo));

  // Track previous editingProfile to detect when we exit edit mode
  const prevEditingProfileRef = useRef(editingProfile);
  const justSavedRef = useRef(false); // Track if we just saved to prevent sync override
  const [saving, setSaving] = useState(false);

  // ONLY sync when exiting edit mode - never while editing
  useEffect(() => {
    const wasEditing = prevEditingProfileRef.current;
    const isEditing = editingProfile;
    
    // Update ref for next comparison
    prevEditingProfileRef.current = editingProfile;
    
    // ONLY sync when transitioning from editing=true to editing=false
    // Skip sync if we just saved (we already updated formData with fresh data)
    if (wasEditing && !isEditing && clientInfo && !justSavedRef.current) {
      setFormData(createFormDataFromClientInfo(clientInfo));
    }
    
    // Reset the justSaved flag after sync check
    if (!isEditing) {
      justSavedRef.current = false;
    }
  }, [editingProfile]); // ONLY depend on editingProfile, NOT clientInfo

  const activeElementRef = useRef(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });

  // Simple input handler - exactly like StartNewProjectSection
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle input focus - store scroll position
  const handleInputFocus = (e) => {
    scrollPositionRef.current = { x: window.scrollX, y: window.scrollY };
    activeElementRef.current = e?.target || document.activeElement;
  };

  // Handle input blur
  const handleInputBlur = () => {
    // No special handling needed
  };

  // Initialize Google Places Autocomplete for mailing address fields
  useEffect(() => {
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    
    const initializeAutocomplete = (inputId, fieldName) => {
      const checkInput = () => {
        const input = document.getElementById(inputId);
        if (input && window.google && window.google.maps && window.google.maps.places) {
          try {
            if (input.dataset.autocompleteInitialized === 'true') {
              return;
            }
            
            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              types: ['address'],
              componentRestrictions: { country: 'us' }
            });
            
            input.dataset.autocompleteInitialized = 'true';
            
            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                handleInputChange(fieldName, place.formatted_address);
                // Also update the input value directly
                const input = document.getElementById(inputId);
                if (input) {
                  input.value = place.formatted_address;
                }
              }
            });
          } catch (error) {
            console.warn('Failed to initialize Google Places Autocomplete:', error);
          }
        } else if (input && !window.google) {
          setTimeout(checkInput, 100);
        }
      };
      
      checkInput();
      setTimeout(checkInput, 100);
    };
    
    if (GOOGLE_MAPS_API_KEY && editingProfile) {
      // Load Google Places API script if not already loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.addEventListener('load', () => {
            initializeAutocomplete('personal-mailing-address-input', 'mailingAddress');
            initializeAutocomplete('company-mailing-address-input', 'companyMailingAddress');
          });
          return;
        }
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          initializeAutocomplete('personal-mailing-address-input', 'mailingAddress');
          initializeAutocomplete('company-mailing-address-input', 'companyMailingAddress');
        };
        script.onerror = () => {
          console.warn('Google Places API failed to load. Address autocomplete disabled.');
        };
        document.head.appendChild(script);
      } else {
        // Google Maps already loaded, initialize immediately
        initializeAutocomplete('personal-mailing-address-input', 'mailingAddress');
        initializeAutocomplete('company-mailing-address-input', 'companyMailingAddress');
      }
    }
  }, [editingProfile]);

  // Fetch team members when component mounts or clientInfo changes
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!clientInfo?.clientCompanyName) return;
      
      setLoadingTeamMembers(true);
      try {
        const token = localStorage.getItem('clientToken');
        if (!token) {
          console.error('No token found');
          return;
        }

        const url = new URL(CLIENT_API_URL);
        url.searchParams.append('action', 'getTeamMembers');
        url.searchParams.append('token', token);

        const response = await fetch(url.toString());
        const result = await response.json();

        if (result.success && result.teamMembers) {
          setTeamMembers(result.teamMembers);
        } else {
          console.error('Failed to fetch team members:', result.error);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoadingTeamMembers(false);
      }
    };

    fetchTeamMembers();
  }, [clientInfo?.clientCompanyName]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Show more specific saving message if uploading files
      const hasPhoto = formData.photo && formData.photo.startsWith('data:image/');
      const hasDocument = formData.taxExemptDocumentUrl && formData.taxExemptDocumentUrl.startsWith('data:');
      
      if (hasPhoto || hasDocument) {
        showAlert('Uploading files and saving profile...');
      }
      
      // Prepare profile data for API
      console.log('Saving profile - title value:', formData.title, 'type:', typeof formData.title);
      const profileData = {
        title: formData.title,
        phone: formData.phone,
        mailingAddress: formData.mailingAddress,
        addressType: formData.addressType,
        companyMailingAddress: formData.companyMailingAddress,
        companyPhone: formData.companyPhone,
        companyEmail: formData.companyEmail,
        birthday: formData.birthday,
        favoriteRestaurant: formData.favoriteRestaurant,
        favoriteBrand: formData.favoriteBrand,
        favoriteFlower: formData.favoriteFlower,
        favoriteColor: formData.favoriteColor,
        preferredCommunication: formData.preferredCommunication,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        taxExempt: formData.taxExempt,
        taxExemptDocumentUrl: formData.taxExemptDocumentUrl,
        photo: formData.photo // Include photo (compressed base64 data URL)
      };
      
      console.log('Profile data being sent:', JSON.stringify(profileData, null, 2));
      console.log('Title in profileData:', profileData.title);
      
      await apiService.updateClientInfo(profileData);
      
      // Refresh client info to get latest data from server (no delay needed - Drive upload happens server-side)
      const updatedInfo = await apiService.getClientInfo(false); // Don't use cache after profile update
      if (updatedInfo.success && updatedInfo.clientInfo) {
        // Update localStorage with fresh data
        const currentClientInfo = JSON.parse(localStorage.getItem('clientInfo') || '{}');
        const mergedClientInfo = {
          ...currentClientInfo,
          ...updatedInfo.clientInfo
        };
        localStorage.setItem('clientInfo', JSON.stringify(mergedClientInfo));
        
        // Debug: Log photo URL
        console.log('Photo URL from server:', updatedInfo.clientInfo.photo);
        
        // Mark that we just saved to prevent sync effect from overriding
        justSavedRef.current = true;
        
        // Directly update formData with fresh data so UI updates immediately
        const newFormData = createFormDataFromClientInfo(mergedClientInfo);
        console.log('Updated formData.photo:', newFormData.photo);
        console.log('Updated formData.title:', newFormData.title);
        console.log('Title from server:', updatedInfo.clientInfo.title);
        setFormData(newFormData);
      }
      
      showAlert('Profile updated successfully!');
      setEditingProfile(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      showAlert('Failed to update profile: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Use the InputField component defined outside
  const InputField = ProfileInputField;

    return (
    <div style={{ paddingBottom: '180px', overflow: 'visible' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .profile-section-header {
            font-size: 24px !important;
            font-weight: 300 !important;
            font-family: 'Domaine Text', serif !important;
            color: #000000 !important;
            text-transform: uppercase !important;
            letter-spacing: normal !important;
          }
          h3.profile-section-header {
            font-size: 24px !important;
            font-weight: 300 !important;
            font-family: 'Domaine Text', serif !important;
            color: #000000 !important;
            text-transform: uppercase !important;
            letter-spacing: normal !important;
          }
          .profile-input-label {
            font-size: 11px !important;
            font-weight: 400 !important;
            color: #6b6b6b !important;
            letter-spacing: 0.15em !important;
          }
          .professional-preferences-label {
            font-size: 11px !important;
            font-weight: 400 !important;
            color: #6b6b6b !important;
            letter-spacing: 0.15em !important;
          }
          .profile-action-buttons {
            justify-content: center !important;
          }
          .profile-action-buttons button {
            text-align: center !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
          }
        }
        @media (min-width: 769px) {
          .profile-section-header .mobile-text {
            display: none !important;
          }
          .main-content {
            overflow: visible !important;
          }
          .content-area {
            overflow: visible !important;
          }
          .profile-section-container {
            align-items: flex-start !important;
            position: relative !important;
            overflow: visible !important;
          }
          .profile-section-left {
            position: -webkit-sticky !important;
            position: sticky !important;
            top: 0 !important;
            align-self: flex-start !important;
            height: fit-content !important;
            z-index: 10 !important;
            will-change: transform !important;
          }
        }
      ` }} />
      <div style={{ display: 'flex', gap: '64px', marginTop: '0', justifyContent: 'flex-start' }} className="profile-section-container">
        {/* Left Column - Profile Icon */}
        <div style={{ 
          flex: '0 0 240px', 
          flexShrink: 0,
          ...(!isMobile ? {
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: 'fit-content',
            zIndex: 10
          } : {
            position: 'relative'
          })
        }} className="profile-section-left">
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: '300', 
            color: '#000000',
            fontFamily: "'Domaine Text', serif",
            marginBottom: '32px',
            letterSpacing: 'normal',
            textTransform: 'uppercase',
            textAlign: 'center'
          }} className="profile-title">
            Your Account
          </h2>
          <div style={{ 
            width: '240px', 
            height: '240px', 
            borderRadius: '8px', 
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }} className="profile-image-container">
            {formData.photo && formData.photo.trim() !== '' ? (
              <img 
                src={formData.photo} 
                alt="Profile" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                onError={(e) => {
                  console.error('Error loading profile photo:', formData.photo);
                  // Try alternative Drive URL format if thumbnail fails
                  if (formData.photo.includes('thumbnail')) {
                    const fileId = formData.photo.match(/id=([^&]+)/)?.[1];
                    if (fileId) {
                      e.target.src = `https://drive.google.com/uc?export=view&id=${fileId}`;
                      return;
                    }
                  }
                  e.target.style.display = 'none';
                }}
                loading="lazy"
              />
            ) : (
              <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="140" height="140" rx="8" fill="#f3f4f6"/>
                <circle cx="70" cy="55" r="22" stroke="#999" strokeWidth="2" fill="none"/>
                <path d="M35 110 Q35 85 70 85 Q105 85 105 110" stroke="#999" strokeWidth="2" fill="none"/>
              </svg>
            )}
          </div>
          {editingProfile && (
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  try {
                    // Compress image before storing (reduces upload time)
                    const compressedBlob = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const img = new Image();
                        img.onload = () => {
                          // Max dimensions for profile photos (800px is plenty for display)
                          const maxWidth = 800;
                          const maxHeight = 800;
                          let width = img.width;
                          let height = img.height;
                          
                          if (width > maxWidth || height > maxHeight) {
                            const ratio = Math.min(maxWidth / width, maxHeight / height);
                            width = width * ratio;
                            height = height * ratio;
                          }
                          
                          // Create canvas and compress
                          const canvas = document.createElement('canvas');
                          canvas.width = width;
                          canvas.height = height;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0, width, height);
                          
                          // Convert to blob with compression
                          const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
                          const quality = 0.85; // Good quality but smaller file
                          canvas.toBlob((blob) => {
                            if (blob) {
                              resolve(blob);
                            } else {
                              reject(new Error('Failed to compress image'));
                            }
                          }, mimeType, quality);
                        };
                        img.onerror = () => reject(new Error('Error loading image'));
                        img.src = reader.result;
                      };
                      reader.readAsDataURL(file);
                    });
                    
                    // Convert compressed blob to data URL
                  const reader = new FileReader();
                  reader.onload = (event) => {
                      setFormData(prev => ({ ...prev, photo: event.target.result }));
                    };
                    reader.readAsDataURL(compressedBlob);
                  } catch (error) {
                    console.error('Error compressing image:', error);
                    // Fallback to original if compression fails
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setFormData(prev => ({ ...prev, photo: event.target.result }));
                  };
                  reader.readAsDataURL(file);
                  }
                }
              }}
              style={{ 
                fontSize: '13px', 
                marginTop: '16px',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}
            />
          )}
          
          {/* Action Buttons - Moved to left column */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '12px', 
            marginTop: '32px',
            width: '100%'
          }}>
            {!editingProfile ? (
              <>
                {/* Edit Button */}
                <button
                  onClick={() => {
                    setEditingProfile(true);
                    // Scroll to top of page smoothly
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  style={{
                    backgroundColor: '#e8e8e3',
                    color: '#000000',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d4d4cd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8e8e3';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Edit
                </button>
                
                {/* Password Reset Button */}
                <button
                  onClick={async () => {
                    const newPassword = await showPrompt('Enter your new password (minimum 8 characters):', 'Password');
                    if (newPassword && newPassword.length >= 8) {
                      // TODO: Implement password reset API call
                      await showAlert('Password reset requested. Please contact support to complete the password change.');
                    } else if (newPassword) {
                      await showAlert('Password must be at least 8 characters long.');
                    }
                  }}
                  style={{
                    backgroundColor: '#e8e8e3',
                    color: '#000000',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d4d4cd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8e8e3';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Reset Password
                </button>
                
                {/* Logout Button */}
                <button
                  onClick={() => {
                    showConfirm('Are you sure you want to log out?').then((confirmed) => {
                      if (confirmed) {
                        onLogout();
                      }
                    });
                  }}
                  style={{
                    backgroundColor: '#e8e8e3',
                    color: '#000000',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d4d4cd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8e8e3';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: saving ? '#b8b8b0' : '#e8e8e3',
                    color: '#000000',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: saving ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    opacity: saving ? 0.7 : 1,
                    width: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.backgroundColor = '#d4d4cd';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.currentTarget.style.backgroundColor = '#e8e8e3';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {saving && (
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid #000000',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      display: 'inline-block'
                    }} />
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                  <style dangerouslySetInnerHTML={{ __html: `
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  ` }} />
                </button>
                <button
                  onClick={() => {
                    setEditingProfile(false);
                    // Reset formData to clientInfo values when canceling
                    setFormData(prev => ({
                      ...prev,
                      fullName: clientInfo?.fullName || prev.fullName,
                      companyName: clientInfo?.clientCompanyName || prev.companyName,
                      title: clientInfo?.title || prev.title,
                      email: clientInfo?.email || prev.email,
                      phone: clientInfo?.phone || prev.phone,
                      mailingAddress: clientInfo?.mailingAddress || prev.mailingAddress,
                      addressType: clientInfo?.addressType || prev.addressType || 'Residence',
                      companyMailingAddress: clientInfo?.companyMailingAddress || prev.companyMailingAddress,
                      companyPhone: clientInfo?.companyPhone || prev.companyPhone,
                      companyEmail: clientInfo?.companyEmail || prev.companyEmail,
                      birthday: clientInfo?.birthday || prev.birthday,
                      favoriteRestaurant: clientInfo?.favoriteRestaurant || prev.favoriteRestaurant,
                      favoriteBrand: clientInfo?.favoriteBrand || prev.favoriteBrand,
                      favoriteFlower: clientInfo?.favoriteFlower || prev.favoriteFlower,
                      favoriteColor: clientInfo?.favoriteColor || prev.favoriteColor,
                      preferredCommunication: clientInfo?.preferredCommunication || prev.preferredCommunication,
                      preferredPaymentMethod: clientInfo?.preferredPaymentMethod || prev.preferredPaymentMethod,
                      taxExempt: clientInfo?.taxExempt || prev.taxExempt,
                      taxExemptDocumentUrl: clientInfo?.taxExemptDocumentUrl || prev.taxExemptDocumentUrl
                    }));
                  }}
                  style={{
                    backgroundColor: '#e8e8e3',
                    color: '#000000',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d4d4cd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8e8e3';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Form Fields */}
        <div style={{ flex: '1', maxWidth: '600px', marginLeft: '120px' }} className="profile-section-right">
          {/* Toggle: Individual Details / Company Details */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '32px',
            marginTop: '32px',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '16px'
          }}>
            <button
              onClick={() => setAccountView('individual')}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: accountView === 'individual' ? '500' : '400',
                color: accountView === 'individual' ? '#000000' : '#8b8b8b',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: accountView === 'individual' ? '2px solid #000000' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.2s',
                marginBottom: '-18px'
              }}
            >
              Individual Details
            </button>
            <button
              onClick={() => setAccountView('company')}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: accountView === 'company' ? '500' : '400',
                color: accountView === 'company' ? '#000000' : '#8b8b8b',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: accountView === 'company' ? '2px solid #000000' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.2s',
                marginBottom: '-18px'
              }}
            >
              Company Details
            </button>
          </div>

          {/* Individual Details View */}
          {accountView === 'individual' && (
            <>
              {/* Reserve Member Details Section */}
              <div style={{ marginBottom: '48px' }}>
            <h3 className="profile-section-header" style={isMobile ? {
              fontSize: '24px',
              fontWeight: '300',
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              textTransform: 'uppercase',
              letterSpacing: 'normal',
              marginBottom: '24px'
            } : {
              fontSize: '11px', 
              fontWeight: '400', 
              color: '#6b6b6b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '24px'
            }}>
              Reserve Member Details
            </h3>
            
            <InputField
              label="Member ID"
              value={memberId}
              readOnly={true}
              displayValue={memberId}
              isEditing={editingProfile}
              memberId={memberId}
            />
            
            <InputField
              label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Company Name"
              value={formData.companyName}
              readOnly={true}
              displayValue={formData.companyName}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={handleInputBlur}
              isEditing={editingProfile}
              type="email"
            />
            
            <InputField
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={handleInputBlur}
              isEditing={editingProfile}
              type="tel"
            />
            
            <InputField
              label="Mailing Address"
              value={formData.mailingAddress}
              onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
              onBlur={handleInputBlur}
              isEditing={editingProfile}
              type="textarea"
              autoComplete="street-address"
              id="personal-mailing-address-input"
            />

            {/* Address Type */}
            <div style={{ marginBottom: '24px' }}>
              <label className="profile-input-label" style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: '600', 
                marginBottom: '8px', 
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Address Type
              </label>
              {editingProfile ? (
                <select
                  value={formData.addressType || 'Residence'}
                  onChange={(e) => handleInputChange('addressType', e.target.value, e)}
                  onBlur={(e) => {
                    handleInputBlur();
                    e.target.style.borderBottomColor = '#d1d5db';
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 0',
                    border: 'none',
                    borderBottom: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '15px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    backgroundColor: 'transparent',
                    color: '#000000',
                    boxSizing: 'border-box',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#000000'}
                >
                  <option value="Residence">Residence</option>
                  <option value="Office">Office</option>
                </select>
              ) : (
                <div style={{ 
                  fontSize: '15px', 
                  color: '#000000', 
                  padding: '8px 0',
                  borderBottom: '1px solid #e5e7eb',
                  minHeight: '23px'
                }}>
                  {formData.addressType || 'Residence'}
                </div>
              )}
            </div>
            
            <InputField
              label="Birthday"
              value={formData.birthday}
              onChange={(e) => handleInputChange('birthday', e.target.value)}
              onBlur={handleInputBlur}
              isEditing={editingProfile}
              type="month-day"
            />
          </div>

          {/* Professional Preferences Section */}
          <div style={{ marginBottom: '48px' }}>
            <h3 className="profile-section-header" style={isMobile ? {
              fontSize: '24px',
              fontWeight: '300',
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              textTransform: 'none',
              letterSpacing: 'normal',
              marginBottom: '24px'
            } : {
              fontSize: '11px', 
              fontWeight: '400', 
              color: '#6b6b6b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '24px'
            }}>
              PROFESSIONAL PREFERENCES
            </h3>
            
            {/* Preferred Method of Communication */}
            <div style={{ marginBottom: '24px' }}>
              <label className="professional-preferences-label" style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: '600', 
                marginBottom: '8px', 
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Preferred Method of Communication
              </label>
              {editingProfile ? (
                <select
                  value={formData.preferredCommunication || ''}
                  onChange={(e) => handleInputChange('preferredCommunication', e.target.value, e)}
                  onBlur={handleInputBlur}
                  style={{
                    width: '100%',
                    padding: '8px 0',
                    border: 'none',
                    borderBottom: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '15px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    backgroundColor: 'transparent',
                    color: '#000000',
                    boxSizing: 'border-box',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <option value="">Select...</option>
                  <option value="Phone Call">Phone Call</option>
                  <option value="Email">Email</option>
                  <option value="Text">Text</option>
                </select>
              ) : (
                <div style={{ 
                  fontSize: '15px', 
                  color: '#000000', 
                  padding: '8px 0',
                  borderBottom: '1px solid #e5e7eb',
                  minHeight: '23px'
                }}>
                  {formData.preferredCommunication || 'Not set'}
                </div>
              )}
            </div>
          </div>

          {/* Personal Interests Section */}
          <div>
            <h3 className="profile-section-header" style={isMobile ? {
              fontSize: '24px',
              fontWeight: '300',
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              textTransform: 'none',
              letterSpacing: 'normal',
              marginBottom: '24px'
            } : {
              fontSize: '11px', 
              fontWeight: '400', 
              color: '#6b6b6b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '24px'
            }}>
              PERSONAL INTERESTS
            </h3>
            
            <InputField
              label="Favorite Restaurant"
              value={formData.favoriteRestaurant}
              onChange={(e) => handleInputChange('favoriteRestaurant', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Favorite Brand / Shop"
              value={formData.favoriteBrand}
              onChange={(e) => handleInputChange('favoriteBrand', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Favorite Flower"
              value={formData.favoriteFlower}
              onChange={(e) => handleInputChange('favoriteFlower', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
            
            <InputField
              label="Favorite Color"
              value={formData.favoriteColor}
              onChange={(e) => handleInputChange('favoriteColor', e.target.value)}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              isEditing={editingProfile}
            />
          </div>
            </>
          )}

          {/* Company Details View */}
          {accountView === 'company' && (
            <>
              {/* Company Details Section */}
              <div style={{ marginBottom: '48px' }}>
                <h3 className="profile-section-header" style={isMobile ? {
                  fontSize: '24px',
                  fontWeight: '300',
                  color: '#000000',
                  fontFamily: "'Domaine Text', serif",
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  marginBottom: '24px'
                } : {
                  fontSize: '11px', 
                  fontWeight: '400', 
                  color: '#6b6b6b',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  marginBottom: '24px'
                }}>
                  COMPANY DETAILS
                </h3>

                <InputField
                  label="Mailing Address"
                  value={formData.companyMailingAddress}
                  onChange={(e) => handleInputChange('companyMailingAddress', e.target.value)}
                  onBlur={handleInputBlur}
                  isEditing={editingProfile}
                  type="textarea"
                  autoComplete="street-address"
                  id="company-mailing-address-input"
                />

                <InputField
                  label="Phone"
                  value={formData.companyPhone}
                  onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                  onBlur={handleInputBlur}
                  isEditing={editingProfile}
                  type="tel"
                />

                <InputField
                  label="Email"
                  value={formData.companyEmail}
                  onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                  onBlur={handleInputBlur}
                  isEditing={editingProfile}
                  type="email"
                />

                {/* Preferred Payment Method */}
                <div style={{ marginBottom: '24px' }}>
                  <label className="professional-preferences-label" style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: '#000000',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Preferred Payment Method
                  </label>
                  {editingProfile ? (
                    <select
                      value={formData.preferredPaymentMethod || ''}
                      onChange={(e) => handleInputChange('preferredPaymentMethod', e.target.value, e)}
                      onBlur={handleInputBlur}
                      style={{
                        width: '100%',
                        padding: '8px 0',
                        border: 'none',
                        borderBottom: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '15px',
                        fontFamily: "'NeueHaasUnica', sans-serif",
                        backgroundColor: 'transparent',
                        color: '#000000',
                        boxSizing: 'border-box',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="ACH/Wire">ACH/Wire</option>
                      <option value="Check">Check</option>
                      <option value="Credit Card (3% Fee)">Credit Card (3% Fee)</option>
                    </select>
                  ) : (
                    <div style={{ 
                      fontSize: '15px', 
                      color: '#000000', 
                      padding: '8px 0',
                      borderBottom: '1px solid #e5e7eb',
                      minHeight: '23px'
                    }}>
                      {formData.preferredPaymentMethod || 'Not set'}
                    </div>
                  )}
                </div>

                {/* Tax Exempt */}
                <div style={{ marginBottom: '24px' }}>
                  <label className="professional-preferences-label" style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: '#000000',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Tax Exempt
                  </label>
                  {editingProfile ? (
                    <select
                      value={formData.taxExempt || 'Not Exempt'}
                      onChange={(e) => handleInputChange('taxExempt', e.target.value, e)}
                      onBlur={handleInputBlur}
                      style={{
                        width: '100%',
                        padding: '8px 0',
                        border: 'none',
                        borderBottom: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '15px',
                        fontFamily: "'NeueHaasUnica', sans-serif",
                        backgroundColor: 'transparent',
                        color: '#000000',
                        boxSizing: 'border-box',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <option value="Not Exempt">Not Exempt</option>
                      <option value="Exempt">Exempt</option>
                    </select>
                  ) : (
                    <div style={{ 
                      fontSize: '15px', 
                      color: '#000000', 
                      padding: '8px 0',
                      borderBottom: '1px solid #e5e7eb',
                      minHeight: '23px'
                    }}>
                      {formData.taxExempt || 'Not Exempt'}
                    </div>
                  )}
                </div>

                {/* Tax Exempt Document Upload */}
                {formData.taxExempt === 'Exempt' && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      color: '#000000',
                      fontFamily: "'NeueHaasUnica', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Tax Exempt Document
                    </label>
                    {editingProfile ? (
                      <div>
                        {formData.taxExemptDocumentUrl && (
                          <div style={{ 
                            marginBottom: '12px',
                            fontSize: '13px',
                            color: '#666',
                            fontFamily: "'NeueHaasUnica', sans-serif"
                          }}>
                            Current document: <a 
                              href={formData.taxExemptDocumentUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#545142', textDecoration: 'underline' }}
                            >
                              View Document
                            </a>
                          </div>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                // #region agent log
                                console.log('[DEBUG] setFormData for taxExemptDocument upload', {location:'App.js:3980',currentFormData:formData,timestamp:Date.now(),hypothesisId:'E'});
                                // #endregion
                                setFormData(prev => ({ 
                                  ...prev, 
                                  taxExemptDocument: file,
                                  taxExemptDocumentUrl: event.target.result
                                }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{ 
                            fontSize: '13px', 
                            fontFamily: "'NeueHaasUnica', sans-serif",
                            padding: '8px 0',
                            borderBottom: '1px solid #d1d5db',
                            width: '100%',
                            cursor: 'pointer'
                          }}
                        />
                        <div style={{ 
                          marginTop: '8px',
                          fontSize: '12px',
                          color: '#666',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          fontStyle: 'italic'
                        }}>
                          Accepted formats: PDF, JPG, PNG
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        fontSize: '15px', 
                        color: '#000000', 
                        padding: '8px 0',
                        borderBottom: '1px solid #e5e7eb',
                        minHeight: '23px'
                      }}>
                        {formData.taxExemptDocumentUrl ? (
                          <a 
                            href={formData.taxExemptDocumentUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#545142', textDecoration: 'underline' }}
                          >
                            View Tax Exempt Document
                          </a>
                        ) : (
                          'No document uploaded'
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Team Members Section */}
              <div style={{ marginBottom: '48px' }}>
                <h3 className="profile-section-header" style={isMobile ? {
                  fontSize: '24px',
                  fontWeight: '300',
                  color: '#000000',
                  fontFamily: "'Domaine Text', serif",
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  marginBottom: '24px'
                } : {
                  fontSize: '11px', 
                  fontWeight: '400', 
                  color: '#6b6b6b',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  marginBottom: '24px'
                }}>
                  TEAM MEMBERS
                </h3>

            {/* Team Members List */}
            {loadingTeamMembers ? (
              <div style={{ 
                fontSize: '14px', 
                color: '#8b8b8b', 
                padding: '24px 0',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}>
                Loading team members...
              </div>
            ) : teamMembers.length > 0 ? (
              <div style={{ marginBottom: '32px' }}>
                {teamMembers.map((member, index) => (
                  <div
                    key={member.email || index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 0',
                      borderBottom: index < teamMembers.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <div>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '400',
                        color: '#000000',
                        fontFamily: "'NeueHaasUnica', sans-serif",
                        marginBottom: '4px'
                      }}>
                        {member.fullName || `${member.firstName} ${member.lastName}`.trim()}
                      </div>
                      {member.title && (
                        <div style={{
                          fontSize: '13px',
                          color: '#8b8b8b',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          marginBottom: '4px'
                        }}>
                          {member.title}
                        </div>
                      )}
                      <div style={{
                        fontSize: '13px',
                        color: '#8b8b8b',
                        fontFamily: "'NeueHaasUnica', sans-serif"
                      }}>
                        {member.email}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedMemberForRemoval(member);
                        setShowRemoveMemberModal(true);
                      }}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#8b8b8b',
                        backgroundColor: 'transparent',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontFamily: "'NeueHaasUnica', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#000000';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#8b8b8b';
                      }}
                    >
                      Request Removal
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                fontSize: '14px', 
                color: '#8b8b8b', 
                padding: '24px 0',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}>
                No team members found.
              </div>
            )}

            {/* Add New Team Member Button */}
            <button
              onClick={() => setShowAddMemberModal(true)}
              style={{
                padding: '12px 24px',
                fontSize: '13px',
                fontWeight: '400',
                color: '#000000',
                backgroundColor: '#F7F6F0',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0ede5';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#F7F6F0';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              Add New Team Member
            </button>
          </div>
            </>
          )}
        </div>
      </div>


      {/* Add Team Member Modal */}
      {showAddMemberModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowAddMemberModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '300',
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              marginBottom: '24px'
            }}>
              Add New Team Member
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                First Name
              </label>
              <input
                type="text"
                value={newMemberData.firstName}
                onChange={(e) => setNewMemberData({ ...newMemberData, firstName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Last Name
              </label>
              <input
                type="text"
                value={newMemberData.lastName}
                onChange={(e) => setNewMemberData({ ...newMemberData, lastName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Email
              </label>
              <input
                type="email"
                value={newMemberData.email}
                onChange={(e) => setNewMemberData({ ...newMemberData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Title
              </label>
              <input
                type="text"
                value={newMemberData.title}
                onChange={(e) => setNewMemberData({ ...newMemberData, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setNewMemberData({ firstName: '', lastName: '', email: '', title: '' });
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '400',
                  color: '#000000',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newMemberData.firstName || !newMemberData.lastName || !newMemberData.email || !newMemberData.title) {
                    showAlert('Please fill in all required fields (First Name, Last Name, Email, and Title).');
                    return;
                  }

                  if (submittingAddMember) return; // Prevent double submission

                  setSubmittingAddMember(true);
                  try {
                    const token = localStorage.getItem('clientToken');
                    const url = new URL(CLIENT_API_URL);
                    url.searchParams.append('action', 'addTeamMember');
                    url.searchParams.append('token', token);
                    url.searchParams.append('firstName', newMemberData.firstName);
                    url.searchParams.append('lastName', newMemberData.lastName);
                    url.searchParams.append('email', newMemberData.email);
                    url.searchParams.append('title', newMemberData.title);

                    const response = await fetch(url.toString());
                    const result = await response.json();

                    if (result.success) {
                      showAlert('Team member request submitted successfully. They will be added once approved.');
                      setShowAddMemberModal(false);
                      setNewMemberData({ firstName: '', lastName: '', email: '', title: '' });
                      // Refresh team members list
                      const fetchTeamMembers = async () => {
                        const token = localStorage.getItem('clientToken');
                        const url = new URL(CLIENT_API_URL);
                        url.searchParams.append('action', 'getTeamMembers');
                        url.searchParams.append('token', token);
                        const response = await fetch(url.toString());
                        const result = await response.json();
                        if (result.success && result.teamMembers) {
                          setTeamMembers(result.teamMembers);
                        }
                      };
                      fetchTeamMembers();
                    } else {
                      showAlert(result.error || 'Failed to add team member.');
                    }
                  } catch (error) {
                    console.error('Error adding team member:', error);
                    showAlert('An error occurred. Please try again.');
                  } finally {
                    setSubmittingAddMember(false);
                  }
                }}
                disabled={submittingAddMember}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '400',
                  color: submittingAddMember ? '#999' : '#FFFFFF',
                  backgroundColor: submittingAddMember ? '#d1d5db' : '#000000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: submittingAddMember ? 'not-allowed' : 'pointer',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  opacity: submittingAddMember ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {submittingAddMember ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Team Member Modal */}
      {showRemoveMemberModal && selectedMemberForRemoval && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => {
          setShowRemoveMemberModal(false);
          setSelectedMemberForRemoval(null);
          setRemovalReason('');
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '300',
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              marginBottom: '8px'
            }}>
              Request Removal of Team Member
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              marginBottom: '24px'
            }}>
              {selectedMemberForRemoval.fullName || `${selectedMemberForRemoval.firstName} ${selectedMemberForRemoval.lastName}`.trim()}
              <br />
              {selectedMemberForRemoval.email}
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Reason for Removal
              </label>
              <select
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select a reason...</option>
                <option value="No longer with company">No longer with company</option>
                <option value="Does not need portal access">Does not need portal access</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRemoveMemberModal(false);
                  setSelectedMemberForRemoval(null);
                  setRemovalReason('');
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '400',
                  color: '#000000',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: "'NeueHaasUnica', sans-serif"
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!removalReason) {
                    showAlert('Please select a reason for removal.');
                    return;
                  }

                  try {
                    const token = localStorage.getItem('clientToken');
                    const url = new URL(CLIENT_API_URL);
                    url.searchParams.append('action', 'requestRemoveTeamMember');
                    url.searchParams.append('token', token);
                    url.searchParams.append('memberEmail', selectedMemberForRemoval.email);
                    url.searchParams.append('reason', removalReason);

                    const response = await fetch(url.toString());
                    const result = await response.json();

                    if (result.success) {
                      showAlert('Thank you—your request has been received. Our team will review and finalize the access update shortly.');
                      setShowRemoveMemberModal(false);
                      setSelectedMemberForRemoval(null);
                      setRemovalReason('');
                      // Refresh team members list
                      const fetchTeamMembers = async () => {
                        const token = localStorage.getItem('clientToken');
                        const url = new URL(CLIENT_API_URL);
                        url.searchParams.append('action', 'getTeamMembers');
                        url.searchParams.append('token', token);
                        const response = await fetch(url.toString());
                        const result = await response.json();
                        if (result.success && result.teamMembers) {
                          setTeamMembers(result.teamMembers);
                        }
                      };
                      fetchTeamMembers();
                    } else {
                      showAlert(result.error || 'Failed to submit removal request.');
                    }
                  } catch (error) {
                    console.error('Error requesting removal:', error);
                    showAlert('An error occurred. Please try again.');
                  } finally {
                    setSubmittingRemoveMember(false);
                  }
                }}
                disabled={submittingRemoveMember || !removalReason}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '400',
                  color: (submittingRemoveMember || !removalReason) ? '#999' : '#FFFFFF',
                  backgroundColor: (submittingRemoveMember || !removalReason) ? '#d1d5db' : '#000000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (submittingRemoveMember || !removalReason) ? 'not-allowed' : 'pointer',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  opacity: (submittingRemoveMember || !removalReason) ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {submittingRemoveMember ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// OVERVIEW SECTION
// ============================================

export function OverviewSection({ clientInfo, spendData, proposals = [], setSelectedProposal, brandCharcoal = '#2C2C2C', setActiveSection, loading = false }) {
  const [shopifyProducts, setShopifyProducts] = useState([]);
  // Always show 2026 data (no year toggle)
  const selectedYear = 2026;
  
  // Helper function to get display status (matching Activity page logic)
  const getDisplayStatus = (proposal) => {
    const status = proposal.status || 'Pending';
    
    // For active proposals, show their actual status (Pending, Approved, Confirmed)
    // Only show "Completed" if the start date has passed and it's not active
    if (status === 'Approved' || status === 'Confirmed') {
      const startDate = proposal.startDate;
      if (startDate) {
        const startDateObj = parseDateSafely(startDate);
        if (startDateObj && !isNaN(startDateObj.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDateOnly = new Date(startDateObj);
          startDateOnly.setHours(0, 0, 0, 0);
          
          // If start date is in the past, show as Completed
          if (startDateOnly.getTime() < today.getTime()) {
            return 'Completed';
          }
        }
      }
    }
    
    // Return original status for all other cases
    return status || 'Pending';
  };
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  // Fetch Shopify products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      const products = await shopifyService.getFeaturedProducts();
      setShopifyProducts(products);
      setLoadingProducts(false);
    };
    fetchProducts();
  }, []);
  
  const currentYear = new Date().getFullYear();
  
  // Helper function to filter proposals by year
  const filterProposalsByYear = (proposalsList, year) => {
    return proposalsList.filter(p => {
      // Exclude cancelled projects
      if (p.status === 'Cancelled') return false;
      
      // Only include Confirmed and Completed projects (exclude Pending, Approved, and Cancelled)
      if (p.status !== 'Confirmed' && p.status !== 'Completed') {
        return false;
      }
      
      // For historical projects, prioritize event date over timestamp
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      
      if (isHistorical) {
        // PRIMARY: Check startDate (event date) for historical projects
        if (p.startDate) {
          const start = parseDateSafely(p.startDate);
          if (start && !isNaN(start.getTime())) {
            const startYear = start.getFullYear();
            return startYear === year;
          }
        }
        
        // FALLBACK: Check eventDate string for historical projects
        if (p.eventDate) {
          const yearMatch = p.eventDate.match(/\d{4}/);
          if (yearMatch) {
            return parseInt(yearMatch[0]) === year;
          }
        }
        
        // LAST RESORT: Use timestamp (should be set to event date for historical projects)
        if (p.timestamp) {
          const confirmationYear = new Date(p.timestamp).getFullYear();
          return confirmationYear === year;
        }
        
        return false;
      }
      
      // For regular (non-historical) projects, use timestamp (booking/confirmation date)
      // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        if (confirmationYear === year) return true;
      }
      
      // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
      // Also check startDate for regular proposals that might have event date in the year
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          const startYear = start.getFullYear();
          if (startYear === year) return true;
        }
      }
      
      // LAST RESORT: For regular proposals with status "Completed" or "Confirmed" but no dates,
      // include them if they're not historical (they're from the proposal app)
      // This ensures regular proposals aren't excluded due to missing dates
      // IMPORTANT: Only include if we're looking at the current year or if status is Completed
      // For Confirmed projects without dates, we can't determine the year, so exclude them
      if (p.status === 'Completed' && !isHistorical) {
        // If it's a completed regular proposal without dates, include it
        // This handles edge cases where proposals might not have proper timestamps
        return true;
      }
      
      // For Confirmed projects without dates, we can't determine which year they belong to
      // Exclude them to avoid incorrect year attribution
      return false;
    }).sort((a, b) => {
      // Use shared utility function for consistent sorting
      const dateA = getSortableDateFromProposal(a);
      const dateB = getSortableDateFromProposal(b);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
      
      // Primary sort: by date (descending - newest first)
      if (timeB !== timeA) {
        return timeB - timeA;
      }
      
      // Secondary sort: by projectNumber (descending) if dates are equal
      const projectNumA = parseInt(a.projectNumber) || 0;
      const projectNumB = parseInt(b.projectNumber) || 0;
      return projectNumB - projectNumA;
    });
  };
  
  // Memoize yearProposals for current year (2026) - only recalculates when proposals change
  const yearProposals = useMemo(() => {
    return filterProposalsByYear(proposals, currentYear);
  }, [proposals, currentYear]);
  
  // Memoize year2025Proposals for 2025 highlights - only recalculates when proposals change
  // IMPORTANT: Include ALL confirmed/completed projects for 2025, regardless of whether they're historical or regular
  const year2025ProposalsForHighlights = useMemo(() => {
    return filterProposalsByYear(proposals, 2025);
  }, [proposals]);
  
  // ALL confirmed/completed projects (for Projects Booked count and Completed list)
  // This includes both regular proposals and historical projects
  const allConfirmedCompletedProposals = useMemo(() => {
    return proposals.filter(p => {
      // Exclude cancelled projects
      if (p.status === 'Cancelled') return false;
      
      // Include Confirmed and Completed projects
      const status = (p.status || '').toLowerCase();
      const isConfirmed = status === 'confirmed' || status.includes('confirmed');
      const isCompleted = status === 'completed' || status.includes('completed');
      
      return isConfirmed || isCompleted;
    }).sort((a, b) => {
      // Use shared utility function for consistent sorting
      const dateA = getSortableDateFromProposal(a);
      const dateB = getSortableDateFromProposal(b);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
      
      // Primary sort: by date (descending - newest first)
      if (timeB !== timeA) {
        return timeB - timeA;
      }
      
      // Secondary sort: by projectNumber (descending) if dates are equal
      const projectNumA = parseInt(a.projectNumber) || 0;
      const projectNumB = parseInt(b.projectNumber) || 0;
      return projectNumB - projectNumA;
    });
  }, [proposals]);
  
  // PERFORMANCE: Use pre-calculated points from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have the values
  const currentYearSpend = useMemo(() => {
    // Use 2025 points from Active Companies tab if available (faster)
    if (clientInfo?.points2025 !== null && clientInfo?.points2025 !== undefined) {
      return clientInfo.points2025;
    }
    // Fallback: Calculate from proposals if not available
    return yearProposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [yearProposals, clientInfo?.points2025]);
  
  // PERFORMANCE: Use tier from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have tier
  let tierBaseSpend = clientInfo?.spend2025 || currentYearSpend;
  let carriedOverTier = null;
  
  // Use tier from Active Companies if available
  if (clientInfo?.tier && clientInfo?.tierDiscount !== null && clientInfo?.tierDiscount !== undefined) {
    carriedOverTier = {
      discount: parseInt(clientInfo.tierDiscount) || 0,
      tier: clientInfo.tier,
      baseSpend: clientInfo.spend2025 || 0
    };
  }
  
  // Memoize year2025Proposals for performance
  // Only Confirmed and Completed projects count toward points
  // For historical projects: Points are attributed based on event date (when the event occurred)
  // For regular projects: Points are attributed based on booking/confirmation date (timestamp)
  const year2025Proposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      
      // Include Confirmed OR Completed projects (exclude Pending, Approved)
      // Also handle "Confirmed and Completed" as a possible status string
      const status = (p.status || '').toLowerCase();
      const isConfirmed = status === 'confirmed' || status.includes('confirmed');
      const isCompleted = status === 'completed' || status.includes('completed');
      
      // Include if status is Confirmed OR Completed (or both)
      if (!isConfirmed && !isCompleted) {
        return false;
      }
      
      // For historical projects, prioritize event date over timestamp
      // Historical projects don't have a "booking date" - use when the event occurred
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      
      if (isHistorical) {
        // PRIMARY: Check startDate (event date) for historical projects
        if (p.startDate) {
          const start = parseDateSafely(p.startDate);
          if (start && !isNaN(start.getTime())) {
            const startYear = start.getFullYear();
            return startYear === 2025;
          }
        }
        
        // FALLBACK: Check eventDate string for historical projects
        if (p.eventDate) {
          const yearMatch = p.eventDate.match(/\d{4}/);
          if (yearMatch) {
            return parseInt(yearMatch[0]) === 2025;
          }
        }
        
        // LAST RESORT: Use timestamp (should be set to event date for historical projects)
        if (p.timestamp) {
          const confirmationYear = new Date(p.timestamp).getFullYear();
          return confirmationYear === 2025;
        }
        
        return false;
      }
      
      // For regular (non-historical) projects, use timestamp (booking/confirmation date)
      // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        return confirmationYear === 2025;
      }
      
      // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          const startYear = start.getFullYear();
          return startYear === 2025;
        }
      }
      
      return false;
    });
  }, [proposals]);
  
  // PERFORMANCE: Use pre-calculated 2025 points from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have the values
  const year2025Spend = useMemo(() => {
    // Use 2025 points from Active Companies tab if available (faster)
    if (clientInfo?.points2025 !== null && clientInfo?.points2025 !== undefined) {
      return clientInfo.points2025;
    }
    // Fallback: Calculate from 2025 proposals if not available
    return year2025ProposalsForHighlights.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2025ProposalsForHighlights, clientInfo?.points2025]);
  
  // Memoize year2026Proposals for performance
  // For historical projects: Points are attributed based on event date (when the event occurred)
  // For regular projects: Points are attributed based on booking/confirmation date (timestamp)
  const year2026Proposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      
      // Only include Confirmed and Completed projects
      if (p.status !== 'Confirmed' && p.status !== 'Completed') {
        return false;
      }
      
      // For historical projects, prioritize event date over timestamp
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      
      if (isHistorical) {
        // PRIMARY: Check startDate (event date) for historical projects
        if (p.startDate) {
          const start = parseDateSafely(p.startDate);
          if (start && !isNaN(start.getTime())) {
            const startYear = start.getFullYear();
            return startYear === 2026;
          }
        }
        
        // FALLBACK: Check eventDate string for historical projects
        if (p.eventDate) {
          const yearMatch = p.eventDate.match(/\d{4}/);
          if (yearMatch) {
            return parseInt(yearMatch[0]) === 2026;
          }
        }
        
        // LAST RESORT: Use timestamp (should be set to event date for historical projects)
        if (p.timestamp) {
          const confirmationYear = new Date(p.timestamp).getFullYear();
          return confirmationYear === 2026;
        }
        
        return false;
      }
      
      // For regular (non-historical) projects, use timestamp (booking/confirmation date)
      // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        return confirmationYear === 2026;
      }
      
      // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          const startYear = start.getFullYear();
          return startYear === 2026;
        }
      }
      
      return false;
    });
  }, [proposals]);
  
  // Memoize year2026Spend for performance
  const year2026Spend = useMemo(() => {
    return year2026Proposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2026Proposals]);
  
  // Calculate 2025 pending proposals (Pending/Approved status)
  const year2025PendingProposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      if (p.status !== 'Pending' && p.status !== 'Approved') return false;
      
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        return confirmationYear === 2025;
      }
      
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          return start.getFullYear() === 2025;
        }
      }
      
      if (p.isHistorical && p.eventDate) {
        const yearMatch = p.eventDate.match(/\d{4}/);
        if (yearMatch) return parseInt(yearMatch[0]) === 2025;
      }
      
      return false;
    });
  }, [proposals]);
  
  // Calculate 2025 pending spend
  const year2025PendingSpend = useMemo(() => {
    return year2025PendingProposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2025PendingProposals]);
  
  // Calculate 2025 money saved
  const year2025MoneySaved = useMemo(() => {
    return year2025Proposals.reduce((total, proposal) => {
      try {
        if (proposal.isHistorical) {
          let histDiscount = 0;
          if (proposal.historicalDiscount !== undefined && proposal.historicalDiscount !== null) {
            const discountStr = String(proposal.historicalDiscount).trim();
            if (discountStr !== '' && discountStr !== 'undefined' && discountStr !== 'null') {
              histDiscount = parseFloat(discountStr);
              if (isNaN(histDiscount)) histDiscount = 0;
            }
          }
          return total + histDiscount;
        }
        if (proposal.status === 'Pending' || proposal.status === 'Approved') return total;
        const sections = JSON.parse(proposal.sectionsJSON || '[]');
        let baseProductTotal = 0;
        sections.forEach(section => {
          if (section.products && Array.isArray(section.products)) {
            section.products.forEach(product => {
              baseProductTotal += (parseFloat(product.quantity) || 0) * (parseFloat(product.price) || 0);
            });
          }
        });
        let rentalMultiplier = 1.0;
        if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
          const parsed = parseFloat(proposal.customRentalMultiplier);
          if (!isNaN(parsed) && parsed > 0) rentalMultiplier = parsed;
        } else {
          const start = parseDateSafely(proposal.startDate);
          const end = parseDateSafely(proposal.endDate);
          if (start && end) {
            const diffTime = end.getTime() - start.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
            rentalMultiplier = getRentalMultiplier(diffDays);
          }
        }
        const extendedProductTotal = baseProductTotal * rentalMultiplier;
        const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
        let discountType = 'percentage';
        if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
          const match = proposal.discountName.match(/^TYPE:(\w+)/);
          if (match) discountType = match[1];
        }
        const standardRateDiscount = discountType === 'dollar' 
          ? discountValue 
          : extendedProductTotal * (discountValue / 100);
        return total + standardRateDiscount;
      } catch (e) {
        return total;
      }
    }, 0);
  }, [year2025Proposals]);
  
  // Check if this is a pre-2026 member (should start at Studio unless they earned higher in 2025)
  const isPre2026Member = clientInfo?.isPre2026Member || false;
  
  // PERFORMANCE: Use tier and points from Active Companies tab (pre-calculated)
  // Only calculate if clientInfo doesn't have the values
  let displayTierBaseSpend = clientInfo?.spend2025 || currentYearSpend;
  let displayCarriedOverTier = null;
  
  // Use tier from Active Companies if available
  if (clientInfo?.tier && clientInfo?.tierDiscount !== null && clientInfo?.tierDiscount !== undefined) {
    displayCarriedOverTier = {
      discount: parseInt(clientInfo.tierDiscount) || 0,
      tier: clientInfo.tier,
      baseSpend: clientInfo.spend2025 || 0
    };
    displayTierBaseSpend = clientInfo.spend2025 || 0;
  } else {
    // FALLBACK: Calculate tier if not available from Active Companies
    if (selectedYear === 2026) {
      // For 2026, use 2025 spend to determine carried-over tier
      // Pre-2026 members start at Studio (15%) minimum, but can earn higher tiers
      if (year2025Spend >= 200000) {
        displayCarriedOverTier = { discount: 30, tier: 'Estate', baseSpend: year2025Spend };
      } else if (year2025Spend >= 100000) {
        displayCarriedOverTier = { discount: 25, tier: 'Conservatory', baseSpend: year2025Spend };
      } else if (year2025Spend >= 25000) {
        displayCarriedOverTier = { discount: 20, tier: 'Gallery', baseSpend: year2025Spend };
      } else if (year2025Spend >= 5000) {
        displayCarriedOverTier = { discount: 15, tier: 'Studio', baseSpend: year2025Spend };
      } else {
        // For pre-2026 members, default to Studio (15%) minimum if they didn't earn a higher tier
        // For new members (post-2025), they start at House (0%)
        if (isPre2026Member) {
          displayCarriedOverTier = { discount: 15, tier: 'Studio', baseSpend: 0 };
        } else {
          displayCarriedOverTier = { discount: 0, tier: 'House', baseSpend: year2025Spend };
        }
      }
      displayTierBaseSpend = displayCarriedOverTier.baseSpend;
    } else {
      // For 2025, use 2025 spend directly
      displayTierBaseSpend = year2025Spend;
    }
  }
  
  // Keep original logic for backward compatibility (only if tier not from Active Companies)
  if (!carriedOverTier && currentYear >= 2026) {
    // Determine tier based on 2025 achievement
    // Pre-2026 members start at Studio (15%) minimum, but can earn higher tiers
    if (year2025Spend >= 200000) {
      carriedOverTier = { discount: 30, tier: 'Estate', baseSpend: year2025Spend };
    } else if (year2025Spend >= 100000) {
      carriedOverTier = { discount: 25, tier: 'Conservatory', baseSpend: year2025Spend };
    } else if (year2025Spend >= 25000) {
      carriedOverTier = { discount: 20, tier: 'Gallery', baseSpend: year2025Spend };
    } else if (year2025Spend >= 5000) {
      carriedOverTier = { discount: 15, tier: 'Studio', baseSpend: year2025Spend };
    } else {
      // For pre-2026 members, default to Studio (15%) minimum if they didn't earn a higher tier
      // For new members (post-2025), they start at House (0%)
      if (isPre2026Member) {
        carriedOverTier = { discount: 15, tier: 'Studio', baseSpend: 0 };
      } else {
        carriedOverTier = { discount: 0, tier: 'House', baseSpend: year2025Spend };
      }
    }
    tierBaseSpend = carriedOverTier.baseSpend;
  }
  
  const getCurrentTier = (useDisplayValues = false) => {
    const baseSpend = useDisplayValues ? displayTierBaseSpend : tierBaseSpend;
    const carriedOver = useDisplayValues ? displayCarriedOverTier : carriedOverTier;
    const yearSpend = useDisplayValues ? (selectedYear === 2026 ? year2026Spend : year2025Spend) : currentYearSpend;
    
    if (carriedOver && selectedYear === 2026) {
      const tier = carriedOver.tier;
      if (tier === 'Estate') {
        return { discount: 30, tier: 'Estate', nextTier: null, progress: 100, nextTierName: null };
      } else if (tier === 'Conservatory') {
        // Conservatory needs 200,000 total points to reach Estate
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 200000 * 100
        const progress = Math.min((yearSpend / 200000) * 100, 100);
        return { discount: 25, tier: 'Conservatory', nextTier: 'Estate (30%)', progress: progress, nextTierName: 'Estate' };
      } else if (tier === 'Gallery') {
        // Gallery needs 100,000 total points to reach Conservatory
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 100000 * 100
        const progress = Math.min((yearSpend / 100000) * 100, 100);
        return { discount: 20, tier: 'Gallery', nextTier: 'Conservatory (25%)', progress: progress, nextTierName: 'Conservatory' };
      } else if (tier === 'Studio') {
        // Studio needs 25,000 total points to reach Gallery
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 25000 * 100
        const progress = Math.min((yearSpend / 25000) * 100, 100);
        return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: progress, nextTierName: 'Gallery' };
      } else {
        // House needs 5,000 total points to reach Studio
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 5000 * 100
        const progress = Math.min((yearSpend / 5000) * 100, 100);
        return { discount: 0, tier: 'House', nextTier: 'Studio (15%)', progress: progress, nextTierName: 'Studio' };
      }
    }
    
    if (baseSpend >= 200000) {
      return { discount: 30, tier: 'Estate', nextTier: null, progress: 100, nextTierName: null };
    } else if (baseSpend >= 100000) {
      return { discount: 25, tier: 'Conservatory', nextTier: 'Estate (30%)', progress: ((baseSpend - 100000) / 100000) * 100, nextTierName: 'Estate' };
    } else if (baseSpend >= 25000) {
      return { discount: 20, tier: 'Gallery', nextTier: 'Conservatory (25%)', progress: ((baseSpend - 25000) / 75000) * 100, nextTierName: 'Conservatory' };
    } else if (baseSpend >= 5000) {
      return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: ((baseSpend - 5000) / 20000) * 100, nextTierName: 'Gallery' };
    } else {
      return { discount: 0, tier: 'House', nextTier: 'Studio (15%)', progress: (baseSpend / 5000) * 100, nextTierName: 'Studio' };
    }
  };

  const tier = getCurrentTier(true); // Use display values for selected year
  
  // Calculate progress details based on selected year
  const yearSpend = selectedYear === 2026 ? year2026Spend : year2025Spend;
  let pointsToNextTier = 0;
  let nextTierPoints = 0;
  
  if (tier.nextTier) {
    if (tier.tier === 'House') {
      nextTierPoints = 5000;
      pointsToNextTier = Math.ceil(5000 - displayTierBaseSpend);
    } else if (tier.tier === 'Studio') {
      nextTierPoints = 25000;
      if (displayCarriedOverTier && selectedYear === 2026) {
        pointsToNextTier = Math.ceil(25000 - (displayCarriedOverTier.baseSpend + yearSpend));
      } else {
        pointsToNextTier = Math.ceil(25000 - displayTierBaseSpend);
      }
    } else if (tier.tier === 'Gallery') {
      nextTierPoints = 100000;
      if (displayCarriedOverTier && selectedYear === 2026) {
        pointsToNextTier = Math.ceil(100000 - (displayCarriedOverTier.baseSpend + yearSpend));
      } else {
        pointsToNextTier = Math.ceil(100000 - displayTierBaseSpend);
      }
    } else if (tier.tier === 'Conservatory') {
      nextTierPoints = 200000;
      if (displayCarriedOverTier && selectedYear === 2026) {
        pointsToNextTier = Math.ceil(200000 - (displayCarriedOverTier.baseSpend + yearSpend));
      } else {
        pointsToNextTier = Math.ceil(200000 - displayTierBaseSpend);
      }
    }
  }
  
  // Calculate currentPoints: total points earned toward next tier
  // For 2026, show only 2026 points (starts at 0, nothing confirmed yet)
  // For 2025, use year2025Spend directly
  let displayPoints = displayTierBaseSpend;
  if (selectedYear === 2026) {
    // For 2026, show only the points earned in 2026 (starts at 0)
    displayPoints = yearSpend; // This will be year2026Spend, which starts at 0
  } else if (selectedYear === 2025) {
    displayPoints = year2025Spend;
  }
  const currentPoints = Math.round(displayPoints);
  const progressPercent = Math.round(tier.progress);
  
  // Check if we're still loading projects
  const isLoadingProjects = loading || (proposals.length === 0 && !spendData);
  
  // Get active proposals (up to 3) - most recent first
  // Active = Pending, Approved, or Confirmed where project start date is today or in the future
  // Conditions:
  // A) Client Name matches (handled by backend)
  // B) Published = TRUE (handled by backend)
  // C) Status is Pending, Approved, or Confirmed
  // D) Start Date (Column F) equals Today or any day in the future
  // Memoized for performance - only recalculates when proposals change
  const activeProposals = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight for date-only comparison
    
    return proposals.filter(p => {
      const status = p.status;
      // Exclude cancelled and completed
      if (status === 'Cancelled' || status === 'Completed') return false;
      // Include Pending, Approved, or Confirmed
      if (status === 'Pending' || status === 'Approved' || status === 'Confirmed') {
        // Use startDate (Column F) - this is the Project Start Date
        const startDate = p.startDate;
        if (!startDate) return false; // No start date means not active
        
        // Parse the start date safely
        const startDateObj = parseDateSafely(startDate);
        if (!startDateObj || isNaN(startDateObj.getTime())) return false;
        
        // Check if start date is today or in the future
        const startDateOnly = new Date(startDateObj);
        startDateOnly.setHours(0, 0, 0, 0); // Set to midnight for date-only comparison
        
        return startDateOnly.getTime() >= today.getTime(); // Must be today or in the future
      }
      return false;
    })
    .sort((a, b) => {
      // Get dates for sorting - soonest event first
      let dateA = null;
      if (a.isHistorical && a.timestamp) {
        dateA = new Date(a.timestamp);
      } else if (a.startDate) {
        dateA = parseDateSafely(a.startDate);
      } else if (a.eventDate) {
        if (typeof a.eventDate === 'string' && a.eventDate.includes('GMT')) {
          dateA = new Date(a.eventDate);
        } else if (a.eventDate instanceof Date) {
          dateA = a.eventDate;
        }
      }
      
      let dateB = null;
      if (b.isHistorical && b.timestamp) {
        dateB = new Date(b.timestamp);
      } else if (b.startDate) {
        dateB = parseDateSafely(b.startDate);
      } else if (b.eventDate) {
        if (typeof b.eventDate === 'string' && b.eventDate.includes('GMT')) {
          dateB = new Date(b.eventDate);
        } else if (b.eventDate instanceof Date) {
          dateB = b.eventDate;
        }
      }
      
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return timeA - timeB; // Ascending: soonest event first
    })
    .slice(0, 3);
  }, [proposals]);
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  
  const firstName = clientInfo?.firstName || clientInfo?.fullName?.split(' ')[0] || '';
  
  // Panel styling (reused throughout)
  const panelStyle = {
    backgroundColor: 'white',
    border: 'none',
    padding: '64px 48px',
    borderRadius: '20px',
    marginBottom: '48px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
  };
  
  const smallerPanelStyle = {
    backgroundColor: 'white',
    border: 'none',
    padding: '32px',
    borderRadius: '20px',
    marginBottom: '48px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  };

  return (
    <div>
      {/* Autographer Font */}
      <style dangerouslySetInnerHTML={{ __html: `
        @font-face {
          font-family: 'Autographer';
          src: url('/Autographer.woff') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      ` }} />
      
      {/* 1. Welcome Hero Section with Background Image */}
      <style dangerouslySetInnerHTML={{ __html: `
        .overview-hero {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          margin-top: -48px !important;
          margin-bottom: 64px;
          min-height: 500px;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .overview-hero {
            margin-top: -100px !important;
          }
        }
        .overview-hero__overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5));
        }
        .overview-hero__content {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 80px 48px;
          width: 100%;
        }
        .overview-hero__logo {
          width: 80px;
          height: 80px;
          margin: 0 auto 40px;
          display: block;
          filter: brightness(0) invert(1);
        }
        .overview-hero__welcome {
          margin-top: 0;
        }
        .overview-hero__title {
          font-family: 'Domaine Text', serif;
          font-size: 50px;
          line-height: 1.3;
          font-weight: 400;
          letter-spacing: -0.01em;
          color: #FFFFFF;
          margin-bottom: 16px;
        }
        .overview-hero__subtitle {
          font-family: 'NeueHaasUnica', sans-serif;
          font-size: 16px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto;
        }
        @media (max-width: 768px) {
          .overview-hero {
            min-height: 400px;
          }
          .overview-hero__content {
            padding: 64px 32px;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          .overview-hero__logo {
            width: 60px;
            height: 60px;
            margin-bottom: 32px;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .overview-hero__title {
            font-size: 36px;
            text-align: center !important;
            width: 100% !important;
          }
          .overview-hero__subtitle {
            font-size: 10px;
            text-align: center !important;
            width: 100% !important;
            padding: 0 16px !important;
          }
          .overview-hero__welcome {
            width: 100% !important;
            text-align: center !important;
          }
        }
        @media (max-width: 480px) {
          .overview-hero {
            min-height: 350px;
          }
          .overview-hero__content {
            padding: 48px 24px;
          }
          .overview-hero__logo {
            width: 50px;
            height: 50px;
            margin-bottom: 24px;
          }
          .overview-hero__title {
            font-size: 28px;
          }
          .overview-hero__subtitle {
            font-size: 10px;
          }
        }
        
        /* Responsive Badge Styles */
        @media (max-width: 768px) {
          .overview-badges-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          
          /* Mobile: Stack overview sections in correct order */
          .overview-section-labels {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            margin-bottom: 0 !important;
            display: contents !important;
          }
          .reserve-highlights-label {
            order: 1 !important;
            margin-bottom: 16px !important;
            display: block !important;
          }
          .member-perk-label-top {
            display: none !important;
          }
          .overview-grid-2-col {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            display: contents !important;
          }
          .reserve-highlights-box {
            order: 2 !important;
            margin-bottom: 0 !important;
            display: block !important;
          }
          .member-perk-label-mobile {
            order: 3 !important;
            display: block !important;
            margin-top: 32px !important;
            margin-bottom: 16px !important;
          }
          .monthly-perk-wrapper {
            order: 4 !important;
            display: block !important;
          }
          
          .snapshot-active-projects-title {
            text-align: left !important;
            white-space: nowrap !important;
            margin-bottom: 16px !important;
          }
          
          /* Match padding with other overview sections on mobile */
          .snapshot-active-projects-container {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
          
          /* Mobile: Center View Product button text */
          .view-product-button {
            text-align: center !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            width: 100% !important;
            padding: 12px 24px !important;
            line-height: 1 !important;
            vertical-align: middle !important;
            height: auto !important;
            min-height: 44px !important;
          }
          .view-product-button * {
            text-align: center !important;
            margin: 0 !important;
            line-height: 1 !important;
          }
          
          /* Product names - mobile sizes */
          .single-product-layout h3 {
            font-size: 12px !important;
          }
          .products-grid h3 {
            font-size: 12px !important;
          }
          
          /* Concierge section - more space between Inquiries and email */
          .concierge-grid > div > div:nth-child(3) {
            margin-bottom: 20px !important;
          }
          .concierge-grid > div a[href^="mailto:"] {
            margin-top: 2px !important;
          }
        }
        
        /* Desktop: Center View Product button text */
        .view-product-button {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 12px 24px !important;
          line-height: 1.5 !important;
          vertical-align: middle !important;
          text-align: center !important;
        }
        
        /* Desktop: Show label in top grid, hide mobile label, stack perk box below label */
        @media (min-width: 769px) {
          .member-perk-label-top {
            display: block !important;
          }
          .member-perk-label-mobile {
            display: none !important;
          }
          .monthly-perk-wrapper {
            display: flex !important;
            flex-direction: column !important;
          }
          .overview-grid-2-col {
            align-items: stretch !important;
          }
        }
        
        /* Mobile: Member Perk card - placard full width, keep background + scrim */
        @media (max-width: 768px) {
          .monthly-perk-container {
            border-radius: 18px !important;
            padding: 28px 20px !important;
          }
          .monthly-perk-container .member-perk-placard {
            max-width: 100% !important;
            padding: 24px 24px !important;
            border-radius: 12px !important;
          }
          .monthly-perk-container .member-perk-hero { font-size: 26px !important; letter-spacing: -0.01em !important; margin-bottom: 10px !important; }
          .monthly-perk-container .member-perk-subhead { font-size: 15px !important; font-weight: 500 !important; line-height: 1.45 !important; margin-bottom: 18px !important; }
          .monthly-perk-container .member-perk-body { font-size: 13px !important; line-height: 1.4 !important; letter-spacing: -0.012em !important; margin-bottom: 12px !important; }
          .monthly-perk-container .member-perk-body + .member-perk-body { margin-bottom: 28px !important; }
          .monthly-perk-container .member-perk-cta-wrap { margin-bottom: 20px !important; }
        }
        @media (max-width: 480px) {
          .monthly-perk-container {
            border-radius: 16px !important;
            padding: 20px 16px !important;
          }
          .monthly-perk-container .member-perk-placard {
            padding: 20px 18px !important;
            border-radius: 12px !important;
          }
          .monthly-perk-container .member-perk-hero { font-size: 22px !important; letter-spacing: -0.01em !important; }
          .monthly-perk-container .member-perk-subhead { font-size: 14px !important; font-weight: 500 !important; line-height: 1.45 !important; }
        }
        
        /* Mobile: Hide top grid label, show label above perk box */
        @media (max-width: 768px) {
          .member-perk-label-top {
            display: none !important;
          }
          .member-perk-label-mobile {
            display: block !important;
          }
        }
        
        /* Mobile: Fix footer text wrapping */
          .overview-footer-text {
            padding: 40px 20px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            hyphens: auto !important;
            font-size: 11px !important;
            line-height: 1.8 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .overview-footer-text > div {
            max-width: 100% !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            padding: 0 !important;
          }
        }
        
        /* Responsive Project Row Styles */
        @media (max-width: 768px) {
          .project-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          .project-row button {
            width: 100%;
            text-align: center;
          }
        }
        
        /* Responsive Products Grid Styles */
        @media (max-width: 1024px) {
          .products-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 24px !important;
          }
        }
        @media (max-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
        }
        @media (max-width: 480px) {
          .products-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .single-product-layout {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      ` }} />
      <div 
        className="overview-hero"
        style={{
          backgroundImage: 'url(/overview-feature-image.jpg)'
        }}
      >
        <div className="overview-hero__overlay"></div>
        <div className="overview-hero__content">
          {/* Logo Mark */}
          <img 
            src="/mayker_round-stamp-lines-black.png" 
            alt="Mayker" 
            className="overview-hero__logo"
            onError={(e) => {
              // Fallback if image doesn't exist
              e.target.style.display = 'none';
            }}
          />
          
          {/* Welcome Text Below */}
          <div className="overview-hero__welcome">
            <div className="overview-hero__title">
              {getGreeting()}, {firstName ? firstName : 'there'}.
            </div>
            
            <div className="overview-hero__subtitle">
              Welcome to your Mayker Reserve portal. A curated landing spot of your membership, projects, and benefits.
            </div>
          </div>
        </div>
      </div>

      {/* 2. Membership Status + Monthly Perk (Side by Side) */}
      <div style={{
        marginBottom: '48px'
      }}>
        {/* Section Labels - Matching Style */}
        <div className="overview-section-labels" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '48px',
          marginBottom: '16px'
        }}>
          <div className="reserve-highlights-label" style={{
            fontSize: '11px',
            fontWeight: '400',
            color: '#000000',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            textAlign: 'left'
          }}>
            Your Reserve Highlights
          </div>
          <div className="member-perk-label-top" style={{
            fontSize: '11px',
            fontWeight: '400',
            color: '#000000',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            textAlign: 'left'
          }}>
            This Month's Member Perk
          </div>
        </div>
        
        <div className="overview-grid-2-col" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '48px',
          alignItems: 'stretch'
        }}>
        {/* Membership Status (Medallion Panel) */}
        <div className="reserve-highlights-box" style={{
          ...panelStyle,
          position: 'relative',
          marginBottom: 0,
          paddingTop: '48px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: `
            radial-gradient(circle at center, rgba(84, 81, 66, 0.02) 0%, rgba(84, 81, 66, 0.03) 50%, rgba(250, 250, 248, 0.4) 100%),
            radial-gradient(circle at center, rgba(250, 250, 248, 0.4) 0%, rgba(250, 250, 248, 0.8) 50%, #fafaf8 100%),
            url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='linen' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h100v100H0z' fill='%23fafaf8'/%3E%3Cpath d='M0 0h1v100H0z' fill='%23f5f5f0' opacity='0.3'/%3E%3Cpath d='M0 0h100v1H0z' fill='%23f5f5f0' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23linen)'/%3E%3C/svg%3E") repeat
          `,
          backgroundSize: 'auto, auto, 100px 100px'
        }}>
        {/* Explanatory Text - Inside Box */}
          <div style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
          right: '16px',
            fontSize: '11px',
            fontWeight: '400',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.5',
            fontStyle: 'italic',
            zIndex: 2
          }}>
            Your 2026 membership reflects the highest tier you achieved in 2025.<br />
            Points reset each January.
          </div>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          flex: 1,
          paddingTop: selectedYear === 2026 ? '48px' : '48px' // Keep consistent spacing regardless of year
        }}>
          {/* Status Medallion - Reduced size */}
          <div style={{
            position: 'relative',
            width: '200px',
            height: '200px',
            marginBottom: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Radial gradient behind medallion */}
            <div style={{
              position: 'absolute',
              width: '240px',
              height: '240px',
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(${tier.tier === 'House' || tier.tier === 'Studio' ? '107, 125, 71' : tier.tier === 'Gallery' ? '212, 175, 55' : '44, 44, 44'}, 0.08) 0%, transparent 70%)`,
              zIndex: 0
            }} />
            {/* Hand-drawn style circle - using SVG for organic feel */}
            <svg 
              width="200" 
              height="200" 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            >
              <circle
                cx="100"
                cy="100"
                r="98"
                fill="none"
                stroke={tier.tier === 'House' || tier.tier === 'Studio' ? '#545142' : tier.tier === 'Gallery' ? '#d4af37' : '#545142'}
                strokeWidth="4"
                strokeLinecap="round"
                style={{
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                  opacity: 0.95
                }}
              />
            </svg>
            <div style={{
              textAlign: 'center',
              zIndex: 2,
              position: 'relative',
              padding: '24px'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '300',
                color: '#2C2C2C',
                fontFamily: "'Domaine Text', serif",
                letterSpacing: '0.4px',
                lineHeight: '1.2',
                marginBottom: '8px'
              }}>
                {(!clientInfo || isLoadingProjects) ? 'Tier Loading' : tier.tier}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '300',
                color: '#8b8b8b',
                fontFamily: "'NeueHaasUnica', sans-serif",
                letterSpacing: '-0.01em'
              }}>
                {(!clientInfo || isLoadingProjects) ? '' : `${tier.discount}% Off`}
              </div>
            </div>
          </div>

          {/* Progress Section */}
          {tier.nextTier && (
            <div style={{
              width: '100%',
              maxWidth: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              {/* Thin horizontal olive rule - matching right card */}
              <div style={{
                width: '60px',
                height: '1px',
                backgroundColor: '#545142',
                marginBottom: '20px',
                opacity: 0.3
              }} />
              <div style={{
                fontSize: '10px',
                color: '#8b8b8b',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                {(clientInfo?.clientCompanyName || clientInfo?.companyName || 'Your')}'s Progress
              </div>
              
              <div style={{
                width: '65%',
                height: '3px',
                backgroundColor: '#e8e8e3',
                borderRadius: '2px',
                marginBottom: '20px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  width: `${Math.min(tier.progress, 100)}%`,
                  height: '100%',
                  backgroundColor: tier.tier === 'House' || tier.tier === 'Studio' ? '#545142' : tier.tier === 'Gallery' ? '#d4af37' : '#2C2C2C',
                  transition: 'width 0.8s ease',
                  borderRadius: '2px'
                }} />
              </div>
              
              <div style={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  fontSize: '16px',
                  color: '#000000',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '500',
                  lineHeight: '1.4'
                }}>
                  {isLoadingProjects ? 'Projects loading...' : `${progressPercent}% to next membership tier`}
                </div>
                
                <div style={{
                  fontSize: '12px',
                  color: '#8b8b8b',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '400',
                  lineHeight: '1.5'
                }}>
                  {isLoadingProjects ? 'Calculating points...' : `${currentPoints.toLocaleString()} points earned • ${nextTierPoints.toLocaleString()} required`}
                </div>
              </div>
            </div>
          )}
          
          {/* Estate Additional Info */}
          {tier.tier === 'Estate' && (
            <div style={{
              marginTop: '32px',
              paddingTop: '32px',
              borderTop: '1px solid rgba(84, 81, 66, 0.15)',
              width: '100%',
              maxWidth: '500px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '400',
                lineHeight: '1.6',
                marginBottom: '24px'
              }}>
                As an Estate member, you receive 30% off all rental orders.
              </div>
              
              {/* Enhanced Badges */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }} className="overview-badges-grid">
                {/* Points YTD Badge */}
                <div style={{
                  backgroundColor: '#F7F6F0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    fontSize: '9px',
                    fontWeight: '500',
                    color: '#8b8b8b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: '8px',
                    fontFamily: "'NeueHaasUnica', sans-serif"
                  }}>
                    Points YTD
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: '300',
                    color: '#000000',
                    fontFamily: "'Domaine Text', serif",
                    lineHeight: '1.2',
                    minHeight: '28px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {loading && !spendData ? (
                      <span style={{ opacity: 0.5 }}>—</span>
                    ) : (
                      Math.round(currentYearSpend).toLocaleString()
                    )}
                  </div>
                </div>
                
                {/* Projects YTD Badge */}
                <div style={{
                  backgroundColor: '#F7F6F0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    fontSize: '9px',
                    fontWeight: '500',
                    color: '#8b8b8b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: '8px',
                    fontFamily: "'NeueHaasUnica', sans-serif"
                  }}>
                    Projects YTD
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: '300',
                    color: '#000000',
                    fontFamily: "'Domaine Text', serif",
                    lineHeight: '1.2',
                    minHeight: '28px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {loading && proposals.length === 0 ? (
                      <span style={{ opacity: 0.5 }}>—</span>
                    ) : (
                      yearProposals.length
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
        
        {/* Monthly Perk - Premium editorial card */}
        <div className="monthly-perk-wrapper" style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          {/* This Month's Member Perk Label - Above perk box on mobile, hidden on desktop */}
          <div className="member-perk-label-mobile" style={{
            fontSize: '11px',
            fontWeight: '400',
            color: '#000000',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            textAlign: 'left',
            marginBottom: '16px',
            display: 'none'
          }}>
            This Month's Member Perk
          </div>
          <div className="monthly-perk-container" style={{
            marginBottom: 0,
            position: 'relative',
            minHeight: '100%',
            overflow: 'hidden',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '48px 32px'
          }}>
            {/* Background image — file: public/August Reserve Perk.jpg */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden'
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/${encodeURIComponent('August Reserve Perk.jpg')}`}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block'
                }}
                onError={(e) => {
                  if (!e.currentTarget.src.includes('/assets/')) {
                    e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/assets/${encodeURIComponent('August Reserve Perk.jpg')}`;
                  }
                }}
              />
            </div>
            {/* Warm scrim overlay */}
            <div aria-hidden="true" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(84, 65, 55, 0.3)',
              pointerEvents: 'none'
            }} />
            {/* Placard - centered, slightly above center; editorial line length */}
            <div className="member-perk-placard" style={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '440px',
              backgroundColor: 'rgba(250, 248, 243, 0.92)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '14px',
              padding: '32px 40px',
              boxShadow: 'inset 0 1px 0 0 rgba(255, 255, 255, 0.6), 0 4px 24px rgba(0, 0, 0, 0.08)'
            }}>
              {/* Headline - largest: monthly benefit announcement */}
              <div className="member-perk-hero" style={{
                fontSize: '28px',
                fontWeight: '300',
                color: '#1a1a1a',
                fontFamily: "'Domaine Text', serif",
                letterSpacing: '-0.01em',
                lineHeight: '1.25',
                marginBottom: '12px'
              }}>
                August Member Perk
              </div>
              {/* Benefit line - medium, same tier as subhead */}
              <div className="member-perk-subhead" style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#2a2a2a',
                fontFamily: "'NeueHaasUnica', sans-serif",
                lineHeight: '1.45',
                marginBottom: '22px',
                letterSpacing: '0.01em'
              }}>
                Consider it Waived
              </div>
              {/* Body — two paragraphs, tighter editorial rhythm */}
              <div className="member-perk-body" style={{
                fontSize: '14px',
                color: '#2a2a2a',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '400',
                lineHeight: '1.4',
                marginBottom: '36px',
                letterSpacing: '-0.012em'
              }}>
                For all projects opened and booked In August, we'll waive the Product Care Fee—a 10% savings. Think of all the pillows you can fluff with that.
              </div>
              {/* CTA */}
              <div className="member-perk-cta-wrap" style={{ marginBottom: '0' }}>
                <a
                  href="https://www.maykerevents.com/collections/outdoor"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '11px',
                    color: '#1a1a1a',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    fontWeight: '500',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'opacity 0.2s ease, text-decoration 0.2s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                    e.currentTarget.style.textUnderlineOffset = '4px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Browse the collection
                  <span style={{ fontSize: '12px' }}>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* 8. New on Reserve Section - Editorial Block */}
      <div 
        className="featured-member-section explore-collection-section"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          marginBottom: '48px',
          borderRadius: '20px',
          overflow: 'hidden',
          backgroundColor: '#FAF8F3',
          alignItems: 'stretch'
        }}
      >
        {/* Left: Image — full column height on desktop (text column sets row height) */}
        <div
          className="featured-member-photo-column explore-collection-photo-column"
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            minHeight: 0,
            overflow: 'hidden',
            borderRadius: '20px 0 0 20px',
            backgroundColor: '#DED6CE'
          }}
        >
          <div
            className="featured-member-photo-frame featured-member-photo-frame--banner explore-collection-photo-frame"
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              backgroundColor: '#DED6CE',
              flex: '1 1 auto',
              minHeight: 0
            }}
          >
            <img
              src="/products-banner.jpg"
              alt="Products banner"
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center center',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>
        
        {/* Right: Editorial Text Block - 50% width, matches image height */}
        <div
          className="featured-member-text-column"
          style={{
          backgroundColor: '#DED6CE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 60px',
          borderRadius: '0 20px 20px 0',
          height: '100%',
          minHeight: '100%'
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: '560px'
          }}>
            {/* Label */}
            <div style={{
              fontSize: '10px',
              fontWeight: '500',
              color: '#000000',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '32px',
              lineHeight: '1.4'
            }}>
              NEW ON RESERVE
            </div>
            
            {/* Headline */}
            <div style={{
              fontSize: '50px',
              fontWeight: '300',
              color: '#1a1a1a',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
              marginBottom: '12px'
            }}>
              Explore the Collection
            </div>
            
            {/* Body Copy */}
            <div style={{
              fontSize: '14px',
              fontWeight: '400',
              color: '#2a2a2a',
              fontFamily: "'NeueHaasUnica', sans-serif",
              lineHeight: '1.8',
              letterSpacing: '0.01em',
              marginBottom: '32px'
            }}>
              Discover our new Products page—your complete view of the Mayker rental collection. Browse the full assortment, save favorites, and build project-specific lists for seamless submission.
            </div>
            
            {/* Link */}
            <button 
              type="button"
              onClick={() => {
                if (setActiveSection) {
                  setActiveSection('products');
                }
                localStorage.setItem('productsCatalogState', JSON.stringify({
                  category: 'new-releases',
                  type: 'show-all',
                  search: ''
                }));
                window.location.hash = '/products?category=new-releases';
              }}
              style={{
                fontSize: '12px',
                color: '#545142',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                fontWeight: '500',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease',
                display: 'inline-block',
                background: 'transparent',
                border: 'none',
                padding: '0',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
                e.target.style.color = '#545142';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
                e.target.style.color = '#545142';
              }}
            >
              Explore Products
            </button>
          </div>
        </div>
      </div>

      {/* 4. Featured Products from Shopify */}
      {shopifyProducts.length > 0 && (
        <div style={{
          ...panelStyle,
          backgroundColor: '#FAFAF8',
          padding: '40px 32px'
        }}>
          {/* Section Header */}
          <div style={{
            fontSize: '11px',
            fontWeight: '400',
            color: '#000000',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            Discover the Newest Releases
          </div>
          
          {loadingProducts ? (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontSize: '14px'
            }}>
              Loading products...
            </div>
          ) : shopifyProducts.length === 1 ? (
            // Single product - large featured layout
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '48px',
              alignItems: 'center'
            }} className="single-product-layout">
              <div>
                {shopifyProducts[0].imageUrl && (
                  <img 
                    src={shopifyProducts[0].imageUrl} 
                    alt={shopifyProducts[0].imageAlt}
                    loading="lazy"
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      aspectRatio: '1',
                      objectFit: 'cover'
                    }}
                  />
                )}
              </div>
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '300',
                  color: '#000000',
                  fontFamily: "'Domaine Text', serif",
                  letterSpacing: '-0.01em',
                  marginBottom: '24px',
                  lineHeight: '1.3',
                  textAlign: 'center'
                }}>
                  {shopifyProducts[0].title}
                </h3>
                <a
                  href={shopifyProducts[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-product-button"
                  style={{
                    ...secondaryButtonStyle,
                    display: 'inline-block',
                    textDecoration: 'none',
                    textAlign: 'center'
                  }}
                  onMouseEnter={secondaryButtonHover}
                  onMouseLeave={secondaryButtonLeave}
                >
                  View Product
                </a>
              </div>
            </div>
          ) : (
            // Multiple products - grid layout
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '32px'
            }} className="products-grid">
              {shopifyProducts.map((product) => (
                <div
                  key={product.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(232, 232, 227, 0.6)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {product.imageUrl && (
                    <img 
                      src={product.imageUrl} 
                      alt={product.imageAlt}
                      loading="lazy"
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        aspectRatio: '1',
                        objectFit: 'cover',
                        marginBottom: '16px'
                      }}
                    />
                  )}
                  <h3 style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#000000',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    marginBottom: '16px',
                    lineHeight: '1.3',
                    textAlign: 'center'
                  }}>
                    {product.title}
                  </h3>
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-product-button"
                    style={{
                      ...secondaryButtonStyle,
                      display: 'inline-block',
                      padding: '10px 20px',
                      fontSize: '12px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      marginTop: 'auto'
                    }}
                    onMouseEnter={secondaryButtonHover}
                    onMouseLeave={secondaryButtonLeave}
                  >
                    View Product
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 8b. Weekly Featured Member — text left, image right (Explore the Collection is image left, text right) */}
      <div
        className="featured-member-section weekly-featured-member"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          marginBottom: '48px',
          borderRadius: '20px',
          overflow: 'hidden',
          backgroundColor: '#FAF8F3',
          alignItems: 'stretch'
        }}
      >
        {/* Left: Editorial Text Block */}
        <div
          className="featured-member-text-column"
          style={{
          backgroundColor: '#DED6CE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 60px',
          borderRadius: '20px 0 0 20px',
          height: '100%',
          minHeight: '100%'
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: '560px'
          }}>
            {/* Label */}
            <div style={{
              fontSize: '10px',
              fontWeight: '500',
              color: '#000000',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '32px',
              lineHeight: '1.4'
            }}>
              WEEKLY FEATURED MEMBER
            </div>
            
            {/* Headline */}
            <div style={{
              fontSize: '50px',
              fontWeight: '300',
              color: '#1a1a1a',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
              marginBottom: '12px'
            }}>
              Hill and Co.
            </div>
            
            {/* Subheadline */}
            <div style={{
              fontSize: '11px',
              fontWeight: '400',
              color: '#6b6b6b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: '40px',
              lineHeight: '1.5'
            }}>
              Charleston, SC
            </div>
            
            {/* Body Copy */}
            <div style={{
              fontSize: '14px',
              fontWeight: '400',
              color: '#2a2a2a',
              fontFamily: "'NeueHaasUnica', sans-serif",
              lineHeight: '1.8',
              letterSpacing: '0.01em',
              marginBottom: '32px'
            }}>
              Hill and Co. brings a quiet confidence to color, pattern, and texture—spaces that feel considered, layered, and beautifully lived in.
            </div>
            
            {/* Link */}
            <a 
              href="https://www.hillandcocreative.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '14px',
                color: '#545142',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                fontWeight: '400',
                letterSpacing: '0.01em',
                transition: 'all 0.2s ease',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
                e.target.style.color = '#545142';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
                e.target.style.color = '#545142';
              }}
            >
              hillandcocreative.com
            </a>
          </div>
        </div>

        {/* Right: Image — full column height on desktop (text column sets row height) */}
        <div
          className="featured-member-photo-column weekly-featured-photo-column"
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            minHeight: 0,
            overflow: 'hidden',
            borderRadius: '0 20px 20px 0',
            backgroundColor: '#DED6CE'
          }}
        >
          <div
            className="featured-member-photo-frame featured-member-photo-frame--member weekly-featured-photo-frame"
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              backgroundColor: '#DED6CE',
              flex: '1 1 auto',
              minHeight: 0
            }}
          >
            <img
              src="/featured-member.jpg"
              alt="Hill & Co. Creative"
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: '32% 15%',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              onError={(e) => {
                if (!e.target.src.includes('/assets/')) {
                  e.target.src = '/assets/featured-member.jpg';
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Responsive Styles for Featured Member Section */}
      <style dangerouslySetInnerHTML={{ __html: `
        .featured-member-section {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          align-items: stretch !important;
        }
        .featured-member-section .featured-member-photo-column {
          min-width: 0 !important;
        }
        .featured-member-section .featured-member-photo-frame {
          position: relative !important;
          width: 100% !important;
          aspect-ratio: 4 / 3 !important;
          flex-shrink: 0 !important;
          overflow: hidden !important;
        }
        .featured-member-section .featured-member-photo-frame img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }
        .featured-member-section .featured-member-photo-frame--banner img {
          object-position: center center !important;
        }
        .featured-member-section .featured-member-photo-frame--member img {
          object-position: 32% 15% !important;
        }
        .featured-member-section .featured-member-photo-filler {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          background-color: #DED6CE !important;
        }
        .featured-member-section .featured-member-text-column {
          height: 100% !important;
          min-height: 100% !important;
        }
        /* Weekly Featured + Explore Collection: photo fills full height of row (no 4:3 + filler gap) */
        @media (min-width: 901px) {
          .weekly-featured-member .weekly-featured-photo-column,
          .explore-collection-section .explore-collection-photo-column {
            height: 100% !important;
            align-self: stretch !important;
          }
          .weekly-featured-member .weekly-featured-photo-frame,
          .explore-collection-section .explore-collection-photo-frame {
            aspect-ratio: unset !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            height: 100% !important;
          }
        }
        @media (max-width: 900px) {
          .featured-member-section {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto !important;
          }
          /* Weekly Featured: DOM is text then image — show image on top like Explore */
          .weekly-featured-member .featured-member-photo-column {
            order: -1 !important;
          }
          .weekly-featured-member .weekly-featured-photo-frame,
          .explore-collection-section .explore-collection-photo-frame {
            aspect-ratio: 4 / 3 !important;
            flex: none !important;
            height: auto !important;
          }
          .featured-member-section .featured-member-photo-column {
            border-radius: 20px 20px 0 0 !important;
            width: 100% !important;
          }
          .featured-member-section .featured-member-photo-filler {
            display: none !important;
          }
          .featured-member-section .featured-member-photo-frame--banner img {
            object-position: center center !important;
          }
          .featured-member-section .featured-member-photo-frame--member img {
            object-position: 32% 15% !important;
          }
          .featured-member-section .featured-member-text-column {
            border-radius: 0 0 20px 20px !important;
            padding: 64px 40px !important;
            height: auto !important;
            min-height: auto !important;
          }
        }
      ` }} />

      {/* 9. Concierge Section - Editorial 3-Column Layout */}
      <div style={{
        backgroundColor: '#F2F1ED',
        padding: '64px 48px',
        marginBottom: '48px',
        borderRadius: '20px'
      }} className="concierge-section">
        <div style={{
          fontSize: '11px',
          fontWeight: '400',
          color: '#000000',
          fontFamily: "'NeueHaasUnica', sans-serif",
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '32px',
          lineHeight: '1.4'
        }}>
          CONCIERGE
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '32px',
          maxWidth: '1200px',
          margin: '0 auto'
        }} className="concierge-grid">
          {/* Column 1: General Inquiries */}
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              width: '100%',
              aspectRatio: '3/4',
              marginBottom: '32px',
              overflow: 'hidden'
            }}>
              <img 
                src="/concierge-1.jpg" 
                alt="General Inquiries"
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  if (!e.target.src.includes('/assets/')) {
                    e.target.src = '/assets/concierge-1.jpg';
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
              />
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '300',
              fontStyle: 'italic',
              color: '#6b6b6b',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '2px'
            }}>
              General
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '300',
              color: '#1a1a1a',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '16px'
            }}>
              Inquiries
            </div>
            <a 
              href="mailto:events@mayker.com"
              style={{
                fontSize: '12px',
                color: '#545142',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                fontWeight: '400',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease',
                display: 'block',
                marginTop: '-4px'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
                e.target.style.color = '#545142';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
                e.target.style.color = '#545142';
              }}
            >
              EVENTS@MAYKER.COM
            </a>
          </div>

          {/* Column 2: Custom Project Inquiries */}
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              width: '100%',
              aspectRatio: '3/4',
              marginBottom: '32px',
              overflow: 'hidden'
            }}>
              <img 
                src="/concierge-2.jpg" 
                alt="Custom Project Inquiries"
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  if (!e.target.src.includes('/assets/')) {
                    e.target.src = '/assets/concierge-2.jpg';
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
              />
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '300',
              fontStyle: 'italic',
              color: '#6b6b6b',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '2px'
            }}>
              Custom Project
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '300',
              color: '#1a1a1a',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '16px'
            }}>
              Inquiries
            </div>
            <a 
              href="mailto:noelle@mayker.com"
              style={{
                fontSize: '12px',
                color: '#545142',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                fontWeight: '400',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease',
                display: 'block',
                marginTop: '-4px'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
                e.target.style.color = '#545142';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
                e.target.style.color = '#545142';
              }}
            >
              NOELLE@MAYKER.COM
            </a>
          </div>

          {/* Column 3: Partnership Inquiries */}
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              width: '100%',
              aspectRatio: '3/4',
              marginBottom: '32px',
              overflow: 'hidden'
            }}>
              <img 
                src="/concierge-3.jpg" 
                alt="Partnership Inquiries"
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  if (!e.target.src.includes('/assets/')) {
                    e.target.src = '/assets/concierge-3.jpg';
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
              />
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '300',
              fontStyle: 'italic',
              color: '#6b6b6b',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '2px'
            }}>
              Partnership
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '300',
              color: '#1a1a1a',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.01em',
              lineHeight: '1.2',
              marginBottom: '16px'
            }}>
              Inquiries
            </div>
            <a 
              href="mailto:constance@mayker.com"
              style={{
                fontSize: '12px',
                color: '#545142',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                fontWeight: '400',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease',
                display: 'block',
                marginTop: '-4px'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
                e.target.style.color = '#545142';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
                e.target.style.color = '#545142';
              }}
            >
              CONSTANCE@MAYKER.COM
            </a>
          </div>
        </div>
      </div>

      {/* 10. Footer Band */}
      <div className="overview-footer-text" style={{
        width: '100%',
        padding: '60px 48px',
        backgroundColor: 'transparent',
        marginTop: '48px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#2a2a2a',
          fontFamily: "'NeueHaasUnica', sans-serif",
          fontWeight: '400',
          letterSpacing: '0.05em',
          lineHeight: '1.6',
          textTransform: 'uppercase',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>
          THANK YOU FOR BEING PART OF MAYKER RESERVE.
        </div>
      </div>
    </div>
  );
}

export function PerformanceSection({ spendData, proposals = [], setSelectedProposal, brandCharcoal = '#2C2C2C', proposalTab, setProposalTab, setActiveSection, clientInfo, loading = false }) {
  // Set default tab to 'pending' if undefined or 'active' (for backward compatibility)
  React.useEffect(() => {
    if (!proposalTab || proposalTab === 'active') {
      setProposalTab && setProposalTab('pending');
    }
  }, [proposalTab, setProposalTab]);
  
  // Use the global calculateProductSpend function for consistency with Projects page
  // Points = Product Total + Service Fee + Product Care - Discount
  
  // Get current year proposals for YTD points
  const currentYear = new Date().getFullYear();
  // Filter and sort year proposals (most recent first)
  // Points are attributed based on booking/confirmation date (timestamp), not event date
  const yearProposals = proposals.filter(p => {
    // Exclude cancelled projects
    if (p.status === 'Cancelled') return false;
    
    // Only include Confirmed and Completed projects (exclude Pending, Approved, and Cancelled)
    if (p.status !== 'Confirmed' && p.status !== 'Completed') {
      return false;
    }
    
    // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
    // Points are attributed based on when the event was booked/confirmed, not when it occurred
    if (p.timestamp) {
      const confirmationYear = new Date(p.timestamp).getFullYear();
      return confirmationYear === currentYear;
    }
    
    // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
    if (p.startDate) {
      const start = parseDateSafely(p.startDate);
      if (start && !isNaN(start.getTime())) {
        const startYear = start.getFullYear();
        return startYear === currentYear;
      }
    }
    
    // FALLBACK: For historical projects, check eventDate as last resort
    if (p.isHistorical && p.eventDate) {
        const yearMatch = p.eventDate.match(/\d{4}/);
        if (yearMatch) {
          return parseInt(yearMatch[0]) === currentYear;
        }
    }
    
    return false;
  }).sort((a, b) => {
    // Use shared utility function for consistent sorting
    const dateA = getSortableDateFromProposal(a);
    const dateB = getSortableDateFromProposal(b);
    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    
    // Primary sort: by date (descending - newest first)
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    
    // Secondary sort: by projectNumber (descending) if dates are equal
    const projectNumA = parseInt(a.projectNumber) || 0;
    const projectNumB = parseInt(b.projectNumber) || 0;
    return projectNumB - projectNumA;
  });
  
  // Filter for Pending/Approved proposals for current year (for pending points calculation)
  // Use timestamp (when proposal was created) to determine year
  const yearPendingProposals = proposals.filter(p => {
    // Exclude cancelled and completed projects
    if (p.status === 'Cancelled' || p.status === 'Completed' || p.status === 'Confirmed') return false;
    
    // Only include Pending and Approved projects
    if (p.status !== 'Pending' && p.status !== 'Approved') {
      return false;
    }
    
    // PRIMARY: Use timestamp (when proposal was created/booked)
      if (p.timestamp) {
        const proposalYear = new Date(p.timestamp).getFullYear();
        return proposalYear === currentYear;
      }
    
    // FALLBACK: If no timestamp, use startDate
    if (p.startDate) {
      const start = parseDateSafely(p.startDate);
      if (start && !isNaN(start.getTime())) {
        const startYear = start.getFullYear();
        return startYear === currentYear;
      }
    }
    
    // FALLBACK: For historical projects, check eventDate as last resort
    if (p.isHistorical && p.eventDate) {
      const yearMatch = p.eventDate.match(/\d{4}/);
      if (yearMatch) {
        return parseInt(yearMatch[0]) === currentYear;
      }
    }
    
    return false;
  });
  
  // Calculate current year YTD spend from product spend (not invoice total)
  // Only includes Confirmed and Completed projects
  const currentYearSpend = yearProposals.reduce((total, proposal) => {
    return total + calculateProductSpend(proposal);
  }, 0);
  
  // Calculate pending points from Pending/Approved proposals
  const currentYearPendingSpend = yearPendingProposals.reduce((total, proposal) => {
    return total + calculateProductSpend(proposal);
  }, 0);
  
  // Calculate total money saved (discounts) for current year
  // Include: historical projects (Column G discount) + confirmed/approved projects (from proposal portal)
  const currentYearMoneySaved = yearProposals.reduce((total, proposal) => {
    try {
      // For historical projects, use historicalDiscount from Column G
      if (proposal.isHistorical) {
        // Check multiple possible property names for the discount
        let histDiscount = 0;
        if (proposal.historicalDiscount !== undefined && proposal.historicalDiscount !== null) {
          // Handle both string and number values
          const discountStr = String(proposal.historicalDiscount).trim();
          if (discountStr !== '' && discountStr !== 'undefined' && discountStr !== 'null') {
            histDiscount = parseFloat(discountStr);
            if (isNaN(histDiscount)) {
              histDiscount = 0;
            }
          }
        }
        console.log(`[Money Saved] Historical project ${proposal.projectNumber || 'N/A'}: raw historicalDiscount="${proposal.historicalDiscount}" (type: ${typeof proposal.historicalDiscount}), parsed=${histDiscount}`);
        // Always add the discount (even if 0) to ensure we're counting all historical projects
          return total + histDiscount;
      }
      
      // For regular projects, only calculate discount for confirmed/completed projects
      // (not pending or approved, as those haven't been finalized yet)
      if (proposal.status === 'Pending' || proposal.status === 'Approved') {
        console.log(`[Money Saved] Skipping ${proposal.status} project ${proposal.projectNumber || 'N/A'}`);
        return total; // Don't count pending/approved projects
      }
      
      // For regular projects (Confirmed, Approved, Completed), use the same logic as calculateDetailedTotals
      const sections = JSON.parse(proposal.sectionsJSON || '[]');
      
      // Calculate base product total
      let baseProductTotal = 0;
      sections.forEach(section => {
        if (section.products && Array.isArray(section.products)) {
          section.products.forEach(product => {
            const quantity = parseFloat(product.quantity) || 0;
            const price = parseFloat(product.price) || 0;
            baseProductTotal += quantity * price;
          });
        }
      });
      
      // Get rental multiplier (same logic as calculateDetailedTotals)
      let rentalMultiplier = 1.0;
      if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
        const parsed = parseFloat(proposal.customRentalMultiplier);
        if (!isNaN(parsed) && parsed > 0) {
          rentalMultiplier = parsed;
        }
      } else {
        const start = parseDateSafely(proposal.startDate);
        const end = parseDateSafely(proposal.endDate);
        if (start && end) {
          const diffTime = end.getTime() - start.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
          rentalMultiplier = getRentalMultiplier(diffDays);
        }
      }
      
      const extendedProductTotal = baseProductTotal * rentalMultiplier;
      
      // Calculate discount (same logic as calculateDetailedTotals)
      const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
      let discountType = 'percentage';
      if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
        const match = proposal.discountName.match(/^TYPE:(\w+)/);
        if (match) discountType = match[1];
      }
      
      const standardRateDiscount = discountType === 'dollar' 
        ? discountValue 
        : extendedProductTotal * (discountValue / 100);
      
      console.log(`[Money Saved] Regular project ${proposal.projectNumber || 'N/A'} (${proposal.status}): baseTotal=${baseProductTotal}, extendedTotal=${extendedProductTotal}, discountValue=${discountValue}, discountType=${discountType}, discount=${standardRateDiscount}`);
      
      return total + standardRateDiscount;
    } catch (e) {
      console.error('Error calculating discount for proposal:', e, proposal);
      return total;
    }
  }, 0);
  
  console.log(`[Money Saved] Total for ${new Date().getFullYear()}: $${currentYearMoneySaved.toFixed(2)}`);
  
  // For 2026+, calculate 2025 total to determine tier status (carryover)
  // For 2025, use current year spend
  // PERFORMANCE: Use tier from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have tier
  let tierBaseSpend = clientInfo?.spend2025 || currentYearSpend;
  let carriedOverTier = null;
  
  // Use tier from Active Companies if available
  if (clientInfo?.tier && clientInfo?.tierDiscount !== null && clientInfo?.tierDiscount !== undefined) {
    carriedOverTier = {
      discount: parseInt(clientInfo.tierDiscount) || 0,
      tier: clientInfo.tier,
      baseSpend: clientInfo.spend2025 || 0
    };
    tierBaseSpend = clientInfo.spend2025 || 0;
  } else if (currentYear >= 2026) {
    // FALLBACK: Calculate tier if not available from Active Companies
    // Calculate 2025 total spend to determine tier status
    // Only Confirmed and Completed projects count toward points
    // Points are attributed based on booking/confirmation date (timestamp), not event date
    const year2025Proposals = proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      
      // Include Confirmed OR Completed projects (exclude Pending, Approved)
      // Also handle "Confirmed and Completed" as a possible status string
      const status = (p.status || '').toLowerCase();
      const isConfirmed = status === 'confirmed' || status.includes('confirmed');
      const isCompleted = status === 'completed' || status.includes('completed');
      
      // Include if status is Confirmed OR Completed (or both)
      if (!isConfirmed && !isCompleted) {
        return false;
      }
      
      // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
      // Points are attributed based on when the event was booked/confirmed, not when it occurred
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        return confirmationYear === 2025;
      }
      
      // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          const startYear = start.getFullYear();
          return startYear === 2025;
        }
      }
      
      // FALLBACK: For historical projects, check eventDate as last resort
      if (p.isHistorical && p.eventDate) {
        const yearMatch = p.eventDate.match(/\d{4}/);
        if (yearMatch) {
          return parseInt(yearMatch[0]) === 2025;
        }
      }
      
      return false;
    });
    
    // PERFORMANCE: Use pre-calculated 2025 points from Active Companies tab instead of calculating
    // Fallback to calculation only if clientInfo doesn't have the values
    const year2025Spend = (clientInfo?.points2025 !== null && clientInfo?.points2025 !== undefined)
      ? clientInfo.points2025
      : year2025Proposals.reduce((total, proposal) => {
          return total + calculateProductSpend(proposal);
        }, 0);
    
    // Check if this is a pre-2026 member (should start at Studio unless they earned higher in 2025)
    const isPre2026Member = clientInfo?.isPre2026Member || false;
    
    // Determine tier based on 2025 achievement
    // Pre-2026 members start at Studio (15%) minimum, but can earn higher tiers
    if (year2025Spend >= 200000) {
      carriedOverTier = { discount: 30, tier: 'Estate', baseSpend: year2025Spend };
    } else if (year2025Spend >= 100000) {
      carriedOverTier = { discount: 25, tier: 'Conservatory', baseSpend: year2025Spend };
    } else if (year2025Spend >= 25000) {
      carriedOverTier = { discount: 20, tier: 'Gallery', baseSpend: year2025Spend };
    } else if (year2025Spend >= 5000) {
      carriedOverTier = { discount: 15, tier: 'Studio', baseSpend: year2025Spend };
    } else {
      // For pre-2026 members, default to Studio (15%) minimum if they didn't earn a higher tier
      // For new members (post-2025), they start at House (0%)
      if (isPre2026Member) {
        carriedOverTier = { discount: 15, tier: 'Studio', baseSpend: 0 };
      } else {
        carriedOverTier = { discount: 0, tier: 'House', baseSpend: year2025Spend };
      }
    }
    
    // Use carried over tier, but progress is based on current year points
    tierBaseSpend = carriedOverTier.baseSpend;
  }
  
  // Year toggle state for Activity page
  const [selectedYear, setSelectedYear] = useState(2026); // Default to 2026 for January launch

  // Calculate year-specific proposals and spend
  // For historical projects: Points are attributed based on event date (when the event occurred)
  // For regular projects: Points are attributed based on booking/confirmation date (timestamp)
  const year2025Proposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      
      // Include Confirmed OR Completed projects (exclude Pending, Approved)
      // Also handle "Confirmed and Completed" as a possible status string
      const status = (p.status || '').toLowerCase();
      const isConfirmed = status === 'confirmed' || status.includes('confirmed');
      const isCompleted = status === 'completed' || status.includes('completed');
      
      // Include if status is Confirmed OR Completed (or both)
      if (!isConfirmed && !isCompleted) {
        return false;
      }
      
      // For historical projects, prioritize event date over timestamp
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      
      if (isHistorical) {
        // PRIMARY: Check startDate (event date) for historical projects
        if (p.startDate) {
          const start = parseDateSafely(p.startDate);
          if (start && !isNaN(start.getTime())) {
            return start.getFullYear() === 2025;
          }
        }
        
        // FALLBACK: Check eventDate string for historical projects
        if (p.eventDate) {
          const yearMatch = p.eventDate.match(/\d{4}/);
          if (yearMatch) return parseInt(yearMatch[0]) === 2025;
        }
        
        // LAST RESORT: Use timestamp (should be set to event date for historical projects)
        if (p.timestamp) {
          const confirmationYear = new Date(p.timestamp).getFullYear();
          return confirmationYear === 2025;
        }
        
        return false;
      }
      
      // For regular (non-historical) projects, use timestamp (booking/confirmation date)
      // PRIMARY: Use timestamp (booking/confirmation date) - this is when the project was confirmed
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        if (confirmationYear === 2025) return true;
      }
      
      // FALLBACK: If no timestamp, use startDate (for projects added before portal launch)
      // Also check startDate for regular proposals that might have event date in 2025
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          const startYear = start.getFullYear();
          if (startYear === 2025) return true;
        }
      }
      
      // LAST RESORT: For regular proposals with status "Completed" but no dates,
      // include them if they're not historical (they're from the proposal app)
      // This ensures regular proposals aren't excluded
      if (isCompleted && !isHistorical) {
        // If it's a completed regular proposal without dates, include it
        // This handles edge cases where proposals might not have proper timestamps
        return true;
      }
      
      return false;
    });
  }, [proposals]);

  const year2026Proposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled') return false;
      if (p.status !== 'Confirmed' && p.status !== 'Completed') return false;
      
      // For historical projects, prioritize event date over timestamp
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      
      if (isHistorical) {
        // PRIMARY: Check startDate (event date) for historical projects
        if (p.startDate) {
          const start = parseDateSafely(p.startDate);
          if (start && !isNaN(start.getTime())) {
            return start.getFullYear() === 2026;
          }
        }
        
        // FALLBACK: Check eventDate string for historical projects
        if (p.eventDate) {
          const yearMatch = p.eventDate.match(/\d{4}/);
          if (yearMatch) return parseInt(yearMatch[0]) === 2026;
        }
        
        // LAST RESORT: Use timestamp (should be set to event date for historical projects)
        if (p.timestamp) {
          const confirmationYear = new Date(p.timestamp).getFullYear();
          return confirmationYear === 2026;
        }
        
        return false;
      }
      
      // For regular (non-historical) projects, use timestamp (booking/confirmation date)
      if (p.timestamp) {
        const confirmationYear = new Date(p.timestamp).getFullYear();
        return confirmationYear === 2026;
      }
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          return start.getFullYear() === 2026;
        }
      }
      return false;
    });
  }, [proposals]);

  // PERFORMANCE: Use pre-calculated 2025 points from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have the values
  const year2025Spend = useMemo(() => {
    // Use 2025 points from Active Companies tab if available (faster and accurate)
    if (clientInfo?.points2025 !== null && clientInfo?.points2025 !== undefined) {
      return clientInfo.points2025;
    }
    // Fallback: Calculate from 2025 proposals if not available from Active Companies
    return year2025Proposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2025Proposals, clientInfo?.points2025]);
  
  // ALL confirmed/completed projects (for Projects Booked count and Completed list)
  // This includes both regular proposals and historical projects
  const allConfirmedCompletedProposals = useMemo(() => {
    return proposals.filter(p => {
      // Exclude cancelled projects
      if (p.status === 'Cancelled') return false;
      
      // Include Confirmed and Completed projects
      const status = (p.status || '').toLowerCase();
      const isConfirmed = status === 'confirmed' || status.includes('confirmed');
      const isCompleted = status === 'completed' || status.includes('completed');
      
      return isConfirmed || isCompleted;
    }).sort((a, b) => {
      // Use shared utility function for consistent sorting
      const dateA = getSortableDateFromProposal(a);
      const dateB = getSortableDateFromProposal(b);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
      
      // Primary sort: by date (descending - newest first)
      if (timeB !== timeA) {
        return timeB - timeA;
      }
      
      // Secondary sort: by projectNumber (descending) if dates are equal
      const projectNumA = parseInt(a.projectNumber) || 0;
      const projectNumB = parseInt(b.projectNumber) || 0;
      return projectNumB - projectNumA;
    });
  }, [proposals]);

  const year2026Spend = useMemo(() => {
    return year2026Proposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2026Proposals]);

  // Calculate year-specific pending proposals
  const year2025PendingProposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled' || p.status === 'Completed' || p.status === 'Confirmed') return false;
      if (p.status !== 'Pending' && p.status !== 'Approved') return false;
      if (p.timestamp) {
        const proposalYear = new Date(p.timestamp).getFullYear();
        return proposalYear === 2025;
      }
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          return start.getFullYear() === 2025;
        }
      }
      if (p.isHistorical && p.eventDate) {
        const yearMatch = p.eventDate.match(/\d{4}/);
        if (yearMatch) return parseInt(yearMatch[0]) === 2025;
      }
      return false;
    });
  }, [proposals]);

  const year2026PendingProposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status === 'Cancelled' || p.status === 'Completed' || p.status === 'Confirmed') return false;
      if (p.status !== 'Pending' && p.status !== 'Approved') return false;
      if (p.timestamp) {
        const proposalYear = new Date(p.timestamp).getFullYear();
        return proposalYear === 2026;
      }
      if (p.startDate) {
        const start = parseDateSafely(p.startDate);
        if (start && !isNaN(start.getTime())) {
          return start.getFullYear() === 2026;
        }
      }
      if (p.isHistorical && p.eventDate) {
        const yearMatch = p.eventDate.match(/\d{4}/);
        if (yearMatch) return parseInt(yearMatch[0]) === 2026;
      }
      return false;
    });
  }, [proposals]);

  const year2025PendingSpend = useMemo(() => {
    return year2025PendingProposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2025PendingProposals]);

  const year2026PendingSpend = useMemo(() => {
    return year2026PendingProposals.reduce((total, proposal) => {
      return total + calculateProductSpend(proposal);
    }, 0);
  }, [year2026PendingProposals]);

  // Calculate year-specific money saved
  const year2025MoneySaved = useMemo(() => {
    return year2025Proposals.reduce((total, proposal) => {
      try {
        if (proposal.isHistorical) {
          let histDiscount = 0;
          if (proposal.historicalDiscount !== undefined && proposal.historicalDiscount !== null) {
            const discountStr = String(proposal.historicalDiscount).trim();
            if (discountStr !== '' && discountStr !== 'undefined' && discountStr !== 'null') {
              histDiscount = parseFloat(discountStr);
              if (isNaN(histDiscount)) histDiscount = 0;
            }
          }
          return total + histDiscount;
        }
        if (proposal.status === 'Pending' || proposal.status === 'Approved') return total;
        const sections = JSON.parse(proposal.sectionsJSON || '[]');
        let baseProductTotal = 0;
        sections.forEach(section => {
          if (section.products && Array.isArray(section.products)) {
            section.products.forEach(product => {
              baseProductTotal += (parseFloat(product.quantity) || 0) * (parseFloat(product.price) || 0);
            });
          }
        });
        let rentalMultiplier = 1.0;
        if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
          const parsed = parseFloat(proposal.customRentalMultiplier);
          if (!isNaN(parsed) && parsed > 0) rentalMultiplier = parsed;
        } else {
          const start = parseDateSafely(proposal.startDate);
          const end = parseDateSafely(proposal.endDate);
          if (start && end) {
            const diffTime = end.getTime() - start.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
            rentalMultiplier = getRentalMultiplier(diffDays);
          }
        }
        const extendedProductTotal = baseProductTotal * rentalMultiplier;
        const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
        let discountType = 'percentage';
        if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
          const match = proposal.discountName.match(/^TYPE:(\w+)/);
          if (match) discountType = match[1];
        }
        const standardRateDiscount = discountType === 'dollar' 
          ? discountValue 
          : extendedProductTotal * (discountValue / 100);
        return total + standardRateDiscount;
      } catch (e) {
        return total;
      }
    }, 0);
  }, [year2025Proposals]);

  const year2026MoneySaved = useMemo(() => {
    return year2026Proposals.reduce((total, proposal) => {
      try {
        if (proposal.isHistorical) {
          let histDiscount = 0;
          if (proposal.historicalDiscount !== undefined && proposal.historicalDiscount !== null) {
            const discountStr = String(proposal.historicalDiscount).trim();
            if (discountStr !== '' && discountStr !== 'undefined' && discountStr !== 'null') {
              histDiscount = parseFloat(discountStr);
              if (isNaN(histDiscount)) histDiscount = 0;
            }
          }
          return total + histDiscount;
        }
        if (proposal.status === 'Pending' || proposal.status === 'Approved') return total;
        const sections = JSON.parse(proposal.sectionsJSON || '[]');
        let baseProductTotal = 0;
        sections.forEach(section => {
          if (section.products && Array.isArray(section.products)) {
            section.products.forEach(product => {
              baseProductTotal += (parseFloat(product.quantity) || 0) * (parseFloat(product.price) || 0);
            });
          }
        });
        let rentalMultiplier = 1.0;
        if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
          const parsed = parseFloat(proposal.customRentalMultiplier);
          if (!isNaN(parsed) && parsed > 0) rentalMultiplier = parsed;
        } else {
          const start = parseDateSafely(proposal.startDate);
          const end = parseDateSafely(proposal.endDate);
          if (start && end) {
            const diffTime = end.getTime() - start.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
            rentalMultiplier = getRentalMultiplier(diffDays);
          }
        }
        const extendedProductTotal = baseProductTotal * rentalMultiplier;
        const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
        let discountType = 'percentage';
        if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
          const match = proposal.discountName.match(/^TYPE:(\w+)/);
          if (match) discountType = match[1];
        }
        const standardRateDiscount = discountType === 'dollar' 
          ? discountValue 
          : extendedProductTotal * (discountValue / 100);
        return total + standardRateDiscount;
      } catch (e) {
        return total;
      }
    }, 0);
  }, [year2026Proposals]);

  // Calculate tier based on selected year (similar to Overview page)
  // PERFORMANCE: Use tier from Active Companies tab instead of calculating
  // Fallback to calculation only if clientInfo doesn't have tier
  // Check if this is a pre-2026 member (should start at Studio unless they earned higher in 2025)
  const isPre2026Member = clientInfo?.isPre2026Member || false;
  
  let displayTierBaseSpend = clientInfo?.spend2025 || currentYearSpend;
  let displayCarriedOverTier = null;
  
  // Use tier from Active Companies if available
  if (clientInfo?.tier && clientInfo?.tierDiscount !== null && clientInfo?.tierDiscount !== undefined) {
    // SAFEGUARD: Pre-2026 members should never be House tier - override if needed
    let tierToUse = clientInfo.tier;
    let discountToUse = parseInt(clientInfo.tierDiscount) || 0;
    
    if (isPre2026Member && selectedYear === 2026 && (tierToUse === 'House' || discountToUse === 0)) {
      // Pre-2026 members get Studio (15%) minimum
      tierToUse = 'Studio';
      discountToUse = 15;
    }
    
    displayCarriedOverTier = {
      discount: discountToUse,
      tier: tierToUse,
      baseSpend: clientInfo.spend2025 || 0
    };
    displayTierBaseSpend = clientInfo.spend2025 || 0;
  } else if (selectedYear === 2026) {
    // FALLBACK: Calculate tier if not available from Active Companies
    // For 2026, use 2025 spend to determine carried-over tier
    
    if (year2025Spend >= 200000) {
      displayCarriedOverTier = { discount: 30, tier: 'Estate', baseSpend: year2025Spend };
    } else if (year2025Spend >= 100000) {
      displayCarriedOverTier = { discount: 25, tier: 'Conservatory', baseSpend: year2025Spend };
    } else if (year2025Spend >= 25000) {
      displayCarriedOverTier = { discount: 20, tier: 'Gallery', baseSpend: year2025Spend };
    } else if (year2025Spend >= 5000) {
      displayCarriedOverTier = { discount: 15, tier: 'Studio', baseSpend: year2025Spend };
    } else {
      // For pre-2026 members, default to Studio (15%) minimum if they didn't earn a higher tier
      // For new members (post-2025), they start at House (0%)
      if (isPre2026Member) {
        displayCarriedOverTier = { discount: 15, tier: 'Studio', baseSpend: 0 };
      } else {
        displayCarriedOverTier = { discount: 0, tier: 'House', baseSpend: year2025Spend };
      }
    }
    displayTierBaseSpend = displayCarriedOverTier.baseSpend;
  } else {
    // For 2025, use 2025 spend directly
    displayTierBaseSpend = year2025Spend;
  }
  
  // Tier system: House ($1,000 credit) at enrollment, Studio (15%) at 5k, Gallery (20%) at 25k, Conservatory (25%) at 100k, Estate (30%) at 200k
  const getCurrentTier = () => {
    const yearSpend = selectedYear === 2026 ? year2026Spend : year2025Spend;
    const carriedOver = selectedYear === 2026 ? displayCarriedOverTier : null;
    
    if (carriedOver && selectedYear === 2026) {
      const tier = carriedOver.tier;
      if (tier === 'Estate') {
        return { discount: 30, tier: 'Estate', nextTier: null, progress: 100, nextTierName: null };
      } else if (tier === 'Conservatory') {
        // Conservatory needs 200,000 total points to reach Estate
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 200000 * 100
        const progress = Math.min((yearSpend / 200000) * 100, 100);
        return { discount: 25, tier: 'Conservatory', nextTier: 'Estate (30%)', progress: progress, nextTierName: 'Estate' };
      } else if (tier === 'Gallery') {
        // Gallery needs 100,000 total points to reach Conservatory
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 100000 * 100
        const progress = Math.min((yearSpend / 100000) * 100, 100);
        return { discount: 20, tier: 'Gallery', nextTier: 'Conservatory (25%)', progress: progress, nextTierName: 'Conservatory' };
      } else if (tier === 'Studio') {
        // Studio needs 25,000 total points to reach Gallery
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 25000 * 100
        const progress = Math.min((yearSpend / 25000) * 100, 100);
        return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: progress, nextTierName: 'Gallery' };
      } else {
        // House needs 5,000 total points to reach Studio
        // Progress in 2026 is based ONLY on 2026 points earned (yearSpend)
        // Progress = yearSpend / 5000 * 100
        const progress = Math.min((yearSpend / 5000) * 100, 100);
        return { discount: 0, tier: 'House', nextTier: 'Studio (15%)', progress: progress, nextTierName: 'Studio' };
      }
    }
    
    // For 2025 or when carriedOver is not set, use standard logic
    // BUT: For pre-2026 members in 2026, if they have less than 5k spend, they should still be Studio
    if (selectedYear === 2026 && isPre2026Member && displayTierBaseSpend < 5000) {
      const progress = Math.min((yearSpend / 20000) * 100, 100);
      return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: progress, nextTierName: 'Gallery' };
    }
    
    if (displayTierBaseSpend >= 200000) {
      return { discount: 30, tier: 'Estate', nextTier: null, progress: 100, nextTierName: null };
    } else if (displayTierBaseSpend >= 100000) {
      return { discount: 25, tier: 'Conservatory', nextTier: 'Estate (30%)', progress: ((displayTierBaseSpend - 100000) / 100000) * 100, nextTierName: 'Estate' };
    } else if (displayTierBaseSpend >= 25000) {
      return { discount: 20, tier: 'Gallery', nextTier: 'Conservatory (25%)', progress: ((displayTierBaseSpend - 25000) / 75000) * 100, nextTierName: 'Conservatory' };
    } else if (displayTierBaseSpend >= 5000) {
      return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: ((displayTierBaseSpend - 5000) / 20000) * 100, nextTierName: 'Gallery' };
    } else {
      // For pre-2026 members, default to Studio (15%) minimum if they didn't earn a higher tier
      // For new members (post-2025), they start at House (0%)
      if (isPre2026Member) {
        const progress = selectedYear === 2026 ? Math.min((yearSpend / 20000) * 100, 100) : (displayTierBaseSpend / 5000) * 100;
        return { discount: 15, tier: 'Studio', nextTier: 'Gallery (20%)', progress: progress, nextTierName: 'Gallery' };
      } else {
        return { discount: 0, tier: 'House', nextTier: 'Studio (15%)', progress: (displayTierBaseSpend / 5000) * 100, nextTierName: 'Studio' };
      }
    }
  };

  const tier = getCurrentTier();

  // Check if we're still loading projects (for tier display)
  const isLoadingProjects = loading || (proposals.length === 0 && !spendData);

  // State for mini contact form
  const [formData, setFormData] = useState({
    venue: '',
    projectDates: '',
    invoiceTotal: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Helper function to get sortable date from proposal (most recent first)
  const getSortableDate = (proposal) => {
    if (proposal.isHistorical && proposal.timestamp) {
      return new Date(proposal.timestamp);
    } else if (proposal.startDate) {
      return parseDateSafely(proposal.startDate);
    } else if (proposal.eventDate) {
      if (typeof proposal.eventDate === 'string' && proposal.eventDate.includes('GMT')) {
        return new Date(proposal.eventDate);
      } else if (proposal.eventDate instanceof Date) {
        return proposal.eventDate;
      }
    }
    return new Date(0);
  };
  
  // Pending = Pending or Approved quotes whose event date has not passed.
  const pendingProposals = useMemo(() => {
    return proposals.filter(p => {
      const status = p.status;
      if (status !== 'Pending' && status !== 'Approved') return false;
      return !proposalDateHasPassed(p);
    }).sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateA.getTime() - dateB.getTime(); // Ascending: soonest event first
    });
  }, [proposals]);

  // Confirmed = Confirmed projects whose event date has not passed.
  const confirmedProposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.status !== 'Confirmed') return false;
      return !proposalDateHasPassed(p);
    }).sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateA.getTime() - dateB.getTime(); // Ascending: soonest event first
    });
  }, [proposals]);
  
  // Completed = Projects with status "Completed" OR "Confirmed" projects where event date has already occurred
  // Memoized for performance - only recalculates when proposals change
  const completedProposals = useMemo(() => {
    return proposals.filter(p => {
      const status = p.status;
      
      // Check if this is a historical project (all historical projects are completed by definition)
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      if (isHistorical) {
        return true; // Always include historical projects in completed tab
      }
      
      // Always include projects with status "Completed"
      if (status === 'Completed') {
        return true;
      }
      
      if (status === 'Confirmed') return proposalDateHasPassed(p);
      
      return false;
    }).sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
  }, [proposals]);

  // Memoized for performance - only recalculates when proposals change
  const cancelledProposals = useMemo(() => {
    return proposals.filter(p => {
      const isHistorical = p.isHistorical || (p.projectNumber && String(p.projectNumber).startsWith('HIST-'));
      if (isHistorical) return false;
      if (p.status === 'Cancelled') return true;
      if (p.status === 'Pending' || p.status === 'Approved') return proposalDateHasPassed(p);
      return false;
    }).sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
  }, [proposals]);

  const getDisplayStatus = (proposal) => {
    const status = proposal.status || 'Pending';
    if ((status === 'Pending' || status === 'Approved') && proposalDateHasPassed(proposal)) {
      return 'Cancelled';
    }
    if (status === 'Confirmed' && proposalDateHasPassed(proposal)) {
      return 'Completed';
    }
    return status;
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      // Get token from localStorage
      const token = localStorage.getItem('clientToken');
      console.log('[Missing Event Form] Token from localStorage:', token ? 'Found (length: ' + token.length + ')' : 'NOT FOUND');
      console.log('[Missing Event Form] Form data:', formData);
      
      if (!token) {
        alert('Session expired. Please log in again.');
        return;
      }
      
      const requestBody = {
        action: 'submitMissingEvent',
        token: token,
        venue: formData.venue,
        projectDates: formData.projectDates,
        invoiceTotal: formData.invoiceTotal,
        notes: formData.notes
      };
      console.log('[Missing Event Form] Request body:', requestBody);
      
      const response = await fetch(CLIENT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(requestBody)
      });

      console.log('[Missing Event Form] Response status:', response.status);
      const result = await response.json();
      console.log('[Missing Event Form] Response result:', result);
      
      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          venue: '',
          projectDates: '',
          invoiceTotal: '',
          notes: ''
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        console.error('[Missing Event Form] Error from server:', result.error);
        throw new Error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('[Missing Event Form] Error submitting form:', error);
      alert('Failed to submit form. Please try again. Error: ' + (error.message || error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getProposalsForTab = () => {
    switch (proposalTab) {
      case 'pending': return pendingProposals;
      case 'confirmed': return confirmedProposals;
      case 'completed': return completedProposals;
      case 'cancelled': return cancelledProposals;
      default: return [];
    }
  };

  // Helper function to calculate total for a proposal
  const calculateTotal = (proposal) => {
    try {
      if (proposal.isHistorical && proposal.historicalTotal) {
        return parseFloat(proposal.historicalTotal) || 0;
      }
      const sections = JSON.parse(proposal.sectionsJSON || '[]');
      let total = 0;
      sections.forEach(section => {
        if (section.products && Array.isArray(section.products)) {
          section.products.forEach(product => {
            const quantity = parseFloat(product.quantity) || 0;
            const price = parseFloat(product.price) || 0;
            total += quantity * price;
          });
        }
      });
      // Add product care, service fee, delivery, tax
      const productCare = parseFloat(proposal.productCareFee) || 0;
      const serviceFee = parseFloat(proposal.serviceFee) || 0;
      const delivery = parseFloat(proposal.deliveryFee) || 0;
      const subtotal = total + productCare + serviceFee + delivery;
      const tax = subtotal * 0.0975;
      return subtotal + tax;
    } catch (e) {
      return 0;
    }
  };

  return (
    <div>
      {/* Responsive Styles for Badges */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .ytd-stats-card {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .activity-year-text {
            display: none !important;
          }
          .tier-and-project-grid {
            grid-template-columns: 1fr !important;
            display: contents !important;
            gap: 0 !important;
            margin-bottom: 0 !important;
          }
          .tier-and-project-grid > div:first-child {
            order: 1 !important;
            margin-bottom: 48px !important;
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .tier-status-card {
            padding: 48px 24px !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .status-medallion-container {
            aspect-ratio: 1 !important;
            width: 240px !important;
            height: 240px !important;
            max-width: calc(100vw - 96px) !important;
            margin-left: auto !important;
            margin-right: auto !important;
            flex-shrink: 0 !important;
          }
          .status-circle-border {
            aspect-ratio: 1 !important;
            width: 100% !important;
            height: 100% !important;
            top: 0 !important;
            left: 0 !important;
          }
          /* Remove "membership" from progress text on mobile */
          .progress-percent-text {
            text-align: center !important;
          }
          .progress-percent-text .membership-word {
            display: none !important;
          }
          /* Break points text into two lines and remove bullet on mobile */
          .progress-points-text {
            text-align: center !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 4px !important;
          }
          .progress-points-text .points-bullet {
            display: none !important;
          }
          .progress-points-text .points-earned {
            display: block !important;
          }
          .progress-points-text .points-required {
            display: block !important;
          }
          .tier-and-project-grid > div:last-child {
            order: 2 !important;
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            margin-left: auto !important;
            margin-right: auto !important;
            margin-bottom: 48px !important;
          }
          /* Your Data heading spacing */
          .your-data-heading {
            margin-top: 48px !important;
            margin-bottom: 16px !important;
          }
          /* Reduce padding on mobile */
          .activity-content-wrapper {
            padding-left: 24px !important;
            padding-right: 24px !important;
            padding-bottom: 120px !important;
          }
          .missing-event-form-section {
            margin-bottom: 48px !important;
          }
          /* Ensure all tier names have consistent font size on mobile - multiple selectors for maximum override */
          .tier-benefits-grid > div > div:first-child {
            font-size: 11px !important;
            font-weight: 500 !important;
          }
          .tier-benefits-grid > div.tier-card-selected > div:first-child {
            font-size: 11px !important;
            font-weight: 500 !important;
          }
          .tier-card-selected > div:first-of-type {
            font-size: 11px !important;
            font-weight: 500 !important;
          }
          /* Override any inline fontSize styles */
          .tier-benefits-grid > div[class*="tier-card"] > div:first-child {
            font-size: 11px !important;
          }
        }
        @media (max-width: 1024px) and (min-width: 769px) {
          .ytd-stats-card {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .ytd-stats-card > div:last-child {
            grid-column: 1 / -1;
          }
        }
      ` }} />
      
      {/* Image Banner */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* Activity Banner - Full Width with proper height */
        .performance-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          margin-top: -48px !important;
          margin-bottom: 64px !important;
          height: 500px !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .performance-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .performance-banner {
            height: 400px !important;
          }
          .performance-banner-title {
            font-size: 36px !important;
          }
          .performance-banner-subtitle {
            font-size: 10px !important;
            padding: 0 16px !important;
          }
        }
        @media (max-width: 480px) {
          .performance-banner {
            height: 350px !important;
          }
          .performance-banner-title {
            font-size: 28px !important;
          }
        }
      ` }} />
      <div className="performance-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        height: '500px',
        marginBottom: '64px',
        marginTop: '-48px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/account-banner.jpg" 
          alt="Your Reserve Status" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            // Fallback if image doesn't exist yet
            e.target.style.display = 'none';
          }}
        />
        {/* Dark overlay for richer look */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div className="performance-banner-title" style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Your Reserve Status
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div className="performance-banner-subtitle" style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            A LOOK AT YOUR COLLABORATION, EXCLUSIVES, AND MEMBER BENEFITS.
          </div>
        </div>
      </div>
      
      {/* Main Content Wrapper with Padding */}
      <div className="activity-content-wrapper" style={{
        maxWidth: '1400px',
        margin: '0 auto',
        paddingLeft: '48px',
        paddingRight: '48px'
      }}>
      {/* Projects Section - Editorial Layout */}
      <div style={{ 
        marginTop: '64px',
        marginBottom: '64px'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '300', 
            color: '#1a1a1a', 
            marginBottom: '8px',
            fontFamily: "'Domaine Text', serif",
            letterSpacing: '-0.01em'
          }}>
            Projects
          </h3>
          <p style={{ 
            fontSize: '14px',
            color: 'rgba(0, 0, 0, 0.5)',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.5',
            margin: 0
          }}>
            This is your project hub—a place to view everything we're working on together, have completed, or explored along the way. Active projects include any confirmed or open orders yet to occur; click into any proposal to view the latest details or request changes. Completed projects reflect our past collaborations, and Cancelled shows projects we quoted but didn't move forward with.
          </p>
        </div>

        {/* Proposal Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)', 
          marginBottom: '32px',
          gap: '0'
        }}>
          {['pending', 'confirmed', 'completed', 'cancelled'].map((tab) => {
            const count = tab === 'pending' ? pendingProposals.length : 
                         tab === 'confirmed' ? confirmedProposals.length : 
                         tab === 'completed' ? completedProposals.length : 
                         cancelledProposals.length;
            return (
              <button
                key={tab}
                onClick={() => setProposalTab && setProposalTab(tab)}
                style={{
                  padding: '8px 20px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: proposalTab === tab ? '1px solid #1a1a1a' : '1px solid transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: proposalTab === tab ? '500' : '400',
                  color: proposalTab === tab ? '#1a1a1a' : 'rgba(0, 0, 0, 0.5)',
                  textTransform: 'capitalize',
                  transition: 'all 0.2s ease',
                  marginBottom: '-1px'
                }}
              >
                {tab} ({count})
              </button>
            );
          })}
        </div>

        {/* Editorial Project List */}
        <div>
          {getProposalsForTab().length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '64px 24px', 
              color: 'rgba(0, 0, 0, 0.5)', 
              fontFamily: "'NeueHaasUnica', sans-serif", 
              fontSize: '14px' 
            }}>
              No {proposalTab} proposals found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {getProposalsForTab().map((proposal, index) => {
                // Use stored invoiceTotal first (faster), fallback to calculation only if missing
                const invoiceTotal = proposal.invoiceTotal !== undefined && proposal.invoiceTotal !== null && parseFloat(proposal.invoiceTotal) > 0
                  ? parseFloat(proposal.invoiceTotal)
                  : (() => {
                      const totals = calculateDetailedTotals(proposal);
                      return totals.total;
                    })();
                const dateRange = formatDateRange(proposal);
                
                // Use stored pointAllocation first (faster), fallback to calculation only if missing
                const points = proposal.pointAllocation !== undefined && proposal.pointAllocation !== null
                  ? Math.round(parseFloat(proposal.pointAllocation))
                  : Math.round(calculateProductSpend(proposal));
                
                // Check if proposal is historical
                const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
                
                // Debug logging
                console.log(`[Activity Display] Project ${proposal.projectNumber || 'N/A'}:`, {
                  isHistorical,
                  invoiceTotal: invoiceTotal.toFixed(2),
                  historicalInvoiceTotal: proposal.historicalInvoiceTotal,
                  historicalProductTotal: proposal.historicalProductTotal,
                  points: points.toFixed(2)
                });
                
                const displayStatus = getDisplayStatus(proposal);

                return (
                  <div
                    key={proposal.id || index}
                    role={proposal.projectNumber ? "button" : undefined}
                    tabIndex={proposal.projectNumber ? 0 : undefined}
                    onClick={() => {
                      if (proposal.projectNumber) {
                        analyticsService.trackProposalView(proposal.projectNumber || proposal.id);
                        setSelectedProposal(proposal);
                      }
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && proposal.projectNumber) {
                        e.preventDefault();
                        analyticsService.trackProposalView(proposal.projectNumber || proposal.id);
                        setSelectedProposal(proposal);
                      }
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '24px 0',
                      borderBottom: index < getProposalsForTab().length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                      transition: 'background-color 0.2s ease',
                      cursor: proposal.projectNumber ? 'pointer' : 'default'
                    }}
                    className="project-row-wrapper"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.01)';
                      const link = e.currentTarget.querySelector('.project-action-link');
                      if (link && proposal.projectNumber) {
                        link.style.color = 'rgba(0, 0, 0, 0.8)';
                        link.style.textDecorationThickness = '1.5px';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      const link = e.currentTarget.querySelector('.project-action-link');
                      if (link && proposal.projectNumber) {
                        link.style.color = 'rgba(0, 0, 0, 0.6)';
                        link.style.textDecorationThickness = '1px';
                      }
                    }}
                    onFocus={(e) => {
                      if (proposal.projectNumber) {
                        e.currentTarget.style.outline = '2px solid rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.outlineOffset = '-2px';
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.01)';
                      }
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.outline = 'none';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Desktop: Two-column layout */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '32px'
                    }} className="project-row-desktop">
                      {/* Left Column: Date + Venue */}
                      <div style={{ flex: '1', minWidth: 0 }}>
                        <div style={{
                          fontSize: '11px',
                          color: 'rgba(0, 0, 0, 0.5)',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          marginBottom: '6px',
                          letterSpacing: '0.01em',
                          lineHeight: '1.4'
                        }}>
                          {dateRange || 'N/A'}
                        </div>
                        <div style={{
                          fontSize: '16px',
                          color: '#1a1a1a',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          fontWeight: '400',
                          lineHeight: '1.5',
                          marginBottom: '4px'
                        }}>
                          {proposal.venueName || 'N/A'}
                        </div>
                        {/* Status - moved to left column */}
                        <div style={{
                          fontSize: '11px',
                          color: 'rgba(0, 0, 0, 0.4)',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          marginTop: '4px',
                          lineHeight: '1.4',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            fontSize: '8px',
                            color: 'rgba(0, 0, 0, 0.3)',
                            lineHeight: '1'
                          }}>
                            {(displayStatus === 'Confirmed' || displayStatus === 'Completed') ? '●' : '○'}
                          </span>
                          <span>{displayStatus}</span>
                        </div>
                      </div>

                      {/* Right Column: Total, Points, Status, Action */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '4px',
                        flexShrink: 0,
                        textAlign: 'right'
                      }}>
                        <div style={{
                          fontSize: '15px',
                          color: '#1a1a1a',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          fontWeight: '400',
                          lineHeight: '1.4'
                        }}>
                          ${invoiceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'rgba(0, 0, 0, 0.5)',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          lineHeight: '1.4'
                        }}>
                          +{points.toLocaleString()} points{(displayStatus === 'Pending' || displayStatus === 'Approved') ? ' (estimated)' : ''}
                        </div>
                        {/* Action Link - Visual Affordance */}
                        {/* Hide "Manage Project" for historical projects (they don't have proposals to link to) */}
                        {proposal.projectNumber && !proposal.projectNumber.startsWith('HIST-') ? (
                          <div
                            style={{
                              fontSize: '13px',
                              color: 'rgba(0, 0, 0, 0.6)',
                              fontFamily: "'NeueHaasUnica', sans-serif",
                              textDecoration: 'underline',
                              textDecorationThickness: '1px',
                              textUnderlineOffset: '2px',
                              marginTop: '8px',
                              transition: 'all 0.2s ease',
                              letterSpacing: '0.05em',
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none'
                            }}
                            className="project-action-link"
                          >
                            Manage Project →
                          </div>
                        ) : (
                          <div style={{
                            fontSize: '13px',
                            color: 'rgba(0, 0, 0, 0.3)',
                            fontFamily: "'NeueHaasUnica', sans-serif",
                            marginTop: '8px'
                          }}>
                            —
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Responsive Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .project-row-desktop {
            flex-direction: column !important;
            gap: 16px !important;
          }
          .project-row-desktop > div:last-child {
            align-items: flex-start !important;
            text-align: left !important;
            width: 100% !important;
          }
          .project-status-mobile {
            display: block !important;
          }
          .project-row-desktop > div:last-child > div:nth-child(3) {
            display: none !important;
          }
        }
      ` }} />
      
      {/* Divider line between Projects and Member Status */}
      <div style={{
        width: '100%',
        height: '1px',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        marginTop: '48px',
        marginBottom: '48px'
      }} />
      
      {/* Tier Status and Start New Project - Side by Side */}
      <div className="tier-and-project-grid" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '48px'
      }}>
        {/* Tier Status - Medallion with Progress Bar */}
        <div className="tier-status-card" style={{ 
          backgroundColor: 'white', 
          border: 'none',
          padding: '64px 48px', 
          borderRadius: '20px', 
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
        {/* Year Toggle - Inside Card */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2
        }}>
          <button
            onClick={() => setSelectedYear(2025)}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: selectedYear === 2025 ? '500' : '400',
              color: selectedYear === 2025 ? '#2C2C2C' : '#8b8b8b',
              backgroundColor: selectedYear === 2025 ? '#f5f5f0' : 'transparent',
              border: '1px solid',
              borderColor: selectedYear === 2025 ? '#545142' : '#d1d1c7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease'
            }}
          >
            2025
          </button>
          <button
            onClick={() => setSelectedYear(2026)}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: selectedYear === 2026 ? '500' : '400',
              color: selectedYear === 2026 ? '#2C2C2C' : '#8b8b8b',
              backgroundColor: selectedYear === 2026 ? '#f5f5f0' : 'transparent',
              border: '1px solid',
              borderColor: selectedYear === 2026 ? '#545142' : '#d1d1c7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease'
            }}
          >
            2026
          </button>
        </div>
        
        {/* Explanatory Text - Inside Card */}
        {selectedYear === 2026 && (
          <div style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            right: '180px',
            fontSize: '11px',
            fontWeight: '400',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.5',
            fontStyle: 'italic',
            zIndex: 2
          }}>
            Your 2026 membership reflects the highest tier you achieved in 2025.<br />
            Points reset each January.
          </div>
        )}
        
        {/* Status Medallion */}
        <div className="status-medallion-container" style={{ 
          position: 'relative',
          width: '240px',
          height: '240px',
          marginBottom: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '48px' // Keep consistent spacing regardless of year
        }}>
          {/* Circle with olive trim */}
          <div className="status-circle-border" style={{
            position: 'absolute',
            width: '240px',
            height: '240px',
            borderRadius: '50%',
            border: tier.tier === 'House' || tier.tier === 'Studio' ? '3px solid #545142' : tier.tier === 'Gallery' ? '3px solid #d4af37' : '3px solid #2C2C2C',
            backgroundColor: 'transparent'
          }} />
          {/* Center content */}
          <div style={{
            textAlign: 'center',
            zIndex: 1,
            padding: '24px'
          }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
              marginBottom: '8px'
            }}>
              {(!clientInfo || isLoadingProjects) ? 'Tier Loading' : tier.tier}
            </div>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '300', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '-0.01em'
            }}>
              {(!clientInfo || isLoadingProjects) ? '' : `${tier.discount}% Off`}
            </div>
          </div>
        </div>

        {/* Progress Section */}
        {tier.nextTier && (() => {
          // Calculate points to next tier based on selected year
          const yearSpend = selectedYear === 2026 ? year2026Spend : year2025Spend;
          let pointsToNextTier = 0;
          let nextTierPoints = 0;
          
          if (tier.tier === 'House') {
            nextTierPoints = 5000;
            pointsToNextTier = Math.ceil(5000 - yearSpend);
          } else if (tier.tier === 'Studio') {
            nextTierPoints = 25000;
            if (selectedYear === 2026 && displayCarriedOverTier) {
              // For 2026 with carried over tier, need remaining points
              pointsToNextTier = Math.ceil(25000 - (displayCarriedOverTier.baseSpend + yearSpend));
            } else {
              pointsToNextTier = Math.ceil(25000 - yearSpend);
            }
          } else if (tier.tier === 'Gallery') {
            nextTierPoints = 100000;
            if (selectedYear === 2026 && displayCarriedOverTier) {
              pointsToNextTier = Math.ceil(100000 - (displayCarriedOverTier.baseSpend + yearSpend));
            } else {
              pointsToNextTier = Math.ceil(100000 - yearSpend);
            }
          } else if (tier.tier === 'Conservatory') {
            nextTierPoints = 200000;
            if (selectedYear === 2026 && displayCarriedOverTier) {
              pointsToNextTier = Math.ceil(200000 - (displayCarriedOverTier.baseSpend + yearSpend));
            } else {
              pointsToNextTier = Math.ceil(100000 - yearSpend);
            }
          }
          
          // Calculate currentPoints: total points earned toward next tier
          // For 2026, show only 2026 points (starts at 0, nothing confirmed yet)
          // For 2025, use year2025Spend directly
          // yearSpend is already declared above
          let displayPoints = yearSpend;
          if (selectedYear === 2026) {
            // For 2026, show only the points earned in 2026 (starts at 0)
            displayPoints = yearSpend;
          } else if (selectedYear === 2025) {
            displayPoints = year2025Spend;
          }
          const currentPoints = Math.round(displayPoints);
          const progressPercent = Math.round(tier.progress);
          
          // Check if we're still loading projects
          const isLoadingProjects = loading || (proposals.length === 0 && !spendData);
          
          return (
            <div style={{ 
              width: '100%',
              maxWidth: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              {/* Label */}
              <div style={{ 
                fontSize: '10px', 
                color: '#8b8b8b',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                {(clientInfo?.clientCompanyName || clientInfo?.companyName || 'Your')}'s Progress
              </div>
              
              {/* Progress Bar */}
              <div style={{ 
                width: '65%', 
                height: '3px', 
                backgroundColor: '#e8e8e3', 
                borderRadius: '2px',
                marginBottom: '20px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  width: `${Math.min(tier.progress, 100)}%`,
                  height: '100%',
                  backgroundColor: tier.tier === 'House' || tier.tier === 'Studio' ? '#545142' : tier.tier === 'Gallery' ? '#d4af37' : '#2C2C2C',
                  transition: 'width 0.8s ease',
                  borderRadius: '2px'
                }} />
              </div>
              
              {/* Progress Copy */}
              <div style={{ 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {/* Line 1 - Primary */}
                <div className="progress-percent-text" style={{ 
                  fontSize: '16px', 
                  color: '#000000', 
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '500',
                  lineHeight: '1.4'
                }}>
                  {isLoadingProjects ? 'Projects loading...' : (
                    <>
                      {progressPercent}% to next{' '}
                      <span className="membership-word">membership </span>
                      tier
                    </>
                  )}
                </div>
                
                {/* Line 2 - Secondary */}
                <div className="progress-points-text" style={{ 
                  fontSize: '12px', 
                  color: '#8b8b8b', 
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '400',
                  lineHeight: '1.5'
                }}>
                  {isLoadingProjects ? (
                    <span>Calculating points...</span>
                  ) : (
                    <>
                      <span className="points-earned">{currentPoints.toLocaleString()} points earned</span>
                      <span className="points-bullet"> • </span>
                      <span className="points-required">{nextTierPoints.toLocaleString()} required</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        </div>

        {/* Start a New Project Block */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
          position: 'relative',
          height: '100%',
          minHeight: '400px'
        }}>
          <img 
            src="/new-project.jpg" 
            alt="Start a New Project" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6))'
          }} />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            padding: '48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            zIndex: 1
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '300',
              fontFamily: "'Domaine Text', serif",
              color: '#FFFFFF',
              marginBottom: '4px',
              letterSpacing: '-0.02em',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}>
              Start a New Project
            </h3>
            <p style={{
              fontSize: '14px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '32px',
              lineHeight: '1.6',
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Ready to begin your next event? Let's get started.
            </p>
            <button
              type="button"
              onClick={() => setActiveSection && setActiveSection('new-project')}
              style={{
                padding: '14px 32px',
                backgroundColor: '#2C2C2C',
                color: '#FAF8F3',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '400',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1a1a1a';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2C2C2C';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Start Project
            </button>
          </div>
        </div>
      </div>

      {/* Your Data Section */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', marginBottom: '24px' }}>
      <h3 className="your-data-heading" style={{ 
        fontSize: '20px', 
        fontWeight: '300', 
        color: '#000000', 
          marginBottom: 0,
        fontFamily: "'Domaine Text', serif",
          letterSpacing: '-0.01em',
          lineHeight: '1.2'
      }}>
        Your Data
      </h3>
        {/* Year Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
          <button
            onClick={() => setSelectedYear(2025)}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: selectedYear === 2025 ? '500' : '400',
              color: selectedYear === 2025 ? '#2C2C2C' : '#8b8b8b',
              backgroundColor: selectedYear === 2025 ? '#f5f5f0' : 'transparent',
              border: '1px solid',
              borderColor: selectedYear === 2025 ? '#545142' : '#d1d1c7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease'
            }}
          >
            2025
          </button>
          <button
            onClick={() => setSelectedYear(2026)}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: selectedYear === 2026 ? '500' : '400',
              color: selectedYear === 2026 ? '#2C2C2C' : '#8b8b8b',
              backgroundColor: selectedYear === 2026 ? '#f5f5f0' : 'transparent',
              border: '1px solid',
              borderColor: selectedYear === 2026 ? '#545142' : '#d1d1c7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: "'NeueHaasUnica', sans-serif",
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease'
            }}
          >
            2026
          </button>
        </div>
      </div>

      {/* YTD Stats Card */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '24px',
        marginBottom: '40px'
      }} className="ytd-stats-card">
        {/* YTD Points Earned Badge */}
        <div style={{ 
          backgroundColor: '#F7F6F0',
          padding: '24px', 
          borderRadius: '8px'
        }}>
          <div style={{ 
            fontSize: '10px', 
            fontWeight: '500', 
            color: '#8b8b8b', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            marginBottom: '12px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            fontFamily: "'NeueHaasUnica', sans-serif"
          }}>
            Year-to-Date Points Earned
          </div>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '300', 
            color: '#000000', 
            marginBottom: '8px',
            fontFamily: "'Domaine Text', serif",
            lineHeight: '1.1'
          }}>
            {Math.round(selectedYear === 2026 ? year2026Spend : year2025Spend).toLocaleString()}
          </div>
          <div className="activity-year-text" style={{
            fontSize: '11px',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            fontWeight: '400'
          }}>
            {selectedYear}
          </div>
        </div>
        
        {/* YTD Points Pending Badge */}
        <div style={{ 
          backgroundColor: '#F7F6F0',
          padding: '24px', 
          borderRadius: '8px'
        }}>
          <div style={{ 
            fontSize: '10px', 
            fontWeight: '500', 
            color: '#8b8b8b', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            marginBottom: '12px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            fontFamily: "'NeueHaasUnica', sans-serif"
          }}>
            Year-to-Date Points Pending
          </div>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '300', 
            color: '#000000', 
            marginBottom: '8px',
            fontFamily: "'Domaine Text', serif",
            lineHeight: '1.1'
          }}>
            {Math.round(selectedYear === 2026 ? year2026PendingSpend : year2025PendingSpend).toLocaleString()}
          </div>
          <div className="activity-year-text" style={{
            fontSize: '11px',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            fontWeight: '400'
          }}>
            {selectedYear}
          </div>
        </div>
        
        {/* YTD Projects Booked Badge */}
        <div style={{ 
          backgroundColor: '#F7F6F0',
          padding: '24px', 
          borderRadius: '8px'
        }}>
          <div style={{ 
            fontSize: '10px', 
            fontWeight: '500', 
            color: '#8b8b8b', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            marginBottom: '12px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            fontFamily: "'NeueHaasUnica', sans-serif"
          }}>
            Year-to-Date Projects Booked
          </div>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '300', 
            color: '#000000', 
            marginBottom: '8px',
            fontFamily: "'Domaine Text', serif",
            lineHeight: '1.1'
          }}>
            {selectedYear === 2026 ? year2026Proposals.length : year2025Proposals.length}
          </div>
          <div className="activity-year-text" style={{
            fontSize: '11px',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            fontWeight: '400'
          }}>
            {selectedYear}
          </div>
        </div>
        
        {/* Money Saved Badge */}
        <div style={{ 
          backgroundColor: '#F7F6F0',
          padding: '24px', 
          borderRadius: '8px'
        }}>
          <div style={{ 
            fontSize: '10px', 
            fontWeight: '500', 
            color: '#8b8b8b', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            marginBottom: '12px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            fontFamily: "'NeueHaasUnica', sans-serif"
          }}>
            Money Saved This Year
          </div>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '300', 
            color: '#000000', 
            marginBottom: '8px',
            fontFamily: "'Domaine Text', serif",
            lineHeight: '1.1'
          }}>
            ${Math.round(selectedYear === 2026 ? year2026MoneySaved : year2025MoneySaved).toLocaleString('en-US')}
          </div>
          <div className="activity-year-text" style={{
            fontSize: '11px',
            color: '#8b8b8b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            fontWeight: '400'
          }}>
            {selectedYear}
          </div>
        </div>
      </div>

      {/* Tier Benefits */}
      <div>
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: '300', 
          color: '#000000', 
          marginBottom: '8px',
          fontFamily: "'Domaine Text', serif",
          letterSpacing: '-0.01em'
        }}>
          Membership Tiers
        </h3>
        <div style={{
          fontSize: '12px',
          fontWeight: '400',
          color: '#8b8b8b',
          fontFamily: "'NeueHaasUnica', sans-serif",
          lineHeight: '1.5',
          fontStyle: 'italic',
          marginBottom: '24px'
        }}>
          From studio to estate: tier names for people who think in beautiful spaces.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '24px' }} className="tier-benefits-grid">
          {/* House - 0% */}
          <div className={tier.tier === 'House' ? 'tier-card-selected' : ''} style={{ 
            padding: '32px', 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            border: tier.tier === 'House' ? '1px solid #545142' : '1px solid #e8e8e3',
            transition: 'all 0.3s ease',
            boxShadow: tier.tier === 'House' 
              ? '0 4px 12px rgba(107, 125, 71, 0.15)' 
              : '0 2px 6px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {tier.tier === 'House' && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#545142'
              }} />
            )}
            <div className="tier-name-text" style={{ 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#8b8b8b',
              marginBottom: '16px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              House
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.03em',
              marginBottom: '12px',
              lineHeight: '1.1'
            }}>
              $1,000 credit
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontWeight: '400',
              lineHeight: '1.5'
            }}>
              {tier.tier === 'House' ? 'Your Current Standing' : 'At enrollment'}
            </div>
          </div>
          
          {/* Studio - 15% */}
          <div className={tier.tier === 'Studio' ? 'tier-card-selected' : ''} style={{ 
            padding: '32px', 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            border: tier.tier === 'Studio' ? '1px solid #545142' : '1px solid #e8e8e3',
            transition: 'all 0.3s ease',
            boxShadow: tier.tier === 'Studio' 
              ? '0 4px 12px rgba(107, 125, 71, 0.15)' 
              : '0 2px 6px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {tier.tier === 'Studio' && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#545142'
              }} />
            )}
            <div className="tier-name-text" style={{ 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#8b8b8b',
              marginBottom: '16px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              Studio
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.03em',
              marginBottom: '12px',
              lineHeight: '1.1'
            }}>
              15% off
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontWeight: '400',
              lineHeight: '1.5'
            }}>
              {tier.tier === 'Studio' ? 'Your Current Standing' : 'At 5k points'}
            </div>
          </div>
          
          {/* Gallery - 20% */}
          <div className={tier.tier === 'Gallery' ? 'tier-card-selected' : ''} style={{ 
            padding: '32px', 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            border: tier.tier === 'Gallery' ? '1px solid #d4af37' : '1px solid #e8e8e3',
            transition: 'all 0.3s ease',
            boxShadow: tier.tier === 'Gallery' 
              ? '0 4px 12px rgba(212, 175, 55, 0.15)' 
              : '0 2px 6px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {tier.tier === 'Gallery' && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#d4af37'
              }} />
            )}
            <div className="tier-name-text" style={{ 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#8b8b8b',
              marginBottom: '16px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              Gallery
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.03em',
              marginBottom: '12px',
              lineHeight: '1.1'
            }}>
              20% off
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontWeight: '400',
              lineHeight: '1.5'
            }}>
              {tier.tier === 'Gallery' ? 'Your Current Standing' : 'At 25k points'}
            </div>
          </div>
          
          {/* Conservatory - 25% */}
          <div className={tier.tier === 'Conservatory' ? 'tier-card-selected' : ''} style={{ 
            padding: '32px', 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            border: tier.tier === 'Conservatory' ? '1px solid #2C2C2C' : '1px solid #e8e8e3',
            transition: 'all 0.3s ease',
            boxShadow: tier.tier === 'Conservatory' 
              ? '0 4px 12px rgba(44, 44, 44, 0.15)' 
              : '0 2px 6px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {tier.tier === 'Conservatory' && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#2C2C2C'
              }} />
            )}
            <div className="tier-name-text" style={{ 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#8b8b8b',
              marginBottom: '16px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              Conservatory
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.03em',
              marginBottom: '12px',
              lineHeight: '1.1'
            }}>
              25% off
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontWeight: '400',
              lineHeight: '1.5'
            }}>
              {tier.tier === 'Conservatory' ? 'Your Current Standing' : 'At 100k points'}
            </div>
          </div>
          
          {/* Estate - 30% */}
          <div className={tier.tier === 'Estate' ? 'tier-card-selected' : ''} style={{ 
            padding: '32px', 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            border: tier.tier === 'Estate' ? '1px solid #2C2C2C' : '1px solid #e8e8e3',
            transition: 'all 0.3s ease',
            boxShadow: tier.tier === 'Estate' 
              ? '0 4px 12px rgba(44, 44, 44, 0.15)' 
              : '0 2px 6px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {tier.tier === 'Estate' && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#2C2C2C'
              }} />
            )}
            <div className="tier-name-text" style={{ 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#8b8b8b',
              marginBottom: '16px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              Estate
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000',
              fontFamily: "'Domaine Text', serif",
              letterSpacing: '-0.03em',
              marginBottom: '12px',
              lineHeight: '1.1'
            }}>
              30% off
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#8b8b8b',
              fontFamily: "'NeueHaasUnica', sans-serif",
              fontWeight: '400',
              lineHeight: '1.5'
            }}>
              {tier.tier === 'Estate' ? 'Your Current Standing' : 'At 200k points'}
            </div>
          </div>
        </div>
      </div>

      {/* Mini Contact Form Section - Subtle Footnote */}
        <div className="missing-event-form-section" style={{
          marginTop: '48px',
          padding: '32px',
          backgroundColor: '#fafaf8',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#6b6b6b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '16px',
            lineHeight: '1.4'
          }}>
            Looking for a Previous Project?
          </div>
          <p style={{
            fontSize: '14px',
            fontFamily: "'NeueHaasUnica', sans-serif",
            color: '#666',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            If we've worked together previously and you don't see one of your projects reflected here, reach out—we're happy to update your account. Keep in mind: Points are calculated based on your invoice subtotal (before delivery and tax). Pending proposals are added to your total once confirmed.
          </p>

          <form onSubmit={handleFormSubmit}>
            <div className="missing-event-form" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '20px'
            }}>
              {/* Venue Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '500',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}>
                  Venue
                </label>
                <input
                  type="text"
                  value={formData.venue}
                  onChange={(e) => handleInputChange('venue', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    color: '#000000',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="Enter venue name"
                />
              </div>

              {/* Project Date(s) Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '500',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}>
                  Project Date(s)
                </label>
                <input
                  type="text"
                  value={formData.projectDates}
                  onChange={(e) => handleInputChange('projectDates', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    color: '#000000',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="e.g., April 5-13, 2025"
                />
              </div>

              {/* Invoice Total Field */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '500',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}>
                  Invoice Total
                </label>
                <input
                  type="text"
                  value={formData.invoiceTotal}
                  onChange={(e) => handleInputChange('invoiceTotal', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    color: '#000000',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="e.g., $15,000.00"
                />
              </div>
            </div>

            {/* Success Message */}
            {submitSuccess && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#d1fae5',
                color: '#065f46',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}>
                Thank you! Your submission has been received. Our team will update your events shortly.
              </div>
            )}

            {/* Submit Button */}
            <div className="form-actions" style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '20px'
            }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#2C2C2C',
                  color: '#FAF8F3',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '400',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  opacity: isSubmitting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#1a1a1a';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#2C2C2C';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>

      {/* Mobile Responsive Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .missing-event-form-section {
            margin-top: 32px !important;
            padding: 24px 20px !important;
          }
          .missing-event-form {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .form-actions {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .form-actions button {
            width: 100% !important;
          }
        }
      ` }} />
      </div>
      {/* End Main Content Wrapper */}
    </div>
  );
}

export function ProposalsSection({ proposals, proposalTab, setProposalTab, setSelectedProposal, brandCharcoal = '#2C2C2C', setActiveSection }) {
  // State for mini contact form
  const [formData, setFormData] = useState({
    venue: '',
    projectDates: '',
    invoiceTotal: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Helper function to get sortable date from proposal (most recent first)
  const getSortableDate = (proposal) => {
    if (proposal.isHistorical && proposal.timestamp) {
      return new Date(proposal.timestamp);
    } else if (proposal.startDate) {
      return parseDateSafely(proposal.startDate);
    } else if (proposal.eventDate) {
      if (typeof proposal.eventDate === 'string' && proposal.eventDate.includes('GMT')) {
        return new Date(proposal.eventDate);
      } else if (proposal.eventDate instanceof Date) {
        return proposal.eventDate;
      }
    }
    return new Date(0);
  };
  
  // Active = Pending, Approved, or Confirmed whose event date has not passed.
  const activeProposals = proposals.filter(p => {
    const status = p.status;
    if (status === 'Cancelled' || status === 'Completed') return false;
    if (status === 'Pending' || status === 'Approved' || status === 'Confirmed') {
      return !proposalDateHasPassed(p);
    }
    return false;
  }).sort((a, b) => {
    const dateA = getSortableDate(a);
    const dateB = getSortableDate(b);
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });
  
  console.log(`[Proposals Active Filter] Found ${activeProposals.length} active proposals out of ${proposals.length} total`);
  
  // Completed = Projects with status "Completed" OR Confirmed projects with event dates in the past
  // IMPORTANT: Only include projects where event date has occurred (is in the past)
  // CRITICAL: Exclude projects that are already in Active to ensure mutual exclusivity
  const completedProposals = (() => {
    // First, get the list of active proposal project numbers to exclude them
    const activeProjectNumbers = new Set(
      activeProposals.map(ap => ap.projectNumber || ap.id)
    );
    
    return proposals.filter(p => {
      const status = p.status;
      const projectId = p.projectNumber || p.id;
      
      // CRITICAL: Exclude projects that are already in Active
      if (activeProjectNumbers.has(projectId)) {
        return false;
      }
      
      // Check if this is a historical project (all historical projects are completed by definition)
      const isHistorical = p.isHistorical || (p.projectNumber && p.projectNumber.startsWith('HIST-'));
      if (isHistorical) {
        return true; // Always include historical projects in completed tab
      }
      
      // Always include projects with status "Completed"
      if (status === 'Completed') return true;
      
      if (status === 'Confirmed') return proposalDateHasPassed(p);
      
      return false;
    }).sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
  })();

  const getDisplayStatus = (proposal) => {
    const status = proposal.status || 'Pending';
    if ((status === 'Pending' || status === 'Approved') && proposalDateHasPassed(proposal)) {
      return 'Cancelled';
    }
    if (status === 'Confirmed' && proposalDateHasPassed(proposal)) {
      return 'Completed';
    }
    return status;
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      // Get token from localStorage
      const token = localStorage.getItem('clientToken');
      console.log('[Missing Event Form] Token from localStorage:', token ? 'Found (length: ' + token.length + ')' : 'NOT FOUND');
      console.log('[Missing Event Form] Form data:', formData);
      
      if (!token) {
        alert('Session expired. Please log in again.');
        return;
      }
      
      const requestBody = {
        action: 'submitMissingEvent',
        token: token,
        venue: formData.venue,
        projectDates: formData.projectDates,
        invoiceTotal: formData.invoiceTotal,
        notes: formData.notes
      };
      console.log('[Missing Event Form] Request body:', requestBody);
      
      const response = await fetch(CLIENT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(requestBody)
      });

      console.log('[Missing Event Form] Response status:', response.status);
      const result = await response.json();
      console.log('[Missing Event Form] Response result:', result);
      
      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          venue: '',
          projectDates: '',
          invoiceTotal: '',
          notes: ''
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        console.error('[Missing Event Form] Error from server:', result.error);
        throw new Error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('[Missing Event Form] Error submitting form:', error);
      alert('Failed to submit form. Please try again. Error: ' + (error.message || error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const cancelledProposals = proposals.filter(p => {
    const isHistorical = p.isHistorical || (p.projectNumber && String(p.projectNumber).startsWith('HIST-'));
    if (isHistorical) return false;
    if (p.status === 'Cancelled') return true;
    if (p.status === 'Pending' || p.status === 'Approved') return proposalDateHasPassed(p);
    return false;
  }).sort((a, b) => {
    const dateA = getSortableDate(a);
    const dateB = getSortableDate(b);
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });

  const getProposalsForTab = () => {
    switch (proposalTab) {
      case 'active': return activeProposals;
      case 'completed': return completedProposals;
      case 'cancelled': return cancelledProposals;
      default: return [];
    }
  };

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: `
        .projects-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .projects-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .projects-banner {
            height: 400px !important;
          }
        }
        @media (max-width: 480px) {
          .projects-banner {
            height: 350px !important;
          }
        }
      ` }} />
      {/* Image Banner */}
      <div className="projects-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/projects-banner.jpg" 
          alt="Projects" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            // Fallback if image doesn't exist yet
            e.target.style.display = 'none';
          }}
        />
        {/* Dark overlay for richer look */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Projects
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div style={{
            fontSize: '14px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            This is your project hub—a place to view everything we're working on together, have completed, or explored along the way. Active projects include any confirmed or open orders yet to occur; click into any proposal to view the latest details or request changes. Completed projects reflect our past collaborations, and Cancelled shows projects we quoted but didn't move forward with.
          </div>
        </div>
      </div>
      
      {/* Proposal Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '24px' }}>
        {['active', 'completed', 'cancelled'].map((tab) => {
          const count = tab === 'active' ? activeProposals.length : tab === 'completed' ? completedProposals.length : cancelledProposals.length;
          return (
            <button
              key={tab}
              onClick={() => setProposalTab(tab)}
              style={{
                padding: '12px 24px',
                backgroundColor: proposalTab === tab ? '#f9fafb' : 'transparent',
                border: 'none',
                borderBottom: proposalTab === tab ? '2px solid ' + brandCharcoal : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: proposalTab === tab ? '600' : '400',
                color: proposalTab === tab ? brandCharcoal : '#666',
                textTransform: 'capitalize'
              }}
            >
              {tab} ({count})
            </button>
          );
        })}
      </div>

      {/* Proposal List */}
      <div>
        {getProposalsForTab().length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>
            No {proposalTab} proposals found.
          </div>
        ) : (
          <div style={{ 
            overflowX: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: 'white'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontFamily: "'NeueHaasUnica', sans-serif"
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Project Date(s)
                  </th>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Venue
                  </th>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'right', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Project Total
                  </th>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'right', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Points
                  </th>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'center', 
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    View
                  </th>
                </tr>
              </thead>
              <tbody>
                {getProposalsForTab().map((proposal, index) => {
                  // Use stored invoiceTotal first (faster), fallback to calculation only if missing
                  const invoiceTotal = proposal.invoiceTotal !== undefined && proposal.invoiceTotal !== null && parseFloat(proposal.invoiceTotal) > 0
                    ? parseFloat(proposal.invoiceTotal)
                    : (() => {
                        const totals = calculateDetailedTotals(proposal);
                        return totals.total;
                      })();
                  const dateRange = formatDateRange(proposal);
                  
                  // Use stored pointAllocation first (faster), fallback to calculation only if missing
                  const points = proposal.pointAllocation !== undefined && proposal.pointAllocation !== null
                    ? Math.round(parseFloat(proposal.pointAllocation))
                    : Math.round(calculateProductSpend(proposal));
                  
                  // Check if proposal is historical
                  const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
                  
                  // Debug logging
                  console.log(`[Proposals Display] Project ${proposal.projectNumber || 'N/A'}:`, {
                    isHistorical,
                    invoiceTotal: invoiceTotal.toFixed(2),
                    historicalInvoiceTotal: proposal.historicalInvoiceTotal,
                    historicalProductTotal: proposal.historicalProductTotal,
                    points: points.toFixed(2)
                  });
                  
                  const displayStatus = getDisplayStatus(proposal);
                  return (
                    <tr 
                      key={proposal.id || index}
                      style={{ 
                        borderBottom: index < getProposalsForTab().length - 1 ? '1px solid #e5e7eb' : 'none'
                      }}
                    >
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px',
                        color: '#000000'
                      }}>
                        {dateRange || 'N/A'}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px',
                        color: '#000000',
                        fontWeight: '500'
                      }}>
                        {proposal.venueName || 'N/A'}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px',
                        color: '#000000',
                        textAlign: 'right',
                        fontFamily: "'NeueHaasUnica', sans-serif"
                      }}>
                        ${invoiceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px',
                        color: '#000000',
                        textAlign: 'right',
                        fontFamily: "'NeueHaasUnica', sans-serif"
                      }}>
                        + {points.toLocaleString()} points
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px'
                      }}>
                        <span style={{ 
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: displayStatus === 'Approved' || displayStatus === 'Confirmed' ? '#d1fae5' : 
                                         displayStatus === 'Completed' ? '#d1fae5' :
                                         displayStatus === 'Pending' ? '#fef3c7' : 
                                         displayStatus === 'Cancelled' ? '#fee2e2' : '#e5e7eb',
                          color: displayStatus === 'Approved' || displayStatus === 'Confirmed' ? '#065f46' :
                                 displayStatus === 'Completed' ? '#065f46' :
                                 displayStatus === 'Pending' ? '#92400e' : 
                                 displayStatus === 'Cancelled' ? '#991b1b' : '#666'
                        }}>
                          {displayStatus}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        {/* Hide "View" button for historical projects (they don't have proposals to link to) */}
                        {proposal.projectNumber && !proposal.projectNumber.startsWith('HIST-') ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              analyticsService.trackProposalView(proposal.projectNumber || proposal.id);
                              setSelectedProposal(proposal);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#F7F6F0',
                              color: '#000000',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              fontFamily: "'NeueHaasUnica', sans-serif",
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#F7F6F0';
                              e.currentTarget.style.opacity = '0.8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#F7F6F0';
                              e.currentTarget.style.opacity = '1';
                            }}
                          >
                            View
                          </button>
                        ) : (
                          <span style={{ 
                            color: '#999',
                            fontSize: '12px',
                            fontFamily: "'NeueHaasUnica', sans-serif"
                          }}>
                            N/A
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Start New Project Button - Centered below projects */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '48px',
        marginBottom: '48px'
      }}>
        <button
          type="button"
          onClick={() => setActiveSection && setActiveSection('new-project')}
          style={{
            padding: '12px 36px',
            backgroundColor: '#2C2C2C',
            color: '#FAF8F3',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: "'NeueHaasUnica', sans-serif",
            fontWeight: '400',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1a1a1a';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2C2C2C';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Start New Project
        </button>
      </div>

      {/* Mini Contact Form Section - Subtle Footnote */}
      <div className="missing-event-form-section" style={{
        marginTop: '48px',
        padding: '32px',
        backgroundColor: '#fafaf8',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          fontSize: '10px',
          fontWeight: '500',
          color: '#6b6b6b',
          fontFamily: "'NeueHaasUnica', sans-serif",
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '16px',
          lineHeight: '1.4'
        }}>
          Looking for a Previous Project?
        </div>
        <p style={{
          fontSize: '14px',
          fontFamily: "'NeueHaasUnica', sans-serif",
          color: '#666',
          marginBottom: '24px',
          lineHeight: '1.5'
        }}>
          If we've worked together previously and you don't see one of your projects reflected here, reach out—we're happy to update your account. Keep in mind: Points are calculated based on your invoice subtotal (before delivery and tax). Pending proposals are added to your total once confirmed.
        </p>

        <form onSubmit={handleFormSubmit}>
          <div className="missing-event-form" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            marginBottom: '20px'
          }}>
            {/* Venue Field */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px'
              }}>
                Venue
              </label>
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => handleInputChange('venue', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#000000',
                  backgroundColor: 'white',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Enter venue name"
              />
            </div>

            {/* Project Date(s) Field */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px'
              }}>
                Project Date(s)
              </label>
              <input
                type="text"
                value={formData.projectDates}
                onChange={(e) => handleInputChange('projectDates', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#000000',
                  backgroundColor: 'white',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="e.g., April 5-13, 2025"
              />
            </div>

            {/* Invoice Total Field */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px'
              }}>
                Invoice Total
              </label>
              <input
                type="text"
                value={formData.invoiceTotal}
                onChange={(e) => handleInputChange('invoiceTotal', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#000000',
                  backgroundColor: 'white',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="e.g., $15,000.00"
              />
            </div>

            {/* Notes Field */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px'
              }}>
                Note(s)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#000000',
                  backgroundColor: 'white',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Additional details or notes..."
              />
            </div>
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#d1fae5',
              color: '#065f46',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '14px',
              fontFamily: "'NeueHaasUnica', sans-serif"
            }}>
              Thank you! Your submission has been received. Our team will update your events shortly.
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions" style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '20px'
          }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 32px',
                backgroundColor: '#000000',
                color: '#FAF8F3',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: "'NeueHaasUnica', sans-serif",
                fontWeight: '400',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                outline: 'none',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#1a1a1a';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#000000';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      {/* Mobile Responsive Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .missing-event-form-section {
            margin-top: 32px !important;
            padding: 24px 20px !important;
          }
          .missing-event-form {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .form-actions {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .form-actions button {
            width: 100% !important;
          }
        }
      ` }} />
    </div>
  );
}

export function ContactSection({ brandCharcoal = '#2C2C2C' }) {
  // Mobile-responsive styles for contact section
  const contactSectionStyles = `
        .contact-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .contact-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .contact-banner {
            height: 400px !important;
            margin-bottom: 64px !important;
          }
          .contact-banner-title {
            font-size: 36px !important;
          }
          .contact-banner-subtitle {
            font-size: 10px !important;
            padding: 0 16px !important;
          }
      .contact-container {
        padding-left: 24px !important;
        padding-right: 24px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
      .contact-grid {
        grid-template-columns: 1fr !important;
        gap: 24px !important;
        justify-content: center !important;
      }
      .contact-card {
        width: 100% !important;
        max-width: 100% !important;
      }
      .contact-section {
        margin-bottom: 48px !important;
      }
      .contact-info-box {
        margin-bottom: 60px !important;
      }
      .contact-info-text {
        font-size: 14px !important;
      }
      .contact-phone-number {
        margin-bottom: 24px !important;
      }
    }
    @media (max-width: 480px) {
      .contact-banner {
        height: 350px !important;
      }
      .contact-banner-title {
        font-size: 28px !important;
      }
    }
  `;

  const teamMembers = [
    {
      name: 'Megan Proby',
      title: 'Founder & CEO',
      email: 'Megan@Mayker.com',
      photo: '/megan-proby.jpg',
      firstName: 'Megan'
    },
    {
      name: 'Noelle Powell',
      title: 'Client Services Director',
      email: 'Noelle@Mayker.com',
      photo: '/noelle-powell.jpg',
      firstName: 'Noelle'
    },
    {
      name: 'Constance Farro',
      title: 'Partnerships Manager',
      email: 'Constance@Mayker.com',
      photo: '/constance-farro.jpg',
      firstName: 'Constance'
    },
    {
      name: 'Mara Meisberger',
      title: 'Administrative Associate',
      email: 'Mara@Mayker.com',
      photo: '/mara-meisberger.jpg',
      firstName: 'Mara'
    },
    {
      name: 'Becca Farris',
      title: 'Warehouse Manager',
      email: 'Becca@Mayker.com',
      photo: '/becca-farris.jpg',
      firstName: 'Becca'
    },
    {
      name: 'Robert Hamm',
      title: 'Inventory Manager',
      email: 'Robert@mayker.com',
      photo: '/robert-hamm.jpg',
      firstName: 'Robert'
    }
  ];

  // Helper to get initials
  const getInitials = (name, customInitials) => {
    if (customInitials) return customInitials;
    return name.split(' ').map(n => n[0]).join('');
  };

  // Contact Card Component - ensures consistent width
  const ContactCard = ({ member, brandCharcoal }) => {
    const initials = getInitials(member.name, member.initials);
    return (
      <div
        className="contact-card"
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px 32px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e8e8e3',
          transition: 'all 0.4s ease',
          textAlign: 'center',
          position: 'relative',
          width: '320px',
          maxWidth: '320px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Photo Container with Low-Opacity Initials Background */}
        <div style={{
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          margin: '0 auto 28px',
          overflow: 'hidden',
          backgroundColor: '#f5f4f0',
          border: '2px solid #e8e8e3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          {/* Low-opacity initials background (Soho vibe) */}
          {!member.hideBackgroundInitials && (
            <div 
              className="bg-initials"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '120px',
                fontWeight: '300',
                color: 'rgba(139, 139, 139, 0.15)',
                fontFamily: "'Domaine Text', serif",
                zIndex: 0,
                pointerEvents: 'none'
              }}
            >
              {initials}
            </div>
          )}
          
          {/* Photo */}
          {member.photo && (
            <img
              src={member.photo}
              alt={member.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'relative',
                zIndex: 1,
                borderRadius: '50%'
              }}
              onError={(e) => {
                // Hide image and show initials prominently if it fails
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                const bgInitials = parent.querySelector('.bg-initials');
                if (bgInitials) {
                  bgInitials.style.color = 'rgba(139, 139, 139, 0.4)';
                  bgInitials.style.fontSize = '80px';
                  bgInitials.style.zIndex = '2';
                }
              }}
            />
          )}
          {!member.photo && (
            <div style={{
              position: 'relative',
              zIndex: 2,
              fontSize: '80px',
              fontWeight: '300',
              color: 'rgba(139, 139, 139, 0.4)',
              fontFamily: "'Domaine Text', serif"
            }}>
              {initials}
            </div>
          )}
        </div>

        {/* Name */}
        <div style={{
          fontSize: '15px',
          fontWeight: '300',
          color: '#000000',
          fontFamily: "'Domaine Text', serif",
          marginBottom: '10px',
          letterSpacing: '-0.01em'
        }}>
          {member.name}
        </div>

        {/* Title */}
        <div style={{
          fontSize: '12px',
          fontWeight: '400',
          color: '#8b8b8b',
          fontFamily: "'NeueHaasUnica', sans-serif",
          marginBottom: '24px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em'
        }}>
          {member.title}
        </div>

        {/* Contact Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          marginTop: '24px'
        }}>
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              style={{
                padding: '12px 20px',
                backgroundColor: '#f5f4f0',
                color: '#000000',
                border: '1px solid #e8e8e3',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.3s ease',
                display: 'block',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F7F6F0';
                e.currentTarget.style.color = '#000000';
                e.currentTarget.style.borderColor = brandCharcoal;
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f4f0';
                e.currentTarget.style.color = '#000000';
                e.currentTarget.style.borderColor = '#e8e8e3';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Email {member.firstName}
            </a>
          )}
          {member.phone && (
            <a
              href={`tel:${member.phone.replace(/\D/g, '')}`}
              style={{
                padding: '12px 20px',
                backgroundColor: '#f5f4f0',
                color: '#000000',
                border: '1px solid #e8e8e3',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: "'NeueHaasUnica', sans-serif",
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.3s ease',
                display: 'block',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F7F6F0';
                e.currentTarget.style.color = '#000000';
                e.currentTarget.style.borderColor = brandCharcoal;
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f4f0';
                e.currentTarget.style.color = '#000000';
                e.currentTarget.style.borderColor = '#e8e8e3';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Call or Text
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: contactSectionStyles }} />
      {/* Image Banner */}
      <div className="contact-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/contact-banner.jpg" 
          alt="Contact" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            // Fallback if image doesn't exist yet
            e.target.style.display = 'none';
          }}
        />
        {/* Dark overlay for richer look */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div className="contact-banner-title" style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Contact
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div className="contact-banner-subtitle" style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            Your dedicated Mayker Reserve team is here to support your projects, partnerships, and events.
          </div>
        </div>
      </div>

      <div className="contact-container" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '48px', paddingRight: '48px', paddingTop: '0', paddingBottom: '0' }}>
        {/* Client Orders Section */}
        <div className="contact-section" style={{ marginBottom: '80px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '300', 
            color: '#000000',
            fontFamily: "'Domaine Text', serif",
            letterSpacing: '-0.01em',
            marginBottom: '24px'
          }}>
            Client Orders
          </h3>
          <p style={{
            fontSize: '14px',
            fontWeight: '400',
            color: '#6B6B6B',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.7',
            marginBottom: '32px',
            maxWidth: '800px'
          }}>
            If you have questions about an existing or upcoming project, please contact your dedicated coordinator or reach out to a member of our Client Team. We're here to help with anything you need.
          </p>
          <div style={{
            width: '60px',
            height: '1px',
            background: '#e8e8e3',
            marginBottom: '40px'
          }} />
          <div className="contact-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 320px))',
            gap: '32px',
            justifyContent: 'flex-start'
          }}>
            {teamMembers.filter(member => 
              member.name === 'Noelle Powell' || 
              member.name === 'Mara Meisberger'
            ).map((member, index) => (
              <ContactCard key={index} member={member} brandCharcoal={brandCharcoal} />
            ))}
          </div>
        </div>

        {/* Strategic Relationships Section */}
        <div className="contact-section" style={{ marginBottom: '80px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '300', 
            color: '#000000',
            fontFamily: "'Domaine Text', serif",
            letterSpacing: '-0.01em',
            marginBottom: '24px'
          }}>
            Strategic Relationships, Partner Projects and Sponsorships
          </h3>
          <p style={{
            fontSize: '14px',
            fontWeight: '400',
            color: '#6B6B6B',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.7',
            marginBottom: '32px',
            maxWidth: '800px'
          }}>
            For inquiries related to strategic collaborations, partner projects, or sponsorship opportunities, please connect with our Partnerships Manager. We'd love to explore how we can work together.
          </p>
          <div style={{
            width: '60px',
            height: '1px',
            background: '#e8e8e3',
            marginBottom: '40px'
          }} />
          <div className="contact-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 320px))',
            gap: '32px',
            justifyContent: 'flex-start'
          }}>
            {teamMembers.filter(member => 
              member.name === 'Constance Farro'
            ).map((member, index) => (
              <ContactCard key={index} member={member} brandCharcoal={brandCharcoal} />
            ))}
          </div>
        </div>

        {/* Logistics & Delivery Section */}
        <div className="contact-section" style={{ marginBottom: '64px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '300', 
            color: '#000000',
            fontFamily: "'Domaine Text', serif",
            letterSpacing: '-0.01em',
            marginBottom: '24px'
          }}>
            Logistics and Delivery
          </h3>
          <p style={{
            fontSize: '14px',
            fontWeight: '400',
            color: '#6B6B6B',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.7',
            marginBottom: '32px',
            maxWidth: '800px'
          }}>
            All Mayker Delivery Supervisors have access to our team phone line, which is actively monitored during every event. If you have a time-sensitive need during your project, please feel free to call or text—we'll respond as quickly as possible.
          </p>
          <div style={{
            width: '60px',
            height: '1px',
            background: '#e8e8e3',
            marginBottom: '40px'
          }} />
          <div className="contact-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 320px))',
            gap: '32px',
            justifyContent: 'flex-start'
          }}>
            {teamMembers.filter(member => 
              member.name === 'Becca Farris'
            ).map((member, index) => (
              <ContactCard key={index} member={member} brandCharcoal={brandCharcoal} />
            ))}
            <ContactCard 
              member={{
                name: 'Delivery Supervisors',
                title: 'Delivery Supervisor',
                email: '',
                photo: '',
                firstName: 'DS',
                initials: 'DS',
                phone: '(629) 213-4475',
                hideBackgroundInitials: true
              }} 
              brandCharcoal={brandCharcoal} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PRODUCT R&D SECTION
// ============================================

export function ProductRDSection({ brandCharcoal = '#2C2C2C' }) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Warm, natural color palette
  const bone = '#FAF8F3';
  const warmGray = '#E8E3DD';
  const mutedSand = '#D4CEC0';
  const charcoal = '#2C2C2C';
  const softGray = '#8B8B8B';
  
  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0',
      backgroundColor: 'white'
    }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .product-rd-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .product-rd-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .product-rd-banner {
            height: 400px !important;
            margin-bottom: 24px !important;
          }
          .product-rd-banner-title {
            font-size: 36px !important;
          }
          .product-rd-banner-subtitle {
            font-size: 10px !important;
            padding: 0 16px !important;
          }
          .product-rd-body-copy {
            margin-top: 24px !important;
          }
          /* Remove border and reduce spacing between notes and submit button */
          .product-rd-submit-section {
            border-top: none !important;
            margin-top: 24px !important;
            padding-top: 0 !important;
          }
          /* Match placeholder font treatment to "Upload sketches" text */
          .product-rd-notes-textarea::placeholder {
            font-size: 14px !important;
            font-family: 'NeueHaasUnica', sans-serif !important;
            color: #2C2C2C !important;
            font-weight: 400 !important;
            opacity: 1 !important;
          }
        }
        @media (min-width: 769px) {
          /* Desktop: Remove border above submit button */
          .product-rd-submit-section {
            border-top: none !important;
          }
          /* Desktop: Reduce whitespace below submit button and footer by half */
          .product-rd-content-container {
            padding-bottom: 60px !important;
          }
        }
        @media (max-width: 480px) {
          .product-rd-banner {
            height: 350px !important;
          }
          .product-rd-banner-title {
            font-size: 28px !important;
          }
        }
        
        /* Hand-drawn pencil line */
        .pencil-line {
          stroke: ${mutedSand};
          stroke-width: 1;
          fill: none;
          stroke-linecap: round;
          opacity: 0.4;
        }
        
        /* Sketchbook texture */
        .sketchbook-bg {
          background-image: 
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 1px,
              rgba(212, 206, 192, 0.03) 1px,
              rgba(212, 206, 192, 0.03) 2px
            );
        }
      ` }} />
      
      {/* Image Banner */}
      <div className="product-rd-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/product-banner.jpg" 
          alt="Product R+D" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {/* Dark overlay for richer look */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div className="product-rd-banner-title" style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Product R+D
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div className="product-rd-banner-subtitle" style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            Let's Create Together
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        paddingLeft: '48px',
        paddingRight: '48px',
        paddingBottom: '120px'
      }} className="product-rd-content-container">
        
        {/* Centered Body Copy Section */}
        <section className="product-rd-body-copy" style={{
          marginTop: '64px',
          marginBottom: '60px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            maxWidth: '900px',
            textAlign: 'center'
          }}>
            {/* Section 1: The Invitation */}
            <div style={{
              marginBottom: '0'
            }}>
              <p style={{
                fontSize: '14px',
                fontWeight: '400',
                color: '#6B6B6B',
                fontFamily: "'NeueHaasUnica', sans-serif",
                lineHeight: '1.4',
                marginBottom: '0'
              }}>
                The best additions to our collection start with you. That sculptural moment you can't source. The proportions that only exist in your mind. The texture, the scale, the silhouette that would make everything work. We're not here to replicate what's already available. We're here to build pieces that are as original and versatile as your work demands. Tell us what you're hunting for—we'll bring it to life.
              </p>
            </div>

          </div>
        </section>

        {/* Upload Section */}
        <section style={{
          maxWidth: '900px',
          margin: '0 auto',
          paddingTop: '0'
        }}>
          {/* Upload Area */}
          <div style={{
            marginTop: '0',
            padding: '64px 48px',
            border: `2px dashed #e5e7eb`,
            borderRadius: '4px',
            backgroundColor: 'white',
            textAlign: 'center',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.backgroundColor = 'white';
          }}
          onClick={() => document.getElementById('file-upload').click()}
          >
            <input
              type="file"
              id="file-upload"
              multiple
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files);
                setUploadedFiles(prev => [...prev, ...files]);
              }}
            />
            
            {/* Upload icon */}
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{
              margin: '0 auto 24px',
              opacity: 0.5
            }}>
              <path d="M12 5v14M5 12h14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            
            <div style={{
              fontSize: '14px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: charcoal,
              fontWeight: '400',
              marginBottom: '8px'
            }}>
              Upload sketches, photos, or documents
            </div>
            <div style={{
              fontSize: '14px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: softGray,
              fontWeight: '300'
            }}>
              Drop files here or click to browse
            </div>
            
            {uploadedFiles.length > 0 && (
              <div style={{
                marginTop: '32px',
                paddingTop: '32px',
                borderTop: `1px solid #e5e7eb`,
                textAlign: 'left'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: softGray,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '16px'
                }}>
                  Uploaded ({uploadedFiles.length})
                </div>
                {uploadedFiles.map((file, idx) => (
                  <div key={idx} style={{
                    fontSize: '14px',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    color: charcoal,
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>{file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFiles(prev => prev.filter((_, i) => i !== idx));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: softGray,
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '0 8px'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes Field */}
          <div style={{
            marginTop: '64px'
          }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: softGray,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '16px',
              fontWeight: '400'
            }}>
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tell us what you're imagining…"
              style={{
                width: '100%',
                minHeight: '280px',
                padding: '32px',
                backgroundColor: 'white',
                border: `1px solid #e5e7eb`,
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: "'NeueHaasUnica', sans-serif",
                color: charcoal,
                lineHeight: '1.7',
                fontWeight: '400',
                letterSpacing: '0.01em',
                resize: 'vertical',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              className="product-rd-notes-textarea"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = 'white';
              }}
            />
          </div>
        </section>

        {/* Submit Button */}
        <div className="product-rd-submit-section" style={{
          marginTop: '80px',
          paddingTop: '48px',
          borderTop: `1px solid ${warmGray}`,
          textAlign: 'center'
        }}>
          <button
            onClick={() => {
              // Handle submission
              console.log('Submitting:', { notes, files: uploadedFiles });
            }}
            style={primaryButtonStyle}
            onMouseEnter={primaryButtonHover}
            onMouseLeave={primaryButtonLeave}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export function ResourcesSection({ brandCharcoal = '#2C2C2C', setActiveSection }) {
  const [rentalAddress, setRentalAddress] = useState('');
  const [rentalDistance, setRentalDistance] = useState(null);
  const [rentalMinimum, setRentalMinimum] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  const [activeResourceSection, setActiveResourceSection] = useState(null);
  const [wishlistText, setWishlistText] = useState('');
  const [isSubmittingWishlist, setIsSubmittingWishlist] = useState(false);
  const [wishlistSubmitted, setWishlistSubmitted] = useState(false);
  const maykerOlive = '#545142';
  const mediumGrey = '#6B6B6B';
  
  const scrollToResourceSection = (sectionId) => {
    setActiveResourceSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${sectionId}`);
    }
  };
  
  // Initialize from URL hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'rental-calculator' || hash === 'product-library' || hash === 'workflow-wishlist') {
      setActiveResourceSection(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Initialize Google Places Autocomplete
  useEffect(() => {
    // IMPORTANT SECURITY NOTE: This API key is visible in client-side code.
    // To secure it, you MUST restrict it in Google Cloud Console:
    // 1. Go to: https://console.cloud.google.com/google/maps-apis/credentials
    // 2. Click on your API key
    // 3. Under "Application restrictions" → Select "HTTP referrers (web sites)"
    // 4. Add your domain: https://yourdomain.com/* and https://*.yourdomain.com/*
    // 5. Under "API restrictions" → Select "Restrict key" → Enable only "Places API"
    // 6. Save changes
    // This prevents unauthorized usage even if the key is visible in the code.
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    
    const initializeAutocomplete = () => {
      // Wait for input to be available in the DOM
      const checkInput = () => {
        const input = document.getElementById('rental-address-input');
        if (input && window.google && window.google.maps && window.google.maps.places) {
          try {
            // Check if autocomplete is already attached to avoid duplicates
            if (input.dataset.autocompleteInitialized === 'true') {
              return;
            }
            
            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              types: ['address'],
              componentRestrictions: { country: 'us' }
            });
            
            // Mark as initialized
            input.dataset.autocompleteInitialized = 'true';
            
            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                setRentalAddress(place.formatted_address);
              }
            });
          } catch (error) {
            console.warn('Failed to initialize Google Places Autocomplete:', error);
          }
        } else if (input && !window.google) {
          // Input exists but Google Maps not loaded yet, try again
          setTimeout(checkInput, 100);
        }
      };
      
      // Try immediately, then with a small delay to ensure DOM is ready
      checkInput();
      setTimeout(checkInput, 100);
    };
    
    if (GOOGLE_MAPS_API_KEY) {
      // Load Google Places API script if not already loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.addEventListener('load', initializeAutocomplete);
          return;
        }
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        script.onerror = () => {
          console.warn('Google Places API failed to load. Address autocomplete disabled.');
        };
        document.head.appendChild(script);
      } else {
        // Google Maps already loaded, initialize immediately
        initializeAutocomplete();
      }
    } else {
      console.info('Google Maps API key not configured. Address autocomplete disabled. Users can still manually enter addresses.');
    }
    
    return () => {
      // Cleanup if needed
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Warehouse address
  const WAREHOUSE_ADDRESS = '258 Mason Road, La Vergne, TN 37086';
  
  // Rental minimum tiers based on distance
  const getRentalMinimum = (distance) => {
    if (distance < 50) return { amount: 500, range: 'Nashville (under 50 miles)' };
    if (distance >= 50 && distance < 100) return { amount: 1500, range: '50-100 miles' };
    if (distance >= 100 && distance < 300) return { amount: 3500, range: '100-300 miles' };
    if (distance >= 300 && distance < 600) return { amount: 10000, range: '300-600 miles' };
    if (distance >= 600 && distance < 800) return { amount: 20000, range: '600-800 miles' };
    if (distance >= 800) return { amount: null, range: 'Inquire for custom solutions' };
    return { amount: null, range: 'Inquire for custom solutions' };
  };
  
  // Calculate distance using Google Maps Distance Matrix API
  const calculateDistance = async () => {
    if (!rentalAddress.trim()) {
      setCalculationError('Please enter an address');
      return;
    }
    
    setIsCalculating(true);
    setCalculationError(null);
    setRentalDistance(null);
    setRentalMinimum(null);
    
    try {
      // Use Google Maps Distance Matrix API
      // Note: In production, you'll need to add your Google Maps API key
      // For now, we'll use a geocoding approach with Haversine formula
      
      // Geocode both addresses
      const geocodeAddress = async (address) => {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=AIzaSyDummyKey`
        );
        // If API key is not available, use a fallback method
        // For now, we'll use a simpler approach with the Distance Matrix API
        // But we need an API key, so let's use a different approach
        
        // Alternative: Use OpenRouteService or similar free API
        // Or use Haversine with geocoding from a free service
        
        // For now, let's use a client-side solution with a geocoding service
        // We'll use a CORS proxy or direct API call if available
        throw new Error('API key needed');
      };
      
      // Try using Google Maps Distance Matrix API via a proxy or direct call
      // Since we don't have an API key configured, we'll use a fallback method
      // Using Haversine formula with coordinates from geocoding
      
      // For production, you would:
      // 1. Get Google Maps API key
      // 2. Use Distance Matrix API for accurate driving distance
      // 3. Or use Haversine for straight-line distance as approximation
      
      // For now, let's create a working solution using a free geocoding service
      // We'll use OpenStreetMap Nominatim API (free, no key required)
      
      const geocodeWithNominatim = async (address) => {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
          {
            headers: {
              'User-Agent': 'MaykerEvents/1.0'
            }
          }
        );
        const data = await response.json();
        if (data && data.length > 0) {
          return {
            lat: parseFloat(data[0].lat),
            lon: parseFloat(data[0].lon)
          };
        }
        throw new Error('Address not found');
      };
      
      // Get coordinates for both addresses
      const [warehouseCoords, destinationCoords] = await Promise.all([
        geocodeWithNominatim(WAREHOUSE_ADDRESS),
        geocodeWithNominatim(rentalAddress)
      ]);
      
      // Calculate distance using Haversine formula (great circle distance)
      const haversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 3959; // Earth's radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      };
      
      const distance = haversineDistance(
        warehouseCoords.lat,
        warehouseCoords.lon,
        destinationCoords.lat,
        destinationCoords.lon
      );
      
      setRentalDistance(Math.round(distance));
      const minimum = getRentalMinimum(distance);
      setRentalMinimum(minimum);
      
    } catch (error) {
      console.error('Error calculating distance:', error);
      setCalculationError('Unable to calculate distance. Please check the address and try again.');
    } finally {
      setIsCalculating(false);
    }
  };
  

  return (
    <div style={{ marginBottom: '0', paddingBottom: '0' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .resources-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          margin-top: -48px !important;
          margin-bottom: 64px !important;
          height: 500px !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .resources-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .resources-banner {
            height: 400px !important;
            margin-bottom: 12px !important;
          }
          .resources-banner-title {
            font-size: 36px !important;
          }
          .resources-banner-subtitle {
            font-size: 10px !important;
            padding: 0 16px !important;
          }
          .resources-sub-nav {
            flex-direction: column !important;
            align-items: center !important;
            gap: 3px !important;
          }
          .resources-sub-nav a {
            display: block !important;
            width: auto !important;
            padding-bottom: 0 !important;
            border-bottom-width: 2px !important;
            line-height: 1.2 !important;
          }
          .resources-nav-container {
            border-bottom: none !important;
            margin-bottom: 12px !important;
            padding-top: 16px !important;
            padding-bottom: 16px !important;
          }
          .product-library-gateway {
            border-top: none !important;
            margin-top: 0 !important;
          }
          #product-library {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .product-library-gateway h3,
          .rental-calculator-container h3 {
            margin-bottom: 16px !important;
          }
          .rental-calculator-container {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            height: auto !important;
            background-color: #FAF8F3 !important;
          }
          .rental-calculator-container > div:first-child {
            order: 2 !important;
          }
          .rental-calculator-container > div:last-child {
            order: 1 !important;
          }
          .rental-calculator-container input[type="text"]::placeholder {
            font-size: 12px !important;
          }
          .workflow-wishlist-container {
            margin-top: 0 !important;
          }
          #workflow-wishlist {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .workflow-wishlist-container > div:first-child {
            order: 1 !important;
          }
          .workflow-wishlist-container > div:last-child {
            order: 2 !important;
          }
          .main-content {
            padding-bottom: 120px !important;
          }
        }
        @media (max-width: 480px) {
          .resources-banner {
            height: 350px !important;
          }
          .resources-banner-title {
            font-size: 28px !important;
          }
        }
      ` }} />
      {/* Image Banner */}
      <div className="resources-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/resources-banner.jpg" 
          alt="Resources" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            // Fallback if image doesn't exist yet
            e.target.style.display = 'none';
          }}
        />
        {/* Dark overlay for richer look */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div className="resources-banner-title" style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Resources
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div className="resources-banner-subtitle" style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            A COLLECTION OF MODERN TOOLS TO SIMPLIFY YOUR PROCESS AND ELEVATE YOUR CRAFT.
          </div>
        </div>
      </div>
      
      {/* Sub Navigation */}
      <nav className="resources-nav-container" style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '16px 0 24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        marginBottom: '24px',
        marginTop: '-40px'
      }}>
        <div className="resources-sub-nav" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          flexWrap: 'wrap'
        }}>
          <a
            href="#product-library"
            onClick={(e) => {
              e.preventDefault();
              if (setActiveSection) {
                setActiveSection('product-visual-library');
              } else {
                scrollToResourceSection('product-library');
              }
            }}
            style={{
              fontSize: '12px',
              fontWeight: '400',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: activeResourceSection === 'product-library' ? maykerOlive : mediumGrey,
              textDecoration: 'none',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              paddingBottom: '2px',
              borderBottom: activeResourceSection === 'product-library' ? `2px solid ${maykerOlive}` : '2px solid transparent',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (activeResourceSection !== 'product-library') {
                e.currentTarget.style.color = maykerOlive;
                e.currentTarget.style.borderBottomColor = maykerOlive;
              }
            }}
            onMouseLeave={(e) => {
              if (activeResourceSection !== 'product-library') {
                e.currentTarget.style.color = mediumGrey;
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            Product Library
          </a>
          <a
            href="#rental-calculator"
            onClick={(e) => {
              e.preventDefault();
              scrollToResourceSection('rental-calculator');
            }}
            style={{
              fontSize: '12px',
              fontWeight: '400',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: activeResourceSection === 'rental-calculator' ? maykerOlive : mediumGrey,
              textDecoration: 'none',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              paddingBottom: '2px',
              borderBottom: activeResourceSection === 'rental-calculator' ? `2px solid ${maykerOlive}` : '2px solid transparent',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (activeResourceSection !== 'rental-calculator') {
                e.currentTarget.style.color = maykerOlive;
                e.currentTarget.style.borderBottomColor = maykerOlive;
              }
            }}
            onMouseLeave={(e) => {
              if (activeResourceSection !== 'rental-calculator') {
                e.currentTarget.style.color = mediumGrey;
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            Rental Min. Calculator
          </a>
          <a
            href="#workflow-wishlist"
            onClick={(e) => {
              e.preventDefault();
              scrollToResourceSection('workflow-wishlist');
            }}
            style={{
              fontSize: '12px',
              fontWeight: '400',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: activeResourceSection === 'workflow-wishlist' ? maykerOlive : mediumGrey,
              textDecoration: 'none',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              paddingBottom: '2px',
              borderBottom: activeResourceSection === 'workflow-wishlist' ? `2px solid ${maykerOlive}` : '2px solid transparent',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (activeResourceSection !== 'workflow-wishlist') {
                e.currentTarget.style.color = maykerOlive;
                e.currentTarget.style.borderBottomColor = maykerOlive;
              }
            }}
            onMouseLeave={(e) => {
              if (activeResourceSection !== 'workflow-wishlist') {
                e.currentTarget.style.color = mediumGrey;
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            Workflow Wishlist
          </a>
        </div>
      </nav>
      
      {/* Product Visual Library Gateway */}
      <section
        id="product-library"
        style={{
          scrollMarginTop: '80px'
        }}
      >
        <div style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#FAF8F3',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0',
          height: '700px'
        }} className="product-library-gateway">
            <style dangerouslySetInnerHTML={{ __html: `
              @media (max-width: 768px) {
                .product-library-gateway {
                  grid-template-columns: 1fr !important;
                  gap: 0 !important;
                }
                .product-library-gateway > div:first-child {
                  order: 1;
                }
                .product-library-gateway > div:last-child {
                  order: 2;
                }
              }
            ` }} />
            {/* Left side - Image */}
            <div style={{
              overflow: 'hidden',
              backgroundColor: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src="/library-resources.jpg" 
                alt="Product Visual Library" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  console.error('library-resources.jpg failed to load:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Right side - Text and CTA */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '80px 64px',
              justifyContent: 'center'
            }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000', 
              marginBottom: '24px',
              fontFamily: "'Domaine Text', serif"
            }}>
              Product Visual Library
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '32px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              lineHeight: '1.7',
              maxWidth: '500px'
            }}>
              Search and download PNG images of all rental products for planning, design presentations, and better execution.
            </p>
            <button
              onClick={() => {
                if (setActiveSection) {
                  setActiveSection('product-visual-library');
                }
              }}
              style={{
                ...textLinkButtonStyle,
                alignSelf: 'flex-start'
              }}
              onMouseEnter={textLinkButtonHover}
              onMouseLeave={textLinkButtonLeave}
            >
              Open Product Visual Library →
            </button>
          </div>
        </div>
      </section>
      
      {/* Rental Minimum Calculator */}
      <section
        id="rental-calculator"
        style={{
          scrollMarginTop: '80px'
        }}
      >
        <div style={{ 
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#ffffff',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0',
          height: '700px'
        }} className="rental-calculator-container">
          {/* Text on left */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '80px 64px',
            justifyContent: 'center'
          }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: '300', 
              color: '#000000', 
              marginBottom: '24px',
              fontFamily: "'Domaine Text', serif"
            }}>
              Rental Minimum Calculator
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '32px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              lineHeight: '1.7',
              maxWidth: '500px'
            }}>
              Enter your event address to see the rental minimum for your area. If you have concerns about meeting the minimum, connect with your Client Coordinator and share any relevant details—our team is here to help find the best solution.
            </p>
            
            <div style={{ 
              marginBottom: '16px'
            }}>
              <input
                id="rental-address-input"
                type="text"
                placeholder="Enter event address (e.g., 123 Main St, Nashville, TN 37203)"
                value={rentalAddress}
                onChange={(e) => setRentalAddress(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    calculateDistance();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  color: '#000000',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#545142';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                }}
              />
            </div>
            
            <div style={{ 
              marginBottom: '24px'
            }}>
              <button
                onClick={calculateDistance}
                disabled={isCalculating}
                style={{
                  ...textLinkButtonStyle,
                  cursor: isCalculating ? 'not-allowed' : 'pointer',
                  opacity: isCalculating ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isCalculating) {
                    textLinkButtonHover(e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCalculating) {
                    textLinkButtonLeave(e);
                  }
                }}
              >
                {isCalculating ? 'Calculating...' : 'Calculate →'}
              </button>
            </div>
            
            {calculationError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '14px',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}>
                {calculationError}
              </div>
            )}
            
            {rentalMinimum && (
              <div style={{
                padding: '24px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e8e8e3',
                marginTop: '24px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#8b8b8b',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '12px'
                }}>
                  Rental Minimum
                </div>
                {rentalMinimum.amount ? (
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '300',
                    color: brandCharcoal,
                    fontFamily: "'Domaine Text', serif"
                  }}>
                    ${rentalMinimum.amount.toLocaleString()}
                  </div>
                ) : (
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '400',
                    color: '#000000',
                    fontFamily: "'NeueHaasUnica', sans-serif"
                  }}>
                    {rentalMinimum.range}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Image on right */}
          <div style={{
            overflow: 'hidden',
            backgroundColor: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img 
              src="/mayker-truck.jpg" 
              alt="Mayker Truck" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                console.error('mayker-truck.jpg failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>
      </section>
      
      {/* Workflow Wishlist Prompt */}
      <section
        id="workflow-wishlist"
        style={{
          scrollMarginTop: '80px'
        }}
      >
        <div style={{ 
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          backgroundColor: '#FAF8F3',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0',
          height: '700px'
        }} className="workflow-wishlist-container">
          <style dangerouslySetInnerHTML={{ __html: `
            @media (max-width: 768px) {
              .workflow-wishlist-container {
                grid-template-columns: 1fr !important;
                gap: 0 !important;
                height: auto !important;
              }
            }
          ` }} />
          {/* Left side - Image */}
          <div style={{
            overflow: 'hidden',
            backgroundColor: '#FAF8F3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img 
              src="/wishlist.jpg" 
              alt="Workflow Wishlist" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                console.error('wishlist.jpg failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          {/* Right side - Text */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '64px',
            paddingBottom: '64px',
            paddingLeft: '64px',
            paddingRight: '64px',
            justifyContent: 'center'
          }}>
          <h3 style={{
            fontSize: '24px',
            fontWeight: '300',
            color: '#000000',
            marginBottom: '24px',
            fontFamily: "'Domaine Text', serif"
          }}>
            Workflow Wishlist
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#666',
            fontFamily: "'NeueHaasUnica', sans-serif",
            lineHeight: '1.7',
            marginBottom: '32px',
            maxWidth: '500px'
          }}>
            Have a resource you'd love to have at your fingertips? Tell us what would elevate your workflow, and our team will explore adding it to the collection.
          </p>
          
          {wishlistSubmitted ? (
            <div style={{
              padding: '24px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #e8e8e3'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#000000',
                fontFamily: "'NeueHaasUnica', sans-serif",
                lineHeight: '1.6'
              }}>
                Thank you for your suggestion! We've received your workflow wishlist request and will review it.
              </div>
            </div>
          ) : (
            <div style={{
              textAlign: 'left'
            }}>
              <textarea
                value={wishlistText}
                onChange={(e) => setWishlistText(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  color: '#000000',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#545142';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                }}
              />
            <div style={{
              marginTop: '16px',
              textAlign: 'left'
            }}>
              <button
                onClick={async () => {
                  if (!wishlistText.trim()) {
                    return;
                  }
                  
                  setIsSubmittingWishlist(true);
                  
                  try {
                    // Send wishlist submission to API
                    // Get token from localStorage
                    const token = localStorage.getItem('clientToken');
                    console.log('[Wishlist Form] Token from localStorage:', token ? 'Found (length: ' + token.length + ')' : 'NOT FOUND');
                    
                    if (!token) {
                      alert('Session expired. Please log in again.');
                      return;
                    }
                    
                    const requestBody = {
                      action: 'submitWishlist',
                      token: token,
                      text: wishlistText.trim()
                    };
                    console.log('[Wishlist Form] Request body:', requestBody);
                    
                    const response = await fetch(CLIENT_API_URL, {
                      method: 'POST',
                      headers: { 'Content-Type': 'text/plain' },
                      body: JSON.stringify(requestBody)
                    });
                    
                    console.log('[Wishlist Form] Response status:', response.status);
                    const result = await response.json();
                    console.log('[Wishlist Form] Response result:', result);
                    
                    if (result.success) {
                      setWishlistSubmitted(true);
                      setWishlistText('');
                    } else {
                      console.error('[Wishlist Form] Error from server:', result.error);
                      alert('Error submitting wishlist. Please try again. Error: ' + (result.error || 'Unknown error'));
                    }
                  } catch (error) {
                    console.error('[Wishlist Form] Error submitting wishlist:', error);
                    alert('Error submitting wishlist. Please try again. Error: ' + (error.message || error));
                    // Still show success message even if API call fails
                    // (in case API endpoint doesn't exist yet)
                    setWishlistSubmitted(true);
                    setWishlistText('');
                  } finally {
                    setIsSubmittingWishlist(false);
                  }
                }}
                disabled={!wishlistText.trim() || isSubmittingWishlist}
                style={{
                  ...textLinkButtonStyle,
                  cursor: wishlistText.trim() && !isSubmittingWishlist ? 'pointer' : 'not-allowed',
                  opacity: wishlistText.trim() && !isSubmittingWishlist ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (wishlistText.trim() && !isSubmittingWishlist) {
                    textLinkButtonHover(e);
                  }
                }}
                onMouseLeave={(e) => {
                  if (wishlistText.trim() && !isSubmittingWishlist) {
                    textLinkButtonLeave(e);
                  }
                }}
              >
                {isSubmittingWishlist ? 'Submitting...' : 'Submit →'}
              </button>
            </div>
          </div>
        )}
          </div>
        </div>
      </section>
    </div>
  );
}

export function StartNewProjectSection({ brandCharcoal = '#2C2C2C' }) {
  const [formData, setFormData] = useState({
    venueName: '',
    venueAddress: '',
    loadInDate: '',
    loadInTime: '',
    loadOutDate: '',
    loadOutTime: '',
    notes: '',
    resourceLinks: '',
    scheduleCall: false
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductQuantity, setNewProductQuantity] = useState('1');
  const [catalog, setCatalog] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  // PROPOSALS_API_URL is already defined at the top of the file using environment variables
  
  // Fetch catalog on mount (portal audience: hide Show on Portal = No)
  useEffect(() => {
    fetch(PROPOSALS_API_URL + '?action=getCatalog&audience=portal', {
      method: 'GET',
      mode: 'cors'
    })
      .then(res => res.json())
      .then(data => {
        if (data && data.catalog) {
          setCatalog(
            data.catalog.filter((p) => p && p.showOnPortal !== false)
          );
        }
      })
      .catch(err => console.error('Error fetching catalog:', err));
  }, []);
  
  // Handle product name input with autocomplete
  const handleProductNameChange = (value) => {
    setNewProductName(value);
    if (value.trim().length > 0) {
      const filtered = catalog
        .filter(product => 
          product.name.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 10);
      setProductSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      
      // If there's an exact match, set it as selected product for thumbnail
      const exactMatch = catalog.find(product => 
        product.name.toLowerCase() === value.toLowerCase().trim()
      );
      if (exactMatch) {
        setSelectedProduct(exactMatch);
      } else {
        setSelectedProduct(null);
      }
    } else {
      setProductSuggestions([]);
      setShowSuggestions(false);
      setSelectedProduct(null);
    }
  };
  
  const handleSelectProduct = (product) => {
    setNewProductName(product.name);
    setSelectedProduct(product);
    setShowSuggestions(false);
  };
  
  // Initialize Google Places Autocomplete for venue address
  useEffect(() => {
    const initializeAutocomplete = () => {
      const checkInput = () => {
        const input = document.getElementById('venue-address-input');
        if (input && window.google && window.google.maps && window.google.maps.places) {
          try {
            if (input.dataset.autocompleteInitialized === 'true') {
              return;
            }
            
            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              types: ['address'],
              componentRestrictions: { country: 'us' }
            });
            
            input.dataset.autocompleteInitialized = 'true';
            
            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                setFormData(prev => ({ ...prev, venueAddress: place.formatted_address }));
              }
            });
          } catch (error) {
            console.warn('Failed to initialize Google Places Autocomplete:', error);
          }
        } else if (input && !window.google) {
          setTimeout(checkInput, 100);
        }
      };
      
      checkInput();
      setTimeout(checkInput, 100);
    };
    
    if (GOOGLE_MAPS_API_KEY) {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.addEventListener('load', initializeAutocomplete);
          return;
        }
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        script.onerror = () => {
          console.warn('Google Places API failed to load. Address autocomplete disabled.');
        };
        document.head.appendChild(script);
      } else {
        initializeAutocomplete();
      }
    }
    
    return () => {
      // Cleanup if needed
    };
  }, []);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleAddProduct = () => {
    if (newProductName.trim()) {
      setProducts(prev => [...prev, {
        id: Date.now(),
        name: newProductName.trim(),
        quantity: parseInt(newProductQuantity) || 1,
        imageUrl: selectedProduct?.imageUrl || null
      }]);
      setNewProductName('');
      setNewProductQuantity('1');
      setSelectedProduct(null);
      setProductSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  const handleRemoveProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  };
  
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png'].includes(extension);
    });
    
    if (validFiles.length !== files.length) {
      alert('Only PDF, JPG, and PNG files are allowed.');
    }
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => [...prev, {
          id: Date.now() + Math.random(),
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result // Base64 encoded file
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    e.target.value = '';
  };
  
  const handleRemoveFile = (id) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };
  
  const handleUpdateProductQuantity = (id, quantity) => {
    setProducts(prev => prev.map(p => 
      p.id === id ? { ...p, quantity: parseInt(quantity) || 1 } : p
    ));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.venueName.trim()) {
      alert('Please enter a venue name');
      return;
    }
    
    if (!formData.venueAddress.trim()) {
      alert('Please enter a venue address');
      return;
    }
    
    if (!formData.loadInDate || !formData.loadOutDate) {
      alert('Please enter both load-in and load-out dates');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Get client company name from session
      const session = authService.getSession();
      const clientCompanyName = session?.clientInfo?.clientCompanyName || 
                                session?.clientInfo?.companyName || 
                                session?.clientInfo?.client_name || '';
      
      const contactName =
        session?.clientInfo?.fullName ||
        [session?.clientInfo?.firstName, session?.clientInfo?.lastName].filter(Boolean).join(' ').trim() ||
        '';
      
      const payload = {
        type: 'submitNewProject',
        clientCompanyName: clientCompanyName,
        contactName: contactName,
        venueName: formData.venueName,
        venueAddress: formData.venueAddress,
        loadInDate: formData.loadInDate,
        loadInTime: formData.loadInTime,
        loadOutDate: formData.loadOutDate,
        loadOutTime: formData.loadOutTime,
        products: products,
        notes: formData.notes,
        resourceLinks: formData.resourceLinks || '',
        uploadedFiles: uploadedFiles || [],
        scheduleCall: formData.scheduleCall || false
      };
      
      console.log('Submitting new project request:', payload);
      console.log('API URL:', PROPOSALS_API_URL);
      
      const response = await fetch(PROPOSALS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        mode: 'cors'
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response not OK. Status:', response.status, 'Text:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Response result:', result);
      
      if (result.success) {
        setSubmitted(true);
        // Reset form
        setFormData({
          venueName: '',
          venueAddress: '',
          loadInDate: '',
          loadInTime: '',
          loadOutDate: '',
          loadOutTime: '',
          notes: '',
          resourceLinks: '',
          scheduleCall: false
        });
        setProducts([]);
        setUploadedFiles([]);
      } else {
        console.error('Submission failed:', result);
        alert('Error submitting project inquiry: ' + (result.error || 'Please try again.'));
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting project inquiry:', error);
      alert('Error submitting project inquiry: ' + (error.message || 'Please try again.'));
      setSubmitting(false);
    }
  };
  
  // Premium input styling - warm, editorial feel
  const inputStyle = {
    width: '100%',
    padding: '12px 0',
    fontSize: '13px',
    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
    border: 'none',
    borderBottom: '1px solid #e8e8e3',
    borderRadius: '0',
    backgroundColor: 'transparent',
    color: '#000000',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  };
  
  const inputStyleFull = {
    ...inputStyle,
    border: '1px solid #e8e8e3',
    borderRadius: '6px',
    padding: '12px 16px',
    backgroundColor: '#fff',
    fontSize: '13px'
  };
  
  const inputStyleFullHover = {
    borderColor: '#d1d5db',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
  };
  
  const inputStyleFullFocus = {
    borderColor: '#a8a8a0',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)'
  };
  
  // Section heading style - match account page mobile headers
  const sectionHeadingStyle = {
    fontSize: '24px',
    fontWeight: '300',
    letterSpacing: 'normal',
    textTransform: 'uppercase',
    color: '#000000',
    fontFamily: "'Domaine Text', serif",
    marginBottom: '12px',
    marginTop: '0'
  };
  
  // Field label style
  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#000000',
    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
  };
  
  return (
    <div className="start-new-project-container" style={{ backgroundColor: 'white', minHeight: '100vh', padding: '0', paddingBottom: '120px' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @media (max-width: 768px) {
          .timing-fields-container {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .time-input-wrapper {
            width: 100% !important;
          }
          .product-name-input {
            width: 100% !important;
            max-width: 100% !important;
            margin-right: 0 !important;
          }
          /* Ensure banner title uses Domaine Text serif font and adjust size for mobile */
          .start-project-banner > div > div:first-child {
            font-family: 'Domaine Text', serif !important;
            font-size: 36px !important;
          }
          /* Ensure consistent font family on mobile for subtitle */
          .start-project-banner > div > div:last-child {
            font-family: 'Neue Haas Unica', 'Inter', sans-serif !important;
          }
          /* Make Add Product button text always black on mobile */
          .add-product-button {
            color: #000000 !important;
          }
          /* Reduce white space below submit button on mobile */
          .start-new-project-container {
            padding-bottom: 80px !important;
          }
        }
        @media (max-width: 480px) {
          .start-project-banner > div > div:first-child {
            font-size: 28px !important;
          }
          /* Section headers match account page mobile style */
          h3[style*="sectionHeadingStyle"] {
            font-size: 24px !important;
            font-weight: 300 !important;
            font-family: 'Domaine Text', serif !important;
            text-transform: uppercase !important;
            letter-spacing: normal !important;
            color: #000000 !important;
            margin-bottom: 24px !important;
          }
          /* Input font size reduced by 1px for auto-filled text */
          input[type="text"],
          input[type="date"],
          input[type="time"],
          input[type="number"],
          textarea {
            font-size: 13px !important;
          }
        }
      ` }} />
      <style dangerouslySetInnerHTML={{ __html: `
        .start-project-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          left: 0 !important;
          right: 0 !important;
        }
        @media (min-width: 769px) {
          .start-project-banner {
            margin-top: -100px !important;
          }
        }
        @media (max-width: 768px) {
          .start-project-banner {
            height: 400px !important;
          }
        }
        @media (max-width: 480px) {
          .start-project-banner {
            height: 350px !important;
          }
        }
      ` }} />
      {/* Hero Image Banner */}
      <div className="start-project-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
        <img 
          src="/start-a-new-project-banner.jpg" 
          alt="Start a New Project"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
            opacity: '0.9'
          }}
          onError={(e) => {
            if (!e.target.src.includes('/assets/')) {
              e.target.src = '/assets/start-a-new-project-banner.jpg';
            } else {
              e.target.style.display = 'none';
            }
          }}
        />
        {/* Dark overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        {/* Text Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Start a New Project
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            Submit requests for any upcoming projects
          </div>
        </div>
      </div>
      
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px 48px' }}>
      
      {submitted ? (
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '40px 32px',
          backgroundColor: '#F8F7F4',
          border: '1px solid #e8e8e3',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: '#2C2C2C',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            marginBottom: '16px',
            letterSpacing: '0.02em',
            marginTop: '0'
          }}>
            Thank you for your submission.
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#333',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            lineHeight: '1.7',
            marginBottom: '32px',
            marginTop: '0',
            fontWeight: '400'
          }}>
            Our team has received your project request and will review the details shortly. We'll be in touch with next steps.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            style={{
              ...primaryButtonStyle,
              marginTop: '0'
            }}
            onMouseEnter={primaryButtonHover}
            onMouseLeave={primaryButtonLeave}
            onFocus={(e) => {
              e.currentTarget.style.outline = '2px solid #2C2C2C';
              e.currentTarget.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
          >
            SUBMIT ANOTHER REQUEST
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Intro Copy */}
          <div style={{ marginBottom: '48px', maxWidth: '680px' }}>
            <p style={{
              fontSize: '14px',
              lineHeight: '1.7',
              color: '#333',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              fontWeight: '400',
              margin: '0'
            }}>
              Planning an upcoming project? Share the details you have below. If you're still refining products or quantities, simply outline your vision in the notes or upload any inspiration. The more you share, the more thoughtfully we can support you.
            </p>
          </div>
          
          {/* Section 1: Project Details */}
          <div style={{ marginBottom: '48px' }}>
            <h3 style={sectionHeadingStyle}>Project Details</h3>
            
            <div style={{ marginBottom: '32px' }}>
              <label style={labelStyle} htmlFor="venue-name">Venue Name</label>
              <input
                id="venue-name"
                type="text"
                value={formData.venueName}
                onChange={(e) => handleInputChange('venueName', e.target.value)}
                style={inputStyleFull}
                onFocus={(e) => {
                  Object.assign(e.target.style, inputStyleFullFocus);
                }}
                onBlur={(e) => {
                  Object.assign(e.target.style, inputStyleFull);
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover });
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, inputStyleFull);
                  }
                }}
                required
              />
            </div>
            
            <div>
              <label style={labelStyle} htmlFor="venue-address-input">Venue Address</label>
              <input
                id="venue-address-input"
                type="text"
                value={formData.venueAddress}
                onChange={(e) => handleInputChange('venueAddress', e.target.value)}
                placeholder="Enter venue address"
                style={inputStyleFull}
                onFocus={(e) => {
                  Object.assign(e.target.style, inputStyleFullFocus);
                }}
                onBlur={(e) => {
                  Object.assign(e.target.style, inputStyleFull);
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover });
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, inputStyleFull);
                  }
                }}
                required
              />
            </div>
          </div>
          
          {/* Section 2: Timing */}
          <div style={{ marginBottom: '48px' }}>
            <h3 style={sectionHeadingStyle}>Timing</h3>
            <p style={{
              fontSize: '14px',
              color: '#666',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Please provide your delivery and strike dates with approximate times. We'll use these as a baseline for planning and can adjust as needed.
            </p>
            
            {/* Load-In */}
            <div style={{ marginBottom: '32px' }}>
              <label style={labelStyle}>Load-In</label>
              <div className="timing-fields-container" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
                <div>
                  <input
                    type="date"
                    value={formData.loadInDate}
                    onChange={(e) => handleInputChange('loadInDate', e.target.value)}
                    style={inputStyleFull}
                    onFocus={(e) => {
                      Object.assign(e.target.style, inputStyleFullFocus);
                    }}
                    onBlur={(e) => {
                      Object.assign(e.target.style, inputStyleFull);
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover });
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        Object.assign(e.target.style, inputStyleFull);
                      }
                    }}
                    required
                  />
                </div>
                <div className="time-input-wrapper" style={{ width: '168px' }}>
                  <MaykerTimeInput
                    id="start-project-loadInTime"
                    value={formData.loadInTime}
                    onChange={(value) => handleInputChange('loadInTime', value)}
                    outputFormat="input24"
                    placeholder="Time (optional)"
                    style={inputStyleFull}
                  />
                </div>
              </div>
            </div>
            
            {/* Load-Out */}
            <div>
              <label style={labelStyle}>Load-Out</label>
              <div className="timing-fields-container" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
                <div>
                  <input
                    type="date"
                    value={formData.loadOutDate}
                    onChange={(e) => handleInputChange('loadOutDate', e.target.value)}
                    style={inputStyleFull}
                    onFocus={(e) => {
                      Object.assign(e.target.style, inputStyleFullFocus);
                    }}
                    onBlur={(e) => {
                      Object.assign(e.target.style, inputStyleFull);
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover });
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        Object.assign(e.target.style, inputStyleFull);
                      }
                    }}
                    required
                  />
                </div>
                <div className="time-input-wrapper" style={{ width: '168px' }}>
                  <MaykerTimeInput
                    id="start-project-loadOutTime"
                    value={formData.loadOutTime}
                    onChange={(value) => handleInputChange('loadOutTime', value)}
                    outputFormat="input24"
                    placeholder="Time (optional)"
                    style={inputStyleFull}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Section 3: Products */}
          <div style={{ marginBottom: '48px' }}>
            <h3 style={sectionHeadingStyle}>Products</h3>
            <p style={{
              fontSize: '14px',
              color: '#666',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              marginTop: '0',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Choose your products and quantities, then click "+ Add Product" to add each item to your proposal.
            </p>
            
            {/* Product List */}
            {products.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                {products.map((product) => (
                  <div key={product.id} style={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#fafaf8',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e3'
                  }}>
                    {product.imageUrl && (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: '1px solid #e8e8e3',
                          flexShrink: 0
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => {
                          setProducts(prev => prev.map(p => 
                            p.id === product.id ? { ...p, name: e.target.value } : p
                          ));
                        }}
                        style={{
                          ...inputStyleFull,
                          marginBottom: '12px',
                          padding: '8px 12px'
                        }}
                        placeholder="Product name"
                        onFocus={(e) => {
                          Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullFocus, padding: '8px 12px' });
                        }}
                        onBlur={(e) => {
                          Object.assign(e.target.style, { ...inputStyleFull, padding: '8px 12px' });
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Quantity:</span>
                        <input
                          type="number"
                          min="1"
                          value={product.quantity}
                          onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value)}
                          style={{
                            ...inputStyleFull,
                            width: '80px',
                            padding: '8px 12px'
                          }}
                          onFocus={(e) => {
                            Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullFocus, width: '80px', padding: '8px 12px' });
                          }}
                          onBlur={(e) => {
                            Object.assign(e.target.style, { ...inputStyleFull, width: '80px', padding: '8px 12px' });
                          }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveProduct(product.id)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#666',
                        backgroundColor: 'transparent',
                        border: '1px solid #e8e8e3',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f2';
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#000';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#e8e8e3';
                        e.currentTarget.style.color = '#666';
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Product */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-end',
              position: 'relative',
              flexWrap: 'wrap'
            }}>
              <div className="product-name-input" style={{ flex: 1, position: 'relative', minWidth: '200px' }}>
                <input
                  type="text"
                  value={newProductName}
                  onChange={(e) => handleProductNameChange(e.target.value)}
                  onFocus={() => {
                    if (productSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Start typing product name..."
                  style={inputStyleFull}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (productSuggestions.length > 0) {
                        handleSelectProduct(productSuggestions[0]);
                        handleAddProduct();
                      } else {
                        handleAddProduct();
                      }
                    }
                  }}
                />
                {/* Autocomplete Suggestions */}
                {showSuggestions && productSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e8e8e3',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {productSuggestions.map((product, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          handleSelectProduct(product);
                        }}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: idx < productSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '14px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          color: '#000000',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fafaf8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        {product.imageUrl && (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e8e8e3',
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <span style={{ flex: 1 }}>{product.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '13px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap' }}>Quantity:</span>
                  <input
                    type="number"
                    min="1"
                    value={newProductQuantity}
                    onChange={(e) => setNewProductQuantity(e.target.value)}
                    style={{
                      ...inputStyleFull,
                      width: '80px'
                    }}
                  />
              </div>
              <button
                type="button"
                className="add-product-button"
                onClick={handleAddProduct}
                disabled={!newProductName.trim()}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  backgroundColor: '#FAF8F3',
                  color: '#000000',
                  border: '1px solid #e8e8e3',
                  borderRadius: '20px',
                  cursor: newProductName.trim() ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  if (newProductName.trim()) {
                    e.currentTarget.style.backgroundColor = '#f5f5f2';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (newProductName.trim()) {
                    e.currentTarget.style.backgroundColor = '#FAF8F3';
                    e.currentTarget.style.borderColor = '#e8e8e3';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                + Add Product
              </button>
            </div>
          </div>
          
          {/* Section 4: Resources */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={sectionHeadingStyle}>Resources and Inspiration</h3>
            <p style={{
              fontSize: '14px',
              color: '#666',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              marginTop: '0',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Have design decks, inspiration images, or web links you'd like us to keep in mind? Upload them here or paste links below.
            </p>
            
            {/* File Upload */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
              }}>
                Upload Files (PDF, JPG, PNG)
              </label>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <input
                  type="file"
                  id="file-upload-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileUpload}
                  style={{
                    position: 'absolute',
                    width: '0.1px',
                    height: '0.1px',
                    opacity: 0,
                    overflow: 'hidden',
                    zIndex: -1
                  }}
                />
                <label
                  htmlFor="file-upload-input"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    fontSize: '13px',
                    fontWeight: '500',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    backgroundColor: '#FAF8F3',
                    color: '#000000',
                    border: '1px solid #e8e8e3',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f2';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FAF8F3';
                    e.currentTarget.style.borderColor = '#e8e8e3';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  + Choose Files
                </label>
              </div>
              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  {uploadedFiles.map((file) => (
                    <div key={file.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#fafaf8',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      border: '1px solid #e8e8e3'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          color: '#000000',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {file.name}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#666',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          marginTop: '2px'
                        }}>
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(file.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#666',
                          backgroundColor: 'transparent',
                          border: '1px solid #e8e8e3',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          transition: 'all 0.2s',
                          marginLeft: '12px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f2';
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.color = '#000';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#e8e8e3';
                          e.currentTarget.style.color = '#666';
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Links Field */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '8px',
                color: '#000000',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
              }}>
                Share Links
              </label>
              <textarea
                value={formData.resourceLinks}
                onChange={(e) => handleInputChange('resourceLinks', e.target.value)}
                rows={3}
                style={{
                  ...inputStyleFull,
                  resize: 'vertical',
                  minHeight: '100px'
                }}
                placeholder="Paste links here (one per line or separated by commas)"
                onFocus={(e) => {
                  Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullFocus, minHeight: '100px' });
                }}
                onBlur={(e) => {
                  Object.assign(e.target.style, { ...inputStyleFull, minHeight: '100px' });
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover, minHeight: '100px' });
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.target) {
                    Object.assign(e.target.style, { ...inputStyleFull, minHeight: '100px' });
                  }
                }}
              />
            </div>
          </div>
          
          {/* Section 5: Notes & Call Option */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '8px',
              color: '#000000',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
            }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={5}
              style={{
                ...inputStyleFull,
                resize: 'vertical',
                minHeight: '120px'
              }}
              placeholder="Any additional details, context, or direction you'd like to share?"
              onFocus={(e) => {
                Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullFocus, minHeight: '120px' });
              }}
              onBlur={(e) => {
                Object.assign(e.target.style, { ...inputStyleFull, minHeight: '120px' });
              }}
              onMouseEnter={(e) => {
                if (document.activeElement !== e.target) {
                  Object.assign(e.target.style, { ...inputStyleFull, ...inputStyleFullHover, minHeight: '120px' });
                }
              }}
              onMouseLeave={(e) => {
                if (document.activeElement !== e.target) {
                  Object.assign(e.target.style, { ...inputStyleFull, minHeight: '120px' });
                }
              }}
            />
          </div>
          
          {/* Schedule Call Checkbox */}
          <div style={{ marginBottom: '40px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              color: '#000000',
              padding: '8px 0'
            }}>
              <input
                type="checkbox"
                checked={formData.scheduleCall}
                onChange={(e) => handleInputChange('scheduleCall', e.target.checked)}
                style={{
                  width: '12px',
                  height: '12px',
                  cursor: 'pointer',
                  accentColor: brandCharcoal,
                  flexShrink: 0
                }}
              />
              <span>I'd love to schedule a call to discuss my project.</span>
            </label>
          </div>
          
          {/* Submit Button */}
          <div style={{ marginTop: '24px' }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...primaryButtonStyle,
                padding: '14px 36px',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  primaryButtonHover(e);
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting) {
                  primaryButtonLeave(e);
                }
              }}
            >
              {submitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
            </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
}

// ============================================
// PRODUCT VISUAL LIBRARY SECTION
// ============================================

export function ProductVisualLibrarySection({ brandCharcoal = '#2C2C2C', setActiveSection }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Fetch products from JSON file
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
        // Fallback to empty array if JSON file doesn't exist
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Filter products based on search query
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name)); // Alphabetize

  // Handle scroll to show/hide "go to top" button
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      setShowScrollToTop(scrollY > 400); // Show button after scrolling 400px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleDownload = async (product) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(product.image);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${product.name} - Mayker Events.png`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback to direct link
      const link = document.createElement('a');
      link.href = product.image;
      link.download = `${product.name} - Mayker Events.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      {/* Back Navigation */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={() => {
            if (setActiveSection) {
              setActiveSection('resources');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#666',
            fontSize: '14px',
            fontFamily: "'NeueHaasUnica', sans-serif",
            cursor: 'pointer',
            padding: '8px 0',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#000000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#666';
          }}
        >
          ← Back to Resources
        </button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: '48px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '300',
          color: '#000000',
          marginBottom: '16px',
          fontFamily: "'Domaine Text', serif",
          letterSpacing: '-0.02em'
        }}>
          Product Visual Library
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#666',
          fontFamily: "'NeueHaasUnica', sans-serif",
          lineHeight: '1.6',
          marginBottom: '32px'
        }}>
          Search and download PNG images of all rental products for planning, design presentations, and better execution.
        </p>

        {/* Search Bar */}
        <div style={{ marginBottom: '32px' }}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '500px',
              padding: '12px 16px',
              fontSize: '14px',
              fontFamily: "'NeueHaasUnica', sans-serif",
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#fff',
              color: '#000000',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#545142';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
            }}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '64px',
          color: '#999',
          fontFamily: "'NeueHaasUnica', sans-serif",
          fontSize: '14px'
        }}>
          Loading products...
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredProducts.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '64px',
          color: '#999',
          fontFamily: "'NeueHaasUnica', sans-serif",
          fontSize: '14px'
        }}>
          {searchQuery ? `No products found matching "${searchQuery}". Try a different search.` : 'No products available at this time.'}
        </div>
      )}

      {/* Product Grid */}
      {!isLoading && filteredProducts.length > 0 && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '32px',
            marginBottom: '48px'
          }}>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                {/* Product Image */}
                <div style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '12px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      padding: '12px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement;
                      placeholder.innerHTML = '<div style="color: #999; font-size: 12px; text-align: center; padding: 20px;">Image not found</div>';
                    }}
                  />
                </div>

                {/* Product Name */}
                <div style={{
                  fontSize: '13px',
                  fontWeight: '400',
                  color: '#000000',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  textAlign: 'center',
                  lineHeight: '1.4',
                  marginBottom: '12px'
                }}>
                  {product.name}
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(product)}
                  style={{
                    ...smallButtonStyle,
                    width: '100%',
                    maxWidth: '200px'
                  }}
                  onMouseEnter={smallButtonHover}
                  onMouseLeave={smallButtonLeave}
                >
                  Download PNG
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#000000',
            color: '#ffffff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontFamily: "'NeueHaasUnica', sans-serif",
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            opacity: showScrollToTop ? 1 : 0,
            pointerEvents: showScrollToTop ? 'auto' : 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#333333';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#000000';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}

// ============================================
// FAQ SECTION
// ============================================

export function FAQSection({ brandCharcoal = '#2C2C2C' }) {
  const [openItems, setOpenItems] = useState({});
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [faqFormData, setFaqFormData] = useState({
    question: ''
  });
  const [isSubmittingFAQ, setIsSubmittingFAQ] = useState(false);
  const [faqSubmitSuccess, setFaqSubmitSuccess] = useState(false);
  const maykerOlive = '#545142';
  const warmIvory = '#FAF8F3';
  const warmCharcoal = '#2A2925';
  const warmGrey = '#8D8A81';
  const mediumGrey = '#6B6B6B'; // Slightly darker for better contrast

  const faqData = {
    'reserve-membership': {
      title: 'Reserve Membership',
      intro: '',
      items: [
        {
          question: 'What are the membership tiers?',
          answer: 'Mayker Reserve has five tiers, with rewards that grow as you do. New members start in House, including a $1,000 rental credit on your first $5,000+ order. As you earn annual points, you\'ll unlock: Studio — 15% off (5,000 points), Gallery — 20% off (25,000 points), Conservatory — 25% off (100,000 points), Estate — 30% off (200,000 points).'
        },
        {
          question: 'How do I earn points?',
          answer: 'Every dollar you spend on invoices earns you a point (delivery fees and taxes excluded). We also offer double point periods throughout the year to help you reach the next tier faster.'
        },
        {
          question: 'Do my points carry over to the next year?',
          answer: 'Points don\'t carry over — they reset each January 1. But your tier does stay with you, so you\'ll begin the new year at the same tier you earned. For example: if you reach Gallery this year, you\'ll start next year as a Gallery member, with 20% off already in place. From there, you\'ll earn points again throughout the year to maintain your tier — or unlock the next. Members who joined before 2026 begin in Studio, unless they earned a higher tier in 2025.'
        },
        {
          question: 'Can multiple people from my company access our Reserve account?',
          answer: 'Yes. Each individual has a personal login, but points are accrued by company, so you\'ll be able to see contributing projects from multiple team members, if applicable.'
        },
        {
          question: 'Where can I track my progress?',
          answer: 'Your Overview page gives you a quick snapshot — your current tier status and highlights from the previous year. For details, visit your Activity page. It\'s your complete record of points, tier status, and projects — past, current, and upcoming.'
        },
        {
          question: 'When does my discount apply?',
          answer: 'Your discount is applied automatically to your invoice for all standard rental fees. It does not apply to extended rental terms, custom fabrication, or procurement projects.'
        }
      ]
    },
    'services-products': {
      title: 'Mayker Services and Products',
      intro: '',
      items: [
        {
          question: 'What does Mayker do?',
          answer: 'We\'re a design resource company. Since 2013, we\'ve provided furniture rentals, custom fabrication, procurement services, and design guidance to brands and individuals. We started in our hometown of Nashville, expanded throughout the Southeast, and now regularly service clients nationwide. We really enjoy getting to know and partnering with the brands, planners, and designers who know that every detail matters.'
        },
        {
          question: 'What types of projects does Mayker work on?',
          answer: 'We work across corporate experiences and activations to social gatherings. Whether it\'s an intimate dinner for twenty or a conference for 2,000, we provide the furniture, fabrication, and flexibility to make it happen seamlessly.'
        },
        {
          question: 'What can I rent?',
          answer: 'We have six core collections: Bars, Furnishings, Lighting, Upholstery, Seating, and Textiles. We focus on the fundamentals, the statement changers, and lean into quality, design versatility, and lasting impressions.'
        },
        {
          question: 'Can you create something custom for me?',
          answer: 'Absolutely. The most memorable details are always authentic. If you\'re looking for something personalized, our Custom Team concepts and delivers original solutions—custom bars, stage fronts, backdrops, display solutions, signage. We also source hand-selected products, whether you need more of something we carry or something entirely new.'
        },
        {
          question: 'What\'s the investment for custom services?',
          answer: 'Custom projects are approached on a case-by-case basis. We have a $2,500 project minimum and provide pricing based on project plan, materials, complexity, and timeline.'
        },
        {
          question: 'How do I get started with a custom project?',
          answer: 'Visit our Product R&D page to submit an inquiry and share what you\'re imagining — concept, install date, and target budget range. Dimensions and inspiration photos are always welcome. The more the merrier.'
        },
        {
          question: 'Do you have any rental minimums?',
          answer: 'Our rental minimums are based on location. Visit our resources page and use the rental minimum calculator to see what the minimum is for your project.'
        }
      ]
    },
    'billing-policies': {
      title: 'Billing and Policies',
      intro: '',
      items: [
        {
          question: 'How do I confirm my reservation?',
          answer: 'Services are confirmed with a signed service agreement and, at minimum, a 50% deposit. The balance is due 30 days prior to your event. For projects booked within 45 days of your event date, full payment is due at contract signing.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all payment methods. A 3% processing fee applies to credit card transactions.'
        },
        {
          question: 'What\'s your cancellation policy?',
          answer: 'Services are non-cancellable and non-refundable. When we reserve products for your project, we remove them from circulation for other clients. This policy protects the integrity of our inventory and ensures availability for all confirmed events.'
        },
        {
          question: 'What happens if a product is damaged during my event?',
          answer: 'Normal wear and tear is expected and covered by your Product Care Fee. However, significant damage, loss, or theft will result in replacement or repair charges. We\'ll work with you to assess any issues and determine appropriate charges. All items are inspected before and after each event.'
        },
        {
          question: 'Are there delivery and setup fees?',
          answer: 'Yes — delivery and setup fees may apply, depending on your market and project needs. Each market has a rental minimum, plus a free standard delivery threshold. Once your order meets that threshold, standard delivery is included. If it doesn\'t, a standard delivery fee applies. If your event requires a non-standard install — multi-day setup, off-hours timing, or difficult access — additional fees may apply. We\'ll always confirm delivery and setup costs with you in advance.'
        },
        {
          question: 'Are there additional fees?',
          answer: 'We charge additional fees for out-of-hours deliveries or challenging load-in conditions that require extra crew and time. Our goal is to keep fees minimal so your budget goes toward product, not process—while still covering what it actually takes to get the job done right.'
        },
        {
          question: 'Do I need insurance for my event?',
          answer: 'We require proof of general liability insurance for all events. Your policy should name Mayker as an additional insured. We can provide the specific certificate language if needed.'
        },
        {
          question: 'What if I need to add or remove items close to my event date?',
          answer: 'We do our best to accommodate changes, but inventory availability becomes limited as your event approaches. Contact us as early as possible, and we\'ll work with you to adjust your order within what\'s feasible.'
        }
      ]
    }
  };

  const toggleItem = (sectionId, itemIndex) => {
    const key = `${sectionId}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const scrollToSection = (sectionId) => {
    setActiveSectionId(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${sectionId}`);
    }
  };

  // Initialize with all items closed
  useEffect(() => {
    // Set active section from URL hash if present
    const hash = window.location.hash.replace('#', '');
    if (hash && faqData[hash]) {
      setActiveSectionId(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle FAQ form submission
  const handleFAQFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingFAQ(true);
    setFaqSubmitSuccess(false);

    try {
      // Get token from localStorage
      const token = localStorage.getItem('clientToken');
      console.log('[FAQ Form] Token from localStorage:', token ? 'Found (length: ' + token.length + ')' : 'NOT FOUND');
      
      if (!token) {
        alert('Session expired. Please log in again.');
        return;
      }
      
      const requestBody = {
        action: 'submitFAQQuestion',
        token: token,
        question: faqFormData.question
      };
      console.log('[FAQ Form] Request body:', requestBody);
      
      const response = await fetch(CLIENT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(requestBody)
      });

      console.log('[FAQ Form] Response status:', response.status);
      const result = await response.json();
      console.log('[FAQ Form] Response result:', result);
      
      if (result.success) {
        setFaqSubmitSuccess(true);
        setFaqFormData({
          question: ''
        });
        // Clear success message after 5 seconds
        setTimeout(() => setFaqSubmitSuccess(false), 5000);
      } else {
        console.error('[FAQ Form] Error from server:', result.error);
        throw new Error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('[FAQ Form] Error submitting FAQ form:', error);
      alert('Failed to submit form. Please try again. Error: ' + (error.message || error));
    } finally {
      setIsSubmittingFAQ(false);
    }
  };

  // Handle FAQ form input changes
  const handleFAQInputChange = (field, value) => {
    setFaqFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: `
        /* FAQ Hero Banner - Full Width with proper height - Match Overview/Activity */
        .faq-hero-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          margin-top: -48px !important;
          margin-bottom: 24px !important;
          height: 500px !important;
          left: 0 !important;
          right: 0 !important;
          position: relative !important;
        }
        @media (min-width: 769px) {
          .faq-hero-banner {
            margin-top: -100px !important; /* Match Overview banner positioning */
          }
        }
        @media (max-width: 768px) {
          .faq-hero-banner {
            height: 400px !important;
          }
          .faq-hero-banner-title {
            font-size: 36px !important;
          }
          .faq-hero-banner-subtitle {
            font-size: 10px !important;
            padding: 0 16px !important;
          }
        }
        @media (max-width: 480px) {
          .faq-hero-banner {
            height: 350px !important;
          }
          .faq-hero-banner-title {
            font-size: 28px !important;
          }
        }
        
        /* FAQ sub-menu items closer together */
        .faq-nav-menu {
          gap: 24px !important;
        }
        
        /* FAQ question boxes wider - Full Width */
        .faq-container {
          max-width: 800px !important;
          width: 100% !important;
          margin: 0 auto !important;
          padding-left: 48px !important;
          padding-right: 48px !important;
          padding-bottom: 48px !important;
          padding-top: 0 !important;
          box-sizing: border-box !important;
        }
        
        /* FAQ Section Full Width - Only break out horizontally for content */
        .faq-section-wrapper {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
          position: relative !important;
          left: 0 !important;
          right: 0 !important;
        }
        .faq-section {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .faq-nav-menu {
            gap: 16px !important;
          }
          .faq-container {
            max-width: 100% !important;
            padding: 0 24px 48px !important;
          }
          .faq-section {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .faq-contact-form {
            max-width: 100% !important;
            padding: 24px 16px !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
      ` }} />
      {/* Image Banner - Outside wrapper, like Overview/Activity */}
      <div className="faq-hero-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '24px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        left: 0,
        right: 0
      }}>
          <img 
            src="/faq-banner.jpg" 
            alt="FAQ" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
              opacity: '0.9'
            }}
            onError={(e) => {
              // Fallback if image doesn't exist yet
              e.target.style.display = 'none';
            }}
          />
          {/* Dark overlay for richer look */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
          }} />
          {/* Text Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
            zIndex: 1
          }}>
            <div className="faq-hero-banner-title" style={{
              fontSize: '50px',
              fontWeight: '300',
              fontFamily: "'Domaine Text', serif",
              color: 'white',
              marginBottom: '20px',
              textAlign: 'center',
              letterSpacing: '-0.02em',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}>
              Mayker FAQ
            </div>
            <div style={{
              width: '60px',
              height: '1px',
              background: 'rgba(255, 255, 255, 0.6)',
              margin: '0 auto 24px'
            }} />
            <div className="faq-hero-banner-subtitle" style={{
              fontSize: '11px',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: "'NeueHaasUnica', sans-serif",
              textAlign: 'center',
              lineHeight: '1.6',
              fontWeight: '400'
            }}>
              A curated guide to your membership, services, billing, and support with Mayker Reserve.
            </div>
          </div>
        </div>
      
      {/* Content wrapper for full-width content */}
      <div className="faq-section-wrapper" style={{ 
        backgroundColor: 'white', 
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        position: 'relative',
        left: 0,
        right: 0
      }}>
        <div className="faq-container" style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          paddingLeft: '48px',
          paddingRight: '48px',
          paddingBottom: '48px',
          paddingTop: '0',
          width: '100%',
          boxSizing: 'border-box'
        }}>
        {/* Chapter Navigation */}
        <nav style={{
          padding: '16px 0 24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          marginBottom: '24px'
        }}>
          <div className="faq-nav-menu" style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            {Object.keys(faqData).map((sectionId) => {
              const isActive = activeSectionId === sectionId;
              return (
                <a
                  key={sectionId}
                  href={`#${sectionId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(sectionId);
                  }}
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    color: isActive ? maykerOlive : mediumGrey,
                    textDecoration: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    paddingBottom: '8px',
                    borderBottom: isActive ? `2px solid ${maykerOlive}` : '2px solid transparent',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = maykerOlive;
                      e.currentTarget.style.borderBottomColor = maykerOlive;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = mediumGrey;
                      e.currentTarget.style.borderBottomColor = 'transparent';
                    }
                  }}
                >
                  {faqData[sectionId].title}
                </a>
              );
            })}
          </div>
        </nav>

        {/* FAQ Sections */}
        {Object.entries(faqData).map(([sectionId, section], sectionIndex) => (
          <section
            key={sectionId}
            id={sectionId}
            className="faq-section"
            style={{
              marginBottom: sectionIndex < Object.keys(faqData).length - 1 ? '80px' : '80px',
              scrollMarginTop: '80px',
              paddingTop: '16px',
              paddingLeft: '0',
              paddingRight: '0'
            }}
          >
            <h2 style={{
              fontSize: '20px',
              fontWeight: '300',
              fontFamily: "'Domaine Text', serif",
              color: warmCharcoal,
              marginBottom: '12px',
              letterSpacing: '-0.02em'
            }}>
              {section.title}
            </h2>
            <p style={{
              fontSize: '13px',
              fontWeight: '400',
              fontFamily: "'NeueHaasUnica', sans-serif",
              color: mediumGrey, // Slightly darker for better readability
              fontStyle: 'italic',
              marginBottom: '40px',
              lineHeight: '1.7', // Increased line height
              textAlign: 'left' // Left-aligned for book-like feel
            }}>
              {section.intro}
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0', // No gap, using dividers instead
              width: '100%'
            }}>
              {section.items.map((item, itemIndex) => {
                const itemKey = `${sectionId}-${itemIndex}`;
                const isOpen = openItems[itemKey];
                const isLast = itemIndex === section.items.length - 1;

                return (
                  <div key={itemIndex} className="faq-question-box">
                    <div
                      style={{
                        backgroundColor: isOpen ? '#F7F6F0' : '#FFFFFF',
                        borderRadius: '8px',
                        border: '1px solid #E4E1D8', // Soft warm-grey border
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)', // Very subtle shadow
                        overflow: 'hidden',
                        transition: 'all 0.25s ease',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F7F6F0';
                      }}
                      onMouseLeave={(e) => {
                        if (!isOpen) {
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                        } else {
                          e.currentTarget.style.backgroundColor = '#F7F6F0';
                        }
                      }}
                    >
                      <button
                        onClick={() => toggleItem(sectionId, itemIndex)}
                        role="button"
                        aria-expanded={isOpen}
                        style={{
                          width: '100%',
                          padding: '20px 30px', // Increased horizontal padding
                          backgroundColor: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '16px'
                        }}
                      >
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          color: warmCharcoal,
                          lineHeight: '1.5',
                          flex: 1
                        }}>
                          {item.question}
                        </span>
                        {/* Plus/Minus symbol */}
                        <div style={{
                          fontSize: '20px',
                          fontWeight: '300',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          color: isOpen ? maykerOlive : mediumGrey,
                          lineHeight: '1',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.2s ease'
                        }}>
                          {isOpen ? '−' : '+'}
                        </div>
                      </button>
                      <div
                        style={{
                          maxHeight: isOpen ? '1000px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.25s ease',
                          padding: isOpen ? '0 30px 16px' : '0 30px',
                          opacity: isOpen ? 1 : 0,
                          transitionProperty: 'max-height, padding, opacity'
                        }}
                      >
                        <div style={{
                          paddingTop: '0px',
                          fontSize: '14px',
                          fontWeight: '400',
                          fontFamily: "'NeueHaasUnica', sans-serif",
                          color: warmCharcoal,
                          lineHeight: '1.7'
                        }}>
                          {item.answer.split('\n').map((paragraph, pIndex) => (
                            <p key={pIndex} style={{ marginBottom: pIndex < item.answer.split('\n').length - 1 ? '8px' : '0' }}>
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Soft divider between items */}
                    {!isLast && (
                      <div style={{
                        height: '1px',
                        backgroundColor: '#EAE7DF',
                        margin: '16px 0'
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* FAQ Contact Form */}
        <div className="faq-contact-form" style={{
          marginTop: '80px',
          padding: '32px',
          backgroundColor: '#fafaf8',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          width: '100%'
        }}>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#6b6b6b',
            fontFamily: "'NeueHaasUnica', sans-serif",
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '16px',
            lineHeight: '1.4'
          }}>
            HAVE A QUESTION?
          </div>
          <p style={{
            fontSize: '13px',
            fontFamily: "'NeueHaasUnica', sans-serif",
            color: '#666',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            Have a question we didn't answer? Feel free to ask here, and we'll update our resources or contact you directly.
          </p>

          <form onSubmit={handleFAQFormSubmit}>
            {/* Question Field */}
            <div style={{ marginBottom: '20px' }}>
              <textarea
                value={faqFormData.question}
                onChange={(e) => handleFAQInputChange('question', e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  color: '#000000',
                  backgroundColor: 'white',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = brandCharcoal}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Enter your question..."
              />
            </div>

            {/* Success Message */}
            {faqSubmitSuccess && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#d1fae5',
                color: '#065f46',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px',
                fontFamily: "'NeueHaasUnica', sans-serif"
              }}>
                Thank you! Your question has been received. We'll review it and update our resources accordingly.
              </div>
            )}

            {/* Submit Button */}
            <div className="form-actions" style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '20px'
            }}>
              <button
                type="submit"
                disabled={isSubmittingFAQ}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  fontSize: '11px',
                  fontFamily: "'NeueHaasUnica', sans-serif",
                  fontWeight: '400',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#000000',
                  cursor: isSubmittingFAQ ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                  opacity: isSubmittingFAQ ? 0.6 : 1,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmittingFAQ) {
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmittingFAQ) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {isSubmittingFAQ ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PRODUCTS SECTION
// ============================================

// Collection hierarchy mapping
const COLLECTION_MAP = {
  "new-releases": [],
  "upholstery": [
    "benches",
    "chairs",
    "modular-sectionals",
    "ottomans",
    "sofas"
  ],
  "furnishings": [
    "accent-tables",
    "coffee-tables",
    "console-tables",
    "shelving"
  ],
  "seating": [
    "banquettes",
    "dining-chairs",
    "stools"
  ],
  "tables": [
    "bistro-tables",
    "folding-tables",
    "dining-tables",
    "pub-tables",
    "specialty-tables"
  ],
  "bars": [],
  "stage-fronts": [],
  "outdoor": [],
  "lighting": [
    "floor-lamps",
    "pendants",
    "table-lamps"
  ],
  "textiles": [
    "pillows",
    "rugs"
  ],
  "miscellaneous": [
    "mirrors",
    "plants",
    "umbrellas"
  ]
};

// Product type filters for subcategories that are not collections
const PRODUCT_TYPE_FILTERS = {
  lighting: {
    "floor-lamps": ["Floor Lamp", "Floor Lamps"],
    "pendants": ["Pendants", "Pendant"],
    "table-lamps": ["Table Lamps", "Table Lamp"]
  },
  textiles: {
    "pillows": ["Pillows", "Pillow"],
    "rugs": ["Rugs", "Rug"]
  },
  miscellaneous: {
    "mirrors": ["Floor Mirror", "Mirrors", "Mirror"],
    "plants": ["Plants", "Plant"],
    "umbrellas": ["Umbrellas", "Umbrella"]
  }
};

// Normalize product name for dimension matching
function normalizeProductName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '');
}

// Catalog dimensions service
const catalogDimensionsService = {
  async getDimensions() {
    try {
      const response = await fetch('/api/products/dimensions');
      if (!response.ok) {
        throw new Error(`Failed to fetch dimensions: ${response.status}`);
      }
      const data = await response.json();
      return data.success ? data.dimensions : {};
    } catch (error) {
      console.error('Error fetching product dimensions:', error);
      return {};
    }
  },

  getDimensionForProduct(productTitle, dimensionsMap) {
    if (!productTitle || !dimensionsMap) return null;
    
    // Try exact match first
    if (dimensionsMap[productTitle]) {
      return dimensionsMap[productTitle];
    }
    
    // Try normalized match
    const normalized = normalizeProductName(productTitle);
    if (dimensionsMap[normalized]) {
      return dimensionsMap[normalized];
    }
    
    // Try partial match (product name contains catalog name or vice versa)
    for (const [catalogName, dimension] of Object.entries(dimensionsMap)) {
      const normalizedCatalog = normalizeProductName(catalogName);
      if (normalized.includes(normalizedCatalog) || normalizedCatalog.includes(normalized)) {
        return dimension;
      }
    }
    
    return null;
  }
};

// Favorites and Wishlists Services (using API endpoints)
const favoritesService = {
  async getFavorites() {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/favorites', window.location.origin);
    url.searchParams.set('token', token.trim());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get favorites');
    }

    return data.favorites || [];
  },

  async toggleFavorite(productId, isFavorite) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/favorites', window.location.origin);
    url.searchParams.set('token', token.trim());

    console.log('[Favorites Service] Calling API:', { url: url.toString().replace(/token=[^&]+/, 'token=***'), productId, isFavorite });

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, isFavorite })
    });

    console.log('[Favorites Service] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Favorites Service] Response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('[Favorites Service] Response data:', data);

    if (!data.success) {
      throw new Error(data.error || 'Failed to update favorite');
    }

    return data.favorites || [];
  }
};

const wishlistsService = {
  async getWishlists() {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/wishlists', window.location.origin);
    url.searchParams.set('token', token.trim());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get wishlists');
    }

    return data.wishlists || [];
  },

  async createWishlist(listName) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/wishlists', window.location.origin);
    url.searchParams.set('token', token.trim());

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listName })
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create wishlist');
    }

    return data.wishlist;
  },

  async updateWishlist(listId, updates) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/wishlists', window.location.origin);
    url.searchParams.set('token', token.trim());

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listId, ...updates })
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to update wishlist');
    }

    return data.wishlist;
  },

  async deleteWishlist(listId) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/wishlists', window.location.origin);
    url.searchParams.set('token', token.trim());
    url.searchParams.set('listId', listId);

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete wishlist');
    }

    return true;
  },

  async updateWishlistProducts(listId, productId, add, quantity = 1) {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = new URL('/api/wishlists', window.location.origin);
    url.searchParams.set('token', token.trim());

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listId, productId, add, quantity })
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to update wishlist');
    }

    return data.wishlist;
  }
};

// Add to List Modal Component
function AddToListModal({ productId, productName, wishlists, onClose, onAddToList }) {
  const [selectedLists, setSelectedLists] = useState(new Map()); // Map of listId -> { selected: boolean, quantity: number }
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListQuantity, setNewListQuantity] = useState(1);
  const [creating, setCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize selected lists based on which lists already contain this product
  useEffect(() => {
    const listsMap = new Map();
    wishlists.forEach(list => {
      const product = list.products?.find(p => p.productId === productId) || 
                     (list.productIds && list.productIds.includes(productId) ? { productId, quantity: 1 } : null);
      if (product) {
        listsMap.set(list.id, { selected: true, quantity: product.quantity || 1 });
      } else {
        listsMap.set(list.id, { selected: false, quantity: 1 });
      }
    });
    setSelectedLists(listsMap);
  }, [productId, wishlists]);

  const handleListToggle = (listId) => {
    const newSelected = new Map(selectedLists);
    const current = newSelected.get(listId) || { selected: false, quantity: 1 };
    newSelected.set(listId, { selected: !current.selected, quantity: current.quantity });
    setSelectedLists(newSelected);
  };

  const handleQuantityChange = (listId, quantity) => {
    const newSelected = new Map(selectedLists);
    const current = newSelected.get(listId) || { selected: false, quantity: 1 };
    const qty = Math.max(1, parseInt(quantity) || 1);
    newSelected.set(listId, { selected: current.selected, quantity: qty });
    setSelectedLists(newSelected);
  };

  const handleSave = async () => {
    if (onAddToList) {
      // Update each list
      for (const list of wishlists) {
        const listData = selectedLists.get(list.id) || { selected: false, quantity: 1 };
        const shouldBeInList = listData.selected;
        const isCurrentlyInList = list.products?.some(p => p.productId === productId) || 
                                 (list.productIds && list.productIds.includes(productId));
        
        if (shouldBeInList !== isCurrentlyInList || (shouldBeInList && listData.quantity !== (list.products?.find(p => p.productId === productId)?.quantity || 1))) {
          // Pass null for newListName (4th param) and quantity as 5th param
          await onAddToList(list.id, productId, shouldBeInList, null, listData.quantity);
        }
      }
    }
    onClose();
  };

  const handleCreateList = async () => {
    const trimmedName = newListName.trim();
    if (!trimmedName) return;

    setCreating(true);
    try {
      if (onAddToList) {
        // Create list and add product with quantity
        await onAddToList(null, productId, true, trimmedName, newListQuantity);
        setNewListName('');
        setNewListQuantity(1);
        setShowCreateForm(false);
        // Show success feedback briefly, then close
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 600);
      }
    } catch (error) {
      console.error('Error creating list:', error);
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '20px',
          maxWidth: '340px',
          width: '90%',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          fontSize: '16px',
          fontWeight: '500',
          marginBottom: '4px',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          color: '#000'
        }}>
          Add to List
        </div>
        <div style={{
          fontSize: '11px',
          color: '#999',
          marginBottom: '20px',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          lineHeight: '1.4'
        }}>
          {productName}
        </div>

        {wishlists.length === 0 && !showCreateForm ? (
          <div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '12px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              lineHeight: '1.5'
            }}>
              Create a list to save this item.
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="List name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e8e8e3',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  backgroundColor: '#fafaf8',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  height: '36px',
                  boxSizing: 'border-box',
                  marginBottom: '8px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e8e8e3';
                  e.target.style.backgroundColor = '#fafaf8';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newListName.trim() && !creating) {
                    handleCreateList();
                  }
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                  fontSize: '11px',
                  color: '#666',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  whiteSpace: 'nowrap'
                }}>
                  Quantity:
                </label>
                <input
                  type="number"
                  min="1"
                  value={newListQuantity}
                  onChange={(e) => setNewListQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: '60px',
                    padding: '6px 8px',
                    border: '1px solid #e8e8e3',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    textAlign: 'center',
                    backgroundColor: '#fafaf8'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleCreateList}
                disabled={!newListName.trim() || creating}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  cursor: creating || !newListName.trim() ? 'not-allowed' : 'pointer',
                  opacity: creating || !newListName.trim() ? 0.5 : 1,
                  height: '36px',
                  transition: 'opacity 0.2s ease'
                }}
              >
                {creating ? 'Creating...' : 'Create list'}
              </button>
            </div>
            <button
              onClick={onClose}
              style={{
                marginTop: '12px',
                background: 'none',
                border: 'none',
                color: '#999',
                fontSize: '11px',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                padding: '0'
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '12px', maxHeight: '240px', overflowY: 'auto' }}>
              {wishlists.map(list => {
                const listData = selectedLists.get(list.id) || { selected: false, quantity: 1 };
                return (
                  <div
                    key={list.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fafaf8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={listData.selected}
                      onChange={() => handleListToggle(list.id)}
                      style={{
                        marginRight: '10px',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '400',
                        color: '#000',
                        lineHeight: '1.4'
                      }}>
                        {list.name}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#999',
                        marginTop: '2px'
                      }}>
                        {list.productCount || 0} {list.productCount === 1 ? 'item' : 'items'}
                      </div>
                    </div>
                    {listData.selected && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '12px' }}>
                      <label style={{
                        fontSize: '11px',
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        whiteSpace: 'nowrap'
                      }}>
                        Qty:
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={listData.quantity}
                        onChange={(e) => handleQuantityChange(list.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '50px',
                          padding: '4px 6px',
                          border: '1px solid #e8e8e3',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {showCreateForm ? (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="List name"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e8e8e3',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      backgroundColor: '#fafaf8',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      height: '36px',
                      boxSizing: 'border-box',
                      marginBottom: '8px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#000';
                      e.target.style.backgroundColor = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e8e8e3';
                      e.target.style.backgroundColor = '#fafaf8';
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newListName.trim() && !creating) {
                        handleCreateList();
                      }
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{
                      fontSize: '11px',
                      color: '#666',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      whiteSpace: 'nowrap'
                    }}>
                      Quantity:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newListQuantity}
                      onChange={(e) => setNewListQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '60px',
                        padding: '6px 8px',
                        border: '1px solid #e8e8e3',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textAlign: 'center',
                        backgroundColor: '#fafaf8'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleCreateList}
                    disabled={!newListName.trim() || creating}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      backgroundColor: '#1a1a1a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      cursor: creating || !newListName.trim() ? 'not-allowed' : 'pointer',
                      opacity: creating || !newListName.trim() ? 0.5 : 1,
                      height: '36px',
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    {creating ? 'Creating...' : 'Create list'}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewListName('');
                    setNewListQuantity(1);
                  }}
                  style={{
                    marginTop: '8px',
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    fontSize: '11px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    padding: '0'
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: '1px dashed #e8e8e3',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#666',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#999';
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e3';
                  e.currentTarget.style.color = '#666';
                }}
              >
                + Create new list
              </button>
            )}
          </>
        )}

        {/* Footer Actions */}
        {wishlists.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #f5f5f2'
          }}>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#999',
                fontSize: '11px',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                padding: '0'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                cursor: 'pointer',
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              Save
            </button>
          </div>
        )}

        {/* Success Feedback */}
        {showSuccess && (
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            padding: '8px 12px',
            backgroundColor: '#f0f9f0',
            border: '1px solid #86efac',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#166534',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Saved to list
          </div>
        )}
      </div>
    </div>
  );
}

// Catalog Toolbar Component (for products view only)
function CatalogToolbar({ searchQuery, setSearchQuery, favorites, wishlists, onNavigateToFavorites, onNavigateToLists, isMobile }) {
  return (
    <div className="products-search-bar-wrapper" style={{ marginBottom: '32px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px', 
        flexWrap: 'wrap',
        width: '100%'
      }}>
        {/* Search Input */}
        <div style={{ flex: '1', minWidth: '200px', maxWidth: '500px' }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} isSticky={false} isMobile={isMobile} />
        </div>
        
        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flexShrink: 0
        }}>
          <button
            onClick={onNavigateToFavorites}
            style={{
              padding: '12px 16px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e8e8e3',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              fontWeight: '400',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              lineHeight: '1',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#000';
              e.currentTarget.style.color = '#000';
              e.currentTarget.style.backgroundColor = '#fafaf8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e8e8e3';
              e.currentTarget.style.color = '#666';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <span>My Favorites</span>
            {favorites.length > 0 && (
              <span style={{ fontSize: '11px', opacity: 0.7 }}>({favorites.length})</span>
            )}
          </button>
          <button
            onClick={onNavigateToLists}
            style={{
              padding: '12px 16px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e8e8e3',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              fontWeight: '400',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              lineHeight: '1',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#000';
              e.currentTarget.style.color = '#000';
              e.currentTarget.style.backgroundColor = '#fafaf8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e8e8e3';
              e.currentTarget.style.color = '#666';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <span>My Lists</span>
            {wishlists.length > 0 && (
              <span style={{ fontSize: '11px', opacity: 0.7 }}>({wishlists.length})</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Products Sub-Tabs Component (Account-style)
function ProductsSubTabs({ activeTab, onTabChange }) {
  const tabs = [
    { id: 'products', label: 'ALL PRODUCTS', route: '/products' },
    { id: 'lists', label: 'PROJECT LISTS', route: '/products/lists' },
    { id: 'favorites', label: 'FAVORITES', route: '/products/favorites' }
  ];

  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      marginBottom: '32px',
      marginTop: '32px',
      borderBottom: '1px solid #e5e7eb',
      paddingBottom: '16px'
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.route)}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: activeTab === tab.id ? '500' : '400',
            color: activeTab === tab.id ? '#000000' : '#8b8b8b',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === tab.id ? '2px solid #000000' : '2px solid transparent',
            cursor: 'pointer',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
            marginBottom: '-18px'
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Saved Toolbar Component (simplified - search only, no back links or big headers)
function SavedToolbar({ 
  searchQuery, 
  setSearchQuery, 
  isMobile = false,
  placeholder = 'Search',
  centered = false
}) {
  return (
    <div style={{ 
      marginBottom: '24px',
      maxWidth: '400px',
      marginLeft: centered ? 'auto' : '0',
      marginRight: centered ? 'auto' : '0'
    }}>
      <SearchBar 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery} 
        isSticky={false} 
        isMobile={isMobile}
        placeholder={placeholder}
      />
    </div>
  );
}

// List Detail View Content Component
function ListDetailViewContent({ 
  selectedListId, 
  wishlists, 
  allProducts, 
  dimensionsMap, 
  loadingAllProducts, 
  listDetailSearchQuery, 
  setListDetailSearchQuery,
  favorites,
  handleToggleFavorite,
  handleAddToList,
  setWishlists,
  navigate,
  isMobile,
  showAlert
}) {
  const selectedList = wishlists.find(list => list.id === selectedListId);
  const listProducts = selectedList && allProducts.length > 0
    ? allProducts.filter(product => {
        if (selectedList.products && Array.isArray(selectedList.products)) {
          return selectedList.products.some(p => {
            const productId = typeof p === 'string' ? p : p.productId;
            return String(productId) === String(product.id);
          });
        }
        if (selectedList.productIds && Array.isArray(selectedList.productIds)) {
          return selectedList.productIds.some(id => String(id) === String(product.id));
        }
        return false;
      })
    : [];
  const productQuantities = selectedList && selectedList.products
    ? selectedList.products.reduce((acc, p) => {
        acc[p.productId] = p.quantity || 1;
        return acc;
      }, {})
    : {};
  
  // State for quantity inputs
  const [quantityInputs, setQuantityInputs] = useState({});
  
  // Submit for Proposal modal state
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmittingProposal, setIsSubmittingProposal] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    loadInDate: '',
    loadInTime: '',
    loadOutDate: '',
    loadOutTime: '',
    venueName: '',
    venueAddress: '',
    additionalNeeds: '',
    projectNotes: ''
  });
  
  // Initialize quantity inputs from productQuantities
  useEffect(() => {
    const initialInputs = {};
    listProducts.forEach(product => {
      initialInputs[product.id] = productQuantities[product.id] || 1;
    });
    setQuantityInputs(initialInputs);
  }, [selectedListId, listProducts.map(p => p.id).join(',')]);
  
  // Initialize Google Places Autocomplete for venue address (modal)
  useEffect(() => {
    if (!isSubmitModalOpen) return;
    
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    const inputId = 'submit-wishlist-venue-address';
    
    const initializeAutocomplete = () => {
      const checkInput = () => {
        const input = document.getElementById(inputId);
        if (input && window.google && window.google.maps && window.google.maps.places) {
          try {
            if (input.dataset.autocompleteInitialized === 'true') {
              return;
            }
            
            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              types: ['address'],
              componentRestrictions: { country: 'us' }
            });
            
            input.dataset.autocompleteInitialized = 'true';
            
            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                setSubmitForm(prev => ({ ...prev, venueAddress: place.formatted_address }));
              }
            });
          } catch (error) {
            console.warn('Failed to initialize Google Places Autocomplete:', error);
          }
        } else if (input && !window.google) {
          setTimeout(checkInput, 100);
        }
      };
      
      checkInput();
      setTimeout(checkInput, 100);
    };
    
    if (GOOGLE_MAPS_API_KEY) {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.addEventListener('load', initializeAutocomplete);
          return;
        }
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        script.onerror = () => {
          console.warn('Google Places API failed to load. Address autocomplete disabled.');
        };
        document.head.appendChild(script);
      } else {
        initializeAutocomplete();
      }
    }
  }, [isSubmitModalOpen]);
  
  const handleSubmitFieldChange = (field, value) => {
    setSubmitForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleOpenSubmitModal = () => {
    setIsSubmitModalOpen(true);
  };
  
  const handleCloseSubmitModal = () => {
    setIsSubmitModalOpen(false);
  };
  
  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    
    if (!submitForm.venueName.trim()) {
      if (showAlert) {
        await showAlert('Please enter a venue name');
      } else {
        alert('Please enter a venue name');
      }
      return;
    }
    
    if (!submitForm.venueAddress.trim()) {
      if (showAlert) {
        await showAlert('Please enter a venue address');
      } else {
        alert('Please enter a venue address');
      }
      return;
    }
    
    if (!submitForm.loadInDate || !submitForm.loadOutDate) {
      if (showAlert) {
        await showAlert('Please enter both load-in and load-out dates');
      } else {
        alert('Please enter both load-in and load-out dates');
      }
      return;
    }
    
    setIsSubmittingProposal(true);
    
    try {
      const session = authService.getSession();
      const clientCompanyName = session?.clientInfo?.clientCompanyName || 
                                session?.clientInfo?.companyName || 
                                session?.clientInfo?.client_name || '';
      const contactName =
        session?.clientInfo?.fullName ||
        [session?.clientInfo?.firstName, session?.clientInfo?.lastName].filter(Boolean).join(' ').trim() ||
        '';
      
      const productsPayload = listProducts.map(product => {
        const rawQty = quantityInputs[product.id] !== undefined ? quantityInputs[product.id] : (productQuantities[product.id] || 1);
        const quantity = parseInt(rawQty, 10);
        return {
          name: product.title || product.name || 'Unnamed',
          quantity: !isNaN(quantity) && quantity >= 1 ? quantity : 1
        };
      });
      
      const notesParts = [];
      if (selectedList?.name) {
        notesParts.push(`List: ${selectedList.name}`);
      }
      if (submitForm.additionalNeeds.trim()) {
        notesParts.push(`Additional needs: ${submitForm.additionalNeeds.trim()}`);
      }
      if (submitForm.projectNotes.trim()) {
        notesParts.push(`Project notes: ${submitForm.projectNotes.trim()}`);
      }
      
      const payload = {
        type: 'submitNewProject',
        clientCompanyName: clientCompanyName,
        contactName: contactName,
        venueName: submitForm.venueName,
        venueAddress: submitForm.venueAddress,
        loadInDate: submitForm.loadInDate,
        loadInTime: submitForm.loadInTime,
        loadOutDate: submitForm.loadOutDate,
        loadOutTime: submitForm.loadOutTime,
        products: productsPayload,
        notes: notesParts.join('\n\n'),
        resourceLinks: '',
        scheduleCall: false,
        sourceListId: selectedList?.id || '',
        sourceListName: selectedList?.name || ''
      };
      
      const response = await fetch(PROPOSALS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      if (result.success) {
        setIsSubmitModalOpen(false);
        setSubmitForm({
          loadInDate: '',
          loadInTime: '',
          loadOutDate: '',
          loadOutTime: '',
          venueName: '',
          venueAddress: '',
          additionalNeeds: '',
          projectNotes: ''
        });
        if (selectedListId) {
          try {
            await wishlistsService.updateWishlist(selectedListId, { status: 'Submitted' });
            const updatedLists = await wishlistsService.getWishlists();
            setWishlists(updatedLists);
          } catch (statusError) {
            console.warn('Failed to update wishlist status:', statusError);
          }
        }
        if (showAlert) {
          await showAlert('Wishlist submitted successfully. Our team will review and follow up.');
        } else {
          alert('Wishlist submitted successfully. Our team will review and follow up.');
        }
      } else {
        if (showAlert) {
          await showAlert('Error submitting wishlist: ' + (result.error || 'Please try again.'));
        } else {
          alert('Error submitting wishlist: ' + (result.error || 'Please try again.'));
        }
      }
    } catch (error) {
      console.error('Error submitting wishlist:', error);
      if (showAlert) {
        await showAlert('Error submitting wishlist: ' + (error.message || 'Please try again.'));
      } else {
        alert('Error submitting wishlist: ' + (error.message || 'Please try again.'));
      }
    } finally {
      setIsSubmittingProposal(false);
    }
  };
  
  const handleQuantityChange = (productId, value) => {
    // Allow empty string temporarily while typing
    if (value === '') {
      setQuantityInputs(prev => ({ ...prev, [productId]: '' }));
      return;
    }
    
    // Parse and validate
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      setQuantityInputs(prev => ({ ...prev, [productId]: numValue }));
    }
  };
  
  const handleQuantityBlur = (productId) => {
    const currentValue = quantityInputs[productId];
    const numValue = parseInt(currentValue, 10);
    
    // If invalid or empty, restore to original quantity
    if (isNaN(numValue) || numValue < 1) {
      const originalQuantity = productQuantities[productId] || 1;
      setQuantityInputs(prev => ({ ...prev, [productId]: originalQuantity }));
      return;
    }
    
    // Only update if changed
    const originalQuantity = productQuantities[productId] || 1;
    if (numValue !== originalQuantity) {
      wishlistsService.updateWishlistProducts(selectedListId, productId, true, numValue)
        .then(() => wishlistsService.getWishlists())
        .then(updatedLists => setWishlists(updatedLists))
        .catch(err => console.error('Error updating quantity:', err));
    }
  };
  
  const handleQuantityKeyPress = (e, productId) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <>
      {isSubmitModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '640px',
            backgroundColor: '#FAF8F3',
            borderRadius: '8px',
            padding: '28px 32px',
            position: 'relative',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
          }}>
            <button
              type="button"
              onClick={handleCloseSubmitModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '18px',
                color: '#999',
                cursor: 'pointer'
              }}
              aria-label="Close"
            >
              ×
            </button>
            
            <h3 style={{
              fontSize: '22px',
              fontWeight: '400',
              margin: '0 0 6px 0',
              fontFamily: "'Domaine Text', serif",
              color: '#2C2C2C'
            }}>
              Submit Wishlist for Proposal
            </h3>
            <div style={{
              fontSize: '13px',
              color: '#666',
              marginBottom: '20px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
            }}>
              Please provide the following event details:
            </div>
            
            <form onSubmit={handleSubmitProposal}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    Load-In Date *
                  </label>
                  <input
                    type="date"
                    value={submitForm.loadInDate}
                    onChange={(e) => handleSubmitFieldChange('loadInDate', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      marginTop: '6px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      fontSize: '13px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    Load-In Time *
                  </label>
                  <MaykerTimeInput
                    id="submit-proposal-loadInTime"
                    value={submitForm.loadInTime}
                    onChange={(value) => handleSubmitFieldChange('loadInTime', value)}
                    outputFormat="input24"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      marginTop: '6px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      fontSize: '13px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    Load-Out Date *
                  </label>
                  <input
                    type="date"
                    value={submitForm.loadOutDate}
                    onChange={(e) => handleSubmitFieldChange('loadOutDate', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      marginTop: '6px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      fontSize: '13px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    Load-Out Time *
                  </label>
                  <MaykerTimeInput
                    id="submit-proposal-loadOutTime"
                    value={submitForm.loadOutTime}
                    onChange={(value) => handleSubmitFieldChange('loadOutTime', value)}
                    outputFormat="input24"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      marginTop: '6px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      fontSize: '13px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                  Venue Name *
                </label>
                <input
                  type="text"
                  value={submitForm.venueName}
                  onChange={(e) => handleSubmitFieldChange('venueName', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    marginTop: '6px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    fontSize: '13px',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                  Venue Address *
                </label>
                <textarea
                  id="submit-wishlist-venue-address"
                  value={submitForm.venueAddress}
                  onChange={(e) => handleSubmitFieldChange('venueAddress', e.target.value)}
                  required
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    marginTop: '6px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    fontSize: '13px',
                    backgroundColor: '#fff',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                  Is there anything you need for your project you weren't able to find in our collection?
                </label>
                <textarea
                  value={submitForm.additionalNeeds}
                  onChange={(e) => handleSubmitFieldChange('additionalNeeds', e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    marginTop: '6px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    fontSize: '13px',
                    backgroundColor: '#fff',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#2C2C2C', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                  Project Notes
                </label>
                <textarea
                  value={submitForm.projectNotes}
                  onChange={(e) => handleSubmitFieldChange('projectNotes', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    marginTop: '6px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    fontSize: '13px',
                    backgroundColor: '#fff',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleCloseSubmitModal}
                  style={{
                    padding: '10px 18px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f5f5f0',
                    color: '#333',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProposal}
                  style={{
                    padding: '10px 18px',
                    border: 'none',
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    cursor: isSubmittingProposal ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingProposal ? 0.7 : 1
                  }}
                >
                  {isSubmittingProposal ? 'Submitting...' : 'Submit Wishlist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div style={{
        display: 'flex',
        gap: isMobile ? '32px' : '64px',
        marginTop: '0',
        justifyContent: 'flex-start',
        flexDirection: isMobile ? 'column' : 'row',
        paddingBottom: isMobile ? '120px' : '0'
      }}>
      {/* Left Column */}
      <div style={{ 
        flex: isMobile ? '1 1 auto' : '0 0 240px',
        flexShrink: 0,
        ...(!isMobile ? {
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: 'fit-content',
          zIndex: 10
        } : {
          position: 'relative'
        })
      }}>
        {/* Breadcrumb */}
        <div style={{
          fontSize: '11px',
          color: '#8b8b8b',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}>
          <button
            type="button"
            onClick={() => navigate('/products/lists')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              color: '#6b6b6b',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              fontSize: '11px',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.06em'
            }}
            aria-label="My Projects"
          >
            My Projects
          </button>
          <span style={{ margin: '0 6px', color: '#b5b5b5' }}>/</span>
          <span>{selectedList?.name || 'Project'}</span>
        </div>

        {/* Title - Project Name */}
        <h2 style={{ 
          fontSize: isMobile ? '26px' : '28px', 
          fontWeight: '300', 
          color: '#000000',
          fontFamily: "'Domaine Text', serif",
          marginBottom: '12px',
          marginTop: '0',
          letterSpacing: 'normal',
          textTransform: 'uppercase',
          textAlign: 'left'
        }}>
          {selectedList?.name || 'List'}
        </h2>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            padding: '4px 8px',
            borderRadius: '999px',
            border: '1px solid #e5e7eb',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            color: '#1a1a1a'
          }}>
            {(selectedList?.status || 'Draft').toUpperCase()}
          </span>
          <span style={{
            fontSize: '12px',
            color: '#6b6b6b',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            {listProducts.length} {listProducts.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        
        {/* About / Description */}
        <div style={{
          fontSize: '13px',
          color: '#666',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          lineHeight: '1.35',
          marginBottom: '14px',
          textAlign: 'left'
        }}>
          When you're ready, submit this wishlist for review by the Mayker team. Once availability is verified, our team will circulate an itemized proposal for your review.
        </div>
        
        {/* Submit Button */}
        <button
          type="button"
          onClick={handleOpenSubmitModal}
          style={{
            width: '100%',
            backgroundColor: '#000000',
            color: '#FFFFFF',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '20px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#333333';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#000000';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Submit for Proposal
        </button>

        <button
          onClick={() => navigate('/products')}
          style={{
            backgroundColor: '#f5f5f0',
            color: '#000000',
            border: '1px solid #d9d9d1',
            padding: '10px 18px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: '600',
            fontFamily: "'NeueHaasUnica', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            cursor: 'pointer',
            transition: 'all 0.2s',
            outline: 'none',
            width: isMobile ? '100%' : 'auto',
            textAlign: 'center',
            marginBottom: '12px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ecece6';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f0';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          aria-label="Add products"
        >
          Add Products
        </button>

        <div style={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'nowrap',
          overflowX: 'hidden',
          marginBottom: '20px'
        }}>
          <button
            type="button"
            onClick={() => navigate('/products')}
            style={{
              padding: '8px 10px',
              borderRadius: '999px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              fontSize: '9px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#1a1a1a',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            All Products
          </button>
          <button
            type="button"
            onClick={() => navigate('/products/favorites')}
            style={{
              padding: '8px 10px',
              borderRadius: '999px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              fontSize: '9px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#1a1a1a',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            Favorites
          </button>
          <button
            type="button"
            onClick={() => navigate('/products/lists')}
            style={{
              padding: '8px 10px',
              borderRadius: '999px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              fontSize: '9px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#1a1a1a',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            My Projects
          </button>
        </div>
        
        {/* Separator */}
        <div style={{
          height: '1px',
          width: '100%',
          backgroundColor: '#e5e7eb',
          marginBottom: '24px'
        }} />
        
        {/* Search Bar */}
        <div style={{ marginBottom: '32px' }}>
          <SearchBar 
            searchQuery={listDetailSearchQuery} 
            setSearchQuery={setListDetailSearchQuery} 
            isSticky={false} 
            isMobile={isMobile}
            placeholder="Search this Project"
          />
        </div>
        
      </div>
      
      {/* Right Column - Product Grid */}
      <div style={{ flex: '1', maxWidth: isMobile ? '100%' : '900px', marginLeft: isMobile ? '0' : '80px', marginTop: '0', paddingTop: '0' }}>
        {loadingAllProducts && allProducts.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            fontSize: '14px',
            color: '#666',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            Loading products...
          </div>
        ) : listProducts.length === 0 ? (
          <div style={{
            padding: '64px 24px',
            textAlign: 'center',
            color: '#666',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            This list is empty.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '24px' : '40px 32px',
            maxWidth: '100%'
          }}>
            {(listDetailSearchQuery.trim() 
              ? listProducts.filter(p => 
                  p.title.toLowerCase().includes(listDetailSearchQuery.toLowerCase())
                )
              : listProducts
            ).map((product) => {
              const dimensions = catalogDimensionsService.getDimensionForProduct(product.title, dimensionsMap);
              const isFavorite = favorites.includes(product.id);
              const quantity = productQuantities[product.id] || 1;
              const inputValue = quantityInputs[product.id] !== undefined ? quantityInputs[product.id] : quantity;
              
              return (
                <div
                  key={product.id}
                  style={{
                    position: 'relative',
                    padding: isMobile ? '16px' : '0',
                    borderRadius: isMobile ? '12px' : '0',
                    border: isMobile ? '1px solid #e8e8e3' : 'none',
                    backgroundColor: isMobile ? '#fafaf8' : 'transparent'
                  }}
                >
                  <ProductCard
                    product={product}
                    dimensions={dimensions}
                    brandCharcoal="#000000"
                    isFavorite={isFavorite}
                    onToggleFavorite={handleToggleFavorite}
                    onAddToList={handleAddToList}
                    wishlists={wishlists}
                    hideActions={true}
                    useCardBackground={false}
                    compactInfo={true}
                  />
                  {/* Quantity Controls - Below Product Name */}
                  <div style={{
                    marginTop: '4px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                  }}>
                    <input
                      type="number"
                      min="1"
                      value={inputValue}
                      onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                      onKeyPress={(e) => handleQuantityKeyPress(e, product.id)}
                      style={{
                        width: isMobile ? '64px' : '72px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        fontSize: '12px',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        color: '#000',
                        fontWeight: '400',
                        textAlign: 'center',
                        backgroundColor: '#fff',
                        transition: 'border-color 0.2s',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                      placeholder="Qty"
                      onFocus={(e) => {
                        e.target.style.borderColor = '#000';
                        e.target.select();
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        handleQuantityBlur(product.id);
                      }}
                    />
                    <button
                      onClick={() => {
                        wishlistsService.updateWishlistProducts(selectedListId, product.id, false)
                          .then(() => wishlistsService.getWishlists())
                          .then(updatedLists => setWishlists(updatedLists))
                          .catch(err => console.error('Error removing product:', err));
                      }}
                      style={{
                        marginLeft: 'auto',
                        padding: '6px 10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        cursor: 'pointer',
                        color: '#8b8b8b',
                        fontWeight: '400',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#1a1a1a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#8b8b8b';
                      }}
                      title="Delete from list"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </>
  );
}

// List Detail Header (minimal title only, no count)
function ListDetailHeader({ title }) {
  if (!title) return null;
  
  return (
    <h2 style={{
      fontSize: '28px',
      fontWeight: '300',
      fontFamily: "'Domaine Text', serif",
      color: '#000',
      textTransform: 'uppercase',
      letterSpacing: 'normal',
      textAlign: 'center',
      margin: '0 0 24px 0',
      padding: '0'
    }} className="profile-title">
      {typeof title === 'string' ? title.toUpperCase() : title}
    </h2>
  );
}

// Wishlists View Component
function WishlistsView({ wishlists, allProducts, dimensionsMap, onDeleteList, onRenameList, onRemoveProduct, favorites, onToggleFavorite, onAddToList, loadingAllProducts, onNavigateToList, searchQuery = '', isMobile = false }) {
  const [selectedListId, setSelectedListId] = useState(null);
  const [editingListId, setEditingListId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  
  // Filter lists by search query
  const filteredLists = searchQuery.trim()
    ? wishlists.filter(list => 
        list.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : wishlists;

  const selectedList = wishlists.find(list => list.id === selectedListId);
  
  // Debug logging (can be removed later)
  if (selectedList && process.env.NODE_ENV === 'development') {
    console.log('[WishlistsView] Selected list:', {
      id: selectedList.id,
      name: selectedList.name,
      products: selectedList.products,
      productIds: selectedList.productIds,
      allProductsCount: allProducts.length
    });
  }
  
  const listProducts = selectedList && allProducts.length > 0
    ? allProducts.filter(product => {
        // Support both old format (productIds array) and new format (products array with quantity)
        if (selectedList.products && Array.isArray(selectedList.products)) {
          const found = selectedList.products.some(p => {
            // Handle both string and object formats
            const productId = typeof p === 'string' ? p : p.productId;
            const matches = String(productId) === String(product.id);
            if (process.env.NODE_ENV === 'development' && matches) {
              console.log('[WishlistsView] Found product match:', { productId, productIdInList: productId, productIdInProduct: product.id });
            }
            return matches;
          });
          return found;
        }
        if (selectedList.productIds && Array.isArray(selectedList.productIds)) {
          return selectedList.productIds.some(id => String(id) === String(product.id));
        }
        return false;
      })
    : [];
  
  // Debug logging
  if (selectedList && process.env.NODE_ENV === 'development') {
    console.log('[WishlistsView] Filtered products:', {
      listProductsCount: listProducts.length,
      expectedCount: selectedList.productCount || selectedList.products?.length || selectedList.productIds?.length || 0
    });
  }
  
  // Create a map of productId -> quantity for display
  const productQuantities = selectedList && selectedList.products
    ? selectedList.products.reduce((acc, p) => {
        acc[p.productId] = p.quantity || 1;
        return acc;
      }, {})
    : {};

  const handleRenameStart = (list) => {
    setEditingListId(list.id);
    setEditingName(list.name);
  };

  const handleRenameSave = async () => {
    if (!editingName.trim() || editingListId === null) return;
    try {
      await onRenameList(editingListId, editingName.trim());
      setEditingListId(null);
      setEditingName('');
    } catch (err) {
      // Error handling is done in parent
    }
  };

  const handleRenameCancel = () => {
    setEditingListId(null);
    setEditingName('');
  };

  useEffect(() => {
    if (!menuOpenId) return;
    const handleOutsideClick = (event) => {
      const menuRoot = event.target.closest(`[data-menu-id="${menuOpenId}"]`);
      if (!menuRoot) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpenId]);

  const handleDelete = async (listId) => {
    try {
      await onDeleteList(listId);
      if (selectedListId === listId) {
        setSelectedListId(null);
      }
      setShowDeleteConfirm(null);
    } catch (err) {
      // Error handling is done in parent
    }
  };

  if (selectedList) {
    // List detail view (internal - used when not using routes)
    // Header is handled by SavedToolbar in route-based views, so this is just a fallback
    return (
      <div>

        {loadingAllProducts && allProducts.length === 0 ? (
          <div style={{
            padding: '64px 24px',
            textAlign: 'center',
            color: '#666',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            Loading products...
          </div>
        ) : listProducts.length === 0 ? (
          <div style={{
            padding: '64px 24px',
            textAlign: 'center',
            color: '#666',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
          }}>
            This list is empty.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '40px 32px'
          }}>
            {listProducts.map((product) => {
              const dimensions = catalogDimensionsService.getDimensionForProduct(product.title, dimensionsMap);
              const isFavorite = favorites.includes(product.id);
              const quantity = productQuantities[product.id] || 1;
              return (
                <div key={product.id} style={{ position: 'relative' }}>
                  <ProductCard
                    product={product}
                    dimensions={dimensions}
                    brandCharcoal="#000000"
                    isFavorite={isFavorite}
                    onToggleFavorite={onToggleFavorite}
                    onAddToList={onAddToList}
                    wishlists={wishlists}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 20
                  }}>
                    {quantity > 1 && (
                      <div style={{
                        padding: '4px 8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        color: '#000',
                        fontWeight: '500',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}>
                        Qty: {quantity}
                      </div>
                    )}
                    <button
                      onClick={() => onRemoveProduct(selectedListId, product.id)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}
                      title="Remove from list"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#666"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Lists overview - Header is handled by SavedToolbar, so we just render the list cards
  return (
    <div>

      {filteredLists.length === 0 ? (
        <div style={{
          padding: '64px 24px',
          textAlign: 'center',
          color: '#666',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
        }}>
          {searchQuery.trim() 
            ? 'No lists found matching your search.'
            : wishlists.length === 0
              ? 'You don\'t have any lists yet. Create your first list by adding products to a list.'
              : 'No lists match your search.'}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: isMobile ? '16px' : '24px'
        }}>
          {filteredLists.map((list) => {
            const itemCount = list.productCount || list.products?.length || list.productIds?.length || 0;
            const thumbnails = allProducts
              .filter(product => {
                if (list.products && Array.isArray(list.products)) {
                  return list.products.some(p => String(p.productId || p) === String(product.id));
                }
                if (list.productIds && Array.isArray(list.productIds)) {
                  return list.productIds.some(id => String(id) === String(product.id));
                }
                return false;
              })
              .map(product => product.imageUrl)
              .filter(Boolean)
              .slice(0, 3);

            return (
              <div
                key={list.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (onNavigateToList) {
                    onNavigateToList(list.id);
                  } else {
                    setSelectedListId(list.id);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (onNavigateToList) {
                      onNavigateToList(list.id);
                    } else {
                      setSelectedListId(list.id);
                    }
                  }
                }}
                style={{
                  padding: isMobile ? '18px' : '24px',
                  backgroundColor: '#fafaf8',
                  border: '1px solid #e8e8e3',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#000';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e3';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  {editingListId === list.id ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRenameSave();
                          } else if (e.key === 'Escape') {
                            handleRenameCancel();
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #000',
                          borderRadius: '6px',
                          fontSize: '15px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
                        }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenameSave();
                          }}
                          style={{
                            padding: '8px 14px',
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            cursor: 'pointer'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenameCancel();
                          }}
                          style={{
                            padding: '8px 14px',
                            backgroundColor: 'transparent',
                            border: '1px solid #e8e8e3',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: isMobile ? '20px' : '22px',
                          fontWeight: '300',
                          fontFamily: "'Domaine Text', serif",
                          color: '#000',
                          lineHeight: '1.3',
                          textAlign: 'left',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {list.name}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginTop: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '999px',
                            border: '1px solid #e5e7eb',
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            color: '#1a1a1a'
                          }}>
                            {(list.status || 'Draft').toUpperCase()}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: '#6b6b6b',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
                          }}>
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                      </div>
                      <div
                        data-menu-id={list.id}
                        style={{ position: 'relative', flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          aria-label="Project actions"
                          onClick={() => setMenuOpenId(menuOpenId === list.id ? null : list.id)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '18px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ⋯
                        </button>
                        {menuOpenId === list.id && (
                          <div
                            role="menu"
                            aria-label="Project actions"
                            style={{
                              position: 'absolute',
                              top: '44px',
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '10px',
                              boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
                              minWidth: '160px',
                              zIndex: 20,
                              overflow: 'hidden'
                            }}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setMenuOpenId(null);
                                handleRenameStart(list);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                border: 'none',
                                backgroundColor: 'white',
                                textAlign: 'left',
                                fontSize: '12px',
                                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                cursor: 'pointer'
                              }}
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setMenuOpenId(null);
                                setShowDeleteConfirm(list.id);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                border: 'none',
                                backgroundColor: 'white',
                                textAlign: 'left',
                                fontSize: '12px',
                                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                cursor: 'pointer',
                                color: '#b42318'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {editingListId !== list.id && !isMobile && (
                  <button
                    type="button"
                    aria-label="Open project"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onNavigateToList) {
                        onNavigateToList(list.id);
                      }
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '8px 16px',
                      borderRadius: '999px',
                      border: '1px solid #1a1a1a',
                      backgroundColor: 'transparent',
                      fontSize: '11px',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      cursor: 'pointer'
                    }}
                  >
                    Open Project
                  </button>
                )}

                {thumbnails.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                    {thumbnails.map((src, index) => (
                      <div
                        key={`${list.id}-thumb-${index}`}
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#f5f5f0',
                          border: '1px solid #efece6'
                        }}
                      >
                        <img
                          src={src}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '12px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
            }}>
              Delete List?
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '24px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
            }}>
              Are you sure you want to delete "{wishlists.find(l => l.id === showDeleteConfirm)?.name}"? This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  cursor: 'pointer',
                  color: '#666',
                  fontWeight: '400',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#666';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  cursor: 'pointer',
                  fontWeight: '400',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Product Card Component
function ProductCard({ product, dimensions, brandCharcoal, isFavorite, onToggleFavorite, onAddToList, wishlists = [], hideActions = false, useCardBackground = true, compactInfo = false }) {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListQuantity, setNewListQuantity] = useState(1);
  const [creatingList, setCreatingList] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const saveModalRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  
  // Check if product is in any list (support both old and new format)
  const isInList = wishlists.some(list => 
    (list.products && list.products.some(p => p.productId === product.id)) ||
    (list.productIds && list.productIds.includes(product.id))
  );
  
  // Detect mobile for tooltip display
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 769);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!showSaveModal || !saveModalRef.current) return;
    const focusables = getFocusableElements(saveModalRef.current);
    if (focusables.length > 0) {
      focusables[0].focus();
    }
  }, [showSaveModal, showListPicker]);

  const getFocusableElements = (container) => {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.disabled && el.offsetParent !== null);
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 2400);
  };

  const handleSaveModalKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowSaveModal(false);
      setShowListPicker(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = getFocusableElements(saveModalRef.current);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleFavoriteToggle = async () => {
    if (!onToggleFavorite) return;
    try {
      await onToggleFavorite(product.id, !isFavorite);
      showToastMessage(!isFavorite ? 'Saved to Favorites' : 'Removed from Favorites');
    } catch (error) {
      console.error('Failed to update favorite', error);
    }
  };

  const handleListToggle = async (list) => {
    if (!onAddToList) return;
    const currentlyInList =
      (list.products && list.products.some((p) => p.productId === product.id)) ||
      (list.productIds && list.productIds.includes(product.id));
    try {
      await onAddToList(list.id, product.id, !currentlyInList, null, 1);
      showToastMessage(
        !currentlyInList ? `Saved to ${list.name}` : `Removed from ${list.name}`
      );
    } catch (error) {
      console.error('Failed to update list', error);
    }
  };

  const handleCreateList = async () => {
    const trimmedName = newListName.trim();
    if (!trimmedName || !onAddToList) return;
    setCreatingList(true);
    try {
      await onAddToList(null, product.id, true, trimmedName, newListQuantity);
      showToastMessage(`Saved to ${trimmedName}`);
      setNewListName('');
      setNewListQuantity(1);
      setShowListPicker(false);
    } catch (error) {
      console.error('Failed to create list', error);
    } finally {
      setCreatingList(false);
    }
  };

  const isSaved = isFavorite || isInList;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: useCardBackground ? 'white' : 'transparent',
        borderRadius: '0',
        overflow: 'hidden',
        transition: 'transform 0.2s ease',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Product Image */}
      {product.imageUrl && (
        <div style={{
          width: '100%',
          paddingTop: '100%',
          position: 'relative',
          backgroundColor: '#fafaf8',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img
            src={product.imageUrl}
            alt={product.imageAlt || product.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          
        </div>
      )}
      
      {/* Product Info - Left Aligned */}
      <div style={{
        padding: compactInfo ? '12px 0 0 0' : '20px 0 0 0',
        display: 'flex',
        flexDirection: 'column',
        gap: compactInfo ? '4px' : '6px'
      }}>
        {/* Product Name - Black, Smaller, Left-Aligned */}
        <div style={{
          fontSize: '14px',
          fontWeight: '400',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          color: '#000000',
          lineHeight: '1.4',
          textAlign: 'left'
        }}>
          {product.title}
        </div>

        {!hideActions && (
          <button
            type="button"
            onClick={() => {
              setShowSaveModal(true);
              setShowListPicker(false);
            }}
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '999px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'transparent',
              fontSize: '11px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#1a1a1a',
              cursor: 'pointer'
            }}
            aria-label={isSaved ? 'Saved' : 'Save'}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a1a1a"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
            {isSaved ? 'Saved' : 'Save'}
          </button>
        )}
        
        {/* Dimensions - Lighter Gray, Smaller, Left-Aligned */}
        {dimensions && (
          <div style={{
            fontSize: '11px',
            fontWeight: '400',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            color: '#999',
            lineHeight: '1.5',
            textAlign: 'left'
          }}>
            {isMobile ? dimensions : `Dimensions: ${dimensions}`}
          </div>
        )}
        
        {!dimensions && (
          <div style={{
            fontSize: '11px',
            fontWeight: '400',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            color: '#999',
            lineHeight: '1.5',
            textAlign: 'left',
            fontStyle: 'italic'
          }}>
            {isMobile ? '—' : 'Dimensions: —'}
          </div>
        )}
      </div>

      {showSaveModal && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowSaveModal(false);
            setShowListPicker(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            zIndex: 2000,
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center'
          }}
        >
          <div
            ref={saveModalRef}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={handleSaveModalKeyDown}
            style={{
              width: '100%',
              maxWidth: isMobile ? '100%' : '420px',
              backgroundColor: '#FAF8F3',
              borderRadius: isMobile ? '20px 20px 0 0' : '14px',
              padding: '20px',
              maxHeight: isMobile ? '88vh' : '80vh',
              overflowY: 'auto',
              boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                color: '#1a1a1a'
              }}>
                Save
              </div>
              <button
                type="button"
                aria-label="Close save"
                onClick={() => {
                  setShowSaveModal(false);
                  setShowListPicker(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#6b6b6b',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              borderTop: '1px solid #e5e7eb',
              paddingTop: '14px'
            }}>
              <button
                type="button"
                onClick={handleFavoriteToggle}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#1a1a1a',
                  cursor: 'pointer'
                }}
              >
                {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                {isFavorite && (
                  <span style={{
                    fontSize: '10px',
                    color: '#6b6b6b',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}>
                    Saved
                  </span>
                )}
              </button>
            </div>

            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setShowListPicker((prev) => !prev)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#1a1a1a',
                  cursor: 'pointer'
                }}
              >
                Add to Project List
                <span style={{ fontSize: '14px', color: '#6b6b6b' }}>
                  {showListPicker ? '–' : '+'}
                </span>
              </button>

              {showListPicker && (
                <div style={{
                  marginTop: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {wishlists.length > 0 ? (
                    wishlists.map((list) => {
                      const listHasProduct =
                        (list.products && list.products.some((p) => p.productId === product.id)) ||
                        (list.productIds && list.productIds.includes(product.id));
                      return (
                        <button
                          key={list.id}
                          type="button"
                          onClick={() => handleListToggle(list)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: listHasProduct ? '#fafaf8' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            color: '#1a1a1a',
                            cursor: 'pointer'
                          }}
                        >
                          <span style={{ textAlign: 'left' }}>{list.name}</span>
                          {listHasProduct && (
                            <span style={{
                              fontSize: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              color: '#6b6b6b'
                            }}>
                              Saved
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div style={{
                      fontSize: '12px',
                      color: '#6b6b6b',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      lineHeight: '1.5'
                    }}>
                      No project lists yet. Create one to save this item.
                    </div>
                  )}

                  <div style={{
                    marginTop: '4px',
                    paddingTop: '8px',
                    borderTop: '1px solid #efece6'
                  }}>
                    <div style={{
                      fontSize: '10px',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                      color: '#6b6b6b',
                      marginBottom: '8px'
                    }}>
                      New List
                    </div>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(event) => setNewListName(event.target.value)}
                      placeholder="List name"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e8e8e3',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        backgroundColor: 'white',
                        marginBottom: '8px'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        value={newListQuantity}
                        onChange={(event) => setNewListQuantity(Math.max(1, parseInt(event.target.value) || 1))}
                        style={{
                          width: '70px',
                          padding: '8px 10px',
                          border: '1px solid #e8e8e3',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          backgroundColor: 'white',
                          textAlign: 'center'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCreateList}
                        disabled={!newListName.trim() || creatingList}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: 'none',
                          backgroundColor: '#1a1a1a',
                          color: 'white',
                          fontSize: '11px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          cursor: creatingList || !newListName.trim() ? 'not-allowed' : 'pointer',
                          opacity: creatingList || !newListName.trim() ? 0.6 : 1
                        }}
                      >
                        {creatingList ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '24px',
            transform: 'translateX(-50%)',
            backgroundColor: '#1a1a1a',
            color: 'white',
            padding: '10px 16px',
            borderRadius: '999px',
            fontSize: '12px',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            letterSpacing: '0.02em',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            zIndex: 3000
          }}
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
}

// Product Grid Component
function ProductGrid({ products, allProducts, dimensionsMap, loading, error, searchQuery, setSearchQuery, activeCategory, activeSubcategory, formatLabel, loadingAllProducts, favorites = [], wishlists = [], showFavoritesOnly = false, onToggleFavorite, onAddToList, isMobile = false }) {
  // Filter products based on search query and favorites filter
  const filteredProducts = useMemo(() => {
    // If showing favorites only, use allProducts (across all categories)
    // Otherwise, use category-filtered products or allProducts if searching
    let productsToFilter = showFavoritesOnly && allProducts.length > 0 
      ? allProducts 
      : (searchQuery.trim() && allProducts.length > 0 ? allProducts : products);
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const categoryLabel = formatLabel ? formatLabel(activeCategory).toLowerCase() : '';
      const subcategoryLabel = (activeSubcategory && activeSubcategory !== 'show-all' && formatLabel) 
        ? formatLabel(activeSubcategory).toLowerCase() 
        : '';
      
      productsToFilter = productsToFilter.filter(product => {
        const title = (product.title || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const handle = (product.handle || '').toLowerCase();
        
        return title.includes(query) || 
               description.includes(query) || 
               handle.includes(query) ||
               categoryLabel.includes(query) ||
               subcategoryLabel.includes(query);
      });
    }
    
    // Apply product type filter when subcategory is a product type (not a collection)
    const productTypeFilters = PRODUCT_TYPE_FILTERS[activeCategory]?.[activeSubcategory];
    if (productTypeFilters && productTypeFilters.length > 0) {
      productsToFilter = productsToFilter.filter(product => {
        const productType = (product.productType || '').toLowerCase();
        return productTypeFilters.some(type => productType === type.toLowerCase());
      });
    }
    
    // Apply favorites filter (show all favorited products across all categories)
    if (showFavoritesOnly) {
      productsToFilter = productsToFilter.filter(product => favorites.includes(product.id));
    }
    
    return productsToFilter;
  }, [products, allProducts, searchQuery, activeCategory, activeSubcategory, formatLabel, favorites, showFavoritesOnly]);

  if (loading || ((searchQuery.trim() || showFavoritesOnly) && loadingAllProducts && allProducts.length === 0)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        fontSize: '14px',
        color: '#666',
        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
      }}>
        {(searchQuery.trim() || showFavoritesOnly) && loadingAllProducts ? 'Loading all products...' : 'Loading products...'}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        fontSize: '14px',
        color: '#dc2626',
        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
      }}>
        {error}
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        gap: '16px'
      }}>
        <div style={{
          fontSize: '14px',
          color: '#666',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
        }}>
          {searchQuery ? 'No results found' : 'No products found in this category.'}
        </div>
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fafaf8',
              border: '1px solid #e8e8e3',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
              color: '#000',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fafaf8';
            }}
          >
            Clear Search
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : (showFavoritesOnly ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))'),
      gap: isMobile ? '24px 16px' : '40px 32px',
      marginTop: '0'
    }}>
      {filteredProducts.map((product) => {
        const dimensions = catalogDimensionsService.getDimensionForProduct(product.title, dimensionsMap);
        const isFavorite = favorites.includes(product.id);
        return (
          <ProductCard
            key={product.id}
            product={product}
            dimensions={dimensions}
            brandCharcoal="#000000"
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
            onAddToList={onAddToList}
            wishlists={wishlists}
          />
        );
      })}
    </div>
  );
}

// Search Bar Component
function SearchBar({ searchQuery, setSearchQuery, isSticky, isMobile }) {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Sync local query with external searchQuery when cleared externally
    if (!searchQuery && localQuery) {
      setLocalQuery('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [searchQuery]);

  useEffect(() => {
    // Debounce search input
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(localQuery);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localQuery, setSearchQuery]);

  const handleClear = () => {
    setLocalQuery('');
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const baseStyles = {
    marginBottom: '0',
    display: 'block',
    width: '100%'
  };
  
  const stickyStyles = isSticky && !isMobile ? {
    position: 'sticky',
    top: '200px', // Below category nav (120px header + ~80px category nav height)
    zIndex: 99,
    backgroundColor: 'white',
    paddingTop: '16px',
    paddingBottom: '16px',
    marginTop: '-16px',
    marginBottom: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
  } : {
    position: 'relative'
  };
  
  return (
    <div style={{
      ...baseStyles,
      ...stickyStyles
    }}>
      <input
        ref={inputRef}
        type="text"
        value={localQuery}
        onChange={(e) => setLocalQuery(e.target.value)}
        placeholder="Search"
        style={{
          width: '100%',
          padding: '12px 16px',
          paddingRight: localQuery ? '40px' : '16px',
          fontSize: '13px',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          color: '#000',
          border: '1px solid #e8e8e3',
          borderRadius: '4px',
          lineHeight: '1.4',
          height: '44px',
          boxSizing: 'border-box',
          backgroundColor: 'white',
          outline: 'none',
          transition: 'border-color 0.2s ease'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#000';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#e8e8e3';
        }}
      />
      {localQuery && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            fontSize: '18px',
            color: '#999',
            cursor: 'pointer',
            padding: '4px',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#999';
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// Primary Category Navigation Component
function CategoryNav({ categories, activeCategory, onCategoryClick, formatLabel, brandCharcoal, isSticky, isMobile, hasSubcategories }) {
  const baseStyles = {
    display: 'flex',
    gap: '6px',
    marginBottom: '32px',
    paddingBottom: '16px',
    paddingLeft: '0',
    borderBottom: 'none',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: 'transparent',
    justifyContent: isMobile ? 'flex-start' : 'center',
    width: '100%'
  };
  
  const stickyStyles = isSticky && !isMobile ? {
    position: 'sticky',
    top: '120px', // Below header (56px header + 48px nav + 16px spacing)
    zIndex: 100,
    paddingTop: '24px',
    marginTop: '-24px',
    marginBottom: '24px',
    backgroundColor: 'transparent',
    boxShadow: 'none'
  } : {};
  
  return (
    <div className="products-primary-nav" style={{
      ...baseStyles,
      ...stickyStyles
    }}>
      {categories.map((categoryHandle) => (
        <button
          key={categoryHandle}
          onClick={() => onCategoryClick(categoryHandle)}
          style={{
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            borderBottom: activeCategory === categoryHandle ? `2px solid ${brandCharcoal}` : '2px solid transparent',
            fontSize: '11px',
            fontWeight: activeCategory === categoryHandle ? '600' : '400',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: activeCategory === categoryHandle ? '#000000' : '#666',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
            backgroundColor: 'transparent',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            if (activeCategory !== categoryHandle) {
              e.currentTarget.style.color = '#000000';
            }
          }}
          onMouseLeave={(e) => {
            if (activeCategory !== categoryHandle) {
              e.currentTarget.style.color = '#666';
            }
          }}
        >
          {formatLabel(categoryHandle)}
        </button>
      ))}
    </div>
  );
}

// Filters Rail Component (Sticky on Desktop)
function FiltersRail({ subcategories, activeSubcategory, onSubcategoryClick, formatLabel, brandCharcoal, isMobile, isOpen, onToggle, isSticky }) {
  const content = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }}>
      <button
        onClick={() => onSubcategoryClick('show-all')}
        style={{
          padding: '12px 16px',
          background: activeSubcategory === 'show-all' ? '#fafaf8' : 'transparent',
          border: 'none',
          textAlign: 'left',
          fontSize: '13px',
          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
          color: activeSubcategory === 'show-all' ? brandCharcoal : '#666',
          fontWeight: activeSubcategory === 'show-all' ? '500' : '400',
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          borderLeft: activeSubcategory === 'show-all' ? `3px solid ${brandCharcoal}` : '3px solid transparent'
        }}
        onMouseEnter={(e) => {
          if (activeSubcategory !== 'show-all') {
            e.currentTarget.style.backgroundColor = '#f5f5f2';
          }
        }}
        onMouseLeave={(e) => {
          if (activeSubcategory !== 'show-all') {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        Show All
      </button>
      {subcategories.map((subcategoryHandle) => (
        <button
          key={subcategoryHandle}
          onClick={() => onSubcategoryClick(subcategoryHandle)}
          style={{
            padding: '12px 16px',
            background: activeSubcategory === subcategoryHandle ? '#fafaf8' : 'transparent',
            border: 'none',
            textAlign: 'left',
            fontSize: '13px',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            color: activeSubcategory === subcategoryHandle ? brandCharcoal : '#666',
            fontWeight: activeSubcategory === subcategoryHandle ? '500' : '400',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            borderLeft: activeSubcategory === subcategoryHandle ? `3px solid ${brandCharcoal}` : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeSubcategory !== subcategoryHandle) {
              e.currentTarget.style.backgroundColor = '#f5f5f2';
            }
          }}
          onMouseLeave={(e) => {
            if (activeSubcategory !== subcategoryHandle) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {formatLabel(subcategoryHandle)}
        </button>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          onClick={onToggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            marginBottom: '24px',
            backgroundColor: '#fafaf8',
            border: '1px solid #e8e8e3',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            fontWeight: '500',
            color: brandCharcoal,
            cursor: 'pointer',
            width: '100%',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          <span>Filter by Type</span>
          <span style={{ marginLeft: 'auto' }}>{isOpen ? '−' : '+'}</span>
        </button>
        {isOpen && (
          <div style={{
            marginBottom: '32px',
            padding: '16px',
            backgroundColor: '#fafaf8',
            borderRadius: '4px',
            border: '1px solid #e8e8e3'
          }}>
            {content}
          </div>
        )}
      </>
    );
  }

  const baseStyles = {
    minWidth: '200px',
    paddingTop: '8px',
    alignSelf: 'flex-start'
  };
  
  const stickyStyles = isSticky && !isMobile ? {
    position: 'sticky',
    top: '200px', // Below category nav (same as search bar)
    zIndex: 98,
    backgroundColor: 'white',
    paddingBottom: '24px',
    maxHeight: 'calc(100vh - 220px)',
    overflowY: 'auto'
  } : {};
  
  return (
    <div className="products-subcategory-menu" style={{
      ...baseStyles,
      ...stickyStyles
    }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: brandCharcoal,
        marginBottom: '16px'
      }}>
        Filter by Type
      </div>
      {content}
    </div>
  );
}

// Routing hook for URL-based navigation
function useProductsRoute() {
  const getInitialRoute = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash && hash.startsWith('/products')) {
      return hash;
    }
    // Check if we have URL params indicating products view
    const params = new URLSearchParams(window.location.search);
    if (params.has('category') || params.has('type') || params.has('q')) {
      return '/products';
    }
    return '/products';
  };

  const [route, setRoute] = useState(getInitialRoute());
  
  const getCatalogState = () => {
    const params = new URLSearchParams(window.location.search);
    const saved = localStorage.getItem('productsCatalogState');
    const parsed = saved ? JSON.parse(saved) : {};
    return {
      category: params.get('category') || parsed.category || 'new-releases',
      type: params.get('type') || parsed.type || 'show-all',
      search: params.get('q') || parsed.search || ''
    };
  };

  const [catalogState, setCatalogState] = useState(getCatalogState());

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || '/products';
      const newRoute = hash.startsWith('/products') ? hash : '/products';
      setRoute(newRoute);
    };
    
    // Initialize hash if not present (avoid extra hashchange render)
    if (!window.location.hash || !window.location.hash.startsWith('#/products')) {
      try {
        const nextUrl = `${window.location.pathname}${window.location.search}#/products`;
        window.history.replaceState(null, '', nextUrl);
      } catch (error) {
        window.location.hash = '/products';
      }
    }
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = useCallback((path, state = null) => {
    // Save catalog state before navigating away from products view
    if (route === '/products' || route.startsWith('/products?')) {
      if (state) {
        const newState = { ...catalogState, ...state };
        setCatalogState(newState);
        localStorage.setItem('productsCatalogState', JSON.stringify(newState));
        // Update URL params for products view
        const params = new URLSearchParams();
        if (newState.category) params.set('category', newState.category);
        if (newState.type && newState.type !== 'show-all') params.set('type', newState.type);
        if (newState.search) params.set('q', newState.search);
        const queryString = params.toString();
        const newPath = queryString ? `${path}?${queryString}` : path;
        window.location.hash = newPath;
        setRoute(newPath);
        return;
      }
    }
    
    // For non-products routes, clear query params
    window.location.hash = path;
    setRoute(path);
  }, [route, catalogState]);

  const restoreCatalogState = useCallback(() => {
    const saved = localStorage.getItem('productsCatalogState');
    if (saved) {
      const parsed = JSON.parse(saved);
      setCatalogState(parsed);
      return parsed;
    }
    const current = getCatalogState();
    setCatalogState(current);
    return current;
  }, []);

  return { route, navigate, catalogState, setCatalogState, restoreCatalogState };
}

export function ProductsSection({ brandCharcoal = '#2C2C2C', setActiveSection, showAlert }) {
  const { route, navigate, catalogState, setCatalogState, restoreCatalogState } = useProductsRoute();
  
  // Derive view mode from route
  const isCatalogView = route === '/products' || route.startsWith('/products?');
  const isFavoritesView = route === '/products/favorites';
  const isListsIndexView = route === '/products/lists';
  const isListDetailView = route.startsWith('/products/lists/');
  const selectedListId = isListDetailView ? route.split('/products/lists/')[1] : null;
  
  // Determine active tab for sub-tabs
  const activeTab = isCatalogView ? 'products' : 
                   isListsIndexView || isListDetailView ? 'lists' : 
                   isFavoritesView ? 'favorites' : 'products';
  
  // Handle tab navigation
  const handleTabChange = (tabRoute) => {
    navigate(tabRoute);
  };
  
  const [activePrimaryCategory, setActivePrimaryCategory] = useState(catalogState.category || 'new-releases');
  const [activeSubcategory, setActiveSubcategory] = useState(catalogState.type || 'show-all');
  const [products, setProducts] = useState([]); // Category-filtered products
  const [allProducts, setAllProducts] = useState([]); // All products for search
  const [dimensionsMap, setDimensionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(catalogState.search || '');
  const [favoritesSearchQuery, setFavoritesSearchQuery] = useState('');
  const [listsSearchQuery, setListsSearchQuery] = useState('');
  const [listDetailSearchQuery, setListDetailSearchQuery] = useState('');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 769);
  const skipInitialCatalogSyncRef = useRef(false);
  const [mobileFilterCategory, setMobileFilterCategory] = useState('new-releases');
  const [mobileFilterSubcategory, setMobileFilterSubcategory] = useState('show-all');
  const [mobileFilterExpanded, setMobileFilterExpanded] = useState('new-releases');
  const mobileFilterRef = useRef(null);
  
  // Favorites state
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  // Wishlists state
  const [wishlists, setWishlists] = useState([]);
  const [loadingWishlists, setLoadingWishlists] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 769);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (isMobileFilterOpen) {
      setMobileFilterCategory(activePrimaryCategory);
      setMobileFilterSubcategory(activeSubcategory || 'show-all');
      setMobileFilterExpanded(activePrimaryCategory);
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobileFilterOpen, isMobile, activePrimaryCategory, activeSubcategory]);

  useEffect(() => {
    if (!isMobileFilterOpen || !mobileFilterRef.current) return;
    const focusable = mobileFilterRef.current.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      focusable.focus();
    }
  }, [isMobileFilterOpen]);

  // Fetch dimensions on mount
  useEffect(() => {
    catalogDimensionsService.getDimensions().then(dims => {
      setDimensionsMap(dims);
    });
  }, []);

  // Fetch favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoadingFavorites(true);
        const favs = await favoritesService.getFavorites();
        setFavorites(favs);
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoadingFavorites(false);
      }
    };
    fetchFavorites();
  }, []);

  // Fetch wishlists on mount
  useEffect(() => {
    const fetchWishlists = async () => {
      try {
        setLoadingWishlists(true);
        const lists = await wishlistsService.getWishlists();
        setWishlists(lists);
      } catch (err) {
        console.error('Error fetching wishlists:', err);
      } finally {
        setLoadingWishlists(false);
      }
    };
    fetchWishlists();
  }, []);

  // Handle favorite toggle
  const handleToggleFavorite = async (productId, isFavorite) => {
    // Optimistic update
    const previousFavorites = [...favorites];
    if (isFavorite) {
      setFavorites([...favorites, productId]);
    } else {
      setFavorites(favorites.filter(id => id !== productId));
    }

    try {
      console.log('[Favorites] Toggling favorite:', { productId, isFavorite });
      const updatedFavorites = await favoritesService.toggleFavorite(productId, isFavorite);
      console.log('[Favorites] Updated favorites:', updatedFavorites);
      setFavorites(updatedFavorites);
    } catch (err) {
      console.error('[Favorites] Error toggling favorite:', err);
      console.error('[Favorites] Error details:', {
        message: err.message,
        stack: err.stack,
        productId,
        isFavorite
      });
      // Rollback on error
      setFavorites(previousFavorites);
      // Show user-friendly error (optional - you could add a toast notification here)
      alert('Failed to save favorite. Please try again.');
    }
  };

  // Handle add/remove product from wishlist
  const handleAddToList = async (listId, productId, add, newListName = null, quantity = 1) => {
    try {
      if (newListName) {
        // Create new list and add product with quantity
        const newList = await wishlistsService.createWishlist(newListName);
        await wishlistsService.updateWishlistProducts(newList.id, productId, true, quantity);
        // Refresh wishlists
        const updatedLists = await wishlistsService.getWishlists();
        setWishlists(updatedLists);
      } else {
        // Update existing list
        await wishlistsService.updateWishlistProducts(listId, productId, add, quantity);
        // Refresh wishlists
        const updatedLists = await wishlistsService.getWishlists();
        setWishlists(updatedLists);
      }
    } catch (err) {
      console.error('Error updating wishlist:', err);
      throw err;
    }
  };

  // Restore catalog state when returning to products view
  useEffect(() => {
    if (isCatalogView) {
      skipInitialCatalogSyncRef.current = true;
      const restored = restoreCatalogState();
      setActivePrimaryCategory(restored.category || 'new-releases');
      setActiveSubcategory(restored.type || 'show-all');
      setSearchQuery(restored.search || '');
    }
  }, [isCatalogView]);

  // Fetch all products for search, favorites view, or wishlists view (lazy load when needed)
  useEffect(() => {
    if ((searchQuery.trim() || isFavoritesView || isListsIndexView || isListDetailView) && allProducts.length === 0 && !loadingAllProducts) {
      const fetchAllProducts = async () => {
        setLoadingAllProducts(true);
        try {
          const primaryCategories = Object.keys(COLLECTION_MAP);
          const allProductsPromises = [];
          
          // Fetch from all primary categories
          for (const categoryHandle of primaryCategories) {
            allProductsPromises.push(shopifyService.getProductsByCollection(categoryHandle));
            
            // Also fetch from subcategories
            const subcategories = COLLECTION_MAP[categoryHandle] || [];
            for (const subcategoryHandle of subcategories) {
              const isProductTypeSubcategory = PRODUCT_TYPE_FILTERS[categoryHandle]?.[subcategoryHandle];
              if (!isProductTypeSubcategory) {
                allProductsPromises.push(shopifyService.getProductsByCollection(subcategoryHandle));
              }
            }
          }
          
          const allProductsArrays = await Promise.all(allProductsPromises);
          // Flatten and deduplicate by product ID
          const productsMap = new Map();
          allProductsArrays.flat().forEach(product => {
            if (!productsMap.has(product.id)) {
              productsMap.set(product.id, product);
            }
          });
          
          setAllProducts(Array.from(productsMap.values()));
        } catch (err) {
          console.error('Error fetching all products:', err);
        } finally {
          setLoadingAllProducts(false);
        }
      };
      
      fetchAllProducts();
    }
  }, [searchQuery, isFavoritesView, isListsIndexView, isListDetailView, allProducts.length, loadingAllProducts]);

  // Update catalog state when search query changes (only on catalog view)
  // Debounce search updates to avoid too many navigations
  useEffect(() => {
    if (!isCatalogView) return;
    if (skipInitialCatalogSyncRef.current) {
      skipInitialCatalogSyncRef.current = false;
      return;
    }
    
    const timeoutId = setTimeout(() => {
      if (searchQuery !== catalogState.search) {
        setCatalogState(prev => ({ ...prev, search: searchQuery }));
        localStorage.setItem('productsCatalogState', JSON.stringify({ 
          category: activePrimaryCategory, 
          type: activeSubcategory, 
          search: searchQuery 
        }));
        // Update URL params - this will exit landing state if we're in it
        const params = new URLSearchParams();
        params.set('category', activePrimaryCategory);
        if (activeSubcategory !== 'show-all') params.set('type', activeSubcategory);
        if (searchQuery) params.set('q', searchQuery);
        const queryString = params.toString();
        const newPath = queryString ? `/products?${queryString}` : '/products';
        window.location.hash = newPath;
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isCatalogView, activePrimaryCategory, activeSubcategory, catalogState.search]);

  // Fetch products when category changes (only on catalog view)
  useEffect(() => {
    if (!isCatalogView) return;
    
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let collectionHandle = activePrimaryCategory;
        const isProductTypeSubcategory = PRODUCT_TYPE_FILTERS[activePrimaryCategory]?.[activeSubcategory];
        
        // If a subcategory is selected (and not "show-all"), use subcategory
        if (!isProductTypeSubcategory && activeSubcategory !== 'show-all' && COLLECTION_MAP[activePrimaryCategory]?.includes(activeSubcategory)) {
          collectionHandle = activeSubcategory;
        }
        
        const fetchedProducts = await shopifyService.getProductsByCollection(collectionHandle);
        setProducts(fetchedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [activePrimaryCategory, activeSubcategory, isCatalogView]);

  const handlePrimaryCategoryClick = (categoryHandle) => {
    setActivePrimaryCategory(categoryHandle);
    setActiveSubcategory('show-all'); // Reset to "Show All"
    setIsMobileFilterOpen(false); // Close mobile filter
    // Navigate to products view and save state
    navigate('/products', { category: categoryHandle, type: 'show-all', search: searchQuery });
  };

  const handleSubcategoryClick = (subcategoryHandle) => {
    setActiveSubcategory(subcategoryHandle);
    if (isMobile) {
      setIsMobileFilterOpen(false); // Close mobile filter after selection
    }
    // Navigate to products view and save state
    navigate('/products', { category: activePrimaryCategory, type: subcategoryHandle, search: searchQuery });
  };

  const handleApplyMobileFilters = () => {
    setActivePrimaryCategory(mobileFilterCategory);
    setActiveSubcategory(mobileFilterSubcategory);
    setIsMobileFilterOpen(false);
    navigate('/products', { 
      category: mobileFilterCategory, 
      type: mobileFilterSubcategory, 
      search: searchQuery 
    });
  };

  const handleResetMobileFilters = () => {
    setMobileFilterCategory('new-releases');
    setMobileFilterSubcategory('show-all');
    setMobileFilterExpanded('new-releases');
  };

  const handleDrawerKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsMobileFilterOpen(false);
      return;
    }
    if (e.key !== 'Tab' || !mobileFilterRef.current) return;
    const focusables = Array.from(
      mobileFilterRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.hasAttribute('disabled'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const primaryCategories = Object.keys(COLLECTION_MAP);
  const subcategories = COLLECTION_MAP[activePrimaryCategory] || [];
  const hasSubcategories = subcategories.length > 0;

  // Format category label for display
  const formatCategoryLabel = (handle) => {
    const labelOverrides = {
      "bars": "Bars & Bar Backs"
    };
    if (labelOverrides[handle]) return labelOverrides[handle];
    return handle
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="products-section-container" style={{ backgroundColor: 'white', minHeight: '100vh', overflow: 'visible' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .products-banner {
          width: 100vw !important;
          max-width: 100vw !important;
          margin-left: calc(-50vw + 50%) !important;
          margin-right: calc(-50vw + 50%) !important;
        }
        @media (min-width: 769px) {
          .products-banner {
            margin-top: -100px !important;
          }
          /* Ensure parent containers don't break sticky positioning */
          .products-section-container {
            overflow: visible !important;
          }
          /* Force sticky positioning to work - override any conflicting styles */
          .products-section-container .products-primary-nav {
            position: sticky !important;
            top: 120px !important;
            z-index: 100 !important;
          }
          .products-section-container .products-subcategory-menu {
            position: sticky !important;
            top: 200px !important;
            z-index: 98 !important;
          }
          .products-section-container .products-search-bar-wrapper {
            position: sticky !important;
            top: 200px !important;
            z-index: 99 !important;
            background-color: white !important;
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            margin-top: -16px !important;
            margin-bottom: 16px !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
          }
        }
        @media (max-width: 768px) {
          .products-banner {
            height: 400px !important;
          }
          .products-primary-nav {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            justify-content: flex-start !important;
            gap: 4px !important;
            padding-bottom: 12px !important;
          }
          .products-primary-nav::-webkit-scrollbar {
            display: none;
          }
          .products-primary-nav button {
            padding: 6px 12px !important;
            font-size: 10px !important;
            letter-spacing: 0.04em !important;
          }
          .products-search-bar-wrapper {
            margin-bottom: 16px !important;
          }
          .products-search-bar-wrapper > div {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          .products-search-bar-wrapper > div > div {
            max-width: 100% !important;
          }
          .products-two-column {
            flex-direction: column !important;
            gap: 32px !important;
          }
          .products-two-column > div {
            max-width: 100% !important;
            margin-left: 0 !important;
          }
          .products-subcategory-menu {
            max-width: 100% !important;
            min-width: 0 !important;
          }
        }
      ` }} />

      {/* Hero Banner - Show on all products-related pages */}
      <div className="products-banner" style={{
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        marginTop: '-48px',
        marginBottom: '64px',
        height: '500px',
        borderRadius: '0',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        backgroundImage: "url('/products-banner.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))'
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          zIndex: 1
        }}>
          <div style={{
            fontSize: '50px',
            fontWeight: '300',
            fontFamily: "'Domaine Text', serif",
            color: 'white',
            marginBottom: '20px',
            textAlign: 'center',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            Products
          </div>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.6)',
            margin: '0 auto 24px'
          }} />
          <div style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
            textAlign: 'center',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            Explore our full rental catalog, save your favorite products, and create project wishlists
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 200px' }}>
        {/* CATALOG VIEW: Show category nav, filter rail, and catalog toolbar */}
        {isCatalogView && (
          <>
            {/* Primary Category Navigation - Desktop Only */}
            {!isMobile && (
              <CategoryNav
                categories={primaryCategories}
                activeCategory={activePrimaryCategory}
                onCategoryClick={handlePrimaryCategoryClick}
                formatLabel={formatCategoryLabel}
                brandCharcoal={brandCharcoal}
                isSticky={true}
                isMobile={isMobile}
                hasSubcategories={hasSubcategories}
              />
            )}

            <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }} className="products-two-column">
              {/* Filters Rail (Desktop Only) */}
              {!isMobile && (
                <FiltersRail
                  subcategories={subcategories}
                  activeSubcategory={activeSubcategory}
                  onSubcategoryClick={handleSubcategoryClick}
                  formatLabel={formatCategoryLabel}
                  brandCharcoal={brandCharcoal}
                  isMobile={isMobile}
                  isOpen={isMobileFilterOpen}
                  onToggle={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                  isSticky={true}
                />
              )}

              {/* Product Grid with Search */}
              <div className="products-content-wrapper" style={{
                flex: 1,
                paddingLeft: !isMobile ? '0' : '0'
              }}>
                {/* Mobile Top Controls */}
                {isMobile ? (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <SearchBar 
                        searchQuery={searchQuery} 
                        setSearchQuery={setSearchQuery} 
                        isSticky={false} 
                        isMobile={true}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsMobileFilterOpen(true)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#fafaf8',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <span>Filter</span>
                      <span style={{ fontSize: '14px', lineHeight: '1' }}>+</span>
                    </button>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          navigate('/products/favorites');
                          if (favorites.length === 0) {
                            favoritesService.getFavorites().then(favs => {
                              setFavorites(favs);
                            });
                          }
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '999px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          fontSize: '11px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                        My Favorites
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/products/lists')}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '999px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          fontSize: '11px',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                          <circle cx="17" cy="7" r="3.5" fill="#111" stroke="none" />
                          <line x1="17" y1="5.5" x2="17" y2="8.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                          <line x1="15.5" y1="7" x2="18.5" y2="7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                        My Lists ({wishlists.length})
                      </button>
                    </div>
                  </div>
                ) : (
                  <CatalogToolbar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    favorites={favorites}
                    wishlists={wishlists}
                    onNavigateToFavorites={() => {
                      navigate('/products/favorites');
                      if (favorites.length === 0) {
                        favoritesService.getFavorites().then(favs => {
                          setFavorites(favs);
                        });
                      }
                    }}
                    onNavigateToLists={() => navigate('/products/lists')}
                    isMobile={isMobile}
                  />
                )}

                {/* Mobile Filter Drawer */}
                {isMobile && isMobileFilterOpen && (
                  <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                      position: 'fixed',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.35)',
                      zIndex: 2000,
                      display: 'flex',
                      alignItems: 'flex-end'
                    }}
                    onClick={() => setIsMobileFilterOpen(false)}
                  >
                    <div
                      ref={mobileFilterRef}
                      onKeyDown={handleDrawerKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        backgroundColor: '#FAF8F3',
                        borderTopLeftRadius: '20px',
                        borderTopRightRadius: '20px',
                        padding: '20px 20px 24px',
                        maxHeight: '88vh',
                        overflowY: 'auto',
                        boxShadow: '0 -12px 24px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                      }}>
                        <button
                          type="button"
                          onClick={handleResetMobileFilters}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: '#6b6b6b',
                            cursor: 'pointer'
                          }}
                        >
                          Reset
                        </button>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          color: '#1a1a1a'
                        }}>
                          Filter
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsMobileFilterOpen(false)}
                          aria-label="Close filter"
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            color: '#6b6b6b',
                            cursor: 'pointer',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div style={{
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: '16px'
                      }}>
                        <div style={{
                          fontSize: '10px',
                          fontWeight: '500',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                          color: '#000',
                          marginBottom: '12px'
                        }}>
                          Categories
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {primaryCategories.map((categoryHandle) => {
                            const isExpanded = mobileFilterExpanded === categoryHandle;
                            const isActiveCategory = mobileFilterCategory === categoryHandle;
                            const categorySubcategories = COLLECTION_MAP[categoryHandle] || [];
                            return (
                              <div key={categoryHandle} style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '10px',
                                backgroundColor: 'white'
                              }}>
                                <button
                                  type="button"
                                  onClick={() => setMobileFilterExpanded(isExpanded ? '' : categoryHandle)}
                                  style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                                    color: isActiveCategory ? '#000' : '#555',
                                    fontWeight: isActiveCategory ? '600' : '500',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {formatCategoryLabel(categoryHandle)}
                                  <span style={{ fontSize: '14px' }}>{isExpanded ? '–' : '+'}</span>
                                </button>
                                {isExpanded && (
                                  <div style={{ padding: '0 14px 12px' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMobileFilterCategory(categoryHandle);
                                        setMobileFilterSubcategory('show-all');
                                        setActivePrimaryCategory(categoryHandle);
                                        setActiveSubcategory('show-all');
                                        setIsMobileFilterOpen(false);
                                        navigate('/products', { 
                                          category: categoryHandle, 
                                          type: 'show-all', 
                                          search: searchQuery 
                                        });
                                      }}
                                      style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        backgroundColor: isActiveCategory && mobileFilterSubcategory === 'show-all' ? '#fafaf8' : 'transparent',
                                        fontSize: '12px',
                                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                                        color: '#1a1a1a',
                                        cursor: 'pointer',
                                        marginBottom: categorySubcategories.length ? '8px' : '0'
                                      }}
                                    >
                                      Show All
                                    </button>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      {categorySubcategories.map((subcategoryHandle) => {
                                        const isSelected = isActiveCategory && mobileFilterSubcategory === subcategoryHandle;
                                        return (
                                          <button
                                            key={subcategoryHandle}
                                            type="button"
                                            onClick={() => {
                                              setMobileFilterCategory(categoryHandle);
                                            setMobileFilterSubcategory(subcategoryHandle);
                                            setActivePrimaryCategory(categoryHandle);
                                            setActiveSubcategory(subcategoryHandle);
                                            setIsMobileFilterOpen(false);
                                            navigate('/products', { 
                                              category: categoryHandle, 
                                              type: subcategoryHandle, 
                                              search: searchQuery 
                                            });
                                            }}
                                            style={{
                                              width: '100%',
                                              textAlign: 'left',
                                              padding: '8px 10px',
                                              borderRadius: '8px',
                                              border: '1px solid #e5e7eb',
                                              backgroundColor: isSelected ? '#fafaf8' : 'transparent',
                                              fontSize: '12px',
                                              fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                                              color: '#1a1a1a',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            {formatCategoryLabel(subcategoryHandle)}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        marginTop: '20px'
                      }}>
                        <button
                          type="button"
                          onClick={() => setIsMobileFilterOpen(false)}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '12px',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            cursor: 'pointer'
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Product Grid */}
                <ProductGrid
                  products={products}
                  allProducts={allProducts}
                  dimensionsMap={dimensionsMap}
                  loading={loading}
                  error={error}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  activeCategory={activePrimaryCategory}
                  activeSubcategory={activeSubcategory}
                  formatLabel={formatCategoryLabel}
                  loadingAllProducts={loadingAllProducts}
                  favorites={favorites}
                  wishlists={wishlists}
                  showFavoritesOnly={false}
                  onToggleFavorite={handleToggleFavorite}
                  onAddToList={handleAddToList}
                  isMobile={isMobile}
                />
              </div>
            </div>
          </>
        )}

        {/* FAVORITES VIEW: Two-column layout matching Account page */}
        {isFavoritesView && (
          <div style={{ display: 'flex', gap: '64px', marginTop: '0', justifyContent: 'flex-start' }} className="products-two-column">
            {/* Left Column */}
            <div style={{ 
              flex: '0 0 240px', 
              flexShrink: 0,
              ...(!isMobile ? {
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                height: 'fit-content',
                zIndex: 10
              } : {
                position: 'relative'
              })
            }}>
              {/* Title */}
              <h2 style={{ 
                fontSize: '28px', 
                fontWeight: '300', 
                color: '#000000',
                fontFamily: "'Domaine Text', serif",
                marginBottom: '24px',
                marginTop: '0',
                letterSpacing: 'normal',
                textTransform: 'uppercase',
                textAlign: 'left'
              }}>
                Favorites
              </h2>
              
              {/* Description */}
              <div style={{
                fontSize: '14px',
                color: '#666',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                lineHeight: '1.6',
                marginBottom: '32px',
                textAlign: 'left'
              }}>
                A curated space for preferred pieces. Save products for future reference and revisit them as projects take shape.
              </div>
              
              {/* Search Bar */}
              <div style={{ marginBottom: '32px' }}>
                <SearchBar 
                  searchQuery={favoritesSearchQuery} 
                  setSearchQuery={setFavoritesSearchQuery} 
                  isSticky={false} 
                  isMobile={isMobile}
                  placeholder="Search favorites"
                />
              </div>
              
              {/* Navigation Buttons */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px', 
                marginTop: '32px',
                width: '100%'
              }}>
                <button
                  onClick={() => navigate('/products')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #d9d9d1',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  All Products
                </button>
                <button
                  onClick={() => navigate('/products/lists')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #d9d9d1',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Project Lists
                </button>
                <button
                  onClick={() => navigate('/products/favorites')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #000000',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Favorites
                </button>
              </div>
            </div>
            
            {/* Right Column - Product Grid */}
            <div style={{ flex: '1', maxWidth: '900px', marginLeft: isMobile ? '0' : '80px', marginTop: '0', paddingTop: '0' }}>
              {loadingAllProducts && allProducts.length === 0 ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  fontSize: '14px',
                  color: '#666',
                  fontFamily: "'Neue Haas Unica', 'Inter', sans-serif"
                }}>
                  Loading favorites...
                </div>
              ) : (
                <ProductGrid
                  products={[]}
                  allProducts={allProducts}
                  dimensionsMap={dimensionsMap}
                  loading={false}
                  error={null}
                  searchQuery={favoritesSearchQuery}
                  setSearchQuery={setFavoritesSearchQuery}
                  activeCategory={null}
                  activeSubcategory={null}
                  formatLabel={formatCategoryLabel}
                  loadingAllProducts={loadingAllProducts}
                  favorites={favorites}
                  wishlists={wishlists}
                  showFavoritesOnly={true}
                  onToggleFavorite={handleToggleFavorite}
                  onAddToList={handleAddToList}
                  isMobile={isMobile}
                />
              )}
            </div>
          </div>
        )}

        {/* LISTS INDEX VIEW: Two-column layout matching Account page */}
        {isListsIndexView && (
          <div style={{ display: 'flex', gap: '64px', marginTop: '0', justifyContent: 'flex-start' }} className="products-two-column">
            {/* Left Column */}
            <div style={{ 
              flex: '0 0 240px', 
              flexShrink: 0,
              ...(!isMobile ? {
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                height: 'fit-content',
                zIndex: 10
              } : {
                position: 'relative'
              })
            }}>
              {/* Title */}
              <h2 style={{ 
                fontSize: '28px', 
                fontWeight: '300', 
                color: '#000000',
                fontFamily: "'Domaine Text', serif",
                marginBottom: '24px',
                marginTop: '0',
                letterSpacing: 'normal',
                textTransform: 'uppercase',
                textAlign: 'left'
              }}>
                Project Lists
              </h2>
              
              {/* Description */}
              <div style={{
                fontSize: '14px',
                color: '#666',
                fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                lineHeight: '1.6',
                marginBottom: '32px',
                textAlign: 'left'
              }}>
                Wishlist, evolved. Curated collections of products organized by project. Save pieces and submit selections for pricing and proposals.
              </div>
              
              {/* Search Bar */}
              <div style={{ marginBottom: '32px' }}>
                <SearchBar 
                  searchQuery={listsSearchQuery} 
                  setSearchQuery={setListsSearchQuery} 
                  isSticky={false} 
                  isMobile={isMobile}
                  placeholder="Search lists"
                />
              </div>
              
              {/* Navigation Buttons */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px', 
                marginTop: '32px',
                width: '100%'
              }}>
                <button
                  onClick={() => navigate('/products')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #d9d9d1',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  All Products
                </button>
                <button
                  onClick={() => navigate('/products/lists')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #000000',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Project Lists
                </button>
                <button
                  onClick={() => navigate('/products/favorites')}
                  style={{
                    backgroundColor: '#f5f5f0',
                    color: '#000000',
                    border: '1px solid #d9d9d1',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: "'NeueHaasUnica', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecece6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Favorites
                </button>
              </div>
            </div>
            
            {/* Right Column - Project Lists */}
            <div style={{ flex: '1', maxWidth: '600px', marginLeft: isMobile ? '0' : '120px', marginTop: '0', paddingTop: '0' }}>
              <WishlistsView
                wishlists={wishlists}
                allProducts={allProducts}
                dimensionsMap={dimensionsMap}
                loadingAllProducts={loadingAllProducts}
                searchQuery={listsSearchQuery}
                onDeleteList={async (listId) => {
                  try {
                    await wishlistsService.deleteWishlist(listId);
                    const updatedLists = await wishlistsService.getWishlists();
                    setWishlists(updatedLists);
                  } catch (err) {
                    console.error('Error deleting wishlist:', err);
                  }
                }}
                onRenameList={async (listId, newName) => {
                  try {
                    await wishlistsService.updateWishlist(listId, { name: newName });
                    const updatedLists = await wishlistsService.getWishlists();
                    setWishlists(updatedLists);
                  } catch (err) {
                    console.error('Error renaming wishlist:', err);
                  }
                }}
                onRemoveProduct={async (listId, productId) => {
                  try {
                    await wishlistsService.updateWishlistProducts(listId, productId, false);
                    const updatedLists = await wishlistsService.getWishlists();
                    setWishlists(updatedLists);
                  } catch (err) {
                    console.error('Error removing product:', err);
                  }
                }}
                favorites={favorites}
                onToggleFavorite={handleToggleFavorite}
                onAddToList={handleAddToList}
                onNavigateToList={(listId) => navigate(`/products/lists/${listId}`)}
                isMobile={isMobile}
              />
            </div>
          </div>
        )}

        {/* LIST DETAIL VIEW: Two-column layout matching Account page */}
        {isListDetailView && selectedListId && (
          <ListDetailViewContent
            selectedListId={selectedListId}
            wishlists={wishlists}
            allProducts={allProducts}
            dimensionsMap={dimensionsMap}
            loadingAllProducts={loadingAllProducts}
            listDetailSearchQuery={listDetailSearchQuery}
            setListDetailSearchQuery={setListDetailSearchQuery}
            favorites={favorites}
            handleToggleFavorite={handleToggleFavorite}
            handleAddToList={handleAddToList}
            setWishlists={setWishlists}
            navigate={navigate}
            isMobile={isMobile}
            showAlert={showAlert}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// DASHBOARD VIEW
// ============================================

// DashboardView has been moved to ./components/DashboardView.jsx for code splitting
// Keeping this comment for reference - the function definition starts around line 15875

// ============================================
// PROPOSAL DETAIL VIEW
// ============================================

const proposalCatalogCache = { at: 0, items: [] };
const PROPOSAL_CATALOG_FRESH_MS = 5 * 60 * 1000;
let proposalCatalogInflight = null;

function loadProposalCatalog() {
  const age = Date.now() - proposalCatalogCache.at;
  if (proposalCatalogCache.items.length > 0 && age < PROPOSAL_CATALOG_FRESH_MS) {
    return Promise.resolve(proposalCatalogCache.items);
  }
  if (!proposalCatalogInflight) {
    proposalCatalogInflight = fetch(PUBLIC_PROPOSAL_API_BASE + '?action=getCatalog', {
      method: 'GET',
      mode: 'cors',
    })
      .then((res) => res.json())
      .then((data) => {
        const items = Array.isArray(data?.catalog) ? data.catalog : [];
        if (items.length > 0) proposalCatalogCache.at = Date.now();
        if (items.length > 0) proposalCatalogCache.items = items;
        return proposalCatalogCache.items;
      })
      .catch((err) => {
        console.error('Error fetching catalog:', err);
        return proposalCatalogCache.items;
      })
      .finally(() => {
        proposalCatalogInflight = null;
      });
  }
  return proposalCatalogInflight;
}

export function ProposalDetailView({ proposal, onBack, onLogout, showAlert, showConfirm, showPrompt, onRefresh, isClientShareableLink = false, clientInfo = null }) {
  const sessionClientInfo = (() => {
    try {
      return authService.getSession()?.clientInfo || null;
    } catch (_) {
      return null;
    }
  })();
  const agreementClientProfile = clientInfo || sessionClientInfo;

  const [isChangeRequestMode, setIsChangeRequestMode] = useState(false);
  const [isReviewingChanges, setIsReviewingChanges] = useState(false);
  const [requestChangesGuidanceOpen, setRequestChangesGuidanceOpen] = useState(true); // session-only; reset on each enter
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [acceptProposalOpen, setAcceptProposalOpen] = useState(false);
  const [localAcceptance, setLocalAcceptance] = useState(null);
  const [showDiscount, setShowDiscount] = useState(true);
  const storedAcceptance = localAcceptance || getAcceptanceFromProposal(proposal);
  const acceptanceUiState = getAcceptanceUiState({
    ...proposal,
    proposalAcceptance: storedAcceptance,
  });
  const canReplayConfirmation = isConfirmationReplayProject(proposal.projectNumber);
  const pendingReview = isPendingReviewProposal(proposal);
  const [catalog, setCatalog] = useState([]);
  const proposalRef = useRef(null);
  const brandMahogany = '#3E0D12';
  const brandSage = '#545142';
  const brandWheat = '#DABE86';
  const brandSlate = '#577591';
  const brandTaupe = brandSage; // Legacy name
  const brandCharcoal = '#000000'; // Black text for proposals
  
  // Scroll to top when this view mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const invoiceSectionVisibility = useMemo(
    () => getInvoiceSectionVisibility(proposal?.customProjectNotes),
    [proposal?.customProjectNotes]
  );
  const coverLabel = invoiceSectionVisibility.showLabelOnProposal
    ? String(proposal?.label || '').trim()
    : '';
  const headerVenue = [String(proposal?.venueName || '').trim(), coverLabel]
    .filter(Boolean)
    .join(' - ');
  
  // One catalog request for photos and for adding a product. The picker still hides portal-only rows.
  useEffect(() => {
    let cancelled = false;
    if (proposalCatalogCache.items.length > 0) setCatalog(proposalCatalogCache.items);
    loadProposalCatalog().then((items) => {
      if (!cancelled && items.length > 0) setCatalog(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const portalPickerCatalog = useMemo(
    () => (Array.isArray(catalog) ? catalog.filter((p) => p && p.showOnPortal !== false) : []),
    [catalog]
  );
  
  // Removed debug footer alignment code that was causing local network permission requests
  
  const rawSections = JSON.parse(proposal.sectionsJSON || '[]');
  const sections = rawSections.map(section => {
    if (section.products && Array.isArray(section.products)) {
      return {
        ...section,
        products: section.products.map(product => ({
          ...product,
          note: product.note || ''
        }))
      };
    }
    return section;
  });
  const productSectionNames = (sections || [])
    .filter((item) => item && item.type !== 'image' && item.products && item.products.length > 0)
    .map((item) => String(item.name || '').trim() || 'Untitled section');
  const lastProductSectionIndex = sections.reduce((last, item, idx) => {
    const isImagePage = (item.type === 'image' || (!item.products || item.products.length === 0)) && (item.imageDriveId || item.imageData || item.imageUrl);
    return isImagePage ? last : idx;
  }, -1);
  const requestChanges = useRequestChangesDraft(proposal);
  const totals = calculateDetailedTotals(proposal);
  const displayTotals = showDiscount ? totals : calculateDetailedTotals({ ...proposal, discountValue: 0, discount: 0 });
  // Complimentary-upgrade presentation: preserves original value in the breakdown
  // and reconciles to the unchanged Rental Total via a distinct deduction line.
  const complimentaryPresentation = computeComplimentaryPresentation(sections);
  
  const handlePrintDownload = () => {
    // Only allow PDF generation on desktop
    if (window.innerWidth <= 768) {
      return;
    }
    
    // Track PDF download
    analyticsService.trackPDFDownload(proposal.projectNumber || proposal.id);
    
    // Open browser print dialog - user can save as PDF from there
    window.print();
  };
  
  // Page counter for sequential numbering
  const pageCounterRef = React.useRef(0);
  
  const getNextPageNumber = () => {
    pageCounterRef.current += 1;
    return pageCounterRef.current;
  };
  
  // Reset counter when component mounts
  React.useEffect(() => {
    pageCounterRef.current = 0;
  }, [proposal]);
  
  // Shared header component for all pages
  const PageHeader = ({ sectionName, showSectionName = false, onBack }) => (
    <div style={{ marginBottom: '20px' }}>
      {/* Top header row */}
      <div className="proposal-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div>
          <div 
            onClick={(e) => { 
              e.preventDefault(); 
              e.stopPropagation();
              try {
                if (onBack && typeof onBack === 'function') {
                  onBack();
                } else {
                  window.location.href = window.location.origin + window.location.pathname.split('/').slice(0, -1).join('/') || '/';
                }
              } catch (error) {
                window.location.href = window.location.origin;
              }
            }} 
            style={{ 
              textDecoration: 'none', 
              cursor: 'pointer',
              display: 'inline-block',
              userSelect: 'none',
              pointerEvents: 'auto',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            className="no-print"
          >
            <img 
              src="/mayker_wordmark-events-black.svg" 
              alt="MAYKER EVENTS" 
              onError={(e) => {
                if (!e.target.src.includes('/assets/')) {
                  e.target.src = '/assets/mayker_wordmark-events-black.svg';
                } else if (!e.target.src.includes('cdn')) {
                  e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_wordmark-events-black.svg';
                } else {
                  e.target.style.display = 'none';
                }
              }}
              style={{ height: '32px', width: 'auto', maxWidth: '300px', display: 'block' }} 
            />
          </div>
          {/* Print version - non-clickable */}
          <img 
            src="/mayker_wordmark-events-black.svg" 
            alt="MAYKER EVENTS" 
            className="print-only"
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_wordmark-events-black.svg';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_wordmark-events-black.svg';
              } else {
                e.target.style.display = 'none';
              }
            }}
            style={{ height: '32px', width: 'auto', maxWidth: '300px', display: 'block' }} 
          />
        </div>
        <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ fontSize: '9px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", lineHeight: '1.4', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            <div>{proposal.clientName}</div>
            <div>{formatDateRange(proposal)}</div>
            <div>{headerVenue}</div>
          </div>
          <img 
            src="/mayker_icon-black.png" 
            alt="M" 
            style={{ height: '38px' }}
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_icon-black.png';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_icon-black.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
      </div>
      {/* Separator line */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: showSectionName ? '15px' : '0' }}></div>
      {/* Section name below separator if provided */}
        {showSectionName && sectionName && (
        <div className="proposal-section-name" style={{ fontSize: '14px', fontWeight: '300', color: brandCharcoal, marginTop: '15px', fontFamily: "'Domaine Text', serif", letterSpacing: '0.02em', textAlign: 'center' }}>{sectionName}</div>
      )}
    </div>
  );
  
  // Footer component
  const PageFooter = ({ pageNum, isDark = false, useFlexbox = false }) => (
    <div className="proposal-page-footer" style={{
      position: useFlexbox ? 'relative' : 'absolute',
      bottom: useFlexbox ? 'auto' : '20px',
      left: '60px',
      right: '60px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '10px',
          color: '#000000',
      fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      marginTop: useFlexbox ? 'auto' : '0',
      paddingTop: useFlexbox ? '20px' : '0'
    }}>
      <div>EVENTS@MAYKER.COM</div>
      <div>{pageNum}</div>
    </div>
  );
  
  const productsPerPage = 9;

  const exitRequestChangesMode = useCallback(() => {
    setAddProductOpen(false);
    setIsReviewingChanges(false);
    setIsChangeRequestMode(false);
    requestChanges.resetDraft();
  }, [requestChanges]);

  const enterRequestChangesMode = useCallback(() => {
    requestChanges.resetDraft();
    setAddProductOpen(false);
    setIsReviewingChanges(false);
    setRequestChangesGuidanceOpen(true);
    setIsChangeRequestMode(true);
    markProposalWelcomeDismissed(getProposalWelcomeStorageKey({
      projectNumber: proposal.projectNumber,
      version: proposal.version,
    }));
    setWelcomeOpen(false);
  }, [proposal.projectNumber, proposal.version, requestChanges]);

  const [setChromeNode, measuredChromeHeight] = useStickyChromeOffset(!isReviewingChanges);
  const welcomeStorageKey = getProposalWelcomeStorageKey({
    projectNumber: proposal.projectNumber,
    version: proposal.version,
  });
  const chromeOffsetPx = measuredChromeHeight || (pendingReview ? 168 : 72);
  const showClientWelcome = Boolean(
    welcomeOpen
    && !isChangeRequestMode
    && !isReviewingChanges
    && !pendingReview
  );
  const showRequestChangesGuidance = Boolean(
    isChangeRequestMode
    && !isReviewingChanges
    && requestChangesGuidanceOpen
  );

  const dismissClientWelcome = useCallback(() => {
    markProposalWelcomeDismissed(welcomeStorageKey);
    setWelcomeOpen(false);
  }, [welcomeStorageKey]);

  const startReviewingProposal = useCallback(() => {
    dismissClientWelcome();
    const scrollToContent = () => {
      const target = typeof document !== 'undefined'
        ? document.getElementById('proposal-content-start')
        : null;
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(scrollToContent));
    }
  }, [dismissClientWelcome]);

  const reopenClientWelcome = useCallback(() => {
    setWelcomeOpen(true);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (pendingReview) {
      setWelcomeOpen(false);
      return;
    }
    setWelcomeOpen(!hasDismissedProposalWelcome(welcomeStorageKey));
  }, [pendingReview, welcomeStorageKey]);

  const handleCancelRequestChanges = useCallback(async () => {
    if (!requestChanges.hasProductEdits) {
      exitRequestChangesMode();
      return;
    }
    const confirmed = await showConfirm('Discard requested changes?\n\nYour changes haven’t been submitted.', {
      confirmLabel: 'Discard Changes',
      cancelLabel: 'Keep Editing'
    });
    if (confirmed) {
      exitRequestChangesMode();
    }
  }, [exitRequestChangesMode, requestChanges.hasProductEdits, showConfirm]);

  if (isReviewingChanges) {
    const hasDiscount = (parseFloat(proposal.discountValue || proposal.discount || 0) || 0) > 0;
    const pricingToggle = (hasDiscount || isClientShareableLink) ? (
      <div
        role="group"
        aria-label="Discount line on proposal and PDF"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
      >
        <span style={{ fontSize: '11px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", color: '#8a8378' }}>
          Show Preferred Pricing
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={showDiscount}
          aria-label={showDiscount ? 'Hide preferred pricing' : 'Show preferred pricing'}
          onClick={() => setShowDiscount(s => !s)}
          style={{
            width: 32,
            height: 18,
            borderRadius: 9,
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: showDiscount ? '#8a8378' : '#d8d0c4',
          }}
        >
          <span style={{
            position: 'absolute',
            top: 2,
            left: showDiscount ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: '#fff',
            transition: 'left 0.2s ease',
          }} />
        </button>
      </div>
    ) : null;
    return (
      <ChangeRequestReviewView
        proposal={proposal}
        sections={sections}
        draft={requestChanges.draft}
        setDateTime={requestChanges.setDateTime}
        setDateTimeFields={requestChanges.setDateTimeFields}
        resetDateTime={requestChanges.resetDateTime}
        setMiscNotes={requestChanges.setMiscNotes}
        removeNewProductRequest={requestChanges.removeNewProductRequest}
        undoProductChange={requestChanges.undoProductChange}
        eventDatesLabel={formatDateRange(proposal)}
        showAlert={showAlert}
        showConfirm={showConfirm}
        onBack={() => setIsReviewingChanges(false)}
        onExit={exitRequestChangesMode}
        onRefresh={onRefresh}
        onSubmitted={async (result) => {
          if (!onRefresh) return;
          await onRefresh(true, {
            projectNumber: result?.projectNumber || proposal.projectNumber,
            version: result?.version,
            pendingReview: true,
            proposal: result?.proposal
          });
        }}
        submitChangeRequest={(data) => apiService.submitChangeRequest(data)}
        getSession={() => authService.getSession()}
        modeHeader={{
          onCancel: handleCancelRequestChanges,
          utility: pricingToggle
        }}
      />
    );
  }
  
  return (
    <div ref={proposalRef} data-proposal-view="true" data-client-shareable-link={isClientShareableLink ? 'true' : 'false'} data-request-changes-mode={isChangeRequestMode ? 'true' : 'false'} data-pending-review={pendingReview ? 'true' : 'false'} data-proposal-welcome={showClientWelcome ? 'open' : 'closed'} style={{ minHeight: '100vh', backgroundColor: 'white', paddingTop: chromeOffsetPx, paddingBottom: isChangeRequestMode ? 84 : 0, '--proposal-chrome-offset': `${chromeOffsetPx}px` }}>
        {/* Print styles */}
        <style dangerouslySetInnerHTML={{ __html: `
          /* Google Fonts loaded via link tag above for better performance */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { 
          font-family: 'Neue Haas Unica', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; 
          background: white;
          overflow-x: hidden !important;
          max-width: 100vw !important;
          width: 100% !important;
        }
        .print-only { display: none !important; }
        .proposal-cover-page {
          background-color: #FFFFFF !important;
        }
        @media screen {
          div[data-proposal-view="true"] .proposal-cover-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page {
            display: none !important;
          }
          #proposal-content-start {
            scroll-margin-top: var(--proposal-chrome-offset, 72px);
          }
        }
        @media print {
          div[data-proposal-view="true"] {
            padding-top: 0 !important;
          }
        }
        .request-changes-product-controls,
        .request-another-product,
        .request-changes-sticky-bar {
          -webkit-print-color-adjust: exact;
        }
        @media print {
          .request-changes-product-controls,
          .request-another-product,
          .request-changes-sticky-bar,
          .request-changes-header-bar,
          .proposal-view-header,
          .proposal-welcome-panel,
          .request-changes-guidance-panel,
          .pending-review-banner,
          .request-changes-editing-rail,
          .request-add-product-modal {
            display: none !important;
          }
        }
        .request-changes-editing-rail,
        .request-changes-editing-rail *:not(.request-changes-qty-input) {
          color: #fff !important;
        }
        .request-changes-qty-input {
          color: #2C2C2C !important;
          border-radius: 2px !important;
          font-weight: 400 !important;
        }
        .request-changes-editing-rail .request-changes-rail-secondary {
          color: rgba(255,255,255,0.72) !important;
        }
        .request-changes-sticky-count {
          color: #fff !important;
        }
        .request-changes-sticky-helper {
          color: rgba(255,255,255,0.78) !important;
        }
        .request-changes-sticky-continue {
          color: #2C2C2C !important;
        }
        .request-changes-sticky-add {
          color: #fff !important;
        }
        .proposal-welcome-copy {
          color: #6b645c !important;
        }
        .proposal-view-header-meta {
          color: #6b645c !important;
        }
        .proposal-welcome-dismiss,
        .proposal-view-header-about,
        .request-changes-guidance-dismiss {
          color: #8a8378 !important;
        }
        .request-changes-guidance-copy {
          color: #6b645c !important;
        }
        .request-changes-guidance-action {
          color: #2C2C2C !important;
        }
        .request-changes-editing-rail .request-changes-remove-label {
          color: rgba(255,255,255,0.78) !important;
        }
        @media screen and (min-width: 769px) and (max-width: 1024px) {
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-view-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-sticky-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-guidance-inner {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
        }
        @media screen and (max-width: 768px) {
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-view-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-sticky-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-guidance-inner {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .request-changes-header-inner {
            flex-wrap: wrap !important;
            align-items: flex-start !important;
          }
          .request-changes-header-actions {
            width: auto !important;
          }
          .request-changes-header-actions button {
            flex: 0 0 auto !important;
          }
          .request-changes-editing-rail {
            row-gap: 0 !important;
          }
          div[data-request-changes-mode="true"] .proposal-nav-bar {
            align-items: flex-start !important;
          }
          div[data-request-changes-mode="true"] .proposal-nav-bar > div {
            width: 100% !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
        }
        
        /* Global mobile/tablet overflow prevention - For client shareable links to match reserve portal */
        @media screen and (max-width: 1024px) {
          div[data-proposal-view="true"][data-client-shareable-link="true"] {
            overflow-x: hidden !important;
            max-width: 100vw !important;
            width: 100% !important;
          }
        }
        /* Global: Ensure Standard Rate cells are italic only, not bold (applies to both web and PDF) */
        /* Target the entire 4th column to remove ALL bold formatting */
        colgroup col:nth-child(4),
        .proposal-invoice-table colgroup col:nth-child(4),
        table.proposal-invoice-table colgroup col:nth-child(4) {
          font-weight: normal !important;
        }
        /* Remove ALL bold formatting from Standard Rate column */
        .standard-rate-cell,
        div.standard-rate-cell,
        td.standard-rate-cell,
        .proposal-invoice-table tbody tr td.standard-rate-cell,
        table.proposal-invoice-table tbody tr td.standard-rate-cell,
        .proposal-invoice-table tbody tr td:nth-child(4),
        table.proposal-invoice-table tbody tr td:nth-child(4),
        .proposal-invoice-table tbody td:nth-child(4),
        table tbody td:nth-child(4),
        .proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4),
        table.proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4) {
          font-weight: normal !important;
          font-style: italic !important;
        }
        /* Also target any nested elements within Standard Rate cells - remove ALL bold */
        .standard-rate-cell *,
        div.standard-rate-cell *,
        td.standard-rate-cell *,
        .proposal-invoice-table tbody tr td.standard-rate-cell *,
        table.proposal-invoice-table tbody tr td.standard-rate-cell *,
        .proposal-invoice-table tbody tr td:nth-child(4) *,
        table.proposal-invoice-table tbody tr td:nth-child(4) *,
        .proposal-invoice-table tbody td:nth-child(4) *,
        table tbody td:nth-child(4) *,
        .proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4) *,
        table.proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4) * {
          font-weight: normal !important;
          font-style: italic !important;
        }
        /* Remove bold from any strong or b tags within Standard Rate cells */
        .standard-rate-cell strong,
        .standard-rate-cell b,
        .proposal-invoice-table tbody td:nth-child(4) strong,
        .proposal-invoice-table tbody td:nth-child(4) b,
        table tbody td:nth-child(4) strong,
        table tbody td:nth-child(4) b {
          font-weight: normal !important;
        }
        /* Ultra-aggressive: Target Standard Rate column with maximum specificity */
        table.proposal-invoice-table tbody tr td.standard-rate-cell,
        table.proposal-invoice-table tbody tr td:nth-child(4),
        div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4),
        div[data-proposal-view="true"] table tbody tr td:nth-child(4) {
          font-weight: normal !important;
          font-style: italic !important;
        }
        div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4) *,
        div[data-proposal-view="true"] table tbody tr td:nth-child(4) * {
          font-weight: normal !important;
          font-style: italic !important;
        }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-break-after { page-break-after: always; }
          body { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
            background: white;
          }
          @page { 
            size: letter; 
            margin: 0;
          }
          @page:first { 
            margin: 0;
          }
          div[data-proposal-view="true"] {
            background: white !important;
          }
          div[data-proposal-view="true"] > div:first-of-type { 
            page-break-after: always; 
          }
          div[style*="background-color: #545142"] {
            background-color: #545142 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .proposal-cover-page {
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
            min-height: 100vh !important;
            height: 100vh !important;
            padding: 60px !important;
            background-color: #FFFFFF !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          thead { display: table-header-group !important; }
          thead tr { page-break-inside: avoid; }
          .no-page-break { page-break-inside: avoid !important; break-inside: avoid !important; }
          /* Fix column alignment in print/PDF by using fixed table layout */
          table.proposal-invoice-table,
          table {
            table-layout: fixed !important;
            width: 100% !important;
          }
          /* Ensure colgroup widths are respected in print */
          table.proposal-invoice-table colgroup col,
          table colgroup col {
            width: inherit !important;
          }
          /* Ensure consistent column alignment between header and body */
          table.proposal-invoice-table thead th,
          table.proposal-invoice-table tbody td,
          table thead th,
          table tbody td {
            vertical-align: top !important;
          }
          /* Ensure consistent padding for proper alignment */
          table.proposal-invoice-table thead th,
          table.proposal-invoice-table tbody td {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          /* Ensure footer has no border in print */
          /* Hide footer completely in print/PDF */
          .proposal-final-footer {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
          .proposal-final-footer * {
            display: none !important;
            visibility: hidden !important;
          }
          /* PDF/Print: Override mobile CSS - Ensure ALL numeric column headers are right-aligned */
          .proposal-invoice-table thead th.proposal-invoice-qty-header,
          .proposal-invoice-table thead th.proposal-invoice-price-header,
          .proposal-invoice-table thead th.proposal-invoice-total-header,
          .proposal-invoice-table thead th:nth-child(3),
          .proposal-invoice-table thead th:nth-child(4),
          .proposal-invoice-table thead th:nth-child(5),
          .proposal-invoice-table thead th:nth-child(6),
          table.proposal-invoice-table thead th:nth-child(3),
          table.proposal-invoice-table thead th:nth-child(4),
          table.proposal-invoice-table thead th:nth-child(5),
          table.proposal-invoice-table thead th:nth-child(6) {
            text-align: right !important;
          }
          /* PDF/Print: Override mobile CSS - Right-align ALL numeric column values */
          .proposal-invoice-table tbody td:nth-child(3),
          .proposal-invoice-table tbody td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(5),
          .proposal-invoice-table tbody td:nth-child(6),
          table.proposal-invoice-table tbody td:nth-child(3),
          table.proposal-invoice-table tbody td:nth-child(4),
          table.proposal-invoice-table tbody td:nth-child(5),
          table.proposal-invoice-table tbody td:nth-child(6) {
            text-align: right !important;
          }
          /* PDF/Print: Ensure Standard Rate values (4th column when discount exists) are italic but NOT bold */
          .standard-rate-cell,
          .proposal-invoice-table tbody tr td.standard-rate-cell,
          table.proposal-invoice-table tbody tr td.standard-rate-cell,
          .proposal-invoice-table tbody tr td:nth-child(4),
          table.proposal-invoice-table tbody tr td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(4) *,
          table.proposal-invoice-table tbody td:nth-child(4),
          table.proposal-invoice-table tbody td:nth-child(4) *,
          .proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4),
          table.proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4) {
            font-weight: normal !important;
            font-style: italic !important;
          }
          /* Remove bold from any strong or b tags within Standard Rate cells in print */
          .standard-rate-cell strong,
          .standard-rate-cell b,
          .proposal-invoice-table tbody td:nth-child(4) strong,
          .proposal-invoice-table tbody td:nth-child(4) b,
          table tbody td:nth-child(4) strong,
          table tbody td:nth-child(4) b {
            font-weight: normal !important;
          }
          /* Ultra-aggressive: Target Standard Rate column with maximum specificity in print */
          table.proposal-invoice-table tbody tr td.standard-rate-cell,
          table.proposal-invoice-table tbody tr td:nth-child(4),
          div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4),
          div[data-proposal-view="true"] table tbody tr td:nth-child(4) {
            font-weight: normal !important;
            font-style: italic !important;
          }
          div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4) *,
          div[data-proposal-view="true"] table tbody tr td:nth-child(4) * {
            font-weight: normal !important;
            font-style: italic !important;
          }
          /* PDF/Print: Ensure Preferred Rate (5th column) and Total (6th column) are NOT italic and NOT bold */
          .proposal-invoice-table tbody tr td:nth-child(5),
          .proposal-invoice-table tbody tr td:nth-child(6),
          table.proposal-invoice-table tbody td:nth-child(5),
          table.proposal-invoice-table tbody td:nth-child(6) {
            font-weight: 400 !important;
            font-style: normal !important;
          }
        }
        @media screen and (max-width: 768px) {
          /* Critical: Prevent horizontal overflow on mobile - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] {
            padding-top: 80px !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          
          /* Ensure all proposal pages respect viewport width - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-image-page {
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          
          /* Header should work the same as reserve portal - no special wrapping needed */
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-nav-bar {
            padding: 0 !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-view-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-inner {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-view-header-utilities button:not(.proposal-view-header-about),
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-view-header-actions button,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-actions button {
            width: auto !important;
            flex: 0 0 auto !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-mobile-hide {
            display: none !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page {
            padding: 60px 16px 40px 16px !important;
            min-height: auto !important;
            gap: 40px !important;
            margin-top: 0 !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content {
            gap: 60px !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content > div:first-child {
            margin-top: 80px !important;
            width: 100% !important;
            max-width: 100% !important;
            text-align: center !important;
            box-sizing: border-box !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content > div:first-child img {
            margin-top: 0 !important;
            max-width: 100% !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content h1 {
            font-size: 20px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content p {
            font-size: 12px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          /* Mobile Invoice View - Show mobile, hide desktop */
          .mobile-invoice-view {
            display: block !important;
            padding-bottom: 0 !important;
            margin-bottom: 0 !important;
          }
          .desktop-invoice-view {
            display: none !important;
          }
          .proposal-page.desktop-invoice {
            display: none !important;
          }
          /* Hide logo in totals section on mobile */
          .proposal-totals-logo {
            display: none !important;
          }
          /* Reduce spacing between mobile invoice and totals box */
          .proposal-totals-section {
            padding-top: 0 !important;
            margin-top: 0 !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-bottom: 32px !important;
          }
          /* Make totals box spacing match spacing between last visual and invoice */
          .proposal-totals-section > div:last-child {
            margin-top: 0 !important;
            padding-top: 120px !important;
            border-top: none !important;
          }
          /* Remove minHeight from totals section on mobile to prevent extra space */
          .proposal-totals-section {
            min-height: auto !important;
          }
          /* Remove top margin from totals box on mobile */
          .proposal-totals-section > div:last-child > div:first-child {
            margin-top: 0 !important;
            margin-bottom: 20px !important;
          }
          /* Adjust totals table spacing on mobile */
          .proposal-totals-table {
            margin-top: 0 !important;
          }
          /* Remove bottom margin from mobile invoice itemized list */
          .mobile-invoice-view > div:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Connect totals box directly to invoice items - small buffer */
          .mobile-invoice-view {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Totals section should have small buffer from invoice */
          .proposal-totals-section {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          /* Mobile-specific proposal page styles - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page {
            padding: 20px 16px 24px 16px !important;
            min-height: auto !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-sticky-inner {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          /* Header should display normally on mobile for client shareable links (same as reserve portal) */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            align-items: flex-start !important;
          }
          
          /* Shrink logo on mobile */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:first-child {
            width: 100% !important;
            max-width: 100% !important;
            flex-shrink: 1 !important;
            min-width: 0 !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:first-child img,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:first-child .no-print img {
            height: 20px !important;
            max-width: 180px !important;
            width: auto !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:last-child {
            width: 100% !important;
            max-width: 100% !important;
            text-align: left !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 4px !important;
            flex-shrink: 0 !important;
            min-width: 0 !important;
            margin-top: 8px !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:last-child > div {
            font-size: 8px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
            white-space: normal !important;
            line-height: 1.3 !important;
            display: block !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:last-child img {
            height: 24px !important;
            width: auto !important;
            margin-top: 4px !important;
            flex-shrink: 0 !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header + div {
            display: block !important;
          }
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-section-name {
            font-size: 20px !important;
            margin-top: 0 !important;
            margin-bottom: 20px !important;
            text-align: left !important;
            display: block !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          
          /* Ensure all text content wraps properly on mobile - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page *,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page *,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-image-page * {
            max-width: 100% !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            box-sizing: border-box !important;
          }
          
          /* Fix product grid on mobile - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-product-grid {
            grid-template-columns: 1fr !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            gap: 16px !important;
          }
          
          /* Ensure product cards don't overflow - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-product-card {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 12px !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-product-card h3 {
            font-size: 12px !important;
            margin-bottom: 6px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-product-card p {
            font-size: 11px !important;
            margin-bottom: 4px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          /* Universal mobile header fixes - shrink logo and ensure project info fits */
          .proposal-page-header {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          
          .proposal-page-header > div:first-child {
            flex-shrink: 1 !important;
            min-width: 0 !important;
            max-width: 50% !important;
          }
          
          .proposal-page-header > div:first-child img,
          .proposal-page-header > div:first-child .no-print img,
          .proposal-page-header .print-only img {
            height: 20px !important;
            max-width: 180px !important;
            width: auto !important;
          }
          
          .proposal-page-header > div:last-child {
            flex-shrink: 0 !important;
            min-width: 0 !important;
            max-width: 50% !important;
            text-align: right !important;
          }
          
          .proposal-page-header > div:last-child > div {
            font-size: 8px !important;
            line-height: 1.3 !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
          }
          
          .proposal-page-header > div:last-child img {
            height: 24px !important;
            width: auto !important;
            margin-top: 4px !important;
          }
          
          .proposal-page-footer {
            display: none !important;
          }
          .proposal-totals-section .proposal-page-footer {
            display: flex !important;
          }
          .proposal-product-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .proposal-product-card {
            padding: 12px !important;
          }
          .proposal-product-card h3 {
            font-size: 12px !important;
            margin-bottom: 6px !important;
          }
          .proposal-product-card p {
            font-size: 11px !important;
            margin-bottom: 4px !important;
          }
          .proposal-invoice-header h2 {
            font-size: 20px !important;
            margin-top: 0 !important;
            margin-bottom: 20px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          /* Hide duplicate header in invoice view on mobile */
          .proposal-invoice-header .proposal-page-header {
            display: none !important;
          }
          
          /* Ensure invoice header container doesn't overflow */
          .proposal-invoice-header {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 0 16px !important;
          }
          .proposal-invoice-table {
            font-size: 12px !important;
            overflow-x: auto !important;
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          .proposal-invoice-table table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }
          .proposal-invoice-table thead th {
            font-size: 10px !important;
            padding: 10px 8px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          
          /* Allow column headers to wrap if needed on very small screens */
          .proposal-invoice-table thead th:nth-child(4),
          .proposal-invoice-table thead th:nth-child(5) {
            font-size: 9px !important;
          }
          .proposal-invoice-table thead th:nth-child(1) {
            width: 12% !important;
          }
          .proposal-invoice-table thead th:nth-child(2) {
            width: 30% !important;
          }
          .proposal-invoice-table thead th:nth-child(3),
          .proposal-invoice-qty-header {
            width: 15% !important;
            text-align: left !important;
          }
          .proposal-invoice-table thead th:nth-child(4),
          .proposal-invoice-price-header {
            width: 21% !important;
            text-align: left !important;
          }
          .proposal-invoice-table thead th:nth-child(5),
          .proposal-invoice-total-header {
            width: 22% !important;
            text-align: left !important;
          }
          .proposal-invoice-table tbody td {
            font-size: 12px !important;
            padding: 10px 8px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          
          /* Allow product names to wrap on mobile */
          .proposal-invoice-table tbody td:nth-child(2) {
            white-space: normal !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          .proposal-invoice-table tbody td:nth-child(1) {
            width: 12% !important;
            font-size: 11px !important;
          }
          .proposal-invoice-table tbody td:nth-child(2) {
            width: 30% !important;
          }
          .proposal-invoice-table tbody td:nth-child(3) {
            width: 15% !important;
            text-align: left !important;
          }
          .proposal-invoice-table tbody td:nth-child(4) {
            width: 21% !important;
            text-align: left !important;
          }
          .proposal-invoice-table tbody td:nth-child(5) {
            width: 22% !important;
            text-align: left !important;
          }
          /* Mobile: Override for discount columns when they exist */
          .proposal-invoice-table thead th:nth-child(6) {
            text-align: left !important;
          }
          .proposal-invoice-table tbody td:nth-child(6) {
            text-align: left !important;
          }
          .proposal-totals-section {
            padding: 20px 16px !important;
            margin-top: 24px !important;
          }
          .proposal-totals-section h2 {
            font-size: 18px !important;
            margin-bottom: 16px !important;
          }
          .proposal-totals-table {
            font-size: 12px !important;
          }
          .proposal-totals-table td {
            padding: 8px 0 !important;
            font-size: 12px !important;
          }
          .proposal-final-footer {
            margin-top: 24px !important;
            padding-top: 0 !important;
          }
          .proposal-footer-logo img {
            height: 20px !important;
          }
          .proposal-final-footer > div {
            font-size: 9px !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          .proposal-project-details {
            flex-direction: column !important;
            padding-top: 24px !important;
            margin-bottom: 24px !important;
          }
          .proposal-project-details > div:first-child {
            width: 100% !important;
            padding-right: 0 !important;
            margin-bottom: 12px !important;
          }
          .proposal-project-details h2 {
            font-size: 18px !important;
            margin-bottom: 0 !important;
          }
          .proposal-project-details > div:last-child {
            width: 100% !important;
          }
          .proposal-project-details p,
          .proposal-project-details ul {
            font-size: 12px !important;
          }
          .proposal-totals-section > div:first-child {
            margin-bottom: 24px !important;
          }
          .proposal-totals-section > div:last-child {
            flex-direction: column !important;
            padding: 24px 16px !important;
          }
          .proposal-totals-section > div:last-child > div:first-child {
            width: 100% !important;
            padding-right: 0 !important;
            margin-bottom: 16px !important;
          }
          .proposal-totals-section > div:last-child > div:first-child h2 {
            font-size: 18px !important;
            margin-bottom: 0 !important;
          }
          .proposal-totals-section > div:last-child > div:last-child {
            width: 100% !important;
          }
          .proposal-totals-table {
            width: 100% !important;
          }
          .proposal-totals-table td {
            font-size: 11px !important;
            padding: 6px 0 !important;
          }
          .proposal-page-footer {
            position: relative !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            padding: 16px !important;
            margin-top: 24px !important;
            font-size: 9px !important;
          }
          .proposal-image-page {
            padding: 20px 16px !important;
            min-height: auto !important;
          }
          .proposal-image-page img {
            max-height: 60vh !important;
          }
        }
        
        /* iPad and tablet styles (768px - 1024px) - For client shareable links to match reserve portal */
        @media screen and (min-width: 769px) and (max-width: 1024px) {
          /* Prevent horizontal overflow on iPad - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] {
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          
          /* Ensure all proposal pages respect viewport width on iPad - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-image-page,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-header-inner,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .request-changes-sticky-inner {
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
          
          /* Header on iPad - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: row !important;
            gap: 16px !important;
            align-items: flex-start !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:last-child {
            flex: 1 1 auto !important;
            min-width: 0 !important;
            max-width: 50% !important;
            text-align: right !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page-header > div:last-child > div {
            font-size: 9px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
            line-height: 1.4 !important;
          }
          
          /* Ensure all text content wraps properly on iPad - For client shareable links to match reserve portal */
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-page *,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page *,
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-image-page * {
            max-width: 100% !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            box-sizing: border-box !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-page {
            padding: 60px 24px 40px 24px !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-cover-content p {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
          
          div[data-proposal-view="true"][data-client-shareable-link="true"] .proposal-section-name {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            max-width: 100% !important;
          }
        }
        
        @media screen and (min-width: 769px) {
          /* Desktop - Hide mobile invoice, show desktop */
          .mobile-invoice-view {
            display: none !important;
          }
          .desktop-invoice-view {
            display: block !important;
          }
          /* Ensure desktop totals section maintains original styling - override mobile styles */
          .proposal-totals-section {
            padding: 50px 80px 40px !important;
            min-height: 100vh !important;
          }
          .proposal-totals-section > div:last-child:not(.proposal-final-footer) {
            padding-top: 0 !important;
            margin-top: 0 !important;
            border-top: 1px solid #2C2C2C !important;
          }
          /* Ensure footer has no border-top on desktop */
          .proposal-totals-section > .proposal-final-footer {
            border-top: none !important;
            border: none !important;
          }
          .proposal-totals-logo {
            display: flex !important;
          }
          /* Restore desktop proposal page styling */
          .proposal-page {
            padding: 30px 60px 40px !important;
            min-height: 100vh !important;
          }
          .proposal-page-header {
            display: flex !important;
          }
          .proposal-page-footer {
            display: flex !important;
            position: absolute !important;
          }
          /* Ensure footer is visible on desktop totals page */
          .proposal-final-footer {
            display: block !important;
            margin-top: 70px !important;
            padding-top: 0 !important;
            border-top: none !important;
            border: none !important;
            border-bottom: none !important;
            border-left: none !important;
            border-right: none !important;
            outline: none !important;
          }
          .proposal-final-footer::before,
          .proposal-final-footer::after {
            display: none !important;
            content: none !important;
            border: none !important;
          }
          .proposal-final-footer > div {
            font-size: 10px !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            border-top: none !important;
            border: none !important;
            border-bottom: none !important;
            border-left: none !important;
            border-right: none !important;
            outline: none !important;
          }
          .proposal-final-footer > div::before,
          .proposal-final-footer > div::after {
            display: none !important;
            content: none !important;
            border: none !important;
          }
          .proposal-footer-logo {
            display: block !important;
          }
          /* Hide clickable logo on desktop screen view, show print-only logo in print */
          .proposal-final-footer .no-print {
            display: inline-block !important;
          }
          .proposal-final-footer .print-only {
            display: none !important;
          }
          /* Ensure print button is visible on desktop */
          .proposal-mobile-hide {
            display: flex !important;
          }
        }
        @media print {
          /* Print - Always use desktop invoice and desktop styling */
          /* CRITICAL: Override ALL mobile styles for print */
          div[data-proposal-view="true"] {
            padding-top: 0 !important;
          }
          .mobile-invoice-view {
            display: none !important;
          }
          .desktop-invoice-view {
            display: block !important;
          }
          /* Ensure print uses desktop proposal page styling - override mobile */
          .proposal-page {
            padding: 30px 60px 40px !important;
            min-height: 100vh !important;
          }
          .proposal-page-header {
            display: flex !important;
          }
          .proposal-page-header + div {
            display: block !important;
          }
          .proposal-page-footer {
            display: flex !important;
            position: absolute !important;
          }
          .proposal-section-name {
            font-size: 14px !important;
            margin-top: 15px !important;
            margin-bottom: 15px !important;
            text-align: center !important;
          }
          .proposal-product-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            grid-auto-rows: min-content !important;
            gap: 14px !important;
          }
          .proposal-product-card {
            padding: 10px !important;
            height: fit-content !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .proposal-product-card > div:first-child {
            flex-shrink: 0 !important;
          }
          .proposal-product-card h3 {
            font-size: 10px !important;
            margin-bottom: 2px !important;
            flex-shrink: 0 !important;
          }
          .proposal-product-card p {
            font-size: 9px !important;
            margin-bottom: 2px !important;
            flex-shrink: 0 !important;
          }
          .proposal-product-card > div:first-child {
            aspect-ratio: 1 !important;
            margin-bottom: 8px !important;
            flex-shrink: 0 !important;
          }
          .proposal-product-card img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
          }
          .proposal-invoice-header h2 {
            font-size: 24px !important;
            margin-top: 0 !important;
            margin-bottom: 20px !important;
          }
          .proposal-invoice-table {
            font-size: 11px !important;
            display: table !important;
            width: 100% !important;
          }
          .proposal-invoice-table thead th {
            font-size: 10px !important;
            padding: 12px !important;
          }
          .proposal-invoice-table tbody td {
            font-size: 11px !important;
            padding: 12px !important;
            font-weight: normal !important;
          }
          /* PDF/Print: Ensure Standard Rate column (4th when discount exists) is italic but not bold - must override all other rules */
          .standard-rate-cell,
          .proposal-invoice-table tbody tr td.standard-rate-cell,
          table.proposal-invoice-table tbody tr td.standard-rate-cell,
          .proposal-invoice-table tbody tr td:nth-child(4),
          table.proposal-invoice-table tbody tr td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(4),
          table.proposal-invoice-table tbody td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(4) *,
          table.proposal-invoice-table tbody td:nth-child(4) *,
          .proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4),
          table.proposal-invoice-table tbody tr:nth-child(n) td:nth-child(4) {
            font-weight: normal !important;
            font-style: italic !important;
          }
          /* Remove bold from any strong or b tags within Standard Rate cells in print */
          .standard-rate-cell strong,
          .standard-rate-cell b,
          .proposal-invoice-table tbody td:nth-child(4) strong,
          .proposal-invoice-table tbody td:nth-child(4) b,
          table tbody td:nth-child(4) strong,
          table tbody td:nth-child(4) b {
            font-weight: normal !important;
          }
          /* Ultra-aggressive: Target Standard Rate column with maximum specificity in print */
          table.proposal-invoice-table tbody tr td.standard-rate-cell,
          table.proposal-invoice-table tbody tr td:nth-child(4),
          div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4),
          div[data-proposal-view="true"] table tbody tr td:nth-child(4) {
            font-weight: normal !important;
            font-style: italic !important;
          }
          div[data-proposal-view="true"] table.proposal-invoice-table tbody tr td:nth-child(4) *,
          div[data-proposal-view="true"] table tbody tr td:nth-child(4) * {
            font-weight: normal !important;
            font-style: italic !important;
          }
          /* PDF/Print: Ensure numeric columns are right-aligned - comprehensive override */
          .proposal-invoice-table thead th.proposal-invoice-qty-header,
          .proposal-invoice-table thead th.proposal-invoice-price-header,
          .proposal-invoice-table thead th.proposal-invoice-total-header,
          .proposal-invoice-table thead th:nth-child(3),
          .proposal-invoice-table thead th:nth-child(4),
          .proposal-invoice-table thead th:nth-child(5),
          .proposal-invoice-table thead th:nth-child(6),
          .proposal-invoice-table tbody td:nth-child(3),
          .proposal-invoice-table tbody td:nth-child(4),
          .proposal-invoice-table tbody td:nth-child(5),
          .proposal-invoice-table tbody td:nth-child(6) {
            text-align: right !important;
          }
          /* PDF/Print: Additional specificity to override mobile styles */
          table.proposal-invoice-table thead th:nth-child(3),
          table.proposal-invoice-table thead th:nth-child(4),
          table.proposal-invoice-table thead th:nth-child(5),
          table.proposal-invoice-table thead th:nth-child(6),
          table.proposal-invoice-table tbody td:nth-child(3),
          table.proposal-invoice-table tbody td:nth-child(4),
          table.proposal-invoice-table tbody td:nth-child(5),
          table.proposal-invoice-table tbody td:nth-child(6) {
            text-align: right !important;
          }
          .proposal-totals-section {
            padding: 50px 80px 40px !important;
            min-height: auto !important;
            margin-top: 0 !important;
            border-bottom: none !important;
            page-break-after: auto !important;
          }
          /* Remove any extra borders that might be causing double lines */
          .proposal-totals-section > div:last-child {
            padding-top: 0 !important;
            margin-top: 0 !important;
            border-top: none !important;
            border-bottom: none !important;
            flex-direction: row !important;
            padding: 24px 0 !important;
          }
          /* Ensure no border between totals section and footer */
          .proposal-totals-section + .proposal-final-footer,
          .proposal-totals-section ~ .proposal-final-footer {
            border-top: none !important;
            border: none !important;
          }
          /* Ensure content box has proper border - only the box itself */
          .proposal-totals-content-box {
            border: 1px solid #2C2C2C !important;
          }
          /* CRITICAL: Remove any border that might appear above footer */
          .proposal-totals-content-box::after {
            display: none !important;
            content: none !important;
            border: none !important;
          }
          /* Ensure no border appears on the last child of totals section */
          .proposal-totals-section > div:last-of-type:not(.proposal-final-footer) {
            border-bottom: none !important;
          }
          /* Ensure the inner bordered container only has one border - no bottom border */
          .proposal-totals-content-box {
            border: 1px solid #2C2C2C !important;
            border-bottom: 1px solid #2C2C2C !important;
            border-top: 1px solid #2C2C2C !important;
            border-left: 1px solid #2C2C2C !important;
            border-right: 1px solid #2C2C2C !important;
          }
          /* Ensure content box has no bottom border that could create a line */
          .proposal-totals-content-box::after {
            display: none !important;
            content: none !important;
            border: none !important;
          }
          /* Hide footer completely in print/PDF */
          .proposal-final-footer {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
          .proposal-final-footer * {
            display: none !important;
            visibility: hidden !important;
          }
          /* Prevent extra page break */
          .proposal-totals-section {
            page-break-after: auto !important;
          }
          .proposal-final-footer {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          /* Ensure all section headers match - CRITICAL for PDF */
          .proposal-totals-content-box h2,
          .proposal-project-details h2,
          .proposal-totals-section h2,
          .proposal-totals-content-box > div > div:first-child h2,
          .proposal-project-details > div:first-child h2 {
            font-size: 13px !important;
            font-weight: 400 !important;
            font-family: 'Domaine Text', serif !important;
            letter-spacing: 0.02em !important;
            color: #2C2C2C !important;
            margin: 0 !important;
          }
          .proposal-totals-section > div:last-child > div:first-child {
            width: 50% !important;
            padding-right: 40px !important;
            margin-bottom: 0 !important;
          }
          .proposal-totals-section > div:last-child > div:last-child {
            width: 50% !important;
          }
          .proposal-totals-table {
            width: 100% !important;
            font-size: 11px !important;
            margin-top: 0 !important;
          }
          .proposal-totals-table td {
            font-size: 11px !important;
            padding: 8px 0 !important;
          }
          .proposal-totals-logo {
            display: flex !important;
          }
          .proposal-project-details {
            flex-direction: row !important;
            padding-top: 30px !important;
            margin-bottom: 30px !important;
          }
          .proposal-project-details > div:first-child {
            width: 140px !important;
            padding-right: 20px !important;
            margin-bottom: 0 !important;
          }
          .proposal-project-details ul {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          .proposal-project-details li {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .proposal-project-details > div:last-child {
            width: 50% !important;
          }
          .proposal-project-details h2 {
            font-size: 20px !important;
            margin-bottom: 12px !important;
          }
          .proposal-project-details p,
          .proposal-project-details ul {
            font-size: 11px !important;
          }
          .proposal-image-page {
            padding: 30px 60px !important;
            min-height: 100vh !important;
          }
          .proposal-image-page img {
            max-height: none !important;
          }
          /* Override cover page mobile styles for print */
          .proposal-cover-page {
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
            padding: 60px !important;
            min-height: 100vh !important;
            height: 100vh !important;
            margin-top: 0 !important;
          }
          .proposal-cover-content {
            gap: 20px !important;
          }
          .proposal-cover-content > div:first-child {
            margin-top: 0 !important;
          }
          .proposal-cover-content h1 {
            font-size: 36px !important;
          }
          .proposal-cover-content p {
            font-size: 14px !important;
          }
          .proposal-final-footer {
            display: block !important;
            margin-top: 30px !important;
            padding-top: 0 !important;
            border-top: none !important;
            border: none !important;
            border-bottom: none !important;
            border-left: none !important;
            border-right: none !important;
            outline: none !important;
            box-shadow: none !important;
            width: 700px !important;
            max-width: 700px !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .proposal-final-footer::before,
          .proposal-final-footer::after {
            display: none !important;
            content: none !important;
            border: none !important;
          }
          .proposal-final-footer {
            width: 100% !important;
            max-width: 700px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 0 !important;
          }
          .proposal-final-footer > div {
            font-size: 10px !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            border-top: none !important;
            border: none !important;
            border-bottom: none !important;
            border-left: none !important;
            border-right: none !important;
            outline: none !important;
            box-shadow: none !important;
            width: 100% !important;
            max-width: 700px !important;
            padding: 0 !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }
          .proposal-final-footer > div > div:first-child,
          .proposal-final-footer > div > div:nth-child(2) {
            flex-shrink: 0 !important;
            flex-grow: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .proposal-final-footer > div > div:last-child {
            text-align: right !important;
            margin-right: 0 !important;
            margin-left: auto !important;
            padding-right: 0 !important;
            padding-left: 0 !important;
            flex-shrink: 0 !important;
            flex-grow: 0 !important;
            white-space: nowrap !important;
          }
          /* Hide footer completely in print/PDF */
          .proposal-final-footer {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
          .proposal-final-footer * {
            display: none !important;
            visibility: hidden !important;
          }
          /* Ensure print button is hidden on mobile */
          .proposal-mobile-hide {
            display: none !important;
          }
        }
      ` }} />
      
      {/* Navigation bar - hidden when printing. */}
      {(() => {
        const hasDiscount = (parseFloat(proposal.discountValue || proposal.discount || 0) || 0) > 0;
        const pricingToggle = (hasDiscount || isClientShareableLink) ? (
          <div
            role="group"
            aria-label="Discount line on proposal and PDF"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            <span style={{ fontSize: '11px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", color: '#8a8378' }}>
              Show Preferred Pricing
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={showDiscount}
              aria-label={showDiscount ? 'Hide preferred pricing' : 'Show preferred pricing'}
              onClick={() => setShowDiscount(s => !s)}
              style={{
                width: 32,
                height: 18,
                borderRadius: 9,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                position: 'relative',
                backgroundColor: showDiscount ? '#8a8378' : '#d8d0c4',
              }}
            >
              <span style={{
                position: 'absolute',
                top: 2,
                left: showDiscount ? 16 : 2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: '#fff',
                transition: 'left 0.2s ease',
              }} />
            </button>
          </div>
        ) : null;
        return (
          <div className="no-print proposal-nav-bar" ref={setChromeNode} style={{ position: 'fixed', top: 0, left: 0, right: 0, backgroundColor: 'transparent', borderBottom: 'none', zIndex: 1000, padding: 0 }}>
            {isChangeRequestMode ? (
              <RequestChangesModeHeader
                onCancel={handleCancelRequestChanges}
                subtitle={showRequestChangesGuidance ? '' : undefined}
                utility={pricingToggle}
              />
            ) : (
              <>
                <ProposalViewHeader
                  clientName={proposal.clientName}
                  eventName={proposal.venueName}
                  eventDate={formatDateRange(proposal)}
                  showPricingToggle={hasDiscount || isClientShareableLink}
                  showDiscount={showDiscount}
                  onToggleDiscount={() => setShowDiscount(s => !s)}
                  onExportPdf={handlePrintDownload}
                  onRequestChanges={enterRequestChangesMode}
                  onAcceptProposal={() => {
                    if (pendingReview) return;
                    dismissClientWelcome();
                    setAcceptProposalOpen(true);
                  }}
                  acceptanceUiState={acceptanceUiState}
                  canReplayConfirmation={canReplayConfirmation}
                  storedAcceptance={storedAcceptance}
                  version={proposal.version}
                  pendingReview={pendingReview}
                  onAboutProposal={reopenClientWelcome}
                  onBack={!isClientShareableLink && onBack ? onBack : undefined}
                  onLogout={!isClientShareableLink && onLogout ? onLogout : undefined}
                />
                {pendingReview ? <PendingReviewBanner onContinueEditing={enterRequestChangesMode} /> : null}
              </>
            )}
          </div>
        );
      })()}

      {showClientWelcome ? (
        <ProposalWelcomePanel
          onDismiss={dismissClientWelcome}
          onStartReviewing={startReviewingProposal}
        />
      ) : null}

      {showRequestChangesGuidance ? (
        <RequestChangesGuidancePanel
          onDismiss={() => setRequestChangesGuidanceOpen(false)}
        />
      ) : null}

      <AcceptProposalFlow
        open={acceptProposalOpen}
        proposal={proposal}
        clientProfile={agreementClientProfile}
        source="reserve"
        contactsPrefillUrl={PUBLIC_PROPOSAL_API_BASE}
        onClose={() => setAcceptProposalOpen(false)}
        onAccepted={(acceptance) => setLocalAcceptance(acceptance)}
        onSubmit={(payload) => submitProposalAcceptance(payload, {
          adminApiUrl: CLIENT_ACCEPTANCE_API,
        })}
      />
      
      {/* COVER PAGE */}
      <div
        className="print-break-after proposal-cover-page"
        style={{
          backgroundColor: '#FFFFFF',
          minHeight: '100vh',
          height: '100vh',
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          position: 'relative',
          boxSizing: 'border-box',
          margin: 0,
          pageBreakAfter: 'always',
          pageBreakBefore: 'auto',
          overflow: 'hidden'
        }}
      >
        <div className="proposal-cover-content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <img 
              src="/mayker_wordmark-events-black.svg" 
              alt="MAYKER EVENTS" 
              style={{ height: '28px', width: 'auto', marginBottom: '16px' }} 
              onError={(e) => {
                if (!e.target.src.includes('/assets/')) {
                  e.target.src = '/assets/mayker_wordmark-events-black.svg';
                } else if (!e.target.src.includes('cdn')) {
                  e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_wordmark-events-black.svg';
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
            <div style={{ width: '40px', height: '0.5px', backgroundColor: 'rgba(44,44,44,0.22)', marginBottom: '16px' }}></div>
            <p style={{ fontSize: '13px', color: '#000000', letterSpacing: '0.1em', marginBottom: '12px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textTransform: 'uppercase' }}>Product Selections</p>
            <p style={{ fontSize: '15px', color: '#000000', marginBottom: '5px', fontWeight: '300', fontFamily: "'Domaine Text', serif" }}>{proposal.clientName ? proposal.clientName.replace(/\s*\(V\d+\)\s*$/, '') : 'Proposal'}{proposal.status === 'Approved' ? ' (Final)' : ''}</p>
            <p style={{ fontSize: '13px', color: '#666666', marginBottom: '3px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>{proposal.venueName}</p>
            {coverLabel ? (
              <p style={{ fontSize: '13px', color: '#666666', marginBottom: '3px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>{coverLabel}</p>
            ) : null}
            <p style={{ fontSize: '13px', color: '#666666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>{formatDateRange(proposal)}</p>
          </div>
          <img 
            src="/mayker_icon-black.png" 
            alt="Mayker Events" 
            style={{ width: '40px', height: '40px', marginTop: '4px' }} 
            onError={(e) => {
              if (!e.target.src.includes('/assets/')) {
                e.target.src = '/assets/mayker_icon-black.png';
              } else if (!e.target.src.includes('cdn')) {
                e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_icon-black.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
          </div>
          
      {(() => {
        const sectionPages = [];
        
        sections.forEach((section, sectionIndex) => {
          // Check if this is an image page (support both new format with type and legacy format)
          const isImagePage = (section.type === 'image' || (!section.products || section.products.length === 0)) && (section.imageDriveId || section.imageData || section.imageUrl);
          if (isImagePage) {
            sectionPages.push(
              <div 
                key={`image-${sectionIndex}`} 
                id={sectionIndex === 0 ? 'proposal-content-start' : undefined}
                className="proposal-image-page proposal-page"
                style={{ minHeight: '100vh', width: '100%', maxWidth: '100%', padding: '30px 60px 40px', position: 'relative', pageBreakBefore: sectionIndex === 0 ? 'auto' : 'always', boxSizing: 'border-box', overflow: 'hidden' }}
              >
                <PageHeader onBack={onBack} />
                
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)', flexDirection: 'column' }}>
                  <img 
                    src={section.imageDriveId ? `https://drive.google.com/uc?export=view&id=${section.imageDriveId}` : (section.imageUrl || section.imageData)} 
                    alt="Floor plan or collage" 
                    style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 200px)', objectFit: 'contain' }}
                    crossOrigin="anonymous"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <PageFooter pageNum={getNextPageNumber()} />
              </div>
            );
            return; // Skip product rendering for image pages
          }
          
          // Regular product section
          const totalPages = Math.ceil((section.products?.length || 0) / productsPerPage);
          
          for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            const startIndex = pageIndex * productsPerPage;
            const endIndex = startIndex + productsPerPage;
            const pageProducts = (section.products || []).slice(startIndex, endIndex);
            const isLastPageOfSection = pageIndex === totalPages - 1;
            const isLastSection = sectionIndex === sections.length - 1;
            const isFirstPageOfSection = pageIndex === 0;
            
            // First product page should not have pageBreakBefore to avoid blank page after cover
            const isFirstProductPage = sectionIndex === 0 && pageIndex === 0;
            
            sectionPages.push(
              <div 
                key={`${sectionIndex}-${pageIndex}`} 
                id={isFirstProductPage ? 'proposal-content-start' : undefined}
                className="proposal-page"
                style={{ minHeight: '100vh', width: '100%', maxWidth: '100%', padding: '30px 60px 40px', position: 'relative', pageBreakBefore: isFirstProductPage ? 'auto' : 'always', pageBreakAfter: 'auto', pageBreakInside: 'avoid', breakInside: 'avoid', boxSizing: 'border-box' }}
              >
                <PageHeader sectionName={isFirstPageOfSection ? section.name : null} showSectionName={isFirstPageOfSection} onBack={onBack} />
                
                <div className="proposal-product-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'min-content', gap: '14px', pageBreakInside: 'avoid', breakInside: 'avoid', width: '100%', boxSizing: 'border-box' }}>
                  {pageProducts.map((product, productIndex) => {
                    const globalProductIdx = startIndex + productIndex;
                    const productChange = requestChanges.getProductChange(sectionIndex, globalProductIdx);
                    const removalRequested = !!(productChange && (productChange.removed || productChange.newQuantity === 0));
                    return (
                    <div
                      key={globalProductIdx}
                      className={`proposal-product-card${removalRequested ? ' is-removal-requested' : ''}`}
                      style={{
                        backgroundColor: '#f9f9f9',
                        padding: '10px',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        height: 'fit-content',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ aspectRatio: '1', backgroundColor: '#e5e5e5', marginBottom: isChangeRequestMode ? 0 : '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#999', overflow: 'hidden', borderRadius: '2px' }}>
                        {proposalProductImageUrl(product, catalog) ? (
                          <img src={proposalProductImageUrl(product, catalog)} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: removalRequested ? 0.45 : 1, transition: 'opacity 0.2s ease' }} onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          '[Product Image]'
                        )}
                      </div>
                      {isChangeRequestMode && (
                        <RequestChangesProductControls
                          product={product}
                          sectionName={section.name}
                          sectionIdx={sectionIndex}
                          productIdx={globalProductIdx}
                          change={productChange}
                          onQuantityChange={requestChanges.setQuantity}
                          onRemove={requestChanges.requestRemoval}
                          onUndo={requestChanges.undoProductChange}
                        />
                      )}
                      <div style={{ padding: isChangeRequestMode ? '8px 0 0' : 0 }}>
                      <h3 className="proposal-product-name" style={{ fontSize: '10px', fontWeight: '500', color: brandCharcoal, textTransform: 'uppercase', marginTop: 0, marginBottom: '2px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", lineHeight: '1.2' }}>
                        {product.name}
                      </h3>
                      <p className="proposal-product-detail" style={{ fontSize: '9px', color: '#666', marginBottom: '2px', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", lineHeight: '1.3' }}>Quantity: {product.quantity}</p>
                      {product.dimensions && (
                        <p className="proposal-product-detail" style={{ fontSize: '9px', color: '#666', marginBottom: product.note && product.note.trim() ? '2px' : '0', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", lineHeight: '1.3' }}>Size: {product.dimensions}</p>
                      )}
                      {product.note && product.note.trim() && (
                        <p className="proposal-product-detail" style={{ fontSize: '9px', color: '#666', marginBottom: '0', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", lineHeight: '1.3' }}>Product Note: {product.note}</p>
                      )}
                      </div>
                    </div>
                    );
                  })}
                </div>
                {isChangeRequestMode && isLastPageOfSection && (
                  <>
                    <RequestAnotherProduct
                      variant="list"
                      sectionName={section.name || ''}
                      draft={requestChanges.draft}
                      onRemove={requestChanges.removeNewProductRequest}
                    />
                    {sectionIndex === lastProductSectionIndex && (
                      <RequestAnotherProduct
                        variant="list"
                        unmatchedOnly
                        sections={productSectionNames}
                        draft={requestChanges.draft}
                        onRemove={requestChanges.removeNewProductRequest}
                      />
                    )}
                  </>
                )}
                <PageFooter pageNum={getNextPageNumber()} />
              </div>
            );
          }
        });
        
        return sectionPages;
      })()}

      {(() => {
        // Collect all products (excluding image pages)
        const allProducts = [];
        sections.forEach((section, sectionIndex) => {
          // Skip image pages
          if (section.type === 'image') return;
          
          (section.products || []).forEach((product, productIndex) => {
            allProducts.push({ section, sectionIndex, product, productIndex });
          });
        });
        
        // Helper: Detect if discount exists (uses separate discount value field)
        const hasDiscount = () => {
          const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
          return discountValue > 0;
        };
        
        // Helper: Calculate preferred prices for all products
        // Uses the same logic as profitability calculation: discount applies to BASE price, not extended
        const calculatePreferredPrices = () => {
          if (!hasDiscount()) {
            return null; // No discount, return null to use simple display
          }
          
          const discountType = proposal.discountType || 'percentage';
          const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
          const rentalMultiplier = totals.rentalMultiplier || 1.0;
          
          // Calculate base product total (pre-extended rental)
          let baseProductTotal = 0;
          allProducts.forEach(({ product }) => {
            baseProductTotal += (product.price || 0) * (product.quantity || 0);
          });
          
          // Calculate total discount amount (same logic as profitability calculation)
          let standardRateDiscount = 0;
          if (discountType === 'dollar') {
            // Direct dollar amount discount
            standardRateDiscount = discountValue;
          } else {
            // Percentage discount is based on product total only, prior to any extended rental fees
            standardRateDiscount = baseProductTotal * (discountValue / 100);
          }
          
          // Calculate preferred prices for each product
          const preferredPrices = {};
          
          allProducts.forEach(({ sectionIndex, productIndex, product }) => {
            const basePrice = product.price || 0;
            const quantity = product.quantity || 0;
            
            // 1. Calculate extended standard price per unit
            const extendedStandardPrice = basePrice * rentalMultiplier;
            
            // 2. Calculate discount per unit (proportional to base price, matching profitability logic)
            let discountPerUnit = 0;
            if (baseProductTotal > 0 && standardRateDiscount > 0) {
              const productBaseTotal = basePrice * quantity;
              const productDiscountShare = (productBaseTotal / baseProductTotal) * standardRateDiscount;
              discountPerUnit = quantity > 0 ? productDiscountShare / quantity : 0;
            }
            
            // 3. Preferred price = extended standard - discount per unit
            const preferredPrice = extendedStandardPrice - discountPerUnit;
            
            preferredPrices[`${sectionIndex}-${productIndex}`] = {
              standard: extendedStandardPrice,
              preferred: Math.round(preferredPrice * 100) / 100
            };
          });
          
          // Ensure totals reconcile by adjusting last item if rounding creates remainder
          let calculatedTotal = 0;
          allProducts.forEach(({ sectionIndex, productIndex, product }) => {
            const key = `${sectionIndex}-${productIndex}`;
            calculatedTotal += preferredPrices[key].preferred * (product.quantity || 0);
          });
          
          const expectedTotal = totals.rentalTotal;
          const difference = expectedTotal - calculatedTotal;
          
          // Apply rounding difference to last product deterministically
          if (Math.abs(difference) > 0.01 && allProducts.length > 0) {
            const lastItem = allProducts[allProducts.length - 1];
            const lastKey = `${lastItem.sectionIndex}-${lastItem.productIndex}`;
            const adjustmentPerUnit = difference / (lastItem.product.quantity || 1);
            preferredPrices[lastKey].preferred = Math.round((preferredPrices[lastKey].preferred + adjustmentPerUnit) * 100) / 100;
          }
          
          return preferredPrices;
        };
        
        const preferredPrices = calculatePreferredPrices();
        const showDiscountColumns = hasDiscount() && preferredPrices !== null && showDiscount;
        const qbInvoiceNum = String(proposal.quickbooksInvoiceId || '').trim();
        const invoiceTitleFirstPage = qbInvoiceNum ? `Invoice ${qbInvoiceNum}` : 'Invoice';

        // Mobile Invoice View - Itemized format (mobile only)
        const MobileInvoiceView = () => {
          // Generate order number from proposal ID or timestamp
          const orderNumber = proposal.projectNumber || proposal.id || String(Date.now()).slice(-10);
          const invoiceDate = proposal.startDate ? parseDateSafely(proposal.startDate) : new Date();
          const formattedDate = formatDisplayDate(invoiceDate);
          
          return (
            <div className="no-print mobile-invoice-view" style={{ 
              backgroundColor: 'white', 
              padding: '20px 16px 0px',
              color: brandCharcoal,
              paddingTop: '80px' // Account for fixed header
            }}>
              {/* Invoice Header */}
              <div style={{ marginBottom: '20px' }}>
                <h1 style={{ 
                  fontSize: '20px', 
                  fontWeight: '300', 
                  color: brandCharcoal, 
                  marginBottom: '20px',
                  textAlign: 'left',
                  fontFamily: "'Domaine Text', serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em'
                }}>
                  {invoiceTitleFirstPage}
                </h1>
              </div>
              
              {/* Itemized List */}
              <div style={{ marginBottom: '0px', paddingBottom: '0px' }}>
                {/* Column Headers */}
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: showDiscountColumns ? '2fr 0.7fr 1fr 1fr 1fr' : '2fr 0.7fr 1.5fr 1.5fr',
                  gap: '16px',
                  paddingBottom: '10px',
                  borderBottom: '1px solid #e5e7eb',
                  marginBottom: '12px'
                }}>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#666',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'left'
                  }}>
                    Product
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#666',
                    fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'right'
                  }}>
                    Qty
                  </div>
                  {showDiscountColumns ? (
                    <>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'right'
                      }}>
                        Standard Rate
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'right'
                      }}>
                        Preferred Rate
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'right'
                      }}>
                        Total
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'right'
                      }}>
                        Unit Price
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#666',
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'right'
                      }}>
                        Total
                      </div>
                    </>
                  )}
                </div>
                
                {/* Items */}
                {allProducts.map((item, index) => {
                  const { sectionIndex, productIndex, product } = item;
                  const extendedPrice = product.price * totals.rentalMultiplier;
                  const priceKey = `${sectionIndex}-${productIndex}`;
                  
                  let standardPrice = extendedPrice;
                  let preferredPrice = extendedPrice;
                  let lineTotal = extendedPrice * product.quantity;
                  
                  if (showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                    standardPrice = preferredPrices[priceKey].standard;
                    preferredPrice = preferredPrices[priceKey].preferred;
                    lineTotal = preferredPrice * product.quantity;
                  } else if (!showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                    preferredPrice = preferredPrices[priceKey].preferred;
                    lineTotal = preferredPrice * product.quantity;
                  }

                  // Complimentary client upgrade: keep the real standard/preferred rates
                  // (never $0) and show a $0 "Complimentary" final total.
                  const rowUpgrade = getComplimentaryRowDisplay(product);
                  if (rowUpgrade.isComplimentary) {
                    standardPrice = rowUpgrade.standardUnitPrice;
                    preferredPrice = rowUpgrade.preferredUnitPrice;
                    lineTotal = 0;
                  }
                  const totalCellDisplay = rowUpgrade.isComplimentary ? 'Complimentary' : `$${formatNumber(lineTotal)}`;
                  const unitCellDisplay = rowUpgrade.isComplimentary
                    ? `$${formatNumber(standardPrice)}`
                    : `$${formatNumber(preferredPrices && preferredPrices[priceKey] ? preferredPrice : extendedPrice)}`;
                  
                  return (
                    <div 
                      key={index}
                      style={{ 
                        display: 'grid',
                        gridTemplateColumns: showDiscountColumns ? '2fr 0.7fr 1fr 1fr 1fr' : '2fr 0.7fr 1.5fr 1.5fr',
                        gap: '16px',
                        paddingBottom: index < allProducts.length - 1 ? '14px' : '0px',
                        marginBottom: index < allProducts.length - 1 ? '14px' : '0px',
                        borderBottom: index < allProducts.length - 1 ? '1px solid #f8f8f8' : 'none'
                      }}
                    >
                      <div style={{ 
                        fontSize: '13px', 
                        color: brandCharcoal,
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        lineHeight: '1.4',
                        fontWeight: '400',
                        textAlign: 'left'
                      }}>
                        {product.name}
                        {rowUpgrade.isComplimentary && (
                          <div style={{ fontSize: '10px', fontStyle: 'italic', color: '#8a8378', marginTop: '2px', letterSpacing: '0.04em', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                            Complimentary Upgrade
                          </div>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        color: brandCharcoal,
                        fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                        textAlign: 'right',
                        fontWeight: '400'
                      }}>
                        {product.quantity}
                      </div>
                      {showDiscountColumns ? (
                        <>
                          <div className="standard-rate-cell" style={{ 
                            fontSize: '13px', 
                            color: brandCharcoal,
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textAlign: 'right',
                            fontStyle: 'italic'
                          }}>
                            ${formatNumber(standardPrice)}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: brandCharcoal,
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textAlign: 'right',
                            fontWeight: '400'
                          }}>
                            ${formatNumber(preferredPrice)}
                          </div>
                          <div style={{ 
                            fontSize: rowUpgrade.isComplimentary ? '12px' : '13px', 
                            color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal,
                            fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textAlign: 'right',
                            fontWeight: '400'
                          }}>
                            {totalCellDisplay}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ 
                            fontSize: '13px', 
                            color: brandCharcoal,
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textAlign: 'right',
                            fontWeight: '400'
                          }}>
                            {unitCellDisplay}
                          </div>
                          <div style={{ 
                            fontSize: rowUpgrade.isComplimentary ? '12px' : '13px', 
                            color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal,
                            fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal',
                            fontFamily: "'Neue Haas Unica', 'Inter', sans-serif",
                            textAlign: 'right',
                            fontWeight: '400'
                          }}>
                            {totalCellDisplay}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        };
        
        // Calculate how many products fit per page (approximately 23 rows)
        const rowsPerPage = 23;
        const totalInvoicePages = Math.ceil(allProducts.length / rowsPerPage);
        
        // Invoice header component
        const InvoiceHeader = ({ isFirstPage }) => (
          <div className="proposal-invoice-header" style={{ marginBottom: '30px', pageBreakInside: 'avoid', breakInside: 'avoid', display: 'block', visibility: 'visible' }}>
            <PageHeader />
            {/* INVOICE title */}
            <h2 style={{ fontSize: '14px', fontWeight: '300', color: brandCharcoal, marginTop: '15px', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.02em', textAlign: 'center', fontFamily: "'Domaine Text', serif" }}>
              {isFirstPage ? invoiceTitleFirstPage : 'Invoice (Cont.)'}
            </h2>
            {/* Column headers */}
            <table className="proposal-invoice-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', borderSpacing: 0, marginBottom: '0' }}>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: showDiscountColumns ? '35%' : '50%', minWidth: '200px' }} />
                <col style={{ width: '10%' }} />
                {showDiscountColumns ? (
                  <>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '14%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '12.5%' }} />
                    <col style={{ width: '12.5%' }} />
                  </>
                )}
              </colgroup>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'left', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Section</th>
                  <th style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'left', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Product</th>
                  <th className="proposal-invoice-qty-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Qty</th>
                  {showDiscountColumns ? (
                    <>
                      <th className="proposal-invoice-price-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Standard Rate</th>
                      <th className="proposal-invoice-price-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Preferred Rate</th>
                      <th className="proposal-invoice-total-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Total</th>
                    </>
                  ) : (
                    <>
                      <th className="proposal-invoice-price-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Unit Price</th>
                      <th className="proposal-invoice-total-header" style={{ padding: '8px 0', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>Total</th>
                    </>
                  )}
                </tr>
              </thead>
            </table>
          </div>
        );
        
        // Create invoice pages
        const invoicePages = [];
        for (let pageIndex = 0; pageIndex < totalInvoicePages; pageIndex++) {
          const startIndex = pageIndex * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const pageProducts = allProducts.slice(startIndex, endIndex);
          const isLastPage = pageIndex === totalInvoicePages - 1;
          const isFirstPage = pageIndex === 0;
          
          const currentPageNum = getNextPageNumber();
          invoicePages.push(
            <div 
              key={`invoice-page-${pageIndex}`} 
              className="proposal-page"
              style={{ 
                minHeight: '100vh', 
                width: '100%',
                maxWidth: '100%',
                padding: '30px 60px',
                paddingBottom: '60px',
                position: 'relative',
                pageBreakBefore: pageIndex > 0 ? 'always' : 'always',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {isLastPage ? (
                <>
                  <div style={{ flex: '0 0 auto', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <InvoiceHeader isFirstPage={isFirstPage} />
                  </div>
                  
                  <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <table className="proposal-invoice-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', borderSpacing: 0 }}>
                      <colgroup>
                        <col style={{ width: '15%' }} />
                        <col style={{ width: showDiscountColumns ? '35%' : '50%', minWidth: '200px' }} />
                        <col style={{ width: '10%' }} />
                        {showDiscountColumns ? (
                          <>
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '14%' }} />
                          </>
                        ) : (
                          <>
                            <col style={{ width: '12.5%' }} />
                            <col style={{ width: '12.5%' }} />
                          </>
                        )}
                      </colgroup>
                      <tbody>
                        {pageProducts.map((item, pageItemIndex) => {
                          const { section, sectionIndex, product, productIndex } = item;
                          const extendedPrice = product.price * totals.rentalMultiplier;
                          const priceKey = `${sectionIndex}-${productIndex}`;
                          
                          let standardPrice = extendedPrice;
                          let preferredPrice = extendedPrice;
                          let lineTotal = extendedPrice * product.quantity;
                          
                          if (showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                            standardPrice = preferredPrices[priceKey].standard;
                            preferredPrice = preferredPrices[priceKey].preferred;
                            lineTotal = preferredPrice * product.quantity;
                          } else if (!showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                            preferredPrice = preferredPrices[priceKey].preferred;
                            lineTotal = preferredPrice * product.quantity;
                          }

                          const rowUpgrade = getComplimentaryRowDisplay(product);
                          if (rowUpgrade.isComplimentary) {
                            standardPrice = rowUpgrade.standardUnitPrice;
                            preferredPrice = rowUpgrade.preferredUnitPrice;
                            lineTotal = 0;
                          }
                          const totalCellDisplay = rowUpgrade.isComplimentary ? 'Complimentary' : `$${formatNumber(lineTotal)}`;
                          const unitCellDisplay = rowUpgrade.isComplimentary
                            ? `$${formatNumber(standardPrice)}`
                            : `$${formatNumber(preferredPrices && preferredPrices[priceKey] ? preferredPrice : extendedPrice)}`;
                          
                          const showSectionName = pageItemIndex === 0 || 
                            (pageItemIndex > 0 && pageProducts[pageItemIndex - 1].sectionIndex !== sectionIndex) ||
                            productIndex === 0;
                          
                          return (
                            <tr key={`${sectionIndex}-${productIndex}`} style={{ borderBottom: '1px solid #f8f8f8' }}>
                              <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: '#888', fontStyle: 'italic', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                                {showSectionName ? section.name : ''}
                              </td>
                              <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.4' }}>
                                {product.name}
                                {rowUpgrade.isComplimentary && (
                                  <div style={{ fontSize: '8.5px', fontStyle: 'italic', color: '#8a8378', marginTop: '2px', letterSpacing: '0.04em', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                                    Complimentary Upgrade
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                                {product.quantity}
                              </td>
                              {showDiscountColumns ? (
                                <>
                                  <td className="standard-rate-cell" style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                    ${formatNumber(standardPrice)}
                                  </td>
                                  <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                    ${formatNumber(preferredPrice)}
                                  </td>
                                  <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: rowUpgrade.isComplimentary ? '10px' : '11px', color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal, fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                    {totalCellDisplay}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                    {unitCellDisplay}
                                  </td>
                                  <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: rowUpgrade.isComplimentary ? '10px' : '11px', color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal, fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                    {totalCellDisplay}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ flex: '0 0 auto' }}>
                    <InvoiceHeader isFirstPage={isFirstPage} />
                  </div>
                  
                  <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                    <table className="proposal-invoice-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', borderSpacing: 0 }}>
                    <colgroup>
                      <col style={{ width: '15%' }} />
                      <col style={{ width: showDiscountColumns ? '35%' : '50%', minWidth: '200px' }} />
                      <col style={{ width: '10%' }} />
                      {showDiscountColumns ? (
                        <>
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '14%' }} />
                        </>
                      ) : (
                        <>
                          <col style={{ width: '12.5%' }} />
                          <col style={{ width: '12.5%' }} />
                        </>
                      )}
                    </colgroup>
                    <tbody>
                      {pageProducts.map((item, pageItemIndex) => {
                        const { section, sectionIndex, product, productIndex } = item;
                        const extendedPrice = product.price * totals.rentalMultiplier;
                        const priceKey = `${sectionIndex}-${productIndex}`;
                        
                        let standardPrice = extendedPrice;
                        let preferredPrice = extendedPrice;
                        let lineTotal = extendedPrice * product.quantity;
                        
                        if (showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                          standardPrice = preferredPrices[priceKey].standard;
                          preferredPrice = preferredPrices[priceKey].preferred;
                          lineTotal = preferredPrice * product.quantity;
                        } else if (!showDiscountColumns && preferredPrices && preferredPrices[priceKey]) {
                          preferredPrice = preferredPrices[priceKey].preferred;
                          lineTotal = preferredPrice * product.quantity;
                        }

                        const rowUpgrade = getComplimentaryRowDisplay(product);
                        if (rowUpgrade.isComplimentary) {
                          standardPrice = rowUpgrade.standardUnitPrice;
                          preferredPrice = rowUpgrade.preferredUnitPrice;
                          lineTotal = 0;
                        }
                        const totalCellDisplay = rowUpgrade.isComplimentary ? 'Complimentary' : `$${formatNumber(lineTotal)}`;
                        const unitCellDisplay = rowUpgrade.isComplimentary
                          ? `$${formatNumber(standardPrice)}`
                          : `$${formatNumber(preferredPrices && preferredPrices[priceKey] ? preferredPrice : extendedPrice)}`;
                        
                        const showSectionName = pageItemIndex === 0 || 
                          (pageItemIndex > 0 && pageProducts[pageItemIndex - 1].sectionIndex !== sectionIndex) ||
                          productIndex === 0;
                        
                        return (
                          <tr key={`${sectionIndex}-${productIndex}`} style={{ borderBottom: '1px solid #f8f8f8' }}>
                            <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: '#888', fontStyle: 'italic', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                              {showSectionName ? section.name : ''}
                            </td>
                            <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.4' }}>
                              {product.name}
                              {rowUpgrade.isComplimentary && (
                                <div style={{ fontSize: '8.5px', fontStyle: 'italic', color: '#8a8378', marginTop: '2px', letterSpacing: '0.04em', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                                  Complimentary Upgrade
                                </div>
                              )}
                            </td>
                            <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                              {product.quantity}
                            </td>
                            {showDiscountColumns ? (
                              <>
                                <td className="standard-rate-cell" style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                  ${formatNumber(standardPrice)}
                                </td>
                                <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                  ${formatNumber(preferredPrice)}
                                </td>
                                <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: rowUpgrade.isComplimentary ? '10px' : '11px', color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal, fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                  {totalCellDisplay}
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                  {unitCellDisplay}
                                </td>
                                <td style={{ padding: pageItemIndex === 0 ? '5px 0 10px 0' : '10px 0', fontSize: rowUpgrade.isComplimentary ? '10px' : '11px', color: rowUpgrade.isComplimentary ? '#8a8378' : brandCharcoal, fontStyle: rowUpgrade.isComplimentary ? 'italic' : 'normal', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'nowrap', fontWeight: '400' }}>
                                  {totalCellDisplay}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
              <PageFooter pageNum={currentPageNum} />
            </div>
          );
        }
        
        return (
          <>
            {/* Mobile Invoice View - Only visible on mobile */}
            <MobileInvoiceView />
            
            {/* Desktop Invoice View - Only visible on desktop */}
            <div className="desktop-invoice-view">
              {invoicePages}
            </div>
          </>
        );
      })()}

      {(() => {
        const currentPageNum = getNextPageNumber();
        return (
          <div key="totals-and-details" className="proposal-page proposal-totals-section" style={{ minHeight: '100vh', width: '100%', maxWidth: '100%', padding: '50px 80px 40px', position: 'relative', pageBreakBefore: 'always', boxSizing: 'border-box', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Template-style header - logo only (hidden on mobile) */}
            <div className="proposal-totals-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '35px' }}>
              <div className="no-print">
                <img 
                  src="/mayker_round-stamp-lines-black.png" 
                  alt="Mayker" 
                  onError={(e) => {
                    if (!e.target.src.includes('/assets/')) {
                      e.target.src = '/assets/mayker_round-stamp-lines-black.png';
                    } else if (!e.target.src.includes('cdn')) {
                      e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_round-stamp-lines-black.png';
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                  style={{ height: '120px', width: 'auto', maxWidth: '400px' }} 
                />
              </div>
              {/* Print version - non-clickable */}
              <div className="print-only">
                <img 
                  src="/mayker_round-stamp-lines-black.png" 
                  alt="Mayker" 
                  onError={(e) => {
                    if (!e.target.src.includes('/assets/')) {
                      e.target.src = '/assets/mayker_round-stamp-lines-black.png';
                    } else if (!e.target.src.includes('cdn')) {
                      e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_round-stamp-lines-black.png';
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                  style={{ height: '120px', width: 'auto', maxWidth: '400px' }} 
                />
              </div>
            </div>
            
            {/* Content container with border */}
            <div className="proposal-totals-content-box" style={{ flex: '1', border: '1px solid #2C2C2C', padding: '40px 35px 30px', backgroundColor: 'white', maxWidth: '700px', margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Totals Section - Two Column Layout */}
              <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ width: '140px', flexShrink: 0, paddingRight: '20px' }}>
                  <h2 style={{ fontSize: '13px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Domaine Text', serif", letterSpacing: '0.02em', margin: 0 }}>
                    Total
                  </h2>
                </div>
                <div className="no-page-break" style={{ flex: '1', minWidth: 0 }}>
                  <table className="no-page-break proposal-totals-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left', width: '50%' }}>Product Subtotal</td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", width: '50%' }}>
                          ${formatNumber(displayTotals.productSubtotal + complimentaryPresentation.complimentaryStandardValue)}
                        </td>
                      </tr>
                      {displayTotals.standardRateDiscount > 0 && (
                        <tr>
                          <td style={{ padding: '6px 0', fontSize: '11px', color: '#059669', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>
                            {displayTotals.discountType === 'dollar'
                              ? 'Discount'
                              : `Discount (${proposal.discount || proposal.discountValue || 0}% off)`}
                          </td>
                          <td style={{ padding: '6px 0', fontSize: '11px', color: '#059669', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                            -${formatNumber(displayTotals.standardRateDiscount + complimentaryPresentation.complimentaryDiscountAddBack)}
                          </td>
                        </tr>
                      )}
                      {complimentaryPresentation.hasComplimentary && (
                        <tr>
                          <td style={{ padding: '6px 0', fontSize: '11px', color: '#059669', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>
                            {complimentaryPresentation.complimentaryCount > 1 ? 'Complimentary Upgrades' : 'Complimentary Upgrade'}
                          </td>
                          <td style={{ padding: '6px 0', fontSize: '11px', color: '#059669', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                            -${formatNumber(complimentaryPresentation.complimentaryWaivedValue)}
                          </td>
                        </tr>
                      )}
                      <tr style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 0', fontSize: '11px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>Product Total</td>
                        <td style={{ padding: '8px 0', fontSize: '11px', fontWeight: '400', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.rentalTotal)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>{displayTotals.productCareFlatDollars != null ? 'Product Care Fee' : `Product Care ${displayTotals.productCareFeeLabelSuffix || '(10%)'}`}</td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.productCare)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>{displayTotals.serviceFeeFlatDollars != null ? 'Service Fee' : `Service Fee ${displayTotals.serviceFeeLabelSuffix || '(5%)'}`}</td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.serviceFee)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>Delivery</td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.delivery)}</td>
                      </tr>
                      <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>Subtotal</td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.subtotal)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>
                          Tax ({proposal.taxRate || 9.75}%)
                        </td>
                        <td style={{ padding: '6px 0', fontSize: '11px', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.tax)}</td>
                      </tr>
                      {/* Credit Card Fee shows only when it is charged. When it is off it is not a
                          discount, so the line is hidden (same as the Admin Dashboard view). */}
                      {!displayTotals.waiveCreditCardFee && (
                        <tr>
                          <td style={{ padding: '6px 0', fontSize: '11px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>Credit Card Fee (3%)</td>
                          <td style={{ padding: '6px 0', fontSize: '11px', textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                            <span style={{ color: brandCharcoal }}>${formatNumber(displayTotals.creditCardFee || 0)}</span>
                          </td>
                        </tr>
                      )}
                      <tr style={{ borderTop: '1px solid #2C2C2C' }}>
                        <td style={{ padding: '10px 0', fontSize: '11px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", textAlign: 'left' }}>Total</td>
                        <td style={{ padding: '10px 0', fontSize: '11px', fontWeight: '400', color: brandCharcoal, textAlign: 'right', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>${formatNumber(displayTotals.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Project Description Section - Two Column Layout */}
              {invoiceSectionVisibility.showProjectDescription ? (
              <div className="proposal-project-details" style={{ paddingTop: '30px', borderTop: '1px solid #e5e7eb', marginBottom: '30px', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ width: '140px', flexShrink: 0, paddingRight: '20px' }}>
                  <h2 style={{ fontSize: '13px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Domaine Text', serif", letterSpacing: '0.02em', margin: 0 }}>
                    Project Description
                  </h2>
                </div>
                <div style={{ flex: '1', minWidth: 0 }}>
                  <p style={{ marginBottom: '12px', fontSize: '12px', lineHeight: '1.5', color: '#444', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    The quoted delivery fee reflects the current rental scope and delivery details. If project needs change, we can adjust, but fees may be updated accordingly:
                  </p>
                  <ul style={{ fontSize: '12px', lineHeight: '1.5', marginBottom: '0', marginTop: '0', color: '#444', listStyle: 'none', padding: 0, fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    <li style={{ marginBottom: '0', paddingBottom: '0' }}><span style={{ fontWeight: '400' }}>Project Address:</span> {proposal.venueName}, {proposal.city}, {proposal.state}</li>
                    <li style={{ marginBottom: '0', paddingBottom: '0' }}><span style={{ fontWeight: '400' }}>Delivery Date:</span> {formatDisplayDate(proposal.startDate) || ''}</li>
                    <li style={{ marginBottom: '0', paddingBottom: '0' }}><span style={{ fontWeight: '400' }}>Preferred Delivery Window:</span> {proposal.deliveryTime || 'TBD'}</li>
                    <li style={{ marginBottom: '0', paddingBottom: '0' }}><span style={{ fontWeight: '400' }}>Pick-Up Date:</span> {formatDisplayDate(proposal.endDate) || ''}</li>
                    <li style={{ marginBottom: '0', paddingBottom: '0' }}><span style={{ fontWeight: '400' }}>Preferred Pick-Up Window:</span> {proposal.strikeTime || 'TBD'}</li>
                  </ul>
                </div>
              </div>
              ) : null}

              {invoiceSectionVisibility.showCustomProjectNotes ? (
              <div style={{ paddingTop: '30px', paddingBottom: '15px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ width: '140px', flexShrink: 0, paddingRight: '20px' }}>
                  <h2 style={{ fontSize: '13px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Domaine Text', serif", letterSpacing: '0.02em', margin: 0 }}>
                    Custom Project Notes
                  </h2>
                </div>
                <div style={{ flex: '1', minWidth: 0 }}>
                  <p style={{ marginBottom: '0', fontSize: '12px', lineHeight: '1.5', color: '#444', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", whiteSpace: 'pre-wrap' }}>
                    {invoiceSectionVisibility.visibleCustomProjectNotes}
                  </p>
                </div>
              </div>
              ) : null}
              
              {/* Confirmation and Payment Section */}
              <div style={{ paddingTop: '30px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ width: '140px', flexShrink: 0, paddingRight: '20px' }}>
                  <h2 style={{ fontSize: '13px', fontWeight: '400', color: brandCharcoal, fontFamily: "'Domaine Text', serif", letterSpacing: '0.02em', margin: 0 }}>
                    Confirmation and Payment
                  </h2>
                </div>
                <div style={{ flex: '1', minWidth: 0 }}>
                  <p style={{ marginBottom: '0', fontSize: '12px', lineHeight: '1.5', color: '#444', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif" }}>
                    {confirmationAndPaymentSentence(proposal)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Template-style footer - outside the bordered container */}
            <div className="proposal-final-footer" style={{ marginTop: '70px', paddingTop: '0', width: '100%', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#666', fontFamily: "'Neue Haas Unica', 'Inter', sans-serif", width: '100%', maxWidth: '700px', boxSizing: 'border-box' }}>
                <div 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation();
                    try {
                      if (onBack && typeof onBack === 'function') {
                        onBack();
                      } else {
                        window.location.href = window.location.origin + window.location.pathname.split('/').slice(0, -1).join('/') || '/';
                      }
                    } catch (error) {
                      window.location.href = window.location.origin;
                    }
                  }} 
                  style={{ 
                    textDecoration: 'none', 
                    cursor: 'pointer',
                    display: 'inline-block',
                    userSelect: 'none',
                    pointerEvents: 'auto',
                    transition: 'opacity 0.2s',
                    flexShrink: 0,
                    flexGrow: 0
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  className="no-print"
                >
                  <img 
                    src="/mayker_wordmark-events-black.svg" 
                    alt="MAYKER EVENTS" 
                    onError={(e) => {
                      if (!e.target.src.includes('/assets/')) {
                        e.target.src = '/assets/mayker_wordmark-events-black.svg';
                      } else if (!e.target.src.includes('cdn')) {
                        e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_wordmark-events-black.svg';
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                    style={{ height: '24px', width: 'auto', maxWidth: '250px', display: 'block' }} 
                  />
                </div>
                {/* Print version - non-clickable */}
                <div className="print-only proposal-footer-logo" style={{ flexShrink: 0, flexGrow: 0, minWidth: 0 }}>
                  <img 
                    src="/mayker_wordmark-events-black.svg" 
                    alt="MAYKER EVENTS" 
                    onError={(e) => {
                      if (!e.target.src.includes('/assets/')) {
                        e.target.src = '/assets/mayker_wordmark-events-black.svg';
                      } else if (!e.target.src.includes('cdn')) {
                        e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_wordmark-events-black.svg';
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                    style={{ height: '24px', width: 'auto', maxWidth: '250px', display: 'block' }} 
                  />
                </div>
                <div style={{ fontSize: '11px', color: brandCharcoal, textAlign: 'right', marginLeft: 'auto', flexShrink: 0, flexGrow: 0, whiteSpace: 'nowrap' }}>events@mayker.com | (615) 970.1244</div>
              </div>
            </div>
          </div>
        );
      })()}
      {isChangeRequestMode && (
        <>
          <RequestAnotherProduct
            variant="modal"
            open={addProductOpen}
            onClose={() => setAddProductOpen(false)}
            sections={productSectionNames}
            draft={requestChanges.draft}
            catalog={portalPickerCatalog}
            catalogUrl={`${PUBLIC_PROPOSAL_API_BASE}?action=getCatalog&audience=portal`}
            onAdd={requestChanges.addNewProductRequest}
          />
          <RequestChangesStickyBar
            changeCount={requestChanges.productChangeCount}
            onAddProduct={() => setAddProductOpen(true)}
            onContinue={() => {
              setAddProductOpen(false);
              setIsReviewingChanges(true);
            }}
          />
        </>
      )}
    </div>
  );
}

// ============================================
// CHANGE REQUEST VIEW (replaced by shared review step)
// ============================================

